// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import type {
  RightsRequestCase,
  CaseListItem,
  CaseNote,
  PaginatedResponse,
  IntakeResponse,
  CaseCreatedResponse,
  PublicIntakePayload,
  AddNotePayload,
  UpdateCasePayload,
  AuditTrailResponse,
  EvidenceChecklistItem,
  EvidenceReference,
  ExportRecord,
  GrcSystem,
  DataCategory,
  ProcessingPurpose,
  RetentionPolicy,
  DataFlow,
  ProcessingActivity,
  RopaRecord,
  EvidenceItem,
  DataMappingAuditEvent,
  DataMappingExport,
  AttestationCycle,
  AttestationDepartmentTask,
  GovernanceAssignment,
  CrossBorderRiskAssessment,
  // Sprint 41.5.0 G1 — Cross-Border Filing lifecycle
  CrossBorderFiling,
  CrossBorderFilingCreateInput,
  CrossBorderFilingStatusTransition,
  CrossBorderFilingStatus,
  GrcVendor,
  VendorSubProcessor,
  VendorTransferMechanism,
  DriftEvent,
  ComplianceScore,
  DeletionPlaybook,
  DeletionExecution,
  ApprovalRecord,
  GovernanceAuditEvent,
  VendorOnboardingWorkflow,
  VendorDpaReview,
  VendorPeriodicReview,
  VendorOffboardingTask,
  ConsentPolicy,
  ConsentPolicyVersion,
  ConsentCatalogPurpose,
  ConsentCatalogLegalBase,
  ConsentWorkflow,
  ConsentCmpSnapshot,
  ChangeAwarenessEntry,
  ConsentAuditEvent,
  DpiaRecord,
  DpiaAutocompleteDraft,
  DpiaAutocompleteFieldKey,
  TiaAutocompleteDraft,
  TiaAutocompleteAcceptResult,
  TiaAutocompleteFieldKey,
  DpiaRisk,
  DpiaMitigation,
  TransferAssessment,
  CrossBorderSafeguard,
  AssessmentDashboard,
  AssessmentTriggerConfig,
  GapReport,
  AssessmentAuditEvent,
  TaxonomyDefinition,
  TaxonomyLevel,
  TaxonomyVersion,
  LabeledObject,
  LifecycleRecord,
  LifecycleEvent,
  ClassificationGap,
  ClassificationAuditEvent,
  MonitoringProgram,
  ProgramPeriod,
  TaskTemplate,
  TaskInstance,
  KpiSnapshot,
  RicCase,
  MonitoringAuditEvent,
  MonitoringChange,
  // Incidents (Sprint 1.8.0 / 21.1.0)
  Incident,
  IncidentTimelineEvent,
  IncidentDecision,
  IncidentTask,
  IncidentAuditEvent,
  BreachNotification,
  BreachNotificationForsetiDraft,
  BreachNotificationDraftResolveRequest,
  BreachNotificationDraftResolveResult,
  IncidentSupplementRequest,
  RemediationAction,
  IncidentAuthorityRequest,
  DisclosureRegisterEntry,
  // Vendor Risk (Sprint 2.1.0 / 21.0.0)
  VendorRiskScore,
  QuestionnaireInstance,
  VendorRiskSignal,
  VendorReassessmentTask,
  VendorRiskAcceptance,
  ReviewRouting,
  // Program Governance (Sprint 2.5.0)
  MaturityModel,
  MaturityAssessment,
  TrendMetric,
  ReportingPeriod,
  ExecutiveSummary,
  AuditReadiness,
  EvidenceGap,
  ProgramGovernanceAuditEvent,
  ObligationMapping,
  RaciAssignment,
  GovernanceDocument,
  DocumentAcknowledgment,
  RemediationPlan,
  AssessmentCriteria,
  // Users / Auth (Sprint 3.0.0+)
  UserResponse,
  Role,
  RoleAssignment,
  Session,
  // API Keys / Service Accounts (Sprint 3.2.0)
  ApiKeyResponse,
  ApiKeyCreateResponse,
  ServiceAccountResponse,
  ServiceAccountCreateResponse,
  // MFA (Sprint 3.1.0)
  MfaEnrollmentResponse,
  MfaConfirmationResponse,
  MfaStatus,
  // SSO (Sprint 3.1.0)
  SsoConfigResponse,
  // Delegation (Sprint 3.2.0)
  DelegationResponse,
  // Break-Glass (Sprint 3.2.0)
  BreakGlassResponse,
  // Org Settings (Sprint 3.2.0)
  IpAllowlistSettings,
  SessionSettings,
  Sector,
  SectorOverlayInstallMode,
  // Access Review (Sprint 3.3.0)
  AccessReviewCampaign,
  RoleUsageStat,
  OverPrivilegedUser,
  TrimRecommendation,
  // Webhooks (Sprint 3.6.0)
  WebhookConfig,
  WebhookDelivery,
  // Domain (Sprint 3.6.0)
  DomainMapping,
  // Export Schedules (Sprint 3.6.0)
  ExportSchedule,
  ExportRun,
  // Sprint 3.3.0: Access Review analytics & SoD additional types
  AttestationScheduleResponse,
  SodRuleResponse,
  SodExceptionResponse,
  SodViolation,
  SodCheckResult,
  AccessReviewItemResponse,
  // Sprint 3.6.0: Email Log
  EmailLogEntry,
  // Sprint 4.1.0: Privacy Scanner
  PrivacyScannerConfig,
  PrivacyScanProgress,
  PrivacyScanResult,
  ScanComparisonResult,
  // Sprint 4.2.0 / Sprint 20.6.0: Consent CMP integration & governance
  CmpWebhookConfig,
  BulkSyncJob,
  CmpPurposeMapping,
  WebConsentRecord,
  WebConsentAnalytics,
  WithdrawalPropagationTask,
  MismatchReconciliationTask,
  CmpSyncEvent,
  // Sprint 20.4.0: DSR Fulfillment
  DepartmentFulfillmentTask,
  DsrResponseTemplate,
  DsrResponseRecord,
  ExtensionRequest,
  // Sprint 43.2.0 batch 1 — Org-level retention policy
  OrgRetentionPolicy,
  // Sprint 4.4.0: Module Config
  ModuleConfig,
  // Sprint 7.6.0 / Sprint 15.7.0: Forms & Dossier
  FormTemplate,
  FormInstance,
  FormOutput,
  FormAuditEvent,
  FormCatalogEntryWithStatus,
  FormInstallStatusResponse,
  Dossier,
  // Sprint 37.1.0 + fix-sprint-20260411-B F13: orchestration contracts
  CreateWithFormPayload,
  UpdateFormValuesPayload,
  UpdateFormValuesResponse,
  SubmitFormResponse,
  FinalizeFormResponse,
  // Sprint 15.7.0: Compliance Rules
  ComplianceRuleDocument,
  // Sprint 11.5.0+: Org Groups
  OrgGroup,
  OrgGroupMembership,
  GroupSharedVendor,
  GroupSharedFramework,
  OrgSettingsInheritance,
  GroupUserAssignment,
  UserOrgMembership,
  OrgSwitchResponse,
  GroupAuditEntry,
  GroupAuditSummary,
  GroupDashboardData,
  GroupDashboardWidget,
  GroupReport,
  // Sprint 17.4.0+: Task Management
  TaskRecord,
  TaskListItem,
  TaskComment,
  // Sprint 17.5.0: Workflows
  WorkflowDefinition,
  WorkflowInstance,
  WorkflowTemplate,
  // Sprint 17.6.0+: Dashboard widgets
  UserDashboardPreferences,
  // Sprint 18.0.0: License & Package Management
  License,
  PackageInfo,
  PackageChangeResult,
  // Sprint 20.3.0: Dossier Filings
  DossierFiling,
  // Sprint 40.0.0: Bulk Import
  ImportJob,
  // Sprint 41.0.0: Generic attachments
  AttachmentRef,
  GenericAttachment,
  // Sprint 41.2.0 E7 — Sub-Processor Register types
  SubProcessorRegisterRecord,
  SubProcessorRegisterChange,
  // Sprint 41.4.0 — Platform → Files Configuration & Browser
  AttachmentOwnerType,
  PlatformFileSettings,
  PlatformFileGlobalSettings,
  PlatformFileOwnerTypeOverride,
  PlatformFileEffectiveSettings,
  PlatformFileAuditEvent,
  PlatformFileAuditEventType,
  FileBrowserFilter,
  // Sprint 43.2.0 batch 4 (E5) — Breach-trigger records for the red
  // banner on TicketDetailPage.
  DataBreach,
  GovernanceModel,
  DataSubjectProfile,
  DataSubjectTimelineEntry,
  // Sprint 47.4.5 — Immutable Audit Trails frontend types
  ImmutableAuditTrailsOrgSettings,
  ProofLink,
  ProofReceipt,
  ProofUsageRecord,
  ProofAlert,
  ProofCreditPackage,
  IatCreditPackageCode,
  AdvisorSummary,
  // Sprint 47.5.8 — Advisor audit lineage strict typing.
  LineageQuery,
  // Sprint 47.9.7 — MCP connector
  McpApiKeyIssueRequest,
  McpApiKeyIssueResponse,
  McpApiKeyListItem,
  McpApiKeyRotateResponse,
  // Sprint 58.x.x — PDPL Evidence Pack (Phase 25)
  PdplEvidencePackAssembleResult,
  PdplEvidencePackAssemblyDoc,
  PdplEvidencePackEligibilityContext,
  PdplEmailDraftComposeResult,
  PdplEmailDraftDoc,
  PdplEmailSentProofDoc,
  PdplEmailSentProofUploadResult,
  PdplEmailSendRecord,
  PdplEmailProviderConfigRedacted,
  PdplEmailProviderCreateInput,
  PdplEmailProviderStatus,
} from '@vn-grc/shared';
import { ApiError, statusToErrorCode } from './api-error.js';
const API_BASE = '/api/v1';

// ── Sprint 46.2.0 — sector overlay client types ───────────────────
export interface SectorOverlayCatalogEntry {
  pack_code: string;
  framework_code: string;
  display_name: string;
  description: string;
  version: string;
  released_at: string;
  authority_name: string;
  jurisdiction_code: string;
  framework_type: 'sector_overlay';
  sector: Sector;
  direct_scope: string[];
  optional_pack: true;
  installed: boolean;
  installed_as: SectorOverlayInstallMode | null;
  installed_at: string | null;
}

export interface InstalledSectorOverlay {
  framework_id: string;
  framework_code: string;
  display_name: string | Record<string, string>;
  sector: Sector;
  direct_scope: string[];
  installed_as: SectorOverlayInstallMode;
  installed_at: string;
}

// Sprint 46.3.0 — phased deadline engine client types
export interface PhasedObligationEarlyImplementationRecord {
  org_id: string;
  framework_code: string;
  deadline_code: string;
  effective_at: string;
  evidence_attachment_id?: string;
  reason_note?: string;
  actor_id: string;
  timestamp: string;
}

/** Helper: read a File into a base64 data URL body for upload. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result.replace(/^data:[^;]+;base64,/, ''));
      } else {
        reject(new Error('Unexpected FileReader result'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read error'));
    reader.readAsDataURL(file);
  });
}
const RETRYABLE_STATUSES = [408, 429, 502, 503, 504];
const IDEMPOTENT_METHODS = ['GET', 'HEAD', 'OPTIONS', 'PUT'];
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 8000;
const REQUEST_TIMEOUT_MS = 30_000;
/** In-flight GET request deduplication cache. */
const inflightGets = new Map<string, Promise<unknown>>();
function deduplicateGet<T>(key: string, executor: () => Promise<T>): Promise<T> {
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<T>;
  const promise = executor().finally(() => {
    inflightGets.delete(key);
  });
  inflightGets.set(key, promise);
  return promise;
}
/**
 * API client for the AesirX ComplianceOne backend.
 * Handles all HTTP communication with CSRF, retry, deduplication, and AbortController support.
 */
class ApiClient {
  private csrfToken: string | null = null;
  private csrfFetched = false;
  private onUnauthorized: (() => void) | null = null;
  /** @deprecated No-op — auth tokens are now managed via httpOnly cookies. */
  setToken(_token: string) {
    // No-op: tokens are managed via httpOnly cookies set by the backend
  }
  /** @deprecated No-op — auth tokens are now managed via httpOnly cookies. */
  clearToken() {
    // No-op: cookies are cleared by backend logout endpoint
  }
  /** Register a callback for 401 auto-logout. */
  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }
  /** Fetch CSRF token from backend (called once on init, or forced on refresh). */
  private async fetchCsrfToken(): Promise<void> {
    if (this.csrfFetched) return;
    try {
      const res = await fetch(`${API_BASE}/csrf-token`, { credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        this.csrfToken = data.csrf_token || null;
      }
    } catch {
      // CSRF endpoint may not exist yet - continue without
    }
    this.csrfFetched = true;
  }
  /** Force-refresh the CSRF token (e.g. after a stale-token 403). */
  private async refreshCsrfToken(): Promise<void> {
    this.csrfFetched = false;
    this.csrfToken = null;
    await this.fetchCsrfToken();
  }
  /**
   * Sprint 47.7.4.1 AC-1 — public CSRF token accessor for the SSE
   * streaming path. `postSse` (`utils/sse-fetch.ts`) is a lower-level
   * fetch helper that bypasses `apiClient.request()`, so the SSE
   * callsites (ChatInput, future workflow streaming) need to fetch
   * the token themselves and pass it as the `csrfToken` option. This
   * method is idempotent and shares the cached token with the regular
   * mutation path; if the token gets invalidated by a `csrf_failed`
   * response on the regular path, the next call here will trigger a
   * fresh fetch via the existing `refreshCsrfToken()` sequence.
   */
  async getCsrfToken(): Promise<string | null> {
    await this.fetchCsrfToken();
    return this.csrfToken;
  }
  /**
   * Builds a query string from a record, stripping invalid/empty values.
   */
  private buildQuery(params?: Record<string, any>): string {
    if (!params) return '';
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '' && value !== 'undefined') {
        query.set(key, String(value));
      }
    }
    const qString = query.toString();
    return qString ? `?${qString}` : '';
  }
  // ── Public endpoints ──────────────────────────────────────────
  async submitIntake(payload: PublicIntakePayload, intakeKey?: string): Promise<IntakeResponse> {
    const query = intakeKey ? `?key=${encodeURIComponent(intakeKey)}` : '';
    return this.post(`/intake${query}`, payload, false);
  }
  // Phase 9 Extension: Public intake config resolution
  async getPublicIntakeConfig(key: string): Promise<{
    org_name: string;
    enabled_request_types: string[];
    default_language: string;
    [key: string]: unknown;
  }> {
    return this.get(`/organizations/public/intake/config?key=${encodeURIComponent(key)}`);
  }
  // Returns org name + enabled request types when exactly one active org exists.
  // Returns null when multiple orgs exist (multi-tenant — key required).
  async getDefaultPublicIntakeConfig(): Promise<{
    org_name: string;
    enabled_request_types: string[];
    default_language: string;
    [key: string]: unknown;
  } | null> {
    const res = await fetch('/api/v1/organizations/public/intake/default-config');
    if (res.status === 204) return null;
    if (!res.ok) return null;
    return res.json();
  }
  // ── Admin endpoints ───────────────────────────────────────────
  async listCases(
    params?: Record<string, string | undefined>,
  ): Promise<PaginatedResponse<CaseListItem>> {
    return this.get(`/cases${this.buildQuery(params)}`);
  }
  async exportCasesCSV(params?: Record<string, string | undefined>): Promise<Blob> {
    const response = await fetch(`${API_BASE}/cases/export/csv${this.buildQuery(params)}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  /**
   * Sprint 43.2.0 batch 2 (E3) — bulk case export.
   *
   * Backs the TicketListPage ExportFormatMenu with three formats:
   *   - `csv`  → delegates to the existing CSV exporter and persists
   *              through the File Component.
   *   - `pdf`  → aggregated summary table of the filtered cases.
   *   - `zip`  → "ZIP of ZIPs" — each filtered case's evidence pack
   *              bundled with an `index.json` cover sheet.
   *
   * Returns the persisted AttachmentRef augmented with `case_count`,
   * `format`, and an optional `warning` (e.g. truncation notice or
   * large-bundle heads-up).
   */
  async exportCasesBulk(
    filter: Record<string, unknown>,
    format: 'pdf' | 'zip' | 'csv',
  ): Promise<{
    file_id: string;
    filename: string;
    mime_type: string;
    size: number;
    storage_path: string;
    uploaded_at: string;
    case_count: number;
    format: 'pdf' | 'zip' | 'csv';
    warning?: string;
  }> {
    const res = await this.post<{
      data: {
        file_id: string;
        filename: string;
        mime_type: string;
        size: number;
        storage_path: string;
        uploaded_at: string;
        case_count: number;
        format: 'pdf' | 'zip' | 'csv';
        warning?: string;
      };
    }>(`/cases/export/bulk`, { filter, format });
    return res.data;
  }
  async getCaseDetail(casePublicId: string): Promise<RightsRequestCase> {
    return this.get(`/cases/${casePublicId}`);
  }
  async updateCase(casePublicId: string, payload: UpdateCasePayload): Promise<RightsRequestCase> {
    return this.patch(`/cases/${casePublicId}`, payload);
  }
  async createCase(payload: Record<string, unknown>): Promise<CaseCreatedResponse> {
    return this.post('/cases', payload);
  }
  // ── Sprint 40.0.0: Rights Request Case Bulk Import ────────
  async importCases(csvData: string, filename: string): Promise<ImportJob> {
    const res: any = await this.post('/cases/import', { csv: csvData, filename });
    return res.data ?? res;
  }
  async getCaseImportHistory(): Promise<ImportJob[]> {
    const res: any = await this.get('/cases/import/history');
    return res.data ?? res;
  }
  async getCaseNotes(casePublicId: string): Promise<{ data: CaseNote[] }> {
    return this.get(`/cases/${casePublicId}/notes`);
  }
  async addNote(casePublicId: string, payload: AddNotePayload): Promise<CaseNote> {
    return this.post(`/cases/${casePublicId}/notes`, payload);
  }
  async getCaseAudit(
    casePublicId: string,
    params?: Record<string, string>,
  ): Promise<AuditTrailResponse> {
    return this.get(`/cases/${casePublicId}/audit${this.buildQuery(params)}`);
  }
  async getGlobalAudit(params?: Record<string, string>): Promise<AuditTrailResponse> {
    return this.get(`/audit${this.buildQuery(params)}`);
  }
  async getSettings(): Promise<Record<string, unknown>> {
    return this.get('/settings');
  }
  async updateNotificationSettings(
    settings: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch('/settings/notifications', settings);
  }
  async updateIntakeSettings(settings: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.patch('/settings/intake', settings);
  }
  // ── Sprint 41.0.0 G0.6 / 41.2.0 E1: Generic attachments (provider-agnostic) ──
  //
  // Sprint 41.2.0 E1 deleted the legacy rights-request-scoped
  // `uploadAttachment`/`listAttachments`/`deleteAttachment`/`downloadAttachment`
  // methods. Rights Requests pages now call the generic methods below
  // with `owner_type='rights_request_attachment'` and `owner_id=<caseDoc._id>`.
  async listGenericAttachments(
    ownerType: string,
    ownerId: string,
  ): Promise<{ data: GenericAttachment[]; total: number }> {
    const qs = `?owner_type=${encodeURIComponent(ownerType)}&owner_id=${encodeURIComponent(ownerId)}`;
    return this.get(`/attachments${qs}`);
  }
  async uploadGenericAttachment(
    ownerType: string,
    ownerId: string | null,
    file: File,
    metadata?: Record<string, unknown>,
  ): Promise<AttachmentRef> {
    const base64 = await fileToBase64(file);
    return this.post<AttachmentRef>('/attachments', {
      owner_type: ownerType,
      owner_id: ownerId,
      filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      size: file.size,
      file_data_base64: base64,
      ...(metadata ? { metadata } : {}),
    });
  }
  downloadGenericAttachment(fileId: string, filename: string): void {
    const url = `${API_BASE}/attachments/${fileId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  async deleteGenericAttachment(fileId: string): Promise<void> {
    return this.del(`/attachments/${fileId}`);
  }

  // ── Sprint 41.4.0 F8: Platform → Files Configuration & Browser ──
  //
  // 15 typed methods backing the FilesConfigPage, FilesBrowserPage,
  // PlatformFileDetailModal, and FilesDashboardPage. Every method
  // routes through one of the F6/F7 backend endpoints. Return types
  // come from the shared types in `platform-file-settings.ts`. No
  // `any` casts; no `langKey` patterns.

  /** GET /api/v1/platform/files/settings — fetch the org's settings doc. */
  async getPlatformFileSettings(): Promise<{
    global: PlatformFileGlobalSettings | null;
    per_owner_type: Partial<Record<AttachmentOwnerType, PlatformFileOwnerTypeOverride>>;
    version: number;
    updated_at: string | null;
    updated_by: string | null;
    empty: boolean;
  }> {
    return this.get('/platform/files/settings');
  }

  /** PUT /api/v1/platform/files/settings/global — update org-level defaults. */
  async updatePlatformFileSettingsGlobal(
    patch: Partial<PlatformFileGlobalSettings>,
    expectedVersion: number,
  ): Promise<PlatformFileSettings> {
    return this.put('/platform/files/settings/global', {
      expected_version: expectedVersion,
      patch,
    });
  }

  /** PUT /api/v1/platform/files/settings/owner-type/:ownerType — add/update override. */
  async updatePlatformFileOwnerTypeOverride(
    ownerType: AttachmentOwnerType,
    patch: PlatformFileOwnerTypeOverride,
    expectedVersion: number,
  ): Promise<PlatformFileSettings> {
    return this.put(`/platform/files/settings/owner-type/${encodeURIComponent(ownerType)}`, {
      expected_version: expectedVersion,
      patch,
    });
  }

  /** DELETE /api/v1/platform/files/settings/owner-type/:ownerType — remove an override. */
  async deletePlatformFileOwnerTypeOverride(
    ownerType: AttachmentOwnerType,
    expectedVersion: number,
  ): Promise<PlatformFileSettings> {
    // Use request() directly because the `del` helper doesn't accept
    // a body and this endpoint requires `expected_version` for
    // optimistic locking.
    return this.request<PlatformFileSettings>(
      'DELETE',
      `/platform/files/settings/owner-type/${encodeURIComponent(ownerType)}`,
      { expected_version: expectedVersion },
      true,
    );
  }

  /** GET /api/v1/platform/files/settings/owner-type/:ownerType/effective — preview merged settings. */
  async getPlatformFileOwnerTypeEffective(
    ownerType: AttachmentOwnerType,
  ): Promise<PlatformFileEffectiveSettings> {
    return this.get(
      `/platform/files/settings/owner-type/${encodeURIComponent(ownerType)}/effective`,
    );
  }

  /** GET /api/v1/platform/files — paginated cross-owner-type list with optional sort. */
  async listPlatformFiles(
    filter: FileBrowserFilter,
    page: number = 1,
    size: number = 50,
    sort?: { field: string; dir: 'asc' | 'desc' },
  ): Promise<{
    data: GenericAttachment[];
    total: number;
    page: number;
    size: number;
    sort_field: string;
    sort_dir: 'asc' | 'desc';
  }> {
    const params: Record<string, unknown> = { page, size };
    if (filter.owner_type && filter.owner_type.length > 0) {
      params.owner_type = filter.owner_type.join(',');
    }
    if (filter.owner_id) params.owner_id = filter.owner_id;
    if (filter.uploader_user_id) params.uploader_user_id = filter.uploader_user_id;
    if (filter.uploaded_after) params.uploaded_after = filter.uploaded_after;
    if (filter.uploaded_before) params.uploaded_before = filter.uploaded_before;
    if (filter.size_min !== undefined) params.size_min = filter.size_min;
    if (filter.size_max !== undefined) params.size_max = filter.size_max;
    if (filter.mime_type && filter.mime_type.length > 0) {
      params.mime_type = filter.mime_type.join(',');
    }
    if (filter.deleted) params.deleted = filter.deleted;
    if (filter.orphaned) params.orphaned = 'true';
    if (filter.search) params.search = filter.search;
    if (sort) {
      params.sort_field = sort.field;
      params.sort_dir = sort.dir;
    }
    return this.get(`/platform/files${this.buildQuery(params)}`);
  }

  /** GET /api/v1/platform/files/:fileId — file detail with audit trail and effective settings. */
  async getPlatformFileDetail(fileId: string): Promise<{
    attachment: GenericAttachment;
    audit: PlatformFileAuditEvent[];
    download_count: number;
    last_downloaded_at: string | null;
    owner_entity_exists: boolean | null;
    effective_settings: PlatformFileEffectiveSettings;
  }> {
    return this.get(`/platform/files/${encodeURIComponent(fileId)}`);
  }

  /** POST /api/v1/platform/files/:fileId/soft-delete — soft delete with mandatory reason. */
  async softDeletePlatformFile(
    fileId: string,
    reason: string,
  ): Promise<{ ok: true; file_id: string }> {
    return this.post(`/platform/files/${encodeURIComponent(fileId)}/soft-delete`, { reason });
  }

  /** POST /api/v1/platform/files/:fileId/restore — restore soft-deleted file. */
  async restorePlatformFile(
    fileId: string,
    reason: string,
  ): Promise<{ ok: true; file_id: string }> {
    return this.post(`/platform/files/${encodeURIComponent(fileId)}/restore`, { reason });
  }

  /** DELETE /api/v1/platform/files/:fileId — hard delete with reason and optional legal-hold override. */
  async hardDeletePlatformFile(
    fileId: string,
    reason: string,
    legalHoldOverride?: boolean,
  ): Promise<{ ok: true; file_id: string }> {
    // Use request() directly because the `del` helper doesn't accept
    // a body and this endpoint requires `reason` (>= 20 chars) and
    // optional `legal_hold_override`.
    return this.request<{ ok: true; file_id: string }>(
      'DELETE',
      `/platform/files/${encodeURIComponent(fileId)}`,
      { reason, legal_hold_override: legalHoldOverride === true },
      true,
    );
  }

  /** POST /api/v1/platform/files/bulk/soft-delete — bulk soft delete with per-id status. */
  async bulkSoftDeletePlatformFiles(
    fileIds: string[],
    reason: string,
  ): Promise<{
    results: Array<{
      file_id: string;
      status: 'deleted' | 'already_deleted' | 'not_found';
    }>;
  }> {
    return this.post('/platform/files/bulk/soft-delete', { file_ids: fileIds, reason });
  }

  /**
   * Trigger an operator-side download of a platform file. The browser
   * navigates to the URL directly so the response stream becomes a
   * file save dialog and the audit event is recorded server-side.
   */
  downloadPlatformFile(fileId: string, filename: string): void {
    const url = `${API_BASE}/platform/files/${encodeURIComponent(fileId)}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /** GET /api/v1/platform/files/metrics — dashboard payload (60s server cache per org). */
  async getPlatformFileMetrics(): Promise<{
    total_count: number;
    total_size_bytes: number;
    by_owner_type: Record<AttachmentOwnerType, { count: number; size_bytes: number }>;
    growth_30d: Array<{ date: string; count: number; size_bytes: number }>;
    orphan_count: number;
    near_quota: { attachments_pct: number; size_pct: number };
  }> {
    return this.get('/platform/files/metrics');
  }

  /** GET /api/v1/platform/files/audit — list operational audit events. */
  async listPlatformFileAudit(
    filter: {
      event_type?: PlatformFileAuditEventType[];
      actor_user_id?: string;
      target_file_id?: string;
      target_owner_type?: AttachmentOwnerType;
      from?: string;
      to?: string;
    } = {},
    page: number = 1,
    size: number = 50,
  ): Promise<{
    data: PlatformFileAuditEvent[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const params: Record<string, unknown> = { page, size };
    if (filter.event_type && filter.event_type.length > 0) {
      params.event_type = filter.event_type.join(',');
    }
    if (filter.actor_user_id) params.actor_user_id = filter.actor_user_id;
    if (filter.target_file_id) params.target_file_id = filter.target_file_id;
    if (filter.target_owner_type) params.target_owner_type = filter.target_owner_type;
    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;
    return this.get(`/platform/files/audit${this.buildQuery(params)}`);
  }

  // ── Rights Requests attachment helpers (Sprint 41.2.0 E1) ─────────
  //
  // Rights Requests pages identify cases by public ID (e.g. "DSR-001"),
  // but the generic attachment endpoint keys on the internal ULID. These
  // helpers resolve the public ID to a ULID once, then call the generic
  // endpoint. They exist so page code stays concise.
  async listRightsRequestAttachments(
    casePublicId: string,
  ): Promise<{ data: GenericAttachment[]; total: number }> {
    const detail = await this.getCaseDetail(casePublicId);
    return this.listGenericAttachments('rights_request_attachment', detail._id);
  }
  async uploadRightsRequestAttachment(
    casePublicId: string,
    file: File,
    metadata?: Record<string, unknown>,
  ): Promise<AttachmentRef> {
    const detail = await this.getCaseDetail(casePublicId);
    return this.uploadGenericAttachment('rights_request_attachment', detail._id, file, metadata);
  }
  // ── Sprint 1.1.0: Evidence Checklist & References ──────────────
  async getChecklist(
    casePublicId: string,
  ): Promise<{ items: EvidenceChecklistItem[]; pct: number }> {
    return this.get(`/cases/${casePublicId}/checklist`);
  }
  async toggleChecklistItem(
    casePublicId: string,
    itemId: string,
    checked: boolean,
  ): Promise<{ items: EvidenceChecklistItem[] }> {
    return this.patch(`/cases/${casePublicId}/checklist`, { item_id: itemId, checked });
  }
  async addReference(
    casePublicId: string,
    payload: Record<string, unknown>,
  ): Promise<EvidenceReference> {
    return this.post(`/cases/${casePublicId}/references`, payload);
  }
  async listReferences(casePublicId: string): Promise<{ data: EvidenceReference[] }> {
    return this.get(`/cases/${casePublicId}/references`);
  }
  // ── Sprint 1.1.0: Identity Verification ────────────────────────
  async verifyIdentity(
    casePublicId: string,
    payload: { verification_method: string; verification_notes?: string },
  ): Promise<RightsRequestCase> {
    return this.post(`/cases/${casePublicId}/verify-identity`, payload);
  }
  // Sprint 43.5.1 consolidation — the Sprint 1.1.0 Response Templates API
  // (`/api/v1/templates/*`) and its `ResponseTemplate` type have been
  // removed. Use the DsrResponseTemplate methods further down in this
  // file (`listDsrResponseTemplates`, `generateDsrResponse`, CRUD set)
  // which are backed by the canonical `grc_dsr_response_templates`
  // collection seeded at org creation (20 templates: 10 types × vi/en).

  // ── Sprint 1.1.0: Evidence Pack Export ─────────────────────────
  async exportPDF(casePublicId: string): Promise<ExportRecord> {
    return this.post(`/cases/${casePublicId}/export/pdf`);
  }
  async exportZIP(casePublicId: string): Promise<ExportRecord> {
    return this.post(`/cases/${casePublicId}/export/zip`);
  }
  async listExports(casePublicId: string): Promise<{ data: ExportRecord[] }> {
    return this.get(`/cases/${casePublicId}/exports`);
  }
  // ── Sprint 1.1.0: SLA Config ──────────────────────────────────
  async getSlaConfig(): Promise<Record<string, unknown>> {
    return this.get('/sla/config');
  }
  async updateSlaConfig(config: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.patch('/sla/config', config);
  }
  async getCaseSla(casePublicId: string): Promise<{
    status: string;
    days_remaining: number;
    days_elapsed: number;
    deadline_days: number;
    extended_deadline_days: number;
    pct_elapsed: number;
  }> {
    return this.get(`/cases/${casePublicId}/sla`);
  }
  // ── Sprint 1.1.0: Audit Export ─────────────────────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob). Caller should
  // call downloadPlatformFile(ref.file_id, ref.filename) to download.
  // The CSV is also persisted in grc_attachments (visible in Files Browser).
  async exportAuditCSV(params?: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/audit/export${this.buildQuery(params)}`);
  }
  /** Sprint 43.5.5 — Audit export now supports XLSX alongside CSV. */
  async exportAudit(
    format: 'csv' | 'xlsx',
    params?: Record<string, string>,
  ): Promise<AttachmentRef> {
    const q = this.buildQuery({ ...(params ?? {}), format });
    return this.get(`/audit/export${q}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Systems ────────────────────
  async listSystems(params?: Record<string, string>): Promise<PaginatedResponse<GrcSystem>> {
    return this.get(`/data-mapping/systems${this.buildQuery(params)}`);
  }
  async getSystem(systemId: string): Promise<GrcSystem> {
    return this.get(`/data-mapping/systems/${systemId}`);
  }
  async createSystem(payload: Record<string, unknown>): Promise<GrcSystem> {
    return this.post('/data-mapping/systems', payload);
  }
  async updateSystem(systemId: string, payload: Record<string, unknown>): Promise<GrcSystem> {
    return this.patch(`/data-mapping/systems/${systemId}`, payload);
  }
  async archiveSystem(systemId: string): Promise<GrcSystem> {
    return this.post(`/data-mapping/systems/${systemId}/archive`, {});
  }
  // ── Sprint 1.2.0: Data Mapping -- Categories ────────────────
  async listCategories(params?: Record<string, string>): Promise<PaginatedResponse<DataCategory>> {
    return this.get(`/data-mapping/categories${this.buildQuery(params)}`);
  }
  async createCategory(payload: Record<string, unknown>): Promise<DataCategory> {
    return this.post('/data-mapping/categories', payload);
  }
  async updateCategory(
    categoryId: string,
    payload: Record<string, unknown>,
  ): Promise<DataCategory> {
    return this.patch(`/data-mapping/categories/${categoryId}`, payload);
  }
  async archiveCategory(categoryId: string): Promise<DataCategory> {
    return this.post(`/data-mapping/categories/${categoryId}/archive`, {});
  }
  // ── Sprint 45.4.0: Data Mapping -- Data Registry ──────────────
  async listRegistryItems(
    params?: Record<string, string>,
  ): Promise<import('@vn-grc/shared').RegistryListResult> {
    return this.get(`/data-mapping/registry${this.buildQuery(params)}`);
  }
  async getRegistryItem(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.get(`/data-mapping/registry/${registryItemId}`);
  }
  async getRegistryItemLineage(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').RegistryLineage> {
    return this.get(`/data-mapping/registry/${registryItemId}/lineage`);
  }
  async createRegistryItem(
    payload: Record<string, unknown>,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.post('/data-mapping/registry', payload);
  }
  async updateRegistryItem(
    registryItemId: string,
    payload: Record<string, unknown>,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.put(`/data-mapping/registry/${registryItemId}`, payload);
  }
  async patchRegistryItem(
    registryItemId: string,
    payload: Record<string, unknown>,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.patch(`/data-mapping/registry/${registryItemId}`, payload);
  }
  async archiveRegistryItem(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.post(`/data-mapping/registry/${registryItemId}/archive`, {});
  }
  async restoreRegistryItem(
    registryItemId: string,
  ): Promise<import('@vn-grc/shared').DataRegistryItemView> {
    return this.post(`/data-mapping/registry/${registryItemId}/restore`, {});
  }
  async importRegistryFromForm(payload: {
    form_submission_id: string;
    dry_run: boolean;
  }): Promise<import('@vn-grc/shared').RegistryFormImportResult> {
    return this.post('/data-mapping/registry/import-from-form', payload);
  }
  async importRegistryHistory(payload: { template_ids?: string[]; dry_run: boolean }): Promise<{
    aggregate: {
      created: number;
      updated: number;
      skipped_archived: number;
      conflicts: number;
    };
    results: import('@vn-grc/shared').RegistryFormImportResult[];
  }> {
    return this.post('/data-mapping/registry/import-history', payload);
  }
  async listMappedRegistryTemplates(): Promise<{
    templates: import('@vn-grc/shared').MappedTemplateDescriptor[];
  }> {
    return this.get('/data-mapping/registry/mapped-templates');
  }
  async getRegistryHygiene(includeInformational: boolean): Promise<{ misses: unknown[] }> {
    return this.get(`/data-mapping/registry/hygiene?include_informational=${includeInformational}`);
  }
  /**
   * Sprint 45.4.0 hotfix-3 — Registry export via UnifiedExportService.
   *
   * Returns an `AttachmentRef` the caller feeds into
   * `downloadPlatformFile(ref.file_id, ref.filename)` (the same pattern
   * AuditTrailPage / RopaCompletenessPage / every other admin export
   * already uses). Previously the RegistryPage constructed a raw URL
   * and called `window.open(...)`, which bypassed the platform file
   * browser, skipped the auth header, and opened a JSON AttachmentRef
   * response as a raw JSON blob in a new tab instead of downloading the
   * generated file.
   */
  async exportRegistryItems(params: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/data-mapping/registry/export${this.buildQuery(params)}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Purposes ──────────────────
  async listPurposes(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ProcessingPurpose>> {
    return this.get(`/data-mapping/purposes${this.buildQuery(params)}`);
  }
  async createPurpose(payload: Record<string, unknown>): Promise<ProcessingPurpose> {
    return this.post('/data-mapping/purposes', payload);
  }
  async updatePurpose(
    purposeId: string,
    payload: Record<string, unknown>,
  ): Promise<ProcessingPurpose> {
    return this.patch(`/data-mapping/purposes/${purposeId}`, payload);
  }
  async archivePurpose(purposeId: string): Promise<ProcessingPurpose> {
    return this.post(`/data-mapping/purposes/${purposeId}/archive`, {});
  }
  // ── Sprint 1.2.0: Data Mapping -- Retention Policies ────────
  async listRetentionPolicies(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<RetentionPolicy>> {
    return this.get(`/data-mapping/retention-policies${this.buildQuery(params)}`);
  }
  async createRetentionPolicy(payload: Record<string, unknown>): Promise<RetentionPolicy> {
    return this.post('/data-mapping/retention-policies', payload);
  }
  async updateRetentionPolicy(
    retentionId: string,
    payload: Record<string, unknown>,
  ): Promise<RetentionPolicy> {
    return this.patch(`/data-mapping/retention-policies/${retentionId}`, payload);
  }
  async archiveRetentionPolicy(retentionId: string): Promise<RetentionPolicy> {
    return this.post(`/data-mapping/retention-policies/${retentionId}/archive`, {});
  }
  // ── Sprint 1.2.0: Data Mapping -- Data Flows ────────────────
  async listFlows(params?: Record<string, string>): Promise<PaginatedResponse<DataFlow>> {
    return this.get(`/data-mapping/flows${this.buildQuery(params)}`);
  }
  async getFlow(flowId: string): Promise<DataFlow> {
    return this.get(`/data-mapping/flows/${flowId}`);
  }
  async createFlow(payload: Record<string, unknown>): Promise<DataFlow> {
    return this.post('/data-mapping/flows', payload);
  }
  async updateFlow(flowId: string, payload: Record<string, unknown>): Promise<DataFlow> {
    return this.patch(`/data-mapping/flows/${flowId}`, payload);
  }
  async archiveFlow(flowId: string): Promise<DataFlow> {
    return this.post(`/data-mapping/flows/${flowId}/archive`, {});
  }
  async listCrossBorderFlows(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DataFlow>> {
    return this.get(`/data-mapping/flows/cross-border${this.buildQuery(params)}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Processing Activities ─────
  async listActivities(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ProcessingActivity>> {
    return this.get(`/data-mapping/activities${this.buildQuery(params)}`);
  }
  async getActivity(activityId: string): Promise<ProcessingActivity> {
    return this.get(`/data-mapping/activities/${activityId}`);
  }
  async createActivity(payload: Record<string, unknown>): Promise<ProcessingActivity> {
    return this.post('/data-mapping/activities', payload);
  }
  async updateActivity(
    activityId: string,
    payload: Record<string, unknown>,
  ): Promise<ProcessingActivity> {
    return this.patch(`/data-mapping/activities/${activityId}`, payload);
  }
  async archiveActivity(activityId: string): Promise<ProcessingActivity> {
    return this.post(`/data-mapping/activities/${activityId}/archive`, {});
  }
  // ── Sprint 46.0.0: ROPA Completeness Report ─────────────────
  async getRopaCompleteness(
    params: Record<string, string>,
  ): Promise<import('@vn-grc/shared').RopaCompletenessReport> {
    return this.get(`/data-mapping/ropa-completeness${this.buildQuery(params)}`);
  }
  async exportRopaCompleteness(params: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/data-mapping/ropa-completeness/export${this.buildQuery(params)}`);
  }
  // Sprint 46.0.1 — list frameworks installed for the requesting org
  async getInstalledRopaFrameworks(): Promise<{
    frameworks: import('@vn-grc/shared').RopaCompletenessFramework[];
  }> {
    return this.get('/data-mapping/ropa-completeness/installed-frameworks');
  }
  // ── Sprint 1.2.0: Data Mapping -- RoPA ──────────────────────
  async listRopaRecords(params?: Record<string, string>): Promise<{ data: RopaRecord[] }> {
    return this.get(`/data-mapping/ropa${this.buildQuery(params)}`);
  }
  async getRopaRecord(ropaRecordId: string): Promise<RopaRecord> {
    return this.get(`/data-mapping/ropa/${ropaRecordId}`);
  }
  async generateRopaRecord(activityId: string): Promise<RopaRecord> {
    return this.post(`/data-mapping/ropa/generate/${activityId}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Evidence ──────────────────
  async listDmEvidence(params?: Record<string, string>): Promise<PaginatedResponse<EvidenceItem>> {
    return this.get(`/data-mapping/evidence${this.buildQuery(params)}`);
  }
  async getDmEvidence(evidenceId: string): Promise<EvidenceItem> {
    return this.get(`/data-mapping/evidence/${evidenceId}`);
  }
  async createDmEvidence(payload: Record<string, unknown>): Promise<EvidenceItem> {
    return this.post('/data-mapping/evidence', payload);
  }
  async updateDmEvidence(
    evidenceId: string,
    payload: Record<string, unknown>,
  ): Promise<EvidenceItem> {
    return this.patch(`/data-mapping/evidence/${evidenceId}`, payload);
  }
  async deleteDmEvidence(evidenceId: string): Promise<void> {
    return this.del(`/data-mapping/evidence/${evidenceId}`);
  }
  async linkDmEvidence(
    evidenceId: string,
    entityType: string,
    entityId: string,
  ): Promise<EvidenceItem> {
    return this.post(`/data-mapping/evidence/${evidenceId}/link`, {
      entity_type: entityType,
      entity_id: entityId,
    });
  }
  async unlinkDmEvidence(evidenceId: string, entityType: string, entityId: string): Promise<void> {
    return this.del(`/data-mapping/evidence/${evidenceId}/link/${entityType}/${entityId}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Exports ───────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportDmCSV(exportType: string): Promise<AttachmentRef> {
    return this.post(`/data-mapping/export/csv/${exportType}`);
  }
  /**
   * Sprint 42.1.0 E4 + 44.8.0 — legacy PDF endpoint now returns an
   * AttachmentRef (JSON), not a Blob. The frontend should call
   * `downloadPlatformFile(ref.file_id, ref.filename)` after this
   * returns to trigger the actual download via the authenticated
   * platform files endpoint.
   *
   * Prior 42.1.0 migration returned an AttachmentRef on the backend
   * but the client still used `response.blob()`, which produced a
   * JSON-as-blob that appeared to be a "broken PDF" when opened.
   */
  async exportDmPDF(exportType: string): Promise<AttachmentRef> {
    return this.post(`/data-mapping/export/pdf/${exportType}`);
  }
  /**
   * Sprint 44.8.0 — Unified multi-format DM export.
   * format ∈ 'pdf' | 'csv' | 'xlsx' | 'docx'.
   */
  async exportDataMapping(
    exportType: string,
    format: 'pdf' | 'csv' | 'xlsx' | 'docx',
  ): Promise<AttachmentRef> {
    return this.post(`/data-mapping/export/${exportType}/${format}`);
  }
  // ── Sprint 1.2.0: Data Mapping -- Audit ─────────────────────
  async getDmAudit(params?: Record<string, string>): Promise<{ data: DataMappingAuditEvent[] }> {
    return this.get(`/data-mapping/audit${this.buildQuery(params)}`);
  }
  async getDmEntityAudit(
    entityType: string,
    entityId: string,
    params?: Record<string, string>,
  ): Promise<{ data: DataMappingAuditEvent[] }> {
    return this.get(`/data-mapping/audit/${entityType}/${entityId}${this.buildQuery(params)}`);
  }
  // ── Sprint 20.5.0: Data Mapping -- Attestation Cycles ───────
  async listAttestationCycles(
    params?: Record<string, string>,
  ): Promise<{ data: AttestationCycle[] }> {
    return this.get(`/data-mapping/attestation/cycles${this.buildQuery(params)}`);
  }
  async getAttestationCycle(cycleId: string): Promise<AttestationCycle> {
    return this.get(`/data-mapping/attestation/cycles/${cycleId}`);
  }
  async createAttestationCycle(data: Record<string, unknown>): Promise<AttestationCycle> {
    return this.post('/data-mapping/attestation/cycles', data);
  }
  async updateAttestationCycle(
    cycleId: string,
    data: Record<string, unknown>,
  ): Promise<AttestationCycle> {
    return this.patch(`/data-mapping/attestation/cycles/${cycleId}`, data);
  }
  async assignAttestationTasks(
    cycleId: string,
    assignments: Record<string, unknown>[],
  ): Promise<{ data: AttestationDepartmentTask[] }> {
    return this.post(`/data-mapping/attestation/cycles/${cycleId}/assign`, { assignments });
  }
  async updateAttestationTask(
    taskId: string,
    updates: Record<string, unknown>,
  ): Promise<AttestationDepartmentTask> {
    return this.patch(`/data-mapping/attestation/tasks/${taskId}`, updates);
  }
  async completeAttestationTask(taskId: string): Promise<AttestationDepartmentTask> {
    return this.post(`/data-mapping/attestation/tasks/${taskId}/complete`);
  }
  async getAttestationSummary(
    cycleId: string,
  ): Promise<{ tasks: Record<string, unknown>[]; [key: string]: unknown }> {
    return this.get(`/data-mapping/attestation/cycles/${cycleId}/summary`);
  }
  async detectStaleEntries(cycleId: string): Promise<{
    stale_systems: Array<Record<string, unknown>>;
    stale_flows: Array<Record<string, unknown>>;
  }> {
    return this.get(`/data-mapping/attestation/cycles/${cycleId}/stale`);
  }
  // ── Sprint 20.5.0: Data Mapping -- Governance Ownership ────
  async assignGovernanceOwnership(data: Record<string, unknown>): Promise<GovernanceAssignment> {
    return this.post('/data-mapping/ownership', data);
  }
  async listGovernanceOwnership(
    params?: Record<string, string>,
  ): Promise<{ data: GovernanceAssignment[] }> {
    return this.get(`/data-mapping/ownership${this.buildQuery(params)}`);
  }
  async removeGovernanceOwnership(assignmentId: string): Promise<void> {
    return this.del(`/data-mapping/ownership/${assignmentId}`);
  }
  // ── Sprint 20.5.0: Data Mapping -- Cross-Border Risk Assessment ─
  async createCrossBorderAssessment(
    flowId: string,
    data: Record<string, unknown>,
  ): Promise<CrossBorderRiskAssessment> {
    return this.post(`/data-mapping/flows/${flowId}/risk-assessment`, data);
  }
  async listCrossBorderAssessments(
    flowId?: string,
  ): Promise<{ data: CrossBorderRiskAssessment[] }> {
    const params = flowId ? `?flow_id=${flowId}` : '';
    return this.get(`/data-mapping/risk-assessments${params}`);
  }
  async updateCrossBorderAssessment(
    assessmentId: string,
    data: Record<string, unknown>,
  ): Promise<CrossBorderRiskAssessment> {
    return this.patch(`/data-mapping/risk-assessments/${assessmentId}`, data);
  }
  async approveCrossBorderAssessment(
    assessmentId: string,
    decision: Record<string, unknown>,
  ): Promise<CrossBorderRiskAssessment> {
    return this.post(`/data-mapping/risk-assessments/${assessmentId}/decide`, decision);
  }
  // ── Sprint 20.5.0: Data Mapping -- Export & Evidence ────────
  async generateAttestationBundle(cycleId: string): Promise<DataMappingExport> {
    return this.post(`/data-mapping/attestation/cycles/${cycleId}/export`);
  }
  async getEntityEvidence(entityType: string, entityId: string): Promise<{ data: EvidenceItem[] }> {
    return this.get(`/data-mapping/evidence/lookup/${entityType}/${entityId}`);
  }
  // ── Sprint 1.3.0: Governance -- Vendors ─────────────────────
  async listVendors(params?: Record<string, string>): Promise<PaginatedResponse<GrcVendor>> {
    return this.get(`/governance/vendors${this.buildQuery(params)}`);
  }
  /**
   * Sprint 43.5.5 — Convenience helper for vendor-name lookup tables
   * (Approval Gates, Risk Scoring, Vendor Reviews, Reassessments,
   * Risk Signals, Risk Acceptance, Review Routing, Sub-Processor
   * Register, Questionnaires, etc). Always requests a large page so
   * lookups resolve to names instead of raw UUIDs for vendors beyond
   * the default 25-row first page.
   *
   * Sprint 43.5.6 — also returns `_id` so callers can resolve name
   * lookups whether their upstream service stores the business
   * `vendor_id` or the Mongo `_id` (seed data + legacy records are
   * inconsistent on this across modules).
   */
  async listAllVendorsForLookup(): Promise<
    Array<{ _id: string; vendor_id: string; name: string }>
  > {
    const raw = await this.listVendors({ page_size: '500' });
    const arr = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as unknown as { data?: unknown[] })?.data)
        ? (raw as unknown as { data: GrcVendor[] }).data
        : [];
    return (arr as GrcVendor[]).map((v) => ({
      _id: v._id,
      vendor_id: v.vendor_id,
      name: v.name,
    }));
  }
  async getVendor(vendorId: string): Promise<GrcVendor> {
    return this.get(`/governance/vendors/${vendorId}`);
  }
  async createVendor(payload: Record<string, unknown>): Promise<GrcVendor> {
    return this.post('/governance/vendors', payload);
  }
  async updateVendor(vendorId: string, payload: Record<string, unknown>): Promise<GrcVendor> {
    return this.patch(`/governance/vendors/${vendorId}`, payload);
  }
  async offboardVendor(vendorId: string, reason: string): Promise<GrcVendor> {
    return this.post(`/governance/vendors/${vendorId}/offboard`, { reason });
  }
  async getVendorStats(): Promise<Record<string, unknown>> {
    return this.get('/governance/vendors/stats');
  }
  // ── Sprint 1.3.0: Governance -- Sub-Processors ────────────
  async listSubProcessors(vendorId: string): Promise<{ data: VendorSubProcessor[] }> {
    return this.get(`/governance/vendors/${vendorId}/subprocessors`);
  }
  async createSubProcessor(
    vendorId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorSubProcessor> {
    return this.post(`/governance/vendors/${vendorId}/subprocessors`, payload);
  }
  async updateSubProcessor(
    relationshipId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorSubProcessor> {
    return this.patch(`/governance/subprocessors/${relationshipId}`, payload);
  }
  async terminateSubProcessor(relationshipId: string): Promise<void> {
    return this.del(`/governance/subprocessors/${relationshipId}`);
  }
  // ── Sprint 1.3.0: Governance -- Transfer Mechanisms ───────
  async listTransferMechanisms(vendorId: string): Promise<{ data: VendorTransferMechanism[] }> {
    return this.get(`/governance/vendors/${vendorId}/transfer-mechanisms`);
  }
  async createTransferMechanism(
    vendorId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorTransferMechanism> {
    return this.post(`/governance/vendors/${vendorId}/transfer-mechanisms`, payload);
  }
  async updateTransferMechanism(
    mechanismId: string,
    payload: Record<string, unknown>,
  ): Promise<VendorTransferMechanism> {
    return this.patch(`/governance/transfer-mechanisms/${mechanismId}`, payload);
  }
  // ── Sprint 1.3.0: Governance -- Cross-Border ──────────────
  async getCrossBorderReport(): Promise<Record<string, unknown>> {
    return this.get('/governance/cross-border/report');
  }
  /**
   * Sprint 41.5.0 G3.1 — return type changed from DataMappingExport
   * (which was a fake placeholder shape) to AttachmentRef. Callers
   * should pass the returned `file_id` + `filename` to
   * `downloadPlatformFile()` to trigger the actual download.
   */
  async exportCrossBorderPDF(
    jurisdiction?: string,
    format: 'pdf' | 'docx' = 'pdf',
  ): Promise<AttachmentRef> {
    return this.post('/governance/cross-border/report/export', { jurisdiction, format });
  }
  /** Sprint 41.5.0 G3.2 — Board Report PDF export.
   *  Sprint 43.5.5 — supports DOCX via optional `format`. */
  async exportBoardReportPDF(payload: {
    date_from: string;
    date_to: string;
    language?: 'vi' | 'en';
    format?: 'pdf' | 'docx';
  }): Promise<AttachmentRef> {
    return this.post('/governance/board-report/export', payload);
  }
  /** Sprint 41.5.0 G3.3 — Governance Evidence bundle PDF export. */
  async exportGovernanceEvidenceBundle(payload: {
    evidence_ids: string[];
    bundle_title?: string;
  }): Promise<AttachmentRef> {
    return this.post('/governance/evidence/bundle/export', payload);
  }
  // Sprint 42.2.2 — incident reports list PDF export (unified File Component)
  async exportIncidentReportsPDF(
    params?: Record<string, string | undefined>,
  ): Promise<AttachmentRef> {
    const qs = params
      ? new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][],
        ).toString()
      : '';
    return this.post(`/incidents/reports/export${qs ? `?${qs}` : ''}`);
  }
  // Sprint 42.2.2 — rights request response PDF export (unified File Component)
  async exportRightsRequestResponsePDF(
    casePublicId: string,
    payload: {
      responses: Array<{ subject?: string; body?: string; response_id?: string | null }>;
      request_type?: string;
    },
  ): Promise<AttachmentRef> {
    return this.post(`/cases/${encodeURIComponent(casePublicId)}/responses/export-pdf`, payload);
  }
  // ── Sprint 41.5.0 G1 — Cross-Border Filings lifecycle ─────
  async listCrossBorderFilings(params?: {
    status?: CrossBorderFilingStatus;
    jurisdiction?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<CrossBorderFiling>> {
    const qp: Record<string, string> = {};
    if (params?.status) qp.status = params.status;
    if (params?.jurisdiction) qp.jurisdiction = params.jurisdiction;
    if (params?.page !== undefined) qp.page = String(params.page);
    if (params?.page_size !== undefined) qp.page_size = String(params.page_size);
    return this.get(`/governance/cross-border/filings${this.buildQuery(qp)}`);
  }
  async createCrossBorderFiling(payload: CrossBorderFilingCreateInput): Promise<CrossBorderFiling> {
    return this.post('/governance/cross-border/filings', payload);
  }
  async getCrossBorderFiling(filingId: string): Promise<CrossBorderFiling> {
    return this.get(`/governance/cross-border/filings/${filingId}`);
  }
  async updateCrossBorderFilingStatus(
    filingId: string,
    payload: CrossBorderFilingStatusTransition,
  ): Promise<CrossBorderFiling> {
    return this.patch(`/governance/cross-border/filings/${filingId}/status`, payload);
  }
  async attachCrossBorderFilingEvidence(
    filingId: string,
    payload: AttachmentRef,
  ): Promise<CrossBorderFiling> {
    return this.post(`/governance/cross-border/filings/${filingId}/evidence`, payload);
  }
  async archiveCrossBorderFiling(filingId: string): Promise<CrossBorderFiling> {
    return this.post(`/governance/cross-border/filings/${filingId}/archive`, {});
  }
  // ── Sprint 1.3.0: Governance -- Drift Events ─────────────
  async listDriftEvents(params?: Record<string, string>): Promise<PaginatedResponse<DriftEvent>> {
    return this.get(`/governance/drift${this.buildQuery(params)}`);
  }
  async getDriftEvent(eventId: string): Promise<DriftEvent> {
    return this.get(`/governance/drift/${eventId}`);
  }
  async updateDriftStatus(eventId: string, status: string, notes?: string): Promise<DriftEvent> {
    return this.patch(`/governance/drift/${eventId}/status`, { status, resolution_notes: notes });
  }
  async assignDriftEvent(eventId: string, assignedTo: string): Promise<DriftEvent> {
    return this.patch(`/governance/drift/${eventId}/assign`, { assigned_to: assignedTo });
  }
  async getDriftDashboard(): Promise<Record<string, unknown>> {
    return this.get('/governance/drift/dashboard');
  }
  // ── Sprint 1.3.0: Governance -- Compliance Scores ─────────
  async getOrgScore(): Promise<ComplianceScore> {
    return this.get('/governance/scores/org');
  }
  async getSystemScore(systemId: string): Promise<ComplianceScore> {
    return this.get(`/governance/scores/systems/${systemId}`);
  }
  async getVendorScore(vendorId: string): Promise<ComplianceScore> {
    return this.get(`/governance/scores/vendors/${vendorId}`);
  }
  // ── Sprint 1.3.0: Governance -- Deletion Playbooks ────────
  async listPlaybooks(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DeletionPlaybook>> {
    return this.get(`/governance/playbooks${this.buildQuery(params)}`);
  }
  async getPlaybook(playbookId: string): Promise<DeletionPlaybook> {
    return this.get(`/governance/playbooks/${playbookId}`);
  }
  async createPlaybook(payload: Record<string, unknown>): Promise<DeletionPlaybook> {
    return this.post('/governance/playbooks', payload);
  }
  async updatePlaybook(
    playbookId: string,
    payload: Record<string, unknown>,
  ): Promise<DeletionPlaybook> {
    return this.patch(`/governance/playbooks/${playbookId}`, payload);
  }
  async archivePlaybook(playbookId: string): Promise<void> {
    return this.del(`/governance/playbooks/${playbookId}`);
  }
  // ── Sprint 1.3.0: Governance -- Deletion Executions ───────
  async initiateExecution(payload: Record<string, unknown>): Promise<DeletionExecution> {
    return this.post('/governance/executions', payload);
  }
  // Sprint 41.0.0 G3 — list active executions for the org
  async listGovernanceExecutions(
    status?: string,
  ): Promise<{ data: DeletionExecution[]; total: number }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.get(`/governance/executions${qs}`);
  }
  async getExecution(executionId: string): Promise<DeletionExecution> {
    return this.get(`/governance/executions/${executionId}`);
  }
  async updateExecutionStep(
    executionId: string,
    stepId: string,
    payload: Record<string, unknown>,
  ): Promise<DeletionExecution> {
    return this.patch(`/governance/executions/${executionId}/steps/${stepId}`, payload);
  }
  async attachStepEvidence(
    executionId: string,
    stepId: string,
    evidenceId: string,
  ): Promise<DeletionExecution> {
    return this.post(`/governance/executions/${executionId}/steps/${stepId}/evidence`, {
      evidence_id: evidenceId,
    });
  }
  async listTicketExecutions(ticketId: string): Promise<{ data: DeletionExecution[] }> {
    return this.get(`/rights-requests/${ticketId}/executions`);
  }
  // ── Sprint 1.3.0: Governance -- Approvals ─────────────────
  async listApprovals(params?: Record<string, string>): Promise<PaginatedResponse<ApprovalRecord>> {
    return this.get(`/governance/approvals${this.buildQuery(params)}`);
  }
  async listPendingApprovals(): Promise<{ data: ApprovalRecord[] }> {
    return this.get('/governance/approvals/pending');
  }
  async getApproval(approvalId: string): Promise<ApprovalRecord> {
    return this.get(`/governance/approvals/${approvalId}`);
  }
  async approveRequest(
    approvalId: string,
    notes: string,
    evidenceIds?: string[],
  ): Promise<ApprovalRecord> {
    return this.post(`/governance/approvals/${approvalId}/approve`, {
      decision_notes: notes,
      evidence_ids: evidenceIds ?? undefined,
    });
  }
  async rejectRequest(
    approvalId: string,
    notes: string,
    evidenceIds?: string[],
  ): Promise<ApprovalRecord> {
    return this.post(`/governance/approvals/${approvalId}/reject`, {
      decision_notes: notes,
      evidence_ids: evidenceIds ?? undefined,
    });
  }
  async getPendingApprovalCount(): Promise<Record<string, unknown>> {
    return this.get('/governance/approvals/count');
  }
  // ── Sprint 1.3.0: Governance -- Audit ─────────────────────
  // Sprint 41.2.0 E6 — paginated shape.
  async getGovernanceAudit(
    params?: Record<string, string>,
  ): Promise<{ data: GovernanceAuditEvent[]; total: number; page: number; page_size: number }> {
    return this.get(`/governance/audit${this.buildQuery(params)}`);
  }
  async getGovernanceEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<{ data: GovernanceAuditEvent[] }> {
    return this.get(`/governance/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 1.3.0: Governance -- Evidence ──────────────────
  async listGovernanceEvidence(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/governance/evidence${this.buildQuery(params)}`);
  }
  async createGovernanceEvidence(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/governance/evidence', payload);
  }
  async linkGovernanceEvidence(
    evidenceId: string,
    entityType: string,
    entityId: string,
  ): Promise<Record<string, unknown>> {
    return this.post(`/governance/evidence/${evidenceId}/link`, {
      entity_type: entityType,
      entity_id: entityId,
    });
  }
  async updateGovernanceEvidence(
    evidenceId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/governance/evidence/${evidenceId}`, payload);
  }
  async deleteGovernanceEvidence(evidenceId: string): Promise<void> {
    return this.del(`/governance/evidence/${evidenceId}`);
  }
  // ── Sprint 20.9.0: Vendor Governance Maturity ───────────────
  async createOnboardingWorkflow(
    payload: Record<string, unknown>,
  ): Promise<VendorOnboardingWorkflow> {
    return this.post('/governance/onboarding', payload);
  }
  // Sprint 41.2.0 E4 — backend now returns paginated shape
  async listOnboardingWorkflows(params?: Record<string, string>): Promise<{
    data: VendorOnboardingWorkflow[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/governance/onboarding${this.buildQuery(params)}`);
  }
  async getOnboardingStatus(workflowId: string): Promise<VendorOnboardingWorkflow> {
    return this.get(`/governance/onboarding/${workflowId}`);
  }
  async advanceOnboardingStage(
    workflowId: string,
    notes: string,
  ): Promise<VendorOnboardingWorkflow> {
    return this.post(`/governance/onboarding/${workflowId}/advance`, { notes });
  }
  async createDpaReview(payload: Record<string, unknown>): Promise<VendorDpaReview> {
    return this.post('/governance/dpa-reviews', payload);
  }
  // Sprint 41.0.0 G6 — list DPA reviews (optional vendor filter)
  // Sprint 41.2.0 E4 — backend now returns paginated shape
  async listDpaReviews(params?: Record<string, string>): Promise<{
    data: VendorDpaReview[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/governance/dpa-reviews${query}`);
  }
  async completeDpaReview(
    reviewId: string,
    data: Record<string, unknown>,
  ): Promise<VendorDpaReview> {
    return this.post(`/governance/dpa-reviews/${reviewId}/complete`, data);
  }
  async linkSecurityAssessment(reviewId: string, assessmentId: string): Promise<VendorDpaReview> {
    return this.post(`/governance/dpa-reviews/${reviewId}/link-assessment`, {
      assessment_id: assessmentId,
    });
  }
  async getCombinedDpaAssessment(vendorId: string): Promise<VendorDpaReview> {
    return this.get(`/governance/dpa-reviews/vendor/${vendorId}`);
  }
  async createPeriodicReview(payload: Record<string, unknown>): Promise<VendorPeriodicReview> {
    return this.post('/governance/periodic-reviews', payload);
  }
  // Sprint 41.2.0 E4 — backend now returns paginated shape
  async listPeriodicReviews(params?: Record<string, string>): Promise<{
    data: VendorPeriodicReview[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/governance/periodic-reviews${this.buildQuery(params)}`);
  }
  async completePeriodicReview(
    reviewId: string,
    data: Record<string, unknown>,
  ): Promise<VendorPeriodicReview> {
    return this.post(`/governance/periodic-reviews/${reviewId}/complete`, data);
  }
  async triggerReassessment(vendorId: string, reason: string): Promise<Record<string, unknown>> {
    return this.post(`/governance/reassessment/${vendorId}`, { reason });
  }
  async initiateOffboarding(
    vendorId: string,
    data: Record<string, unknown>,
  ): Promise<VendorOffboardingTask> {
    return this.post(`/governance/offboarding/${vendorId}`, data);
  }
  async getOffboardingStatus(taskId: string): Promise<VendorOffboardingTask> {
    return this.get(`/governance/offboarding/${taskId}/status`);
  }
  async verifyDataReturn(
    taskId: string,
    data: Record<string, unknown>,
  ): Promise<VendorOffboardingTask> {
    return this.post(`/governance/offboarding/${taskId}/verify-data`, data);
  }
  async signOffOffboarding(taskId: string, notes: string): Promise<VendorOffboardingTask> {
    return this.post(`/governance/offboarding/${taskId}/sign-off`, { notes });
  }
  // ── Sprint 1.4.0: Consent -- Policies ──────────────────────
  async listConsentPolicies(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentPolicy>> {
    return this.get(`/consent/policies${this.buildQuery(params)}`);
  }
  async getConsentPolicy(policyId: string): Promise<ConsentPolicy> {
    return this.get(`/consent/policies/${policyId}`);
  }
  async createConsentPolicy(payload: Record<string, unknown>): Promise<ConsentPolicy> {
    return this.post('/consent/policies', payload);
  }
  async updateConsentPolicy(
    policyId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentPolicy> {
    return this.patch(`/consent/policies/${policyId}`, payload);
  }
  async submitPolicyForReview(policyId: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/submit-review`);
  }
  async approvePolicyReview(
    policyId: string,
    decision: string,
    comments?: string,
  ): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/approve`, { decision, comments });
  }
  async activatePolicy(policyId: string, body?: Record<string, unknown>): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/activate`, body);
  }
  async suspendPolicy(policyId: string, reason?: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/suspend`, reason ? { reason } : undefined);
  }
  async retirePolicy(policyId: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/retire`);
  }
  async getPolicyVersions(policyId: string): Promise<ConsentPolicyVersion[]> {
    return this.get(`/consent/policies/${policyId}/versions`);
  }
  // ── Sprint 1.4.0: Consent -- Purposes ─────────────────────
  async listConsentPurposes(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentCatalogPurpose>> {
    return this.get(`/consent/purposes${this.buildQuery(params)}`);
  }
  async createConsentPurpose(payload: Record<string, unknown>): Promise<ConsentCatalogPurpose> {
    return this.post('/consent/purposes', payload);
  }
  async updateConsentPurpose(
    purposeId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentCatalogPurpose> {
    return this.patch(`/consent/purposes/${purposeId}`, payload);
  }
  async deprecateConsentPurpose(purposeId: string): Promise<ConsentCatalogPurpose> {
    return this.post(`/consent/purposes/${purposeId}/deprecate`);
  }
  // ── Sprint 1.4.0: Consent -- Legal Bases ──────────────────
  async listConsentLegalBases(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentCatalogLegalBase>> {
    return this.get(`/consent/legal-bases${this.buildQuery(params)}`);
  }
  async createConsentLegalBase(payload: Record<string, unknown>): Promise<ConsentCatalogLegalBase> {
    return this.post('/consent/legal-bases', payload);
  }
  // ── Sprint 1.4.0: Consent -- Workflows ────────────────────
  async listConsentWorkflows(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentWorkflow>> {
    return this.get(`/consent/workflows${this.buildQuery(params)}`);
  }
  async createConsentWorkflow(payload: Record<string, unknown>): Promise<ConsentWorkflow> {
    return this.post('/consent/workflows', payload);
  }
  async getConsentWorkflow(workflowId: string): Promise<ConsentWorkflow> {
    return this.get(`/consent/workflows/${workflowId}`);
  }
  async updateConsentWorkflow(
    workflowId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}`, payload);
  }
  async updateWorkflowStatus(workflowId: string, status: string): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}/status`, { status });
  }
  async updateWorkflowAssessment(
    workflowId: string,
    outcome: string,
    justification?: string,
  ): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}/assessment`, { outcome, justification });
  }
  async addWorkflowAction(
    workflowId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentWorkflow> {
    return this.post(`/consent/workflows/${workflowId}/actions`, payload);
  }
  async updateWorkflowAction(
    workflowId: string,
    actionId: string,
    payload: Record<string, unknown>,
  ): Promise<ConsentWorkflow> {
    return this.patch(`/consent/workflows/${workflowId}/actions/${actionId}`, payload);
  }
  async getWorkflowDashboard(): Promise<Record<string, unknown>> {
    return this.get('/consent/workflows/dashboard');
  }
  // ── Sprint 40.0.0 / 44.4.0: Consent Workflow Bulk Import ───────────
  async importConsentWorkflows(
    csvData: string,
    filename: string,
    options?: { createProfiles?: boolean },
  ): Promise<ImportJob> {
    const createProfiles = options?.createProfiles;
    const query =
      typeof createProfiles === 'boolean'
        ? this.buildQuery({ create_profiles: String(createProfiles) })
        : '';
    return this.post(`/consent/workflows/import${query}`, { csv: csvData, filename });
  }
  async getConsentImportHistory(): Promise<ImportJob[]> {
    const res: any = await this.get('/consent/workflows/import/history');
    return res.data ?? res;
  }
  // ── Sprint 1.4.0: Consent -- CMP Snapshots ───────────────
  async listCmpSnapshots(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentCmpSnapshot>> {
    return this.get(`/consent/snapshots${this.buildQuery(params)}`);
  }
  async createCmpSnapshot(payload: Record<string, unknown>): Promise<ConsentCmpSnapshot> {
    return this.post('/consent/snapshots', payload);
  }
  async getCmpSnapshot(snapshotId: string): Promise<ConsentCmpSnapshot> {
    return this.get(`/consent/snapshots/${snapshotId}`);
  }
  async compareCmpSnapshots(
    snapshotId1: string,
    snapshotId2: string,
  ): Promise<Record<string, unknown>> {
    return this.get(
      `/consent/snapshots/compare?snapshot_id_1=${snapshotId1}&snapshot_id_2=${snapshotId2}`,
    );
  }
  // ── Sprint 1.4.0: Consent -- Change Awareness ────────────
  async listChangeAwareness(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ChangeAwarenessEntry>> {
    return this.get(`/consent/change-awareness${this.buildQuery(params)}`);
  }
  async createChangeAwareness(payload: Record<string, unknown>): Promise<ChangeAwarenessEntry> {
    return this.post('/consent/change-awareness', payload);
  }
  async updateChangeAwarenessStatus(
    entryId: string,
    status: string,
    notes?: string,
  ): Promise<ChangeAwarenessEntry> {
    return this.patch(`/consent/change-awareness/${entryId}/status`, {
      status,
      resolution_notes: notes,
    });
  }
  async getChangeAwarenessDashboard(): Promise<Record<string, unknown>> {
    return this.get('/consent/change-awareness/dashboard');
  }
  // ── Sprint 1.4.0: Consent -- Audit ────────────────────────
  async getConsentAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ConsentAuditEvent>> {
    return this.get(`/consent/audit${this.buildQuery(params)}`);
  }
  async getConsentEntityAudit(entityType: string, entityId: string): Promise<ConsentAuditEvent[]> {
    return this.get(`/consent/audit/${entityType}/${entityId}`);
  }
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportConsentAuditCSV(params?: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/consent/audit/export${this.buildQuery(params)}`);
  }
  // ── Sprint 1.5.0: Assessments -- DPIAs ─────────────────────
  async listDpias(params?: Record<string, string>): Promise<PaginatedResponse<DpiaRecord>> {
    return this.get(`/assessments/dpias${this.buildQuery(params)}`);
  }
  async createDpia(payload: Record<string, unknown>): Promise<DpiaRecord> {
    return this.post('/assessments/dpias', payload);
  }
  async getDpia(dpiaId: string): Promise<DpiaRecord> {
    return this.get(`/assessments/dpias/${dpiaId}`);
  }
  async updateDpia(dpiaId: string, payload: Record<string, unknown>): Promise<DpiaRecord> {
    return this.patch(`/assessments/dpias/${dpiaId}`, payload);
  }
  async submitDpiaForReview(dpiaId: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/submit-review`);
  }
  async approveDpia(dpiaId: string, decision: string, comments?: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/approve`, { decision, comments });
  }
  async deprecateDpia(dpiaId: string): Promise<DpiaRecord> {
    return this.post(`/assessments/dpias/${dpiaId}/deprecate`);
  }
  async getDpiaApprovalLog(dpiaId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/assessments/dpias/${dpiaId}/approval-log`);
  }
  // Sprint 47.9.6 — Forseti DPIA autocomplete cross-module workflow exemplar.
  // Sprint 48.1.0 — added optional `frameworkVariant` to drive the
  //                 per-framework templates + per-framework regulatory
  //                 pack search scope. Defaults server-side to 'gdpr'.
  async draftDpiaAutocomplete(
    dpiaId: string,
    activityId: string,
    frameworkVariant?: 'gdpr' | 'pdpl' | 'nis2' | 'iso-27001' | 'soc2',
  ): Promise<
    DpiaAutocompleteDraft & {
      framework_variant?: 'gdpr' | 'pdpl' | 'nis2' | 'iso-27001' | 'soc2';
      cross_framework_conflict_ids?: string[];
    }
  > {
    return this.post(`/assessments/dpias/${dpiaId}/autocomplete`, {
      activity_id: activityId,
      framework_variant: frameworkVariant,
    });
  }
  async acceptDpiaAutocomplete(
    dpiaId: string,
    payload: {
      draft_id: string;
      accepted_fields: DpiaAutocompleteFieldKey[];
      rejected_fields?: DpiaAutocompleteFieldKey[];
    },
  ): Promise<{ accepted: DpiaAutocompleteFieldKey[]; rejected: DpiaAutocompleteFieldKey[] }> {
    return this.post(`/assessments/dpias/${dpiaId}/autocomplete/accept`, payload);
  }
  // Sprint 48.1.5 — Breach notification timeline.
  async computeBreachTimeline(
    incidentId: string,
    payload?: { installed_frameworks?: string[] },
  ): Promise<unknown> {
    return this.post(`/incidents/${incidentId}/breach-timeline`, payload ?? {});
  }
  async acknowledgeBreachTimeline(
    timelineId: string,
    payload?: { note?: string },
  ): Promise<{ status: string }> {
    return this.post(`/incidents/breach-timelines/${timelineId}/acknowledge`, payload ?? {});
  }
  // Sprint 48.1.7 — Privacy scanner anomaly explanation.
  async explainPrivacyScannerAnomalies(scanId: string): Promise<unknown> {
    return this.post(`/privacy-scanner/scans/${scanId}/explain-anomalies`, {});
  }
  async resolvePrivacyScannerAnomalyExplanation(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; remediation_task_ids: string[] }> {
    return this.post(`/privacy-scanner/anomaly-explanations/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.1.8 — Per-cookie / per-tracker AI insight.
  async producePrivacyScannerCookieInsight(scanId: string): Promise<unknown> {
    return this.post(`/privacy-scanner/scans/${scanId}/cookie-insight`, {});
  }
  async resolvePrivacyScannerCookieInsight(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/privacy-scanner/cookie-insights/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.2.7 — Remediation plan generator from compliance gaps.
  async generateRemediationPlanFromGaps(
    sourceModule: string,
    sourceParentId: string,
    gaps: Array<{
      id: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      description: string;
      theme?: 'technical' | 'process' | 'training' | 'governance';
      source_module?: string;
    }>,
  ): Promise<unknown> {
    return this.post(`/remediation-plans/generate-from-gaps`, {
      source_module: sourceModule,
      source_parent_id: sourceParentId,
      gaps,
    });
  }
  // Sprint 48.2.7 fix — cross-module gap-set ingestion. Returns accepted
  // gap-sets from consent / policy / vendor draft collections in a unified
  // UpstreamGap[] shape for the RemediationPlanGeneratorPanel.
  async loadUpstreamRemediationGaps(
    source?: 'consent' | 'policy' | 'vendor',
    limit?: number,
  ): Promise<{
    data: Array<{
      id: string;
      source: 'consent' | 'policy' | 'vendor';
      source_record_id: string;
      gap_id: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      description: string;
      theme?: 'technical' | 'process' | 'training' | 'governance';
      framework_codes?: string[];
    }>;
  }> {
    const params: Record<string, string | number> = {};
    if (source) params.source = source;
    if (typeof limit === 'number') params.limit = limit;
    return this.get(`/remediation-plans/upstream-gaps${this.buildQuery(params)}`);
  }
  async resolveRemediationPlanDraft(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; created_task_ids: string[] }> {
    return this.post(`/remediation-plans/generate/${draftId}/resolve`, { decision, note });
  }
  async exportRemediationPlanDocx(draftId: string): Promise<unknown> {
    return this.post(`/remediation-plans/generate/${draftId}/export-docx`, {});
  }
  // Sprint 48.2.6 — Governance document policy gap detection.
  async runPolicyGapAnalysis(documentId: string): Promise<unknown> {
    return this.post(`/governance-documents/${documentId}/gap-analysis`, {});
  }
  async resolvePolicyGapAnalysis(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/governance-documents/gap-analysis/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  async exportPolicyGapAnalysisDocx(draftId: string): Promise<AttachmentRef> {
    return this.post<AttachmentRef>(
      `/governance-documents/gap-analysis/${draftId}/export-docx`,
      {},
    );
  }
  // Sprint 48.2.5 — Maturity assessment AI gap walkthrough.
  async runMaturityWalkthrough(assessmentId: string): Promise<unknown> {
    return this.post(`/maturity-assessments/${assessmentId}/walkthrough`, {});
  }
  async resolveMaturityWalkthrough(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/maturity-assessments/walkthrough/${draftId}/resolve`, { decision, note });
  }
  async exportMaturityWalkthroughDocx(draftId: string): Promise<AttachmentRef> {
    return this.post<AttachmentRef>(`/maturity-assessments/walkthrough/${draftId}/export-docx`, {});
  }
  // Sprint 48.2.4 — Risk acceptance reasoning generator.
  async draftRiskAcceptanceReasoning(acceptanceId: string): Promise<unknown> {
    return this.post(`/risk-acceptances/${acceptanceId}/draft-reasoning`, {});
  }
  async resolveRiskAcceptanceReasoning(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/risk-acceptances/reasoning/${draftId}/resolve`, { decision, note });
  }
  // Sprint 48.2.3 — Contract redline assistant + clause library.
  async runContractRedline(contractId: string, inlineText?: string): Promise<unknown> {
    return this.post(`/contracts/${contractId}/redline-assistant`, {
      inline_text: inlineText,
    });
  }
  async resolveContractRedline(
    draftId: string,
    decision: 'accepted' | 'rejected' | 'partially_accepted',
    appliedSuggestionIds?: string[],
    note?: string,
  ): Promise<{ status: string; applied_count: number }> {
    return this.post(`/contracts/redline/${draftId}/resolve`, {
      decision,
      applied_suggestion_ids: appliedSuggestionIds,
      note,
    });
  }
  async listClauseLibrary(jurisdiction?: string): Promise<unknown> {
    const qs = jurisdiction ? `?jurisdiction=${encodeURIComponent(jurisdiction)}` : '';
    return this.get(`/contracts/clause-library${qs}`);
  }
  async createClauseLibraryEntry(input: {
    slug: string;
    title: string;
    body: string;
    jurisdiction?: string;
    tags?: string[];
    must_contain?: string[];
    severity_when_missing?: 'critical' | 'high' | 'medium' | 'low';
  }): Promise<unknown> {
    return this.post(`/contracts/clause-library`, input);
  }
  async updateClauseLibraryEntry(
    slug: string,
    input: {
      title?: string;
      body?: string;
      jurisdiction?: string;
      tags?: string[];
      must_contain?: string[];
      severity_when_missing?: 'critical' | 'high' | 'medium' | 'low';
    },
  ): Promise<unknown> {
    return this.put(`/contracts/clause-library/${slug}`, input);
  }
  async deleteClauseLibraryEntry(slug: string): Promise<unknown> {
    return this.del(`/contracts/clause-library/${slug}`);
  }
  // Sprint 48.2.2 — Contract obligation extraction.
  async extractContractObligations(contractId: string, inlineText?: string): Promise<unknown> {
    return this.post(`/contracts/${contractId}/extract-obligations`, {
      inline_text: inlineText,
    });
  }
  async resolveContractObligations(
    draftId: string,
    decision: 'accepted' | 'rejected',
    overrides?: Record<string, { summary?: string; due_date?: string }>,
    note?: string,
  ): Promise<{ status: string; created_task_ids: string[] }> {
    return this.post(`/contracts/obligations/${draftId}/resolve`, {
      decision,
      overrides,
      note,
    });
  }
  // Sprint 48.2.1 — Sub-processor change impact.
  async assessSubprocessorChange(changeId: string): Promise<unknown> {
    return this.post(`/vendor-risk/subprocessor-changes/${changeId}/assess`, {});
  }
  async resolveSubprocessorImpact(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; notification_task_id: string | null }> {
    return this.post(`/vendor-risk/subprocessor-impact/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.2.0 — Vendor onboarding evidence collector.
  async collectVendorOnboardingEvidence(caseId: string): Promise<unknown> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/collect-evidence`, {});
  }
  async resolveVendorOnboardingEvidence(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string; created_task_ids: string[] }> {
    return this.post(`/vendor-risk/onboarding-evidence/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.1.9 — Dossier filing pre-flight + auto-assembly.
  async runDossierPreflight(filingId: string): Promise<unknown> {
    return this.post(`/assessments/dossier-filings/${filingId}/preflight`, {});
  }
  async resolveDossierPreflight(
    draftId: string,
    decision: 'accepted' | 'rejected' | 'overridden',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/assessments/dossier-filings/preflight/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  async assembleDossierPackage(
    filingId: string,
    preflightDraftId?: string,
    forceThroughBlock?: boolean,
  ): Promise<unknown> {
    return this.post(`/assessments/dossier-filings/${filingId}/auto-assemble`, {
      preflight_draft_id: preflightDraftId,
      force_through_block: forceThroughBlock === true,
    });
  }
  // Sprint 48.1.6 — Authority request response draft.
  async draftAuthorityResponse(incidentId: string, authorityRequestId: string): Promise<unknown> {
    return this.post(
      `/incidents/${incidentId}/authority-requests/${authorityRequestId}/draft-response`,
      {},
    );
  }
  async resolveAuthorityResponseDraft(
    draftId: string,
    decision: 'accepted' | 'rejected',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/incidents/authority-response-drafts/${draftId}/resolve`, {
      decision,
      note,
    });
  }
  // Sprint 48.1.4 — Consent gap analysis Forseti workflow.
  async runConsentGapAnalysis(payload: {
    framework_codes?: Array<'gdpr' | 'pdpl' | 'pipl' | 'soc2'>;
    org_flags?: Record<string, boolean>;
  }): Promise<unknown> {
    return this.post('/consent/gap-analysis', payload);
  }
  async triageConsentGap(
    analysisId: string,
    payload: {
      row_index: number;
      decision: 'dismissed_not_applicable' | 'resolved';
      note?: string;
    },
  ): Promise<{ status: string }> {
    return this.post(`/consent/gap-analysis/${analysisId}/triage`, payload);
  }
  // Sprint 48.1.3 — Cross-border TIA Forseti autocomplete.
  async draftTiaAutocomplete(transferId: string): Promise<TiaAutocompleteDraft> {
    return this.post(`/assessments/transfers/${transferId}/tia-autocomplete`, {});
  }
  async acceptTiaAutocomplete(
    transferId: string,
    payload: {
      draft_id: string;
      accepted_fields: TiaAutocompleteFieldKey[];
      rejected_fields?: TiaAutocompleteFieldKey[];
    },
  ): Promise<TiaAutocompleteAcceptResult> {
    return this.post(`/assessments/transfers/${transferId}/tia-autocomplete/accept`, payload);
  }
  // Sprint 47.5.8.4a — cross-module Forseti advisor-summary endpoints.
  async getDpiaAdvisorSummary(dpiaId: string): Promise<AdvisorSummary> {
    return this.get(`/assessments/dpias/${dpiaId}/advisor-summary`);
  }
  async getDossierFilingAdvisorSummary(filingId: string): Promise<AdvisorSummary> {
    return this.get(`/assessments/dossier-filings/${filingId}/advisor-summary`);
  }
  async getTransferAssessmentAdvisorSummary(assessmentId: string): Promise<AdvisorSummary> {
    return this.get(`/assessments/transfers/${assessmentId}/advisor-summary`);
  }
  async getActivityAdvisorSummary(activityId: string): Promise<AdvisorSummary> {
    return this.get(`/data-mapping/activities/${activityId}/advisor-summary`);
  }
  async getRightsRequestAdvisorSummary(casePublicId: string): Promise<AdvisorSummary> {
    return this.get(`/cases/${casePublicId}/advisor-summary`);
  }
  // Sprint 47.5.8.4b — Risk + Vendor lane advisor-summary endpoints.
  async getVendorAdvisorSummary(vendorId: string): Promise<AdvisorSummary> {
    return this.get(`/governance/vendors/${vendorId}/advisor-summary`);
  }
  async getContractAdvisorSummary(contractId: string): Promise<AdvisorSummary> {
    return this.get(`/contracts/${contractId}/advisor-summary`);
  }
  async getVendorRiskAcceptanceAdvisorSummary(acceptanceId: string): Promise<AdvisorSummary> {
    return this.get(`/vendor-risk/acceptances/${acceptanceId}/advisor-summary`);
  }
  async getPlaybookActivationAdvisorSummary(activationId: string): Promise<AdvisorSummary> {
    return this.get(`/incident-ops/activations/${activationId}/advisor-summary`);
  }
  async getBreachNotificationAdvisorSummary(notificationId: string): Promise<AdvisorSummary> {
    return this.get(`/incidents/breach-notifications/${notificationId}/advisor-summary`);
  }
  async getAuthorityRequestAdvisorSummary(requestId: string): Promise<AdvisorSummary> {
    return this.get(`/incidents/authority-requests/${requestId}/advisor-summary`);
  }
  async getSupplementRequestAdvisorSummary(supplementId: string): Promise<AdvisorSummary> {
    return this.get(`/incidents/supplements/${supplementId}/advisor-summary`);
  }
  // Sprint 47.5.8.4c — Governance lane advisor-summary endpoints.
  async getCrossBorderFilingAdvisorSummary(filingId: string): Promise<AdvisorSummary> {
    return this.get(`/governance/cross-border/filings/${filingId}/advisor-summary`);
  }
  async getMaturityAssessmentAdvisorSummary(assessmentId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/assessments/${assessmentId}/advisor-summary`);
  }
  async getRemediationPlanAdvisorSummary(planId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/remediation-plans/${planId}/advisor-summary`);
  }
  async getGovernanceDocumentAdvisorSummary(docId: string): Promise<AdvisorSummary> {
    return this.get(`/program-governance/governance-documents/${docId}/advisor-summary`);
  }
  /**
   * Record-type → advisor-summary dispatcher. Lets a generic consumer
   * (e.g. the row-action `AskForsetiModal` launchpad, which only knows
   * `{ recordType, recordId }`) fetch the one-paragraph case summary +
   * suggested persona without each list page wiring its own per-module
   * call. Best-effort: returns `null` for record types that have no
   * summary endpoint, and swallows transport errors so a slow/absent
   * summary never blocks the launchpad from rendering.
   */
  async getAdvisorSummaryForRecord(
    recordType: import('@vn-grc/shared').ParentRecordRef['type'],
    recordId: string,
  ): Promise<AdvisorSummary | null> {
    try {
      switch (recordType) {
        case 'dpia':
          return await this.getDpiaAdvisorSummary(recordId);
        case 'dossier_filing':
          return await this.getDossierFilingAdvisorSummary(recordId);
        case 'transfer_assessment':
          return await this.getTransferAssessmentAdvisorSummary(recordId);
        case 'data_mapping_activity':
          return await this.getActivityAdvisorSummary(recordId);
        case 'rights_request':
          return await this.getRightsRequestAdvisorSummary(recordId);
        case 'vendor':
          return await this.getVendorAdvisorSummary(recordId);
        case 'contract':
          return await this.getContractAdvisorSummary(recordId);
        case 'vendor_acceptance':
          return await this.getVendorRiskAcceptanceAdvisorSummary(recordId);
        case 'incident_op':
          return await this.getPlaybookActivationAdvisorSummary(recordId);
        case 'breach_notification':
          return await this.getBreachNotificationAdvisorSummary(recordId);
        case 'authority_request':
          return await this.getAuthorityRequestAdvisorSummary(recordId);
        case 'supplement_request':
          return await this.getSupplementRequestAdvisorSummary(recordId);
        case 'cross_border_filing':
          return await this.getCrossBorderFilingAdvisorSummary(recordId);
        case 'remediation_plan':
          return await this.getRemediationPlanAdvisorSummary(recordId);
        case 'governance_document':
          return await this.getGovernanceDocumentAdvisorSummary(recordId);
        case 'program':
          // Maturity assessments reuse the `program` ParentRecordType
          // (there is no dedicated `maturity_assessment` value), and they
          // are the only consumer of `program` in the Forseti row action,
          // so `recordId` is the assessment id.
          return await this.getMaturityAssessmentAdvisorSummary(recordId);
        default:
          // vendor_contract / incident / task / compliance_rule have no
          // advisor-summary endpoint — render the launchpad without a
          // summary line (the preset persona + 3 actions still apply).
          return null;
      }
    } catch {
      return null;
    }
  }
  // ── Sprint 1.5.0: Assessments -- DPIA Risks ──────────────
  async listDpiaRisks(dpiaId: string): Promise<{ data: DpiaRisk[] }> {
    return this.get(`/assessments/dpias/${dpiaId}/risks`);
  }
  async createDpiaRisk(dpiaId: string, payload: Record<string, unknown>): Promise<DpiaRisk> {
    return this.post(`/assessments/dpias/${dpiaId}/risks`, payload);
  }
  async updateDpiaRisk(riskId: string, payload: Record<string, unknown>): Promise<DpiaRisk> {
    return this.patch(`/assessments/risks/${riskId}`, payload);
  }
  async deleteDpiaRisk(riskId: string): Promise<{ message: string }> {
    return this.del(`/assessments/risks/${riskId}`);
  }
  async updateResidualRisk(
    riskId: string,
    residualLikelihood: number,
    residualImpact: number,
  ): Promise<DpiaRisk> {
    return this.patch(`/assessments/risks/${riskId}/residual`, {
      residual_likelihood: residualLikelihood,
      residual_impact: residualImpact,
    });
  }
  // ── Sprint 1.5.0: Assessments -- DPIA Mitigations ────────
  async listDpiaMitigations(dpiaId: string): Promise<{ data: DpiaMitigation[] }> {
    return this.get(`/assessments/dpias/${dpiaId}/mitigations`);
  }
  async createDpiaMitigation(
    dpiaId: string,
    payload: Record<string, unknown>,
  ): Promise<DpiaMitigation> {
    return this.post(`/assessments/dpias/${dpiaId}/mitigations`, payload);
  }
  async updateDpiaMitigation(
    mitigationId: string,
    payload: Record<string, unknown>,
  ): Promise<DpiaMitigation> {
    return this.patch(`/assessments/mitigations/${mitigationId}`, payload);
  }
  async updateDpiaMitigationStatus(mitigationId: string, status: string): Promise<DpiaMitigation> {
    return this.patch(`/assessments/mitigations/${mitigationId}/status`, { status });
  }
  // ── Sprint 1.5.0: Assessments -- Transfer Assessments ─────
  async listTransferAssessments(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TransferAssessment>> {
    return this.get(`/assessments/transfers${this.buildQuery(params)}`);
  }
  async createTransferAssessment(payload: Record<string, unknown>): Promise<TransferAssessment> {
    return this.post('/assessments/transfers', payload);
  }
  async getTransferAssessment(assessmentId: string): Promise<TransferAssessment> {
    return this.get(`/assessments/transfers/${assessmentId}`);
  }
  async updateTransferAssessment(
    assessmentId: string,
    payload: Record<string, unknown>,
  ): Promise<TransferAssessment> {
    return this.patch(`/assessments/transfers/${assessmentId}`, payload);
  }
  async submitTransferForReview(assessmentId: string): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/submit-review`);
  }
  async approveTransfer(
    assessmentId: string,
    decision: string,
    comments?: string,
  ): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/approve`, { decision, comments });
  }
  async deprecateTransfer(assessmentId: string): Promise<TransferAssessment> {
    return this.post(`/assessments/transfers/${assessmentId}/deprecate`);
  }
  async evaluateTransferRisk(
    assessmentId: string,
    evaluation: Record<string, unknown>,
  ): Promise<TransferAssessment> {
    return this.patch(`/assessments/transfers/${assessmentId}/risk-evaluation`, evaluation);
  }
  // ── Sprint 1.5.0: Assessments -- Safeguards ──────────────
  async listSafeguards(assessmentId: string): Promise<{ data: CrossBorderSafeguard[] }> {
    return this.get(`/assessments/transfers/${assessmentId}/safeguards`);
  }
  async createSafeguard(
    assessmentId: string,
    payload: Record<string, unknown>,
  ): Promise<CrossBorderSafeguard> {
    return this.post(`/assessments/transfers/${assessmentId}/safeguards`, payload);
  }
  async updateSafeguard(
    safeguardId: string,
    payload: Record<string, unknown>,
  ): Promise<CrossBorderSafeguard> {
    return this.patch(`/assessments/safeguards/${safeguardId}`, payload);
  }
  async updateSafeguardStatus(safeguardId: string, status: string): Promise<CrossBorderSafeguard> {
    return this.patch(`/assessments/safeguards/${safeguardId}/status`, { status });
  }
  // ── Sprint 1.5.0: Assessments -- Dashboard & Gap Report ──
  async getAssessmentDashboard(params?: Record<string, string>): Promise<AssessmentDashboard> {
    return this.get(`/assessments/dashboard${this.buildQuery(params)}`);
  }
  async getGapReport(params?: Record<string, string>): Promise<GapReport> {
    return this.get(`/assessments/gap-report${this.buildQuery(params)}`);
  }
  async getAssessmentAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<AssessmentAuditEvent>> {
    return this.get(`/assessments/audit${this.buildQuery(params)}`);
  }
  // ── Sprint 1.6.0: Classification -- Taxonomies ──────────────
  async listTaxonomies(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TaxonomyDefinition>> {
    return this.get(`/classification/taxonomies${this.buildQuery(params)}`);
  }
  async getTaxonomy(taxonomyId: string): Promise<TaxonomyDefinition> {
    return this.get(`/classification/taxonomies/${taxonomyId}`);
  }
  async createTaxonomy(payload: Record<string, unknown>): Promise<TaxonomyDefinition> {
    return this.post('/classification/taxonomies', payload);
  }
  async updateTaxonomy(
    taxonomyId: string,
    payload: Record<string, unknown>,
  ): Promise<TaxonomyDefinition> {
    return this.patch(`/classification/taxonomies/${taxonomyId}`, payload);
  }
  async deleteTaxonomy(taxonomyId: string): Promise<void> {
    return this.del(`/classification/taxonomies/${taxonomyId}`);
  }
  async activateTaxonomy(taxonomyId: string): Promise<TaxonomyDefinition> {
    return this.post(`/classification/taxonomies/${taxonomyId}/activate`);
  }
  async deprecateTaxonomy(taxonomyId: string): Promise<TaxonomyDefinition> {
    return this.post(`/classification/taxonomies/${taxonomyId}/deprecate`);
  }
  async seedDefaultTaxonomies(): Promise<Record<string, unknown>> {
    return this.post('/classification/taxonomies/seed-defaults');
  }
  // ── Sprint 1.6.0: Classification -- Taxonomy Levels ────────
  async listTaxonomyLevels(taxonomyId: string): Promise<TaxonomyLevel[]> {
    return this.get(`/classification/taxonomies/${taxonomyId}/levels`);
  }
  async createTaxonomyLevel(
    taxonomyId: string,
    payload: Record<string, unknown>,
  ): Promise<TaxonomyLevel> {
    return this.post(`/classification/taxonomies/${taxonomyId}/levels`, payload);
  }
  async updateTaxonomyLevel(
    levelId: string,
    payload: Record<string, unknown>,
  ): Promise<TaxonomyLevel> {
    return this.patch(`/classification/levels/${levelId}`, payload);
  }
  async deleteTaxonomyLevel(levelId: string): Promise<void> {
    return this.del(`/classification/levels/${levelId}`);
  }
  async reorderTaxonomyLevels(taxonomyId: string, levelIds: string[]): Promise<TaxonomyLevel[]> {
    return this.post(`/classification/taxonomies/${taxonomyId}/levels/reorder`, {
      level_ids: levelIds,
    });
  }
  // ── Sprint 1.6.0: Classification -- Taxonomy Versions ──────
  async listTaxonomyVersions(taxonomyId: string): Promise<TaxonomyVersion[]> {
    return this.get(`/classification/taxonomies/${taxonomyId}/versions`);
  }
  async getTaxonomyVersion(versionId: string): Promise<TaxonomyVersion> {
    return this.get(`/classification/versions/${versionId}`);
  }
  // ── Sprint 1.6.0: Classification -- Labels ─────────────────
  async listLabels(params?: Record<string, string>): Promise<PaginatedResponse<LabeledObject>> {
    return this.get(`/classification/labels${this.buildQuery(params)}`);
  }
  async getLabel(labelId: string): Promise<LabeledObject> {
    return this.get(`/classification/labels/${labelId}`);
  }
  async assignLabel(payload: Record<string, unknown>): Promise<LabeledObject> {
    return this.post('/classification/labels', payload);
  }
  async updateLabel(labelId: string, payload: Record<string, unknown>): Promise<LabeledObject> {
    return this.patch(`/classification/labels/${labelId}`, payload);
  }
  async deleteLabel(labelId: string): Promise<void> {
    return this.del(`/classification/labels/${labelId}`);
  }
  async bulkAssignLabels(payload: Record<string, unknown>): Promise<LabeledObject[]> {
    return this.post('/classification/labels/bulk', payload);
  }
  async reviewLabel(labelId: string, payload?: Record<string, unknown>): Promise<LabeledObject> {
    return this.post(`/classification/labels/${labelId}/review`, payload);
  }
  async getStaleLabels(days?: number): Promise<LabeledObject[]> {
    const query = days ? `?days=${days}` : '';
    return this.get(`/classification/labels/stale${query}`);
  }
  async getLabelConflicts(): Promise<Record<string, unknown>[]> {
    return this.get('/classification/labels/conflicts');
  }
  // ── Sprint 1.6.0: Classification -- Lifecycle ──────────────
  async listLifecycleRecords(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<LifecycleRecord>> {
    return this.get(`/classification/lifecycle${this.buildQuery(params)}`);
  }
  async getLifecycleRecord(recordId: string): Promise<LifecycleRecord> {
    return this.get(`/classification/lifecycle/${recordId}`);
  }
  async createLifecycleRecord(payload: Record<string, unknown>): Promise<LifecycleRecord> {
    return this.post('/classification/lifecycle', payload);
  }
  async updateLifecycleRecord(
    recordId: string,
    payload: Record<string, unknown>,
  ): Promise<LifecycleRecord> {
    return this.patch(`/classification/lifecycle/${recordId}`, payload);
  }
  async transitionLifecycleStage(
    recordId: string,
    toStage: string,
    description?: string,
  ): Promise<LifecycleRecord> {
    return this.post(`/classification/lifecycle/${recordId}/transition`, {
      to_stage: toStage,
      description,
    });
  }
  async placeLifecycleHold(recordId: string, reason: string): Promise<LifecycleRecord> {
    return this.post(`/classification/lifecycle/${recordId}/hold`, { hold_reason: reason });
  }
  async releaseLifecycleHold(recordId: string): Promise<LifecycleRecord> {
    return this.post(`/classification/lifecycle/${recordId}/release-hold`);
  }
  async listLifecycleEvents(
    recordId: string,
    params?: Record<string, string>,
  ): Promise<LifecycleEvent[]> {
    return this.get(`/classification/lifecycle/${recordId}/events${this.buildQuery(params)}`);
  }
  async createLifecycleEvent(
    recordId: string,
    payload: Record<string, unknown>,
  ): Promise<LifecycleEvent> {
    return this.post(`/classification/lifecycle/${recordId}/events`, payload);
  }
  async getExpiredRetentions(): Promise<LifecycleRecord[]> {
    return this.get('/classification/lifecycle/expired');
  }
  // ── Sprint 1.6.0: Classification -- Gaps ───────────────────
  async listClassificationGaps(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ClassificationGap>> {
    return this.get(`/classification/gaps${this.buildQuery(params)}`);
  }
  async getClassificationGap(gapId: string): Promise<ClassificationGap> {
    return this.get(`/classification/gaps/${gapId}`);
  }
  async acknowledgeGap(gapId: string): Promise<ClassificationGap> {
    return this.post(`/classification/gaps/${gapId}/acknowledge`);
  }
  async remediateGap(gapId: string, notes?: string): Promise<ClassificationGap> {
    return this.post(`/classification/gaps/${gapId}/remediate`, { notes });
  }
  async acceptGapRisk(gapId: string, justification: string): Promise<ClassificationGap> {
    return this.post(`/classification/gaps/${gapId}/accept-risk`, { justification });
  }
  async getGapSummary(): Promise<Record<string, unknown>> {
    return this.get('/classification/gaps/summary');
  }
  async detectGaps(): Promise<Record<string, unknown>> {
    return this.post('/classification/gaps/detect');
  }
  // ── Sprint 1.6.0: Classification -- Audit ──────────────────
  async getClassificationAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ClassificationAuditEvent>> {
    return this.get(`/classification/audit${this.buildQuery(params)}`);
  }
  async getClassificationEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<ClassificationAuditEvent[]> {
    return this.get(`/classification/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 1.6.0: Classification -- Exports ────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportClassificationCSV(exportType: string): Promise<AttachmentRef> {
    return this.post(`/classification/exports/csv/${exportType}`);
  }
  async exportClassificationPDF(exportType: string): Promise<Blob> {
    await this.fetchCsrfToken();
    const headers: Record<string, string> = {};
    if (this.csrfToken) headers['X-CSRF-Token'] = this.csrfToken;
    const response = await fetch(`${API_BASE}/classification/exports/pdf/${exportType}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  async scheduleDiscoveryCycle(
    cycleId: string,
    data: { interval_days: number; enabled: boolean },
  ): Promise<unknown> {
    return this.post(`/discovery/cycles/${cycleId}/schedule`, data);
  }
  // ── Sprint 20.8.0: Classification Maturity ──────────────────
  async submitLabelForReview(labelId: string) {
    return this.post(`/classification/labels/${labelId}/submit-review`, {});
  }
  async approveLabel(labelId: string) {
    return this.post(`/classification/labels/${labelId}/approve`, {});
  }
  async deprecateLabel(labelId: string) {
    return this.post(`/classification/labels/${labelId}/deprecate`, {});
  }
  async listReviewTasks(params?: Record<string, string>) {
    return this.get(`/classification/review-tasks${this.buildQuery(params)}`);
  }
  async resolveReviewTask(taskId: string, data: { resolution: string; notes: string }) {
    return this.post(`/classification/review-tasks/${taskId}/resolve`, data);
  }
  async listDmReviewTasks(params?: Record<string, string>) {
    return this.get(`/data-mapping/review-tasks${this.buildQuery(params)}`);
  }
  async resolveDmReviewTask(taskId: string, data: { resolution: string; notes: string }) {
    return this.post(`/data-mapping/review-tasks/${taskId}/resolve`, data);
  }
  async bulkDmReviewTasks(taskIds: string[], action: 'approve' | 'reject') {
    return this.post('/data-mapping/review-tasks/bulk', { task_ids: taskIds, action });
  }
  async createReviewTask(data: Record<string, any>) {
    return this.post('/classification/review-tasks', data);
  }
  async getEscalatedTasks() {
    return this.get('/classification/review-tasks/escalated');
  }
  async createClassificationException(data: Record<string, any>) {
    return this.post('/classification/exceptions', data);
  }
  async listClassificationExceptions(params?: Record<string, string>) {
    return this.get(`/classification/exceptions${this.buildQuery(params)}`);
  }
  async resolveClassificationException(
    exceptionId: string,
    notes: string,
    resolution: 'resolved' | 'accepted_risk',
  ) {
    return this.post(`/classification/exceptions/${exceptionId}/resolve`, {
      resolution,
      resolution_notes: notes,
    });
  }
  async getExceptionDashboard() {
    return this.get('/classification/exceptions/dashboard');
  }
  async getEnterpriseInventory() {
    return this.get('/classification/inventory');
  }
  async getInventorySummary() {
    return this.get('/classification/inventory/summary');
  }
  async listDiscoveryCycles(params?: Record<string, string>) {
    return this.get(`/discovery/cycles${this.buildQuery(params)}`);
  }
  async createDiscoveryCycle(data: Record<string, any>) {
    return this.post('/discovery/cycles', data);
  }
  async runDeltaDetection(cycleId: string) {
    return this.post(`/discovery/cycles/${cycleId}/delta`, {});
  }
  // ── Sprint 21.4.0: Discovery Cycle Orchestration ──────────────
  async advanceCycleStatus(cycleId: string, status: string) {
    return this.patch(`/discovery/cycles/${cycleId}/status`, { status });
  }
  async compareCycles(currentId: string, previousId: string) {
    return this.get(
      `/discovery/cycles/compare?current_cycle_id=${currentId}&previous_cycle_id=${previousId}`,
    );
  }
  async configureScheduledScan(data: Record<string, any>) {
    return this.post('/discovery/cycles/schedule', data);
  }
  async routeStagedRecordToOwner(recordId: string, ownerId: string) {
    return this.post('/discovery/review-tasks', { record_id: recordId, owner_id: ownerId });
  }
  async listOwnerReviewTasks(params?: Record<string, string>) {
    return this.get(`/discovery/review-tasks/owner/${(params as any)?.owner_id ?? 'me'}`);
  }
  async resolveOwnerReviewTask(taskId: string, decision: string, notes?: string) {
    return this.post(`/discovery/review-tasks/${taskId}/resolve`, { decision, notes });
  }
  async bulkResolveReviewTasks(taskIds: string[], decision: string, notes?: string) {
    return this.post('/discovery/review-tasks/bulk-resolve', {
      task_ids: taskIds,
      decision,
      notes,
    });
  }
  async listExceptionItems(params?: Record<string, string>) {
    return this.get(`/discovery/review-tasks/exceptions${this.buildQuery(params)}`);
  }
  async linkSuggestionToLabel(suggestionId: string, proposedLabelId: string) {
    return this.post('/discovery/lineage/link-suggestion', {
      suggestion_id: suggestionId,
      proposed_label_id: proposedLabelId,
    });
  }
  async getDiscoveryLineage(recordId: string) {
    return this.get(`/discovery/lineage/${recordId}`);
  }
  // ── Sprint 1.7.0: Monitoring -- Programs ─────────────────────
  async listPrograms(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<MonitoringProgram>> {
    return this.get(`/monitoring/programs${this.buildQuery(params)}`);
  }
  async getProgram(programId: string): Promise<MonitoringProgram> {
    return this.get(`/monitoring/programs/${programId}`);
  }
  async createProgram(payload: Record<string, unknown>): Promise<MonitoringProgram> {
    return this.post('/monitoring/programs', payload);
  }
  async updateProgram(
    programId: string,
    payload: Record<string, unknown>,
  ): Promise<MonitoringProgram> {
    return this.patch(`/monitoring/programs/${programId}`, payload);
  }
  async activateProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/activate`);
  }
  async pauseProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/pause`);
  }
  async completeProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/complete`);
  }
  async archiveProgram(programId: string): Promise<MonitoringProgram> {
    return this.post(`/monitoring/programs/${programId}/archive`);
  }
  // ── Sprint 1.7.0: Monitoring -- Periods ─────────────────────
  async listProgramPeriods(
    programId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ProgramPeriod>> {
    return this.get(`/monitoring/programs/${programId}/periods${this.buildQuery(params)}`);
  }
  async getPeriod(periodId: string): Promise<ProgramPeriod> {
    return this.get(`/monitoring/periods/${periodId}`);
  }
  async createPeriod(programId: string, payload: Record<string, unknown>): Promise<ProgramPeriod> {
    return this.post(`/monitoring/programs/${programId}/periods`, payload);
  }
  async autoGeneratePeriods(programId: string): Promise<ProgramPeriod[]> {
    return this.post(`/monitoring/programs/${programId}/periods/auto-generate`);
  }
  async startPeriod(periodId: string): Promise<ProgramPeriod> {
    return this.post(`/monitoring/periods/${periodId}/start`);
  }
  async closePeriod(periodId: string): Promise<ProgramPeriod> {
    return this.post(`/monitoring/periods/${periodId}/close`);
  }
  async updatePeriodReviewers(periodId: string, reviewerIds: string[]): Promise<ProgramPeriod> {
    return this.patch(`/monitoring/periods/${periodId}/reviewers`, { reviewer_ids: reviewerIds });
  }
  // ── Sprint 1.7.0: Monitoring -- Task Templates ──────────────
  async listTaskTemplates(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TaskTemplate>> {
    return this.get(`/monitoring/task-templates${this.buildQuery(params)}`);
  }
  async getTaskTemplate(templateId: string): Promise<TaskTemplate> {
    return this.get(`/monitoring/task-templates/${templateId}`);
  }
  async createTaskTemplate(payload: Record<string, unknown>): Promise<TaskTemplate> {
    return this.post('/monitoring/task-templates', payload);
  }
  async updateTaskTemplate(
    templateId: string,
    payload: Record<string, unknown>,
  ): Promise<TaskTemplate> {
    return this.patch(`/monitoring/task-templates/${templateId}`, payload);
  }
  async deleteTaskTemplate(templateId: string): Promise<void> {
    return this.del(`/monitoring/task-templates/${templateId}`);
  }
  async seedTaskTemplateDefaults(): Promise<Record<string, unknown>> {
    return this.post('/monitoring/task-templates/seed-defaults');
  }
  // ── Sprint 1.7.0: Monitoring -- Task Instances ──────────────
  async listTaskInstances(
    periodId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<TaskInstance>> {
    return this.get(`/monitoring/periods/${periodId}/tasks${this.buildQuery(params)}`);
  }
  async getTaskInstance(taskId: string): Promise<TaskInstance> {
    return this.get(`/monitoring/tasks/${taskId}`);
  }
  async createTaskInstance(
    periodId: string,
    payload: Record<string, unknown>,
  ): Promise<TaskInstance> {
    return this.post(`/monitoring/periods/${periodId}/tasks`, payload);
  }
  async instantiateTasksFromTemplates(periodId: string): Promise<TaskInstance[]> {
    return this.post(`/monitoring/periods/${periodId}/tasks/instantiate`);
  }
  async updateTaskStatus(taskId: string, status: string): Promise<TaskInstance> {
    return this.patch(`/monitoring/tasks/${taskId}/status`, { status });
  }
  async updateTaskChecklist(
    taskId: string,
    itemId: string,
    checked: boolean,
  ): Promise<TaskInstance> {
    return this.patch(`/monitoring/tasks/${taskId}/checklist`, { item_id: itemId, checked });
  }
  async setTaskChecklist(
    taskId: string,
    checklistItems: Array<{ id?: string; label: string; description?: string; required?: boolean }>,
  ): Promise<TaskInstance> {
    return this.put(`/monitoring/tasks/${taskId}/checklist-items`, {
      checklist_items: checklistItems,
    });
  }
  async deferTask(taskId: string, reason: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/defer`, { reason });
  }
  async blockTask(taskId: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/block`);
  }
  async completeTask(taskId: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/complete`);
  }
  async overrideCompleteTask(taskId: string, justification: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/override-complete`, {
      justification,
    });
  }
  async assignTask(taskId: string, assignedTo: string): Promise<TaskInstance> {
    return this.patch(`/monitoring/tasks/${taskId}/assign`, { assigned_to: assignedTo });
  }
  async addTaskEvidence(taskId: string, evidenceId: string): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/evidence`, { evidence_id: evidenceId });
  }
  async addTaskChangeRef(
    taskId: string,
    changeRef: Record<string, unknown>,
  ): Promise<TaskInstance> {
    return this.post(`/monitoring/tasks/${taskId}/change-ref`, changeRef);
  }
  // ── Sprint 1.7.0: Monitoring -- Change Detection ────────────
  async detectProgramChanges(programId: string): Promise<MonitoringChange[]> {
    return this.post(`/monitoring/programs/${programId}/detect-changes`);
  }
  async getPeriodChangeDigest(periodId: string): Promise<Record<string, unknown>> {
    return this.get(`/monitoring/periods/${periodId}/change-digest`);
  }
  // ── Sprint 1.7.0: Monitoring -- KPIs ────────────────────────
  async listKpiSnapshots(
    programId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<KpiSnapshot>> {
    return this.get(`/monitoring/programs/${programId}/kpis${this.buildQuery(params)}`);
  }
  async getLatestKpiSnapshot(programId: string): Promise<KpiSnapshot> {
    return this.get(`/monitoring/programs/${programId}/kpis/latest`);
  }
  async createKpiSnapshot(programId: string): Promise<KpiSnapshot> {
    return this.post(`/monitoring/programs/${programId}/kpis/snapshot`);
  }
  async getKpiTrends(programId: string): Promise<Record<string, unknown>> {
    return this.get(`/monitoring/programs/${programId}/kpis/trends`);
  }
  // ── Sprint 1.7.0: Monitoring -- RIC Cases ───────────────────
  async listRicCases(params?: Record<string, string>): Promise<PaginatedResponse<RicCase>> {
    return this.get(`/monitoring/ric${this.buildQuery(params)}`);
  }
  async getRicCase(ricId: string): Promise<RicCase> {
    return this.get(`/monitoring/ric/${ricId}`);
  }
  async createRicCase(payload: Record<string, unknown>): Promise<RicCase> {
    return this.post('/monitoring/ric', payload);
  }
  async updateRicCase(ricId: string, payload: Record<string, unknown>): Promise<RicCase> {
    return this.patch(`/monitoring/ric/${ricId}`, payload);
  }
  async transitionRicStatus(ricId: string, status: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/transition`, { status });
  }
  async addRicFollowUp(ricId: string, question: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/follow-up`, { question });
  }
  async respondToRicFollowUp(ricId: string, itemId: string, response: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/follow-up/${itemId}/respond`, { response });
  }
  async linkRicEvidence(ricId: string, evidenceId: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/link-evidence`, { evidence_id: evidenceId });
  }
  async addRicEvidence(ricId: string, evidenceId: string): Promise<RicCase> {
    return this.post(`/monitoring/ric/${ricId}/evidence`, { evidence_id: evidenceId });
  }
  async getRicTemplates(): Promise<Record<string, unknown>[]> {
    return this.get('/monitoring/ric/templates');
  }
  // ── Sprint 1.7.0: Monitoring -- Audit ───────────────────────
  async getMonitoringAudit(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<MonitoringAuditEvent>> {
    return this.get(`/monitoring/audit${this.buildQuery(params)}`);
  }
  async getMonitoringEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<MonitoringAuditEvent[]> {
    return this.get(`/monitoring/audit/${entityType}/${entityId}`);
  }
  // ── Monitoring Maturity (Sprint 21.3.0) ─────────────────────
  async listFrameworkLinks(params?: Record<string, any>) {
    return this.get(`/monitoring/framework-links${this.buildQuery(params)}`);
  }
  async createFrameworkLink(programId: string, frameworkCode: string) {
    return this.post('/monitoring/framework-links', {
      program_id: programId,
      framework_code: frameworkCode,
    });
  }
  async deleteFrameworkLink(linkId: string) {
    return this.del(`/monitoring/framework-links/${linkId}`);
  }
  async seedMonitoringDefaults() {
    return this.post('/monitoring/framework-links/seed-defaults', {});
  }
  async getReadinessScore(linkId: string) {
    return this.get(`/monitoring/framework-links/${encodeURIComponent(linkId)}/readiness`);
  }
  async listAuthorityReadiness(params?: Record<string, any>) {
    return this.get(`/monitoring/authority-readiness${this.buildQuery(params)}`);
  }
  async createAuthorityReadiness(data: Record<string, any>) {
    return this.post('/monitoring/authority-readiness', data);
  }
  async updateReadinessStatus(entryId: string, status: string) {
    return this.patch(`/monitoring/authority-readiness/${encodeURIComponent(entryId)}/status`, {
      readiness_status: status,
    });
  }
  async runReadinessDrill(entryId: string) {
    return this.post(`/monitoring/authority-readiness/${encodeURIComponent(entryId)}/drill`, {});
  }
  async listMonitoringChanges(params?: Record<string, any>) {
    return this.get(`/monitoring/changes${this.buildQuery(params)}`);
  }
  async listChangeReviews(params?: Record<string, any>) {
    return this.get(`/monitoring/change-reviews${this.buildQuery(params)}`);
  }
  async createChangeReview(data: Record<string, any>) {
    return this.post('/monitoring/change-reviews', data);
  }
  async updateChangeReviewDecision(reviewId: string, decision: string) {
    return this.patch(`/monitoring/change-reviews/${encodeURIComponent(reviewId)}/decision`, {
      decision,
    });
  }
  async configureAutoCycle(data: Record<string, any>) {
    return this.post('/monitoring/change-reviews/auto-cycle', data);
  }
  /**
   * Sprint 43.7.3 Wave 3 — R-33 list-export migration for Monitoring.
   *
   * Each method routes the page's "export list" action through the
   * backend UnifiedExportService (csv / xlsx / pdf / docx) and returns
   * an AttachmentRef. The caller is expected to invoke
   * `downloadPlatformFile(ref.file_id, ref.filename)` to trigger the
   * browser download. These replace in-browser `downloadClientExport()`
   * usages on 9 of the 10 Monitoring list/dashboard pages (the 10th,
   * GovernanceRaciPage, reuses `exportRaciMatrix` under
   * /program-governance/raci/export).
   */
  async exportMonitoringProgramsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/programs/export${this.buildQuery(params)}`);
  }
  async exportMonitoringPeriodsList(
    programId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/monitoring/programs/${encodeURIComponent(programId)}/periods/export${this.buildQuery(params)}`,
    );
  }
  async exportMonitoringTaskTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/task-templates/export${this.buildQuery(params)}`);
  }
  async exportMonitoringTasksList(
    periodId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/monitoring/periods/${encodeURIComponent(periodId)}/tasks/export${this.buildQuery(params)}`,
    );
  }
  async exportMonitoringKpi(
    programId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/monitoring/programs/${encodeURIComponent(programId)}/kpis/export${this.buildQuery(params)}`,
    );
  }
  async exportMonitoringRicCasesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/ric/export${this.buildQuery(params)}`);
  }
  async exportMonitoringFrameworkLinksList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/framework-links/export${this.buildQuery(params)}`);
  }
  async exportMonitoringAuthorityReadinessList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/authority-readiness/export${this.buildQuery(params)}`);
  }
  async exportMonitoringChangeReviewsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/monitoring/change-reviews/export${this.buildQuery(params)}`);
  }
  // ── Sprint 17.8.0: UI Assist ─────────────────────────────────
  async getUIAssistStatus(): Promise<{
    enabled: boolean;
    hasPermission: boolean;
    wsUrl: string | null;
  }> {
    return this.get('/ui-assist/status');
  }
  async listUIAssistSessions(): Promise<{ sessions: any[] }> {
    return this.get('/ui-assist/sessions');
  }
  async getUIAssistSession(sessionId: string): Promise<Record<string, unknown>> {
    return this.get(`/ui-assist/sessions/${encodeURIComponent(sessionId)}`);
  }
  async appendUIAssistMessage(
    sessionId: string,
    message: { role: string; content: string; timestamp?: string; messageIndex?: number },
    elementLabel?: string,
  ): Promise<void> {
    return this.post(`/ui-assist/sessions/${encodeURIComponent(sessionId)}/messages`, {
      ...message,
      ...(elementLabel ? { elementLabel } : {}),
    });
  }
  async updateUIAssistSessionStatus(
    sessionId: string,
    status: 'complete' | 'error',
  ): Promise<void> {
    return this.patch(`/ui-assist/sessions/${encodeURIComponent(sessionId)}/status`, { status });
  }
  async deleteUIAssistSession(sessionId: string): Promise<void> {
    return this.del(`/ui-assist/sessions/${encodeURIComponent(sessionId)}`);
  }

  // ── Agent Lobby (Multi-Session Coordination) ─────────────────

  async listLobbies(): Promise<{ lobbies: import('@vn-grc/shared').LobbySession[] }> {
    return this.get('/agent-lobby/lobbies');
  }
  async getActiveLobby(): Promise<{ lobby: import('@vn-grc/shared').LobbySession | null }> {
    return this.get('/agent-lobby/lobbies/active');
  }
  async createLobby(name: string): Promise<{ lobby: import('@vn-grc/shared').LobbySession }> {
    return this.post('/agent-lobby/lobbies', { name });
  }
  async getLobby(lobbyId: string): Promise<{ lobby: import('@vn-grc/shared').LobbySession }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}`);
  }
  async updateLobbyStatus(
    lobbyId: string,
    status: 'active' | 'paused' | 'closed',
  ): Promise<{ ok: boolean }> {
    return this.patch(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/status`, { status });
  }
  async getLobbyAgents(
    lobbyId: string,
  ): Promise<{ agents: import('@vn-grc/shared').LobbyAgent[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/agents`);
  }
  async getLobbyMessages(
    lobbyId: string,
    limit = 100,
  ): Promise<{ messages: import('@vn-grc/shared').LobbyMessage[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/messages?limit=${limit}`);
  }
  async sendLobbyMessage(
    lobbyId: string,
    content: string,
    toAgentId = 'all',
  ): Promise<{ message: import('@vn-grc/shared').LobbyMessage }> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/messages`, {
      content,
      to_agent_id: toAgentId,
    });
  }
  async getLobbyRequests(
    lobbyId: string,
  ): Promise<{ requests: import('@vn-grc/shared').LobbyRequest[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/requests`);
  }
  async getLobbyPendingRequests(
    lobbyId: string,
  ): Promise<{ requests: import('@vn-grc/shared').LobbyRequest[]; count: number }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/requests/pending`);
  }
  async appointBossAgent(
    lobbyId: string,
    agentId: string,
  ): Promise<{ ok: boolean; boss_agent_id: string }> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/boss-agent`, {
      agent_id: agentId,
    });
  }
  async decideLobbyRequest(
    lobbyId: string,
    requestId: string,
    status: 'approved' | 'rejected' | 'deferred',
    decisionNotes?: string,
  ): Promise<{ request: import('@vn-grc/shared').LobbyRequest }> {
    return this.post(
      `/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/requests/${encodeURIComponent(requestId)}/decide`,
      { status, decision_notes: decisionNotes },
    );
  }

  // ── Lobby Tasks (Sprint 35.5.0) ──
  async getLobbyTasks(lobbyId: string): Promise<{ tasks: import('@vn-grc/shared').LobbyTask[] }> {
    return this.get(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/tasks`);
  }
  async createLobbyTask(
    lobbyId: string,
    payload: import('@vn-grc/shared').CreateLobbyTaskPayload,
  ): Promise<{ task: import('@vn-grc/shared').LobbyTask }> {
    return this.post(`/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/tasks`, payload);
  }
  async deleteLobbyTask(lobbyId: string, taskId: string): Promise<{ ok: boolean }> {
    return this.del(
      `/agent-lobby/lobbies/${encodeURIComponent(lobbyId)}/tasks/${encodeURIComponent(taskId)}`,
    );
  }

  // ── HTTP helpers ──────────────────────────────────────────────
  private async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request('GET', path, undefined, true, signal);
  }
  private async post<T>(
    path: string,
    body?: unknown,
    auth = true,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request('POST', path, body, auth, signal);
  }
  private async patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request('PATCH', path, body, true, signal);
  }
  private async put<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request('PUT', path, body, true, signal);
  }
  private async del<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return this.request('DELETE', path, body, true, signal);
  }
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    _auth = true,
    signal?: AbortSignal,
  ): Promise<T> {
    const url = `${API_BASE}${path}`;
    const isIdempotent = IDEMPOTENT_METHODS.includes(method);
    const maxRetries = isIdempotent ? MAX_RETRIES : 0;
    const doRequest = async (): Promise<T> => {
      // Fetch CSRF token for mutation requests
      if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
        await this.fetchCsrfToken();
      }
      let csrfRetried = false;
      let lastError: unknown;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          // Attach CSRF token for mutations
          if (this.csrfToken && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
            headers['X-CSRF-Token'] = this.csrfToken;
          }
          // Combine caller signal with a per-request timeout so fetch never hangs indefinitely
          const timeoutSignal = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
          const effectiveSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
          const response = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            signal: effectiveSignal,
            credentials: 'same-origin',
          });
          // 204 No Content — success with no body (e.g. DELETE)
          if (response.status === 204) return undefined as T;
          // Detect HTML responses (e.g. SPA fallback when backend is unavailable)
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) {
            throw new ApiError({
              code: 'SERVER_ERROR',
              message: 'Backend API is not connected. Please ensure the backend server is running.',
              status: 0,
            });
          }
          if (!response.ok) {
            const errorData = await response
              .json()
              .catch(() => ({ error: 'unknown', message: 'Request failed' }));
            // CSRF token expired — refresh and retry once (without consuming a retry attempt)
            if (response.status === 403 && errorData.error === 'csrf_failed' && !csrfRetried) {
              csrfRetried = true;
              await this.refreshCsrfToken();
              attempt--;
              continue;
            }
            // 401 → auto-logout
            if (response.status === 401 && this.onUnauthorized) {
              this.onUnauthorized();
            }
            // Retry on transient failures (idempotent methods only)
            if (RETRYABLE_STATUSES.includes(response.status) && attempt < maxRetries) {
              const retryAfter = response.headers.get('Retry-After');
              const baseDelay = retryAfter
                ? parseInt(retryAfter, 10) * 1000
                : Math.min(RETRY_BASE_MS * Math.pow(2, attempt), RETRY_MAX_MS);
              const jitter = baseDelay * (0.8 + Math.random() * 0.4);
              await new Promise((r) => setTimeout(r, jitter));
              continue;
            }
            throw new ApiError({
              code: statusToErrorCode(response.status),
              message: errorData.message || 'Request failed',
              status: response.status,
              details: errorData.details,
              // Preserve the backend's semantic code (`error` field) so call
              // sites can localize by it — the HTTP-bucket `code` above cannot
              // tell e.g. invoice_package_mismatch from invoice_not_paid.
              serverCode: typeof errorData.error === 'string' ? errorData.error : undefined,
            });
          }
          if (response.status === 204) return undefined as T;
          return response.json();
        } catch (err: any) {
          // Don't retry aborted requests
          if (err.name === 'AbortError') throw err;
          // Don't retry typed ApiErrors (non-transient)
          if (err instanceof ApiError) throw err;
          // Retry network errors (idempotent only)
          if (attempt < maxRetries) {
            lastError = err;
            const baseDelay = Math.min(RETRY_BASE_MS * Math.pow(2, attempt), RETRY_MAX_MS);
            const jitter = baseDelay * (0.8 + Math.random() * 0.4);
            await new Promise((r) => setTimeout(r, jitter));
            continue;
          }
          throw new ApiError({
            code: 'NETWORK_ERROR',
            message: err.message || 'Network request failed',
          });
        }
      }
      throw lastError || new ApiError({ code: 'UNKNOWN', message: 'Request failed after retries' });
    };
    // Deduplicate in-flight GET requests
    if (method === 'GET') {
      return deduplicateGet<T>(url, doRequest);
    }
    return doRequest();
  }
  // ── Sprint 1.8.0: Incidents -- Core ──────────────────────────
  async listIncidents(params?: Record<string, any>): Promise<{ data: Incident[] }> {
    return this.get(`/incidents/incidents${this.buildQuery(params)}`);
  }
  async getIncident(id: string): Promise<Incident> {
    return this.get(`/incidents/incidents/${id}`);
  }
  async createIncident(payload: Record<string, any>): Promise<Incident> {
    return this.post('/incidents/incidents', payload);
  }
  async updateIncident(id: string, payload: Record<string, any>): Promise<Incident> {
    return this.patch(`/incidents/incidents/${id}`, payload);
  }
  async transitionIncidentStatus(id: string, toStatus: string): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/transition`, { to_status: toStatus });
  }
  async completeTriage(id: string, triageData: Record<string, any>): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/triage`, triageData);
  }
  async updateImpactAssessment(id: string, data: Record<string, any>): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/impact-assessment`, data);
  }
  async updateBreachAssessment(id: string, data: Record<string, any>): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/breach-assessment`, data);
  }
  async updateNotificationStatus(
    id: string,
    jurisdiction: string,
    field: string,
    status: string,
  ): Promise<Incident> {
    return this.patch(`/incidents/incidents/${id}/notification-status`, {
      jurisdiction,
      field,
      status,
    });
  }
  async addIncidentEvidence(id: string, evidenceId: string): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/evidence`, { evidence_id: evidenceId });
  }
  async closeIncident(id: string, justification: string): Promise<Incident> {
    return this.post(`/incidents/incidents/${id}/close`, { justification });
  }
  // ── Sprint 1.8.0: Incidents -- Timeline ─────────────────────
  async listTimelineEvents(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: IncidentTimelineEvent[] }> {
    return this.get(`/incidents/incidents/${incidentId}/timeline${this.buildQuery(params)}`);
  }
  async createTimelineEvent(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<IncidentTimelineEvent> {
    return this.post(`/incidents/incidents/${incidentId}/timeline`, payload);
  }
  // ── Sprint 1.8.0: Incidents -- Decisions ────────────────────
  async listDecisions(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: IncidentDecision[] }> {
    return this.get(`/incidents/incidents/${incidentId}/decisions${this.buildQuery(params)}`);
  }
  async createDecision(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<IncidentDecision> {
    return this.post(`/incidents/incidents/${incidentId}/decisions`, payload);
  }
  // ── Sprint 1.8.0: Incidents -- Tasks ────────────────────────
  async listIncidentTasks(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: IncidentTask[] }> {
    return this.get(`/incidents/incidents/${incidentId}/tasks${this.buildQuery(params)}`);
  }
  async getIncidentTask(taskId: string): Promise<IncidentTask> {
    return this.get(`/incidents/tasks/${taskId}`);
  }
  async createIncidentTask(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<IncidentTask> {
    return this.post(`/incidents/incidents/${incidentId}/tasks`, payload);
  }
  async updateIncidentTaskStatus(taskId: string, status: string): Promise<IncidentTask> {
    return this.patch(`/incidents/tasks/${taskId}/status`, { status });
  }
  async updateIncidentTaskChecklist(
    taskId: string,
    itemId: string,
    checked: boolean,
  ): Promise<IncidentTask> {
    return this.patch(`/incidents/tasks/${taskId}/checklist`, { item_id: itemId, checked });
  }
  async assignIncidentTask(taskId: string, assignedTo: string): Promise<IncidentTask> {
    return this.patch(`/incidents/tasks/${taskId}/assign`, { assigned_to: assignedTo });
  }
  async addIncidentTaskEvidence(taskId: string, evidenceId: string): Promise<IncidentTask> {
    return this.post(`/incidents/tasks/${taskId}/evidence`, { evidence_id: evidenceId });
  }
  async escalateIncidentTask(taskId: string): Promise<IncidentTask> {
    return this.post(`/incidents/tasks/${taskId}/escalate`, {});
  }
  async getIncidentTaskTemplates(): Promise<{ data: Record<string, unknown>[] }> {
    return this.get('/incidents/task-templates');
  }
  // ── Sprint 21.1.0: Incident Response Maturity ──────────────
  async createBreachNotification(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<BreachNotification> {
    return this.post('/incidents/breach-notifications', { incident_id: incidentId, ...payload });
  }
  async listBreachNotifications(
    params?: Record<string, any>,
  ): Promise<{ data: BreachNotification[] }> {
    return this.get(`/incidents/breach-notifications${this.buildQuery(params)}`);
  }
  async getBreachNotification(id: string): Promise<BreachNotification> {
    return this.get(`/incidents/breach-notifications/${id}`);
  }
  async draftBreachNotification(id: string): Promise<BreachNotificationForsetiDraft> {
    return this.post(`/incidents/breach-notifications/${encodeURIComponent(id)}/forseti-draft`, {});
  }
  async resolveBreachNotificationDraft(
    draftId: string,
    payload: BreachNotificationDraftResolveRequest,
  ): Promise<BreachNotificationDraftResolveResult> {
    return this.post(
      `/incidents/breach-notification-drafts/${encodeURIComponent(draftId)}/resolve`,
      payload,
    );
  }
  async transitionNotificationStatus(id: string, toStatus: string): Promise<BreachNotification> {
    return this.post(`/incidents/breach-notifications/${id}/transition`, { to_status: toStatus });
  }
  async makeNotificationDecision(
    id: string,
    decision: string,
    rationale: string,
  ): Promise<BreachNotification> {
    return this.post(`/incidents/breach-notifications/${id}/decision`, { decision, rationale });
  }
  async submitNotificationToMps(
    id: string,
    channel: string,
    confirmation: string,
  ): Promise<BreachNotification> {
    return this.post(`/incidents/breach-notifications/${id}/submit`, { channel, confirmation });
  }
  async generateForm08(id: string): Promise<Record<string, unknown>> {
    return this.get(`/incidents/breach-notifications/${id}/form08`);
  }
  async createSupplementRequest(
    notificationId: string,
    requestDetails: string,
    deadline: string | null,
  ): Promise<IncidentSupplementRequest> {
    return this.post(`/incidents/breach-notifications/${notificationId}/supplements`, {
      request_details: requestDetails,
      deadline,
    });
  }
  async listSupplements(notificationId: string): Promise<{ data: IncidentSupplementRequest[] }> {
    return this.get(`/incidents/breach-notifications/${notificationId}/supplements`);
  }
  async submitSupplementResponse(
    supplementId: string,
    responsePackage: string,
  ): Promise<IncidentSupplementRequest> {
    return this.post(`/incidents/supplements/${supplementId}/submit`, {
      response_package: responsePackage,
    });
  }
  async createRemediationAction(
    incidentId: string,
    payload: Record<string, any>,
  ): Promise<RemediationAction> {
    return this.post('/incidents/remediation-actions', { incident_id: incidentId, ...payload });
  }
  async listRemediationActions(
    incidentId: string,
    params?: Record<string, any>,
  ): Promise<{ data: RemediationAction[] }> {
    return this.get(`/incidents/incidents/${incidentId}/remediation${this.buildQuery(params)}`);
  }
  async updateRemediationStatus(actionId: string, status: string): Promise<RemediationAction> {
    return this.patch(`/incidents/remediation-actions/${actionId}/status`, { status });
  }
  async verifyRemediation(actionId: string): Promise<RemediationAction> {
    return this.post(`/incidents/remediation-actions/${actionId}/verify`, {});
  }
  async createAuthorityRequest(payload: Record<string, any>): Promise<IncidentAuthorityRequest> {
    return this.post('/incidents/authority-requests', payload);
  }
  async listAuthorityRequests(
    params?: Record<string, any>,
  ): Promise<{ data: IncidentAuthorityRequest[] }> {
    return this.get(`/incidents/authority-requests${this.buildQuery(params)}`);
  }
  async submitAuthorityResponse(
    requestId: string,
    responsePackage: string,
    channel: string,
    confirmation: string,
    opts?: {
      response_context?:
        | 'authority_supplement_response'
        | 'authority_information_request_response'
        | 'authority_inspection_response';
      regulatory_framework_code?: string;
      linked_form_instance_id?: string;
    },
  ): Promise<IncidentAuthorityRequest> {
    return this.post(`/incidents/authority-requests/${requestId}/submit`, {
      response_package: responsePackage,
      channel,
      confirmation,
      ...(opts?.response_context ? { response_context: opts.response_context } : {}),
      ...(opts?.regulatory_framework_code
        ? { regulatory_framework_code: opts.regulatory_framework_code }
        : {}),
      ...(opts?.linked_form_instance_id
        ? { linked_form_instance_id: opts.linked_form_instance_id }
        : {}),
    });
  }
  // Sprint 44.5.0 — retry + status for authority-response materialisation
  async retryAuthorityResponseMaterialisation(requestId: string): Promise<{
    request_id: string;
    submission_dossier_id: string;
    materialisation_status: 'pending' | 'success' | 'failed';
    created: boolean;
  }> {
    return this.post(`/incidents/authority-requests/${requestId}/retry-materialisation`);
  }
  async getAuthorityResponseMaterialisationStatus(requestId: string): Promise<{
    filing_id: string;
    status: 'pending' | 'success' | 'failed' | null;
    dossier_id: string | null;
    failure_reason: string | null;
    last_attempt_at: string | null;
    failed_at: string | null;
    retry_count: number;
    eligible_to_retry: boolean;
  }> {
    return this.get(`/incidents/authority-requests/${requestId}/materialisation-status`);
  }
  async transitionAuthorityRequestStatus(
    requestId: string,
    status: string,
  ): Promise<IncidentAuthorityRequest> {
    return this.patch(`/incidents/authority-requests/${requestId}/status`, { status });
  }
  async updateAuthorityRequestChecklist(
    requestId: string,
    checklist: { item: string; checked: boolean }[],
  ): Promise<IncidentAuthorityRequest> {
    return this.patch(`/incidents/authority-requests/${requestId}/checklist`, { checklist });
  }
  async listDisclosureRegister(
    params?: Record<string, any>,
  ): Promise<{ data: DisclosureRegisterEntry[] }> {
    return this.get(`/incidents/disclosure-register${this.buildQuery(params)}`);
  }
  async createDisclosureEntry(payload: Record<string, any>): Promise<DisclosureRegisterEntry> {
    return this.post('/incidents/disclosure-register', payload);
  }
  // ── Sprint 27.2.1: Incident Export ────────────────────────
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async exportIncidentsCsv(params?: Record<string, string>): Promise<AttachmentRef> {
    return this.get(`/incidents/export/csv${this.buildQuery(params)}`);
  }
  async exportIncidentDetailPdf(incidentId: string): Promise<Record<string, unknown>> {
    return this.get(`/incidents/export/pdf/${incidentId}`);
  }
  // ── Sprint 1.8.0: Incidents -- Audit ────────────────────────
  async listIncidentAudit(params?: Record<string, any>): Promise<{ data: IncidentAuditEvent[] }> {
    return this.get(`/incidents/audit${this.buildQuery(params)}`);
  }
  async listIncidentAuditForIncident(incidentId: string): Promise<{ data: IncidentAuditEvent[] }> {
    return this.get(`/incidents/audit/incident/${incidentId}`);
  }
  // ── Sprint 1.9.0: Onboarding -- Setup Wizard ──────────────
  async getWizard() {
    return this.get('/onboarding/wizard');
  }
  async initializeWizard() {
    return this.post('/onboarding/wizard/initialize', {});
  }
  async completeWizardStep(stepId: string, data: Record<string, any>) {
    return this.post(`/onboarding/wizard/steps/${stepId}/complete`, data);
  }
  async completeWizard() {
    return this.post('/onboarding/wizard/complete', {});
  }
  async skipWizard() {
    return this.post('/onboarding/wizard/skip', {});
  }
  // Public setup wizard (no auth required)
  async validateLicensePublic(license_key: string): Promise<Record<string, unknown>> {
    return this.post('/setup/license/validate', { license_key });
  }
  async getSetupWizard() {
    return this.get('/setup/wizard');
  }
  async initializeSetupWizard() {
    return this.post('/setup/wizard/initialize', {});
  }
  async completeSetupWizardStep(stepId: string, data: Record<string, any>) {
    return this.post(`/setup/wizard/steps/${stepId}/complete`, data);
  }
  async completeSetupWizard() {
    return this.post('/setup/wizard/complete', {});
  }
  async skipSetupWizard() {
    return this.post('/setup/wizard/skip', {});
  }
  async createSetupAccount(data: {
    name: string;
    email: string;
    password: string;
    org_name: string;
  }) {
    return this.post('/setup/account/create', data);
  }
  // ── Sprint 1.9.0: Onboarding -- Tours ─────────────────────
  async getTourDefinitions() {
    return this.get('/onboarding/tours/definitions');
  }
  async getTourProgress() {
    return this.get('/onboarding/tours/progress');
  }
  async getTourProgressForModule(module: string) {
    return this.get(`/onboarding/tours/progress/${module}`);
  }
  async startTour(module: string) {
    return this.post(`/onboarding/tours/${module}/start`, {});
  }
  async advanceTour(module: string, stepIndex: number) {
    return this.post(`/onboarding/tours/${module}/advance`, { step_index: stepIndex });
  }
  async completeTour(module: string) {
    return this.post(`/onboarding/tours/${module}/complete`, {});
  }
  async dismissTour(module: string) {
    return this.post(`/onboarding/tours/${module}/dismiss`, {});
  }
  async resetTour(module: string) {
    return this.post(`/onboarding/tours/${module}/reset`, {});
  }
  // ── Sprint 1.9.0: Onboarding -- Help Center ──────────────
  async listHelpArticles(params?: Record<string, any>) {
    return this.get(`/onboarding/help/articles${this.buildQuery(params)}`);
  }
  async searchHelpArticles(q: string, locale?: string) {
    const params = `q=${encodeURIComponent(q)}${locale ? `&locale=${locale}` : ''}`;
    return this.get(`/onboarding/help/articles/search?${params}`);
  }
  async getHelpCategories() {
    return this.get('/onboarding/help/categories');
  }
  async getHelpModules() {
    return this.get('/onboarding/help/modules');
  }
  async getHelpTooltips() {
    return this.get('/onboarding/help/tooltips');
  }
  async getHelpArticleBySlug(slug: string) {
    return this.get(`/onboarding/help/articles/by-slug/${slug}`);
  }
  async getHelpArticle(id: string) {
    return this.get(`/onboarding/help/articles/${id}`);
  }
  async createHelpArticle(payload: Record<string, any>) {
    return this.post('/onboarding/help/articles', payload);
  }
  async updateHelpArticle(id: string, payload: Record<string, any>) {
    return this.patch(`/onboarding/help/articles/${id}`, payload);
  }
  async deleteHelpArticle(id: string) {
    return this.del(`/onboarding/help/articles/${id}`);
  }
  // ── Sprint 1.9.0: Onboarding -- Sample Data ──────────────
  async listSampleDataOperations() {
    return this.get('/onboarding/sample-data/operations');
  }
  async getSampleDataSummary() {
    return this.get('/onboarding/sample-data/summary');
  }
  async loadSampleData(modules?: string[]) {
    return this.post('/onboarding/sample-data/load', modules ? { modules } : {});
  }
  async clearSampleData(modules?: string[]) {
    return this.post('/onboarding/sample-data/clear', modules ? { modules } : {});
  }
  // ── Sprint 1.9.0: Onboarding -- Audit ─────────────────────
  async listOnboardingAudit(params?: Record<string, any>) {
    return this.get(`/onboarding/audit${this.buildQuery(params)}`);
  }
  // ── Sprint 22.4.0: Onboarding Maturity ─────────────────────
  async getPersonaPaths() {
    return this.get('/onboarding/maturity/persona-paths');
  }
  async getPersonaPath(personaId: string) {
    return this.get(`/onboarding/maturity/persona-paths/${personaId}`);
  }
  async detectPersona() {
    return this.get('/onboarding/maturity/detect-persona');
  }
  async getTierModuleConfig(tier: string) {
    return this.get(`/onboarding/maturity/tier-config/${tier}`);
  }
  async getFrameworkSetupOptions() {
    return this.get('/onboarding/maturity/framework-options');
  }
  async selectFrameworks(frameworkCodes: string[]) {
    return this.post('/onboarding/maturity/select-frameworks', { framework_codes: frameworkCodes });
  }
  async getQuickStartScenarios(personaId?: string) {
    const q = personaId ? `?persona_id=${personaId}` : '';
    return this.get(`/onboarding/maturity/quick-starts${q}`);
  }
  async startQuickStart(scenarioId: string) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/start`, {});
  }
  async advanceQuickStart(scenarioId: string, stepIndex: number) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/advance`, {
      step_index: stepIndex,
    });
  }
  async completeQuickStart(scenarioId: string) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/complete`, {});
  }
  async resetQuickStart(scenarioId: string) {
    return this.post(`/onboarding/maturity/quick-starts/${scenarioId}/reset`, {});
  }
  async getOnboardingProgress() {
    return this.get('/onboarding/maturity/progress');
  }
  async getQuickStartProgress() {
    return this.get('/onboarding/maturity/quick-starts/progress');
  }
  async initializeOnboardingProgress(payload: Record<string, any>) {
    return this.post('/onboarding/maturity/progress/initialize', payload);
  }
  // ── Sprint 2.0.0: Connectors -- Definitions ────────────────
  async listConnectorDefinitions(params?: Record<string, any>) {
    return this.get(`/connectors/definitions${this.buildQuery(params)}`);
  }
  async getConnectorDefinition(connectorId: string) {
    return this.get(`/connectors/definitions/${connectorId}`);
  }
  async createConnectorDefinition(payload: Record<string, any>) {
    return this.post('/connectors/definitions', payload);
  }
  async updateConnectorDefinition(connectorId: string, payload: Record<string, any>) {
    return this.patch(`/connectors/definitions/${connectorId}`, payload);
  }
  // ── Sprint 2.0.0: Connectors -- Instances ─────────────────
  async listConnectorInstances(params?: Record<string, any>) {
    return this.get(`/connectors/instances${this.buildQuery(params)}`);
  }
  async getConnectorInstance(id: string) {
    return this.get(`/connectors/instances/${id}`);
  }
  async createConnectorInstance(payload: Record<string, any>) {
    return this.post('/connectors/instances', payload);
  }
  async updateConnectorInstance(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/instances/${id}`, payload);
  }
  async updateConnectorInstanceStatus(id: string, status: string) {
    return this.post(`/connectors/instances/${id}/status`, { status });
  }
  async testConnectorInstance(id: string) {
    return this.post(`/connectors/instances/${id}/test`, {});
  }
  async deleteConnectorInstance(id: string) {
    return this.del(`/connectors/instances/${id}`);
  }
  // ── Sprint 2.0.0: Connectors -- Sync History ─────────────
  async listInstanceSyncHistory(instanceId: string, params?: Record<string, any>) {
    return this.get(`/connectors/instances/${instanceId}/sync-history${this.buildQuery(params)}`);
  }
  async getSyncHistoryEntry(id: string) {
    return this.get(`/connectors/sync-history/${id}`);
  }
  async triggerManualSync(instanceId: string) {
    return this.post(`/connectors/instances/${instanceId}/sync`, {});
  }
  // ── Sprint 2.0.0: Connectors -- Credentials ──────────────
  async listCredentials(params?: Record<string, any>) {
    return this.get(`/connectors/credentials${this.buildQuery(params)}`);
  }
  async getCredential(id: string) {
    return this.get(`/connectors/credentials/${id}`);
  }
  async createCredential(payload: Record<string, any>) {
    return this.post('/connectors/credentials', payload);
  }
  async updateCredential(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/credentials/${id}`, payload);
  }
  async rotateCredential(id: string, value: string) {
    return this.post(`/connectors/credentials/${id}/rotate`, { value });
  }
  async deleteCredential(id: string) {
    return this.del(`/connectors/credentials/${id}`);
  }
  async getCredentialHealth() {
    return this.get('/connectors/credentials/health');
  }
  async getCredentialUsage(id: string) {
    return this.get(`/connectors/credentials/${id}/usage`);
  }
  async listOrphanedCredentials() {
    return this.get('/connectors/credentials/orphans');
  }
  // ── Sprint 2.0.0: Connectors -- Event Bus Events ─────────
  async listEventBusEvents(params?: Record<string, any>) {
    return this.get(`/connectors/events${this.buildQuery(params)}`);
  }
  async getEventBusEvent(id: string) {
    return this.get(`/connectors/events/${id}`);
  }
  async publishEvent(payload: Record<string, any>) {
    return this.post('/connectors/events', payload);
  }
  // ── Sprint 2.0.0: Connectors -- Event Bus Subscriptions ──
  async listEventBusSubscriptions(params?: Record<string, any>) {
    return this.get(`/connectors/subscriptions${this.buildQuery(params)}`);
  }
  async getEventBusSubscription(id: string) {
    return this.get(`/connectors/subscriptions/${id}`);
  }
  async createEventBusSubscription(payload: Record<string, any>) {
    return this.post('/connectors/subscriptions', payload);
  }
  async updateEventBusSubscription(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/subscriptions/${id}`, payload);
  }
  async toggleEventBusSubscription(id: string, enabled: boolean) {
    return this.post(`/connectors/subscriptions/${id}/toggle`, { enabled });
  }
  async deleteEventBusSubscription(id: string) {
    return this.del(`/connectors/subscriptions/${id}`);
  }
  // ── Sprint 2.0.0: Connectors -- DLQ ──────────────────────
  async listDlqEntries(params?: Record<string, any>) {
    return this.get(`/connectors/dlq${this.buildQuery(params)}`);
  }
  async retryDlqEntry(id: string) {
    return this.post(`/connectors/dlq/${id}/retry`, {});
  }
  async dismissDlqEntry(id: string) {
    return this.post(`/connectors/dlq/${id}/dismiss`, {});
  }
  // ── Sprint 2.0.0: Connectors -- Audit ────────────────────
  async listConnectorAudit(params?: Record<string, any>) {
    return this.get(`/connectors/audit${this.buildQuery(params)}`);
  }
  async getConnectorEntityAudit(entityType: string, entityId: string) {
    return this.get(`/connectors/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.6.0: Connectors -- BRD Capture & Decision Records ──
  async listConnectorBrds(params?: Record<string, any>) {
    return this.get(`/connectors/brds${this.buildQuery(params)}`);
  }
  async getConnectorBrd(id: string) {
    return this.get(`/connectors/brds/${id}`);
  }
  async createConnectorBrd(payload: Record<string, any>) {
    return this.post('/connectors/brds', payload);
  }
  async updateConnectorBrdStatus(id: string, status: string) {
    return this.patch(`/connectors/brds/${id}/status`, { status });
  }
  async listConnectorDecisionRecords(params?: Record<string, any>) {
    return this.get(`/connectors/decision-records${this.buildQuery(params)}`);
  }
  async createConnectorDecisionRecord(payload: Record<string, any>) {
    return this.post('/connectors/decision-records', payload);
  }
  // ── Sprint 21.6.0: Connectors -- Control Mapping & Security Review ──
  async createConnectorControlMapping(payload: Record<string, any>) {
    return this.post('/connectors/control-mappings', payload);
  }
  async listConnectorControlMappings(connectorId: string) {
    return this.get(`/connectors/connectors/${connectorId}/control-mappings`);
  }
  async listAllConnectorControlMappings(params?: Record<string, any>) {
    return this.get(`/connectors/control-mappings${this.buildQuery(params)}`);
  }
  async listConnectorSecurityReviews(params?: Record<string, any>) {
    return this.get(`/connectors/security-reviews${this.buildQuery(params)}`);
  }
  async createConnectorSecurityReview(payload: Record<string, any>) {
    return this.post('/connectors/security-reviews', payload);
  }
  async updateConnectorSecurityReviewStatus(id: string, status: string) {
    return this.patch(`/connectors/security-reviews/${id}/status`, { status });
  }
  // ── Sprint 21.6.0: Connectors -- POC Scorecards & Health ──
  async listConnectorPocScorecards(params?: Record<string, any>) {
    return this.get(`/connectors/poc-scorecards${this.buildQuery(params)}`);
  }
  async createConnectorPocScorecard(payload: Record<string, any>) {
    return this.post('/connectors/poc-scorecards', payload);
  }
  async getConnectorPocScorecard(id: string) {
    return this.get(`/connectors/poc-scorecards/${id}`);
  }
  async getConnectorHealthMetrics(instanceId: string) {
    return this.get(`/connectors/connectors/${instanceId}/health-metrics`);
  }
  // ── Sprint 2.1.0: Vendor Risk -- Configuration ─────────────
  async getVendorRiskConfig() {
    return this.get('/vendor-risk/config');
  }
  async updateVendorRiskConfig(payload: Record<string, any>) {
    return this.patch('/vendor-risk/config', payload);
  }
  async updateGateCriteria(criteria: Record<string, any>[]) {
    return this.put('/vendor-risk/config/gate-criteria', { criteria });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Scores ───────────────────
  async listVendorRiskScores(params?: Record<string, any>) {
    return this.get(`/vendor-risk/scores${this.buildQuery(params)}`);
  }
  async calculateVendorRiskScore(payload: Record<string, any>) {
    return this.post('/vendor-risk/scores', payload);
  }
  async getVendorRiskDistribution() {
    return this.get('/vendor-risk/scores/distribution');
  }
  async getVendorRiskTierChanges(params?: Record<string, any>) {
    return this.get(`/vendor-risk/scores/tier-changes${this.buildQuery(params)}`);
  }
  async getVendorScoreHistory(vendorId: string, params?: Record<string, any>) {
    return this.get(`/vendor-risk/scores/vendor/${vendorId}${this.buildQuery(params)}`);
  }
  async getVendorLatestScore(vendorId: string) {
    return this.get(`/vendor-risk/scores/vendor/${vendorId}/latest`);
  }
  async getVendorRiskScore(scoreId: string) {
    return this.get(`/vendor-risk/scores/${scoreId}`);
  }
  async compareVendorRiskScores(scoreId1: string, scoreId2: string) {
    return this.get(`/vendor-risk/scores/compare/${scoreId1}/${scoreId2}`);
  }
  async batchRecalculateScores(vendorIds: string[]) {
    return this.post('/vendor-risk/scores/batch-recalculate', { vendor_ids: vendorIds });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Questionnaire Templates ──
  async listQuestionnaireTemplates(params?: Record<string, any>) {
    return this.get(`/vendor-risk/questionnaire-templates${this.buildQuery(params)}`);
  }
  async getQuestionnaireTemplate(templateId: string) {
    return this.get(`/vendor-risk/questionnaire-templates/${templateId}`);
  }
  async createQuestionnaireTemplate(payload: Record<string, any>) {
    return this.post('/vendor-risk/questionnaire-templates', payload);
  }
  async updateQuestionnaireTemplate(templateId: string, payload: Record<string, any>) {
    return this.patch(`/vendor-risk/questionnaire-templates/${templateId}`, payload);
  }
  async updateQuestionnaireTemplateStatus(templateId: string, status: string) {
    return this.post(`/vendor-risk/questionnaire-templates/${templateId}/status`, { status });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Questionnaire Instances ──
  async listQuestionnaires(params?: Record<string, any>) {
    return this.get(`/vendor-risk/questionnaires${this.buildQuery(params)}`);
  }
  async getQuestionnaire(instanceId: string) {
    return this.get(`/vendor-risk/questionnaires/${instanceId}`);
  }
  async initiateQuestionnaire(payload: Record<string, any>) {
    return this.post('/vendor-risk/questionnaires', payload);
  }
  async updateQuestionnaireResponses(instanceId: string, responses: Record<string, any>[]) {
    return this.patch(`/vendor-risk/questionnaires/${instanceId}/responses`, { responses });
  }
  async submitQuestionnaire(instanceId: string) {
    return this.post(`/vendor-risk/questionnaires/${instanceId}/submit`, {});
  }
  async reviewQuestionnaire(instanceId: string, payload: Record<string, any>) {
    return this.post(`/vendor-risk/questionnaires/${instanceId}/review`, payload);
  }
  async resubmitQuestionnaire(instanceId: string) {
    return this.post(`/vendor-risk/questionnaires/${instanceId}/resubmit`, {});
  }
  /**
   * Sprint 43.6.0 W4 — single-instance export (PDF / DOCX audit record
   * or CSV / XLSX analytics). Returns AttachmentRef persisted via
   * UnifiedExportService; caller triggers the download via
   * downloadPlatformFile(ref.file_id, ref.filename).
   */
  async exportQuestionnaireInstance(args: {
    id: string;
    format: 'pdf' | 'docx' | 'csv' | 'xlsx';
  }): Promise<AttachmentRef> {
    return this.get(
      `/vendor-risk/questionnaires/${args.id}/export${this.buildQuery({ format: args.format })}`,
    );
  }
  /**
   * Sprint 43.6.0 W4 — portfolio-level CSV / XLSX export. Filters match
   * the listQuestionnaires endpoint. Returns AttachmentRef via File
   * Component; caller invokes downloadPlatformFile to trigger download.
   */
  async exportQuestionnaireList(args: {
    filters?: Record<string, string | undefined>;
    format: 'csv' | 'xlsx';
  }): Promise<AttachmentRef> {
    const params: Record<string, string> = { format: args.format };
    if (args.filters) {
      for (const [k, v] of Object.entries(args.filters)) {
        if (typeof v === 'string' && v.length > 0) params[k] = v;
      }
    }
    return this.get(`/vendor-risk/questionnaires/export${this.buildQuery(params)}`);
  }
  // ── Sprint 47.5.8.1: Contracts (first-class module) ───────
  async listContracts(params?: Record<string, any>) {
    return this.get(`/contracts${this.buildQuery(params)}`);
  }
  async getExpiringContracts(withinDays?: number) {
    const query = withinDays ? `?within_days=${withinDays}` : '';
    return this.get(`/contracts/expiring${query}`);
  }
  async getContractCoverage() {
    return this.get('/contracts/coverage');
  }
  async getContract(contractId: string) {
    return this.get(`/contracts/${contractId}`);
  }
  async createContract(payload: Record<string, any>) {
    return this.post('/contracts', payload);
  }
  async updateContract(contractId: string, payload: Record<string, any>) {
    return this.patch(`/contracts/${contractId}`, payload);
  }
  async updateContractStatus(contractId: string, status: string) {
    return this.post(`/contracts/${contractId}/status`, { status });
  }
  async deleteContract(contractId: string) {
    return this.del(`/contracts/${contractId}`);
  }
  // ── Sprint 2.1.0: Vendor Risk -- Gate Evaluations ─────────
  async evaluateVendorGate(vendorId: string, approvalId: string) {
    return this.post('/vendor-risk/gates/evaluate', {
      vendor_id: vendorId,
      approval_id: approvalId,
    });
  }
  async listGateEvaluations(params?: Record<string, any>) {
    return this.get(`/vendor-risk/gates${this.buildQuery(params)}`);
  }
  async getVendorLatestGate(vendorId: string) {
    return this.get(`/vendor-risk/gates/vendor/${vendorId}/latest`);
  }
  async getGateEvaluation(evaluationId: string) {
    return this.get(`/vendor-risk/gates/${evaluationId}`);
  }
  async overrideGateEvaluation(evaluationId: string, justification: string) {
    return this.post(`/vendor-risk/gates/${evaluationId}/override`, { justification });
  }
  // ── Sprint 2.1.0: Vendor Risk -- Reviews ──────────────────
  async listVendorReviews(params?: Record<string, any>) {
    return this.get(`/vendor-risk/reviews${this.buildQuery(params)}`);
  }
  async getVendorReviewKpis() {
    return this.get('/vendor-risk/reviews/kpis');
  }
  async getVendorReviewHistory(vendorId: string, params?: Record<string, any>) {
    return this.get(`/vendor-risk/reviews/vendor/${vendorId}${this.buildQuery(params)}`);
  }
  async getVendorReview(reviewId: string) {
    return this.get(`/vendor-risk/reviews/${reviewId}`);
  }
  async createVendorReview(payload: Record<string, any>) {
    return this.post('/vendor-risk/reviews', payload);
  }
  async updateVendorReview(reviewId: string, payload: Record<string, any>) {
    return this.patch(`/vendor-risk/reviews/${reviewId}`, payload);
  }
  // ── Sprint 2.1.0: Vendor Risk -- Audit ────────────────────
  async listVendorRiskAudit(params?: Record<string, any>) {
    return this.get(`/vendor-risk/audit${this.buildQuery(params)}`);
  }
  async getVendorRiskEntityAudit(entityType: string, entityId: string) {
    return this.get(`/vendor-risk/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.0.0: Vendor Risk Maturity ──────────────────────
  async createRiskSignal(payload: Record<string, unknown>): Promise<VendorRiskSignal> {
    return this.post('/vendor-risk/signals', payload);
  }
  async listRiskSignals(params?: Record<string, string>): Promise<VendorRiskSignal[]> {
    return this.get(`/vendor-risk/signals${this.buildQuery(params)}`);
  }
  async triggerVendorReassessment(
    payload: Record<string, unknown>,
  ): Promise<VendorReassessmentTask> {
    return this.post('/vendor-risk/reassessments', payload);
  }
  async listReassessmentTasks(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<VendorReassessmentTask>> {
    return this.get(`/vendor-risk/reassessments${this.buildQuery(params)}`);
  }
  async completeVendorReassessment(
    taskId: string,
    data: Record<string, unknown>,
  ): Promise<VendorReassessmentTask> {
    return this.post(`/vendor-risk/reassessments/${taskId}/complete`, data);
  }
  async getVendorRiskTrend(vendorId: string): Promise<VendorRiskScore[]> {
    return this.get(`/vendor-risk/vendors/${vendorId}/risk-trend`);
  }
  async getVendorRiskPortfolio(): Promise<Record<string, unknown>> {
    return this.get('/vendor-risk/portfolio/dashboard');
  }
  async getVendorRiskTrendById(vendorId: string): Promise<VendorRiskScore[]> {
    return this.get(`/vendor-risk/portfolio/vendor-trend/${vendorId}`);
  }
  async listOutstandingQuestionnaires(): Promise<QuestionnaireInstance[]> {
    return this.get('/vendor-risk/portfolio/outstanding-questionnaires');
  }
  async listOverdueAssessments(): Promise<Record<string, unknown>[]> {
    return this.get('/vendor-risk/portfolio/overdue-assessments');
  }
  async routeToVendorReviewers(payload: Record<string, unknown>): Promise<ReviewRouting> {
    return this.post('/vendor-risk/review-routing', payload);
  }
  async getVendorReviewRoutingStatus(questionnaireId: string): Promise<ReviewRouting> {
    return this.get(`/vendor-risk/review-routing/${questionnaireId}`);
  }
  async createRiskAcceptance(payload: Record<string, unknown>): Promise<VendorRiskAcceptance> {
    return this.post('/vendor-risk/acceptances', payload);
  }
  async listRiskAcceptances(params?: Record<string, string>): Promise<VendorRiskAcceptance[]> {
    return this.get(`/vendor-risk/acceptances${this.buildQuery(params)}`);
  }
  async reviewAcceptanceCondition(
    acceptanceId: string,
    conditionIndex: number,
    data: Record<string, unknown>,
  ): Promise<VendorRiskAcceptance> {
    return this.post(`/vendor-risk/acceptances/${acceptanceId}/conditions/${conditionIndex}`, data);
  }
  async exportAcceptanceRegister(): Promise<Record<string, unknown>> {
    return this.get('/vendor-risk/acceptances/export');
  }
  /**
   * Sprint 43.5.5 — Unified export for Risk Acceptance Register.
   * Returns AttachmentRef persisted via UnifiedExportService (File
   * Component). Call downloadPlatformFile() to trigger the download.
   */
  async exportAcceptanceRegisterUnified(format: 'csv' | 'xlsx' | 'json'): Promise<AttachmentRef> {
    return this.get(`/vendor-risk/acceptances/export${this.buildQuery({ format })}`);
  }
  /**
   * Sprint 43.5.5 — Unified export for Sub-Processor Register.
   * Supports CSV / XLSX / JSON, persisted in File Component.
   */
  async exportSubProcessorRegisterUnified(format: 'csv' | 'xlsx' | 'json'): Promise<AttachmentRef> {
    return this.get(`/vendor-risk/sub-processor-register/export${this.buildQuery({ format })}`);
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Runs ───────────
  async listOrchestrationRuns(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/runs${this.buildQuery(params)}`);
  }
  async getOrchestrationRun(runId: string) {
    return this.get(`/deletion-orchestration/runs/${runId}`);
  }
  async createOrchestrationRun(payload: Record<string, any>) {
    return this.post('/deletion-orchestration/runs', payload);
  }
  async updateOrchestrationRunTargets(runId: string, targets: Record<string, any>) {
    return this.patch(`/deletion-orchestration/runs/${runId}/targets`, targets);
  }
  async startOrchestrationRun(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/start`, {});
  }
  async updateOrchestrationRunStatus(runId: string, status: string) {
    return this.patch(`/deletion-orchestration/runs/${runId}/status`, { status });
  }
  async getOrchestrationDashboard() {
    return this.get('/deletion-orchestration/runs/dashboard');
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Execution Tasks ─
  async listExecutionTasks(runId: string, params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/runs/${runId}/tasks${this.buildQuery(params)}`);
  }
  async getExecutionTask(taskId: string) {
    return this.get(`/deletion-orchestration/tasks/${taskId}`);
  }
  async createExecutionTasks(runId: string, payload: Record<string, any>) {
    return this.post(`/deletion-orchestration/runs/${runId}/tasks`, payload);
  }
  async assignExecutionTask(taskId: string, assigneeId: string) {
    return this.patch(`/deletion-orchestration/tasks/${taskId}/assign`, {
      assigned_to: assigneeId,
    });
  }
  async updateExecutionTaskStatus(taskId: string, status: string, opts?: Record<string, any>) {
    return this.patch(`/deletion-orchestration/tasks/${taskId}/status`, { status, ...opts });
  }
  async updateDeletionExecutionStep(taskId: string, stepId: string, updates: Record<string, any>) {
    return this.patch(`/deletion-orchestration/tasks/${taskId}/steps/${stepId}`, updates);
  }
  async verifyExecutionTask(taskId: string) {
    return this.post(`/deletion-orchestration/tasks/${taskId}/verify`, {});
  }
  async getOverdueExecutionTasks() {
    return this.get('/deletion-orchestration/tasks/overdue');
  }
  async getMyExecutionTasks(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/tasks/my-tasks${this.buildQuery(params)}`);
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Checklist Templates
  async listChecklistTemplates(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/templates${this.buildQuery(params)}`);
  }
  async getChecklistTemplate(templateId: string) {
    return this.get(`/deletion-orchestration/templates/${templateId}`);
  }
  async createChecklistTemplate(payload: Record<string, any>) {
    return this.post('/deletion-orchestration/templates', payload);
  }
  async updateChecklistTemplate(templateId: string, payload: Record<string, any>) {
    return this.patch(`/deletion-orchestration/templates/${templateId}`, payload);
  }
  async updateChecklistTemplateSteps(templateId: string, steps: Record<string, any>[]) {
    return this.put(`/deletion-orchestration/templates/${templateId}/steps`, { steps });
  }
  async cloneChecklistTemplate(templateId: string, newName: string) {
    return this.post(`/deletion-orchestration/templates/${templateId}/clone`, { name: newName });
  }
  async archiveChecklistTemplate(templateId: string) {
    return this.post(`/deletion-orchestration/templates/${templateId}/archive`, {});
  }
  async activateChecklistTemplate(templateId: string) {
    return this.post(`/deletion-orchestration/templates/${templateId}/activate`, {});
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Vendor Deletion Requests
  async listVendorDeletionRequests(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/vendor-requests${this.buildQuery(params)}`);
  }
  async getVendorDeletionRequest(requestId: string) {
    return this.get(`/deletion-orchestration/vendor-requests/${requestId}`);
  }
  async createVendorDeletionRequest(payload: Record<string, any>) {
    return this.post('/deletion-orchestration/vendor-requests', payload);
  }
  async updateVendorDeletionRequestStatus(requestId: string, status: string) {
    return this.patch(`/deletion-orchestration/vendor-requests/${requestId}/status`, { status });
  }
  async addVendorDeletionCommunication(requestId: string, entry: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-requests/${requestId}/communication`, entry);
  }
  async recordVendorDeletionConfirmation(requestId: string, confirmation: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-requests/${requestId}/confirm`, confirmation);
  }
  async recordVendorDeletionDispute(requestId: string, dispute: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-requests/${requestId}/dispute`, dispute);
  }
  async getOverdueVendorDeletionRequests() {
    return this.get('/deletion-orchestration/vendor-requests/overdue');
  }
  async getVendorDeletionMetrics(vendorId?: string) {
    const query = vendorId ? `?vendor_id=${vendorId}` : '';
    return this.get(`/deletion-orchestration/vendor-requests/metrics${query}`);
  }
  async getVendorDeletionDashboard() {
    return this.get('/deletion-orchestration/vendor-requests/dashboard');
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Proof Records ──
  async listProofRecords(runId: string) {
    return this.get(`/deletion-orchestration/runs/${runId}/proof`);
  }
  async verifyProofChain(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/proof/verify`, {});
  }
  async generateAttestation(runId: string, payload: Record<string, any>) {
    return this.post(`/deletion-orchestration/runs/${runId}/proof/attestation`, payload);
  }
  /**
   * Sprint 44.0.4 Wave 2 — R-33 backend export for the three
   * Deletion Orchestration pages that previously bypassed
   * UnifiedExportService via `downloadClientExport`. Each returns an
   * AttachmentRef; caller invokes `downloadPlatformFile(ref.file_id,
   * ref.filename)` to trigger the browser download.
   */
  async exportDeletionExecutionTasks(
    runId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/deletion-orchestration/runs/${runId}/tasks/export${this.buildQuery(params)}`);
  }
  async exportDeletionVendorRequests(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/deletion-orchestration/vendor-requests/export${this.buildQuery(params)}`);
  }
  async exportDeletionProofChain(
    runId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/deletion-orchestration/runs/${runId}/proof/export${this.buildQuery(params)}`);
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Reports ────────
  async generateCompletionReport(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/report`, {});
  }
  async generateExceptionReport(runId: string) {
    return this.post(`/deletion-orchestration/runs/${runId}/exception-report`, {});
  }
  // ── Sprint 2.2.0: Deletion Orchestration -- Audit ──────────
  async listDeletionOrchestrationAudit(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/audit${this.buildQuery(params)}`);
  }
  async getDeletionOrchestrationEntityAudit(entityType: string, entityId: string) {
    return this.get(`/deletion-orchestration/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.5.0: Deletion Orchestration Maturity ─────────
  async listDsrRuns(params?: Record<string, any>) {
    return this.get(`/deletion-orchestration/dsr-runs${this.buildQuery(params)}`);
  }
  async createDsrDeletionRun(data: Record<string, any>) {
    return this.post('/deletion-orchestration/dsr-runs', data);
  }
  async getDsrDeletionStatus(dsrCaseId: string) {
    return this.get(`/deletion-orchestration/dsr-runs/${dsrCaseId}/status`);
  }
  async updateDsrRunStatus(dsrRunId: string, status: string) {
    return this.patch(`/deletion-orchestration/dsr-runs/${dsrRunId}/status`, { status });
  }
  async routeTaskToDepartment(taskId: string, departmentOwnerId: string) {
    return this.post(`/deletion-orchestration/tasks/${taskId}/route`, {
      department_owner_id: departmentOwnerId,
    });
  }
  async confirmDepartmentDeletion(taskId: string, proofData: Record<string, any>) {
    return this.post(`/deletion-orchestration/tasks/${taskId}/confirm`, proofData);
  }
  async createVendorFollowup(data: Record<string, any>) {
    return this.post('/deletion-orchestration/vendor-followups', data);
  }
  async recordVendorConfirmation(followupId: string, data: Record<string, any>) {
    return this.post(`/deletion-orchestration/vendor-followups/${followupId}/confirm`, data);
  }
  async listOverdueVendorDeletions(params?: Record<string, string>) {
    return this.get(`/deletion-orchestration/vendor-followups/overdue${this.buildQuery(params)}`);
  }
  async listVendorFollowups(params?: Record<string, string>) {
    return this.get(`/deletion-orchestration/vendor-followups${this.buildQuery(params)}`);
  }
  async escalateVendorDeletion(followupId: string) {
    return this.post(`/deletion-orchestration/vendor-followups/${followupId}/escalate`, {});
  }
  async getDeletionCompleteness(dataSubjectId: string) {
    return this.get(`/deletion-orchestration/completeness/${dataSubjectId}`);
  }
  async checkRetentionConflicts(data: Record<string, any>) {
    return this.post('/deletion-orchestration/retention-conflicts/check', data);
  }
  async resolveRetentionConflict(conflictId: string, resolution: string) {
    return this.post(`/deletion-orchestration/retention-conflicts/${conflictId}/resolve`, {
      resolution,
    });
  }
  // ── Sprint 2.3.0: Discovery & Classification Automation ───
  // Discovery Scans
  async getDiscoveryDashboard() {
    return this.get('/discovery/scans/dashboard');
  }
  async listDiscoveryScans(params?: Record<string, any>) {
    return this.get(`/discovery/scans${this.buildQuery(params)}`);
  }
  async getDiscoveryScan(scanId: string) {
    return this.get(`/discovery/scans/${scanId}`);
  }
  async createDiscoveryScan(payload: any) {
    return this.post('/discovery/scans', payload);
  }
  async updateDiscoveryScanStatus(scanId: string, status: string) {
    return this.patch(`/discovery/scans/${scanId}/status`, { status });
  }
  async updateDiscoveryScanProgress(scanId: string, progress: any) {
    return this.patch(`/discovery/scans/${scanId}/progress`, progress);
  }
  // Staged Records
  async listStagedRecords(params?: Record<string, any>) {
    return this.get(`/discovery/staged-records${this.buildQuery(params)}`);
  }
  async getStagedRecord(recordId: string) {
    return this.get(`/discovery/staged-records/${recordId}`);
  }
  async createStagedRecord(payload: any) {
    return this.post('/discovery/staged-records', payload);
  }
  async reviewStagedRecord(recordId: string) {
    return this.post(`/discovery/staged-records/${recordId}/review`, {});
  }
  async promoteStagedRecord(recordId: string) {
    return this.post(`/discovery/staged-records/${recordId}/promote`, {});
  }
  async rejectStagedRecord(recordId: string, reason: string) {
    return this.post(`/discovery/staged-records/${recordId}/reject`, { reason });
  }
  async mergeStagedRecord(recordId: string, targetInventoryId: string) {
    return this.post(`/discovery/staged-records/${recordId}/merge`, {
      target_inventory_id: targetInventoryId,
    });
  }
  async bulkPromoteStagedRecords(ids: string[]) {
    return this.post('/discovery/staged-records/bulk-promote', { ids });
  }
  async bulkRejectStagedRecords(ids: string[], reason: string) {
    return this.post('/discovery/staged-records/bulk-reject', { ids, reason });
  }
  // Sprint 45.5.0 — Discovery → Registry promotion
  async promoteStagedRecordToRegistry(
    recordId: string,
    payload: {
      category_id: string;
      name?: string;
      field_identifiers?: string[];
      proposed_sensitivity?: 'standard' | 'sensitive' | 'special_category';
    },
  ): Promise<{ staged_record: unknown; registry_item_id: string }> {
    return this.post(`/discovery/staged-records/${recordId}/promote-to-registry`, payload);
  }
  async bulkPromoteStagedRecordsToRegistry(
    items: Array<{
      staged_record_id: string;
      category_id: string;
      name?: string;
      field_identifiers?: string[];
      proposed_sensitivity?: 'standard' | 'sensitive' | 'special_category';
    }>,
  ): Promise<{
    results: Array<{
      staged_record_id: string;
      status: 'promoted' | 'error';
      registry_item_id?: string;
      error?: string;
    }>;
  }> {
    return this.post('/discovery/staged-records/bulk-promote-to-registry', { items });
  }
  // Dedup Rules
  async listDedupRules(params?: Record<string, any>) {
    return this.get(`/discovery/dedup-rules${this.buildQuery(params)}`);
  }
  async getDedupRule(ruleId: string) {
    return this.get(`/discovery/dedup-rules/${ruleId}`);
  }
  async createDedupRule(payload: any) {
    return this.post('/discovery/dedup-rules', payload);
  }
  async updateDedupRule(ruleId: string, payload: any) {
    return this.put(`/discovery/dedup-rules/${ruleId}`, payload);
  }
  async deleteDedupRule(ruleId: string) {
    return this.del(`/discovery/dedup-rules/${ruleId}`);
  }
  async toggleDedupRule(ruleId: string, enabled: boolean) {
    return this.patch(`/discovery/dedup-rules/${ruleId}/toggle`, { enabled });
  }
  async seedDedupDefaults() {
    return this.post('/discovery/dedup-rules/seed-defaults', {});
  }
  // Classification Rules
  async listClassificationRules(params?: Record<string, any>) {
    return this.get(`/discovery/classification-rules${this.buildQuery(params)}`);
  }
  async getClassificationRule(ruleId: string) {
    return this.get(`/discovery/classification-rules/${ruleId}`);
  }
  async createClassificationRule(payload: any) {
    return this.post('/discovery/classification-rules', payload);
  }
  async updateClassificationRule(ruleId: string, payload: any) {
    return this.put(`/discovery/classification-rules/${ruleId}`, payload);
  }
  async deleteClassificationRule(ruleId: string) {
    return this.del(`/discovery/classification-rules/${ruleId}`);
  }
  async toggleClassificationRule(ruleId: string, enabled: boolean) {
    return this.patch(`/discovery/classification-rules/${ruleId}/toggle`, { enabled });
  }
  async seedClassificationRuleDefaults() {
    return this.post('/discovery/classification-rules/seed-defaults', {});
  }
  async runClassificationRules(): Promise<{ created: number; skipped: number }> {
    return this.post('/discovery/classification-rules/run', {});
  }
  async getClassificationRuleEffectiveness() {
    return this.get('/discovery/classification-rules/effectiveness');
  }
  // Classification Suggestions
  async listClassificationSuggestions(params?: Record<string, any>) {
    return this.get(`/discovery/suggestions${this.buildQuery(params)}`);
  }
  async getClassificationSuggestion(suggestionId: string) {
    return this.get(`/discovery/suggestions/${suggestionId}`);
  }
  async createClassificationSuggestion(payload: any) {
    return this.post('/discovery/suggestions', payload);
  }
  async approveSuggestion(suggestionId: string, appliedLevelId?: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/approve`, {
      applied_level_id: appliedLevelId,
    });
  }
  async rejectSuggestion(suggestionId: string, reason: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/reject`, { reason });
  }
  async modifyAndApproveSuggestion(suggestionId: string, newLevelId: string, reason?: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/modify-approve`, {
      new_level_id: newLevelId,
      reason,
    });
  }
  async delegateSuggestion(suggestionId: string, targetUserId: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/delegate`, {
      target_user_id: targetUserId,
    });
  }
  async escalateSuggestion(suggestionId: string) {
    return this.post(`/discovery/suggestions/${suggestionId}/escalate`, {});
  }
  async bulkApproveSuggestions(ids: string[]) {
    return this.post('/discovery/suggestions/bulk-approve', { ids });
  }
  async bulkRejectSuggestions(ids: string[], reason: string) {
    return this.post('/discovery/suggestions/bulk-reject', { ids, reason });
  }
  async getDiscoverySuggestionAnalytics() {
    return this.get('/discovery/suggestions/analytics');
  }
  // Import Templates
  async listImportTemplates(params?: Record<string, any>) {
    return this.get(`/discovery/import-templates${this.buildQuery(params)}`);
  }
  async getImportTemplate(templateId: string) {
    return this.get(`/discovery/import-templates/${templateId}`);
  }
  async createImportTemplate(payload: any) {
    return this.post('/discovery/import-templates', payload);
  }
  async updateImportTemplate(templateId: string, payload: any) {
    return this.put(`/discovery/import-templates/${templateId}`, payload);
  }
  async deleteImportTemplate(templateId: string) {
    return this.del(`/discovery/import-templates/${templateId}`);
  }
  // Discovery Audit
  async listDiscoveryAudit(params?: Record<string, any>) {
    return this.get(`/discovery/audit${this.buildQuery(params)}`);
  }
  async getDiscoveryEntityAudit(entityType: string, entityId: string) {
    return this.get(`/discovery/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 2.4.0: Incident Operations Level 2 ──────────────
  // Playbook Templates
  async listPlaybookTemplates(params?: Record<string, any>) {
    return this.get(`/incident-ops/playbooks${this.buildQuery(params)}`);
  }
  async getPlaybookTemplate(playbookId: string) {
    return this.get(`/incident-ops/playbooks/${playbookId}`);
  }
  async createPlaybookTemplate(payload: Record<string, any>) {
    return this.post('/incident-ops/playbooks', payload);
  }
  async updatePlaybookTemplate(playbookId: string, payload: Record<string, any>) {
    return this.patch(`/incident-ops/playbooks/${playbookId}`, payload);
  }
  async updatePlaybookTemplateStatus(playbookId: string, status: string) {
    return this.post(`/incident-ops/playbooks/${playbookId}/status`, { status });
  }
  async clonePlaybookTemplate(playbookId: string, name: string) {
    return this.post(`/incident-ops/playbooks/${playbookId}/clone`, { name });
  }
  async seedPlaybookDefaults() {
    return this.post('/incident-ops/playbooks/seed-defaults', {});
  }
  // Playbook Activations
  async listPlaybookActivations(params?: Record<string, any>) {
    return this.get(`/incident-ops/activations${this.buildQuery(params)}`);
  }
  async getPlaybookActivation(activationId: string) {
    return this.get(`/incident-ops/activations/${activationId}`);
  }
  async activatePlaybook(payload: Record<string, any>) {
    return this.post('/incident-ops/activations', payload);
  }
  async deactivatePlaybook(activationId: string, reason: string) {
    return this.post(`/incident-ops/activations/${activationId}/deactivate`, { reason });
  }
  async completePlaybookActivation(activationId: string, metrics?: Record<string, any>) {
    return this.post(`/incident-ops/activations/${activationId}/complete`, { metrics });
  }
  async updatePlaybookPhaseStatus(activationId: string, phaseId: string, status: string) {
    return this.patch(`/incident-ops/activations/${activationId}/phases/${phaseId}`, { status });
  }
  // SLA Definitions
  async listIncidentSlaDefinitions(params?: Record<string, any>) {
    return this.get(`/incident-ops/sla-definitions${this.buildQuery(params)}`);
  }
  async getIncidentSlaDefinition(slaId: string) {
    return this.get(`/incident-ops/sla-definitions/${slaId}`);
  }
  async createIncidentSlaDefinition(payload: Record<string, any>) {
    return this.post('/incident-ops/sla-definitions', payload);
  }
  async updateIncidentSlaDefinition(slaId: string, payload: Record<string, any>) {
    return this.patch(`/incident-ops/sla-definitions/${slaId}`, payload);
  }
  async seedIncidentSlaDefaults() {
    return this.post('/incident-ops/sla-definitions/seed-defaults', {});
  }
  // SLA Records
  async listIncidentSlaRecords(params?: Record<string, any>) {
    return this.get(`/incident-ops/sla-records${this.buildQuery(params)}`);
  }
  async getIncidentSlaDashboard() {
    return this.get('/incident-ops/sla-records/dashboard');
  }
  async getIncidentSlaRecord(recordId: string) {
    return this.get(`/incident-ops/sla-records/${recordId}`);
  }
  async createIncidentSlaRecord(payload: Record<string, any>) {
    return this.post('/incident-ops/sla-records', payload);
  }
  async updateIncidentSlaRecordStatus(recordId: string, status: string) {
    return this.patch(`/incident-ops/sla-records/${recordId}/status`, { status });
  }
  async pauseIncidentSlaClock(recordId: string) {
    return this.post(`/incident-ops/sla-records/${recordId}/pause`, {});
  }
  async resumeIncidentSlaClock(recordId: string) {
    return this.post(`/incident-ops/sla-records/${recordId}/resume`, {});
  }
  async stopIncidentSlaClock(recordId: string) {
    return this.post(`/incident-ops/sla-records/${recordId}/stop`, {});
  }
  // Escalations
  async listIncidentEscalations(params?: Record<string, any>) {
    return this.get(`/incident-ops/escalations${this.buildQuery(params)}`);
  }
  async getIncidentEscalation(escalationId: string) {
    return this.get(`/incident-ops/escalations/${escalationId}`);
  }
  async createIncidentEscalation(payload: Record<string, any>) {
    return this.post('/incident-ops/escalations', payload);
  }
  async resolveIncidentEscalation(escalationId: string) {
    return this.post(`/incident-ops/escalations/${escalationId}/resolve`, {});
  }
  // Regulator Exports
  async listRegulatorExports(params?: Record<string, any>) {
    return this.get(`/incident-ops/regulator-exports${this.buildQuery(params)}`);
  }
  async getRegulatorExport(exportId: string) {
    return this.get(`/incident-ops/regulator-exports/${exportId}`);
  }
  async createRegulatorExport(payload: Record<string, any>) {
    return this.post('/incident-ops/regulator-exports', payload);
  }
  async updateRegulatorExportSubmissionStatus(exportId: string, status: string) {
    return this.patch(`/incident-ops/regulator-exports/${exportId}/submission-status`, { status });
  }
  // Vendor Coordination
  async listVendorCoordination(params?: Record<string, any>) {
    return this.get(`/incident-ops/vendor-coordination${this.buildQuery(params)}`);
  }
  async getVendorCoordination(coordinationId: string) {
    return this.get(`/incident-ops/vendor-coordination/${coordinationId}`);
  }
  async createVendorCoordination(payload: Record<string, any>) {
    return this.post('/incident-ops/vendor-coordination', payload);
  }
  async updateVendorCoordinationStatus(coordinationId: string, status: string) {
    return this.patch(`/incident-ops/vendor-coordination/${coordinationId}/status`, { status });
  }
  async addVendorCoordinationCommunication(coordinationId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/vendor-coordination/${coordinationId}/communications`, data);
  }
  async addVendorCoordinationInfoRequest(coordinationId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/vendor-coordination/${coordinationId}/info-requests`, data);
  }
  async updateVendorInfoRequestStatus(coordinationId: string, requestId: string, status: string) {
    return this.patch(
      `/incident-ops/vendor-coordination/${coordinationId}/info-requests/${requestId}/status`,
      { status },
    );
  }
  async addVendorCoordinationActionRequest(coordinationId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/vendor-coordination/${coordinationId}/action-requests`, data);
  }
  async updateVendorActionRequestStatus(coordinationId: string, requestId: string, status: string) {
    return this.patch(
      `/incident-ops/vendor-coordination/${coordinationId}/action-requests/${requestId}/status`,
      { status },
    );
  }
  async getCrossVendorTimeline(incidentId: string) {
    return this.get(`/incident-ops/vendor-coordination/cross-timeline/${incidentId}`);
  }
  /**
   * Sprint 44.0.5 Wave 2 — R-33 backend exports for the six Incident
   * Operations list/dashboard pages that previously bypassed
   * UnifiedExportService via `downloadClientExport`. Each returns an
   * AttachmentRef; caller invokes `downloadPlatformFile(ref.file_id,
   * ref.filename)` to trigger the browser download. Two of the
   * endpoints (dashboard-export and metrics-export) share the
   * `/sla-records` backend service method but emit distinct
   * AttachmentOwnerType values so the File Component audit trail
   * distinguishes which page emitted each artefact.
   */
  async exportIncidentOpsPlaybookTemplates(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/playbooks/export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsRegulatorExports(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/regulator-exports/list-export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsResponseMetrics(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/sla-records/metrics-export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsSlaDashboard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/sla-records/dashboard-export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsSlaDefinitions(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/sla-definitions/export${this.buildQuery(params)}`);
  }
  async exportIncidentOpsVendorCoordination(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/incident-ops/vendor-coordination/export${this.buildQuery(params)}`);
  }
  // Incident Ops Audit
  async listIncidentOpsAudit(params?: Record<string, any>) {
    return this.get(`/incident-ops/audit${this.buildQuery(params)}`);
  }
  async getIncidentOpsEntityAudit(entityType: string, entityId: string) {
    return this.get(`/incident-ops/audit/${entityType}/${entityId}`);
  }
  // ── Incident Ops Maturity (Sprint 21.2.0) ─────────────────
  async listPlaybookNotificationLinks(params?: Record<string, any>) {
    return this.get(`/incident-ops/playbook-links${this.buildQuery(params)}`);
  }
  async createPlaybookNotificationLink(activationId: string, notificationId: string) {
    return this.post('/incident-ops/playbook-links', {
      activation_id: activationId,
      notification_id: notificationId,
    });
  }
  async seedPdplPlaybooks() {
    return this.post('/incident-ops/playbook-links/seed-pdpl', {});
  }
  async listRegulatorySlaMapping(params?: Record<string, any>) {
    return this.get(`/incident-ops/regulatory-sla${this.buildQuery(params)}`);
  }
  async createRegulatorySlaMapping(data: Record<string, any>) {
    return this.post('/incident-ops/regulatory-sla', data);
  }
  async seedPdplSlaDefaults() {
    return this.post('/incident-ops/regulatory-sla/seed-pdpl', {});
  }
  async listCoordinationTeams(incidentId: string) {
    return this.get(`/incident-ops/coordination-teams/${encodeURIComponent(incidentId)}`);
  }
  async createCoordinationTeam(incidentId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/coordination-teams/${encodeURIComponent(incidentId)}`, data);
  }
  async updateCoordinationTeam(incidentId: string, teamId: string, data: Record<string, any>) {
    return this.patch(
      `/incident-ops/coordination-teams/${encodeURIComponent(incidentId)}/${encodeURIComponent(teamId)}`,
      data,
    );
  }
  async listEvidenceChain(incidentId: string) {
    return this.get(`/incident-ops/evidence-chain/${encodeURIComponent(incidentId)}`);
  }
  async createEvidenceChainEntry(data: Record<string, any>) {
    return this.post('/incident-ops/evidence-chain', data);
  }
  async getPostIncidentReview(incidentId: string) {
    return this.get(`/incident-ops/post-reviews/${encodeURIComponent(incidentId)}`);
  }
  async createPostIncidentReview(incidentId: string, data: Record<string, any>) {
    return this.post(`/incident-ops/post-reviews/${encodeURIComponent(incidentId)}`, data);
  }
  async updatePostIncidentReview(incidentId: string, reviewId: string, data: Record<string, any>) {
    return this.patch(
      `/incident-ops/post-reviews/${encodeURIComponent(incidentId)}/${encodeURIComponent(reviewId)}`,
      data,
    );
  }
  async signOffPostIncidentReview(incidentId: string, reviewId: string) {
    return this.post(
      `/incident-ops/post-reviews/${encodeURIComponent(incidentId)}/${encodeURIComponent(reviewId)}/sign-off`,
      {},
    );
  }
  // ── Program Governance (Sprint 2.5.0) ─────────────────────
  // Maturity Models
  async listMaturityModels(params?: Record<string, any>): Promise<{ data: MaturityModel[] }> {
    return this.get(`/program-governance/models${this.buildQuery(params)}`);
  }
  async createMaturityModel(data: Record<string, any>): Promise<MaturityModel> {
    return this.post('/program-governance/models', data);
  }
  async getMaturityModel(modelId: string): Promise<MaturityModel> {
    return this.get(`/program-governance/models/${modelId}`);
  }
  async updateMaturityModel(modelId: string, data: Record<string, any>): Promise<MaturityModel> {
    return this.put(`/program-governance/models/${modelId}`, data);
  }
  async updateMaturityModelStatus(
    modelId: string,
    data: Record<string, any>,
  ): Promise<MaturityModel> {
    return this.patch(`/program-governance/models/${modelId}/status`, data);
  }
  async seedMaturityModelDefaults(): Promise<Record<string, unknown>> {
    return this.post('/program-governance/models/seed-defaults', {});
  }
  // Maturity Assessments
  async listMaturityAssessments(
    params?: Record<string, any>,
  ): Promise<{ data: MaturityAssessment[] }> {
    return this.get(`/program-governance/assessments${this.buildQuery(params)}`);
  }
  async createMaturityAssessment(data: Record<string, any>): Promise<MaturityAssessment> {
    return this.post('/program-governance/assessments', data);
  }
  async getMaturityAssessment(assessmentId: string): Promise<MaturityAssessment> {
    return this.get(`/program-governance/assessments/${assessmentId}`);
  }
  async updateMaturityAssessment(
    assessmentId: string,
    data: Record<string, any>,
  ): Promise<MaturityAssessment> {
    return this.put(`/program-governance/assessments/${assessmentId}`, data);
  }
  async evaluateDomain(
    assessmentId: string,
    domainId: string,
    data: Record<string, any>,
  ): Promise<MaturityAssessment> {
    return this.put(`/program-governance/assessments/${assessmentId}/domains/${domainId}`, data);
  }
  async submitAssessmentForReview(assessmentId: string): Promise<MaturityAssessment> {
    return this.post(`/program-governance/assessments/${assessmentId}/submit`, {});
  }
  async approveAssessmentDomain(
    assessmentId: string,
    domainId: string,
    data: Record<string, any>,
  ): Promise<MaturityAssessment> {
    return this.post(
      `/program-governance/assessments/${assessmentId}/domains/${domainId}/approve`,
      data,
    );
  }
  async finalizeAssessment(assessmentId: string): Promise<MaturityAssessment> {
    return this.post(`/program-governance/assessments/${assessmentId}/finalize`, {});
  }
  async updateAssessmentStatus(assessmentId: string, status: string): Promise<MaturityAssessment> {
    return this.patch(`/program-governance/assessments/${assessmentId}/status`, { status });
  }
  // Trend Metrics
  async listTrendMetrics(params?: Record<string, any>): Promise<{ data: TrendMetric[] }> {
    return this.get(`/program-governance/trends${this.buildQuery(params)}`);
  }
  async createTrendMetric(data: Record<string, any>): Promise<TrendMetric> {
    return this.post('/program-governance/trends', data);
  }
  async createTrendMetricBatch(data: Record<string, any>): Promise<{ data: TrendMetric[] }> {
    return this.post('/program-governance/trends/batch', data);
  }
  async getTrendMetricSummary(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/trends/summary');
  }
  async getTrendMetricByCode(
    metricCode: string,
    params?: Record<string, any>,
  ): Promise<{ data: TrendMetric[] }> {
    return this.get(`/program-governance/trends/${metricCode}${this.buildQuery(params)}`);
  }
  // Reporting Periods
  async listReportingPeriods(params?: Record<string, any>): Promise<{ data: ReportingPeriod[] }> {
    return this.get(`/program-governance/reporting-periods${this.buildQuery(params)}`);
  }
  async createReportingPeriod(data: Record<string, any>): Promise<ReportingPeriod> {
    return this.post('/program-governance/reporting-periods', data);
  }
  async getReportingPeriod(periodId: string): Promise<ReportingPeriod> {
    return this.get(`/program-governance/reporting-periods/${periodId}`);
  }
  async updateReportingPeriod(
    periodId: string,
    data: Record<string, any>,
  ): Promise<ReportingPeriod> {
    return this.put(`/program-governance/reporting-periods/${periodId}`, data);
  }
  async updateReportingPeriodStatus(
    periodId: string,
    data: Record<string, any>,
  ): Promise<ReportingPeriod> {
    return this.patch(`/program-governance/reporting-periods/${periodId}/status`, data);
  }
  async autoGenerateReportingPeriods(
    data: Record<string, any>,
  ): Promise<{ data: ReportingPeriod[] }> {
    return this.post('/program-governance/reporting-periods/auto-generate', data);
  }
  // Executive Summaries
  async listExecutiveSummaries(
    params?: Record<string, any>,
  ): Promise<{ data: ExecutiveSummary[] }> {
    return this.get(`/program-governance/summaries${this.buildQuery(params)}`);
  }
  async generateExecutiveSummary(data: Record<string, any>): Promise<ExecutiveSummary> {
    return this.post('/program-governance/summaries/generate', data);
  }
  async getExecutiveSummary(summaryId: string): Promise<ExecutiveSummary> {
    return this.get(`/program-governance/summaries/${summaryId}`);
  }
  async editExecutiveSummary(
    summaryId: string,
    data: Record<string, any>,
  ): Promise<ExecutiveSummary> {
    return this.put(`/program-governance/summaries/${summaryId}`, data);
  }
  // Audit Readiness
  async listAuditReadiness(params?: Record<string, any>): Promise<{ data: AuditReadiness[] }> {
    return this.get(`/program-governance/readiness${this.buildQuery(params)}`);
  }
  async getLatestAuditReadiness(): Promise<AuditReadiness> {
    return this.get('/program-governance/readiness/latest');
  }
  async calculateAuditReadiness(data: Record<string, any>): Promise<AuditReadiness> {
    return this.post('/program-governance/readiness/calculate', data);
  }
  // Evidence Gaps
  async listEvidenceGaps(params?: Record<string, any>): Promise<{ data: EvidenceGap[] }> {
    return this.get(`/program-governance/evidence-gaps${this.buildQuery(params)}`);
  }
  async createEvidenceGap(data: Record<string, any>): Promise<EvidenceGap> {
    return this.post('/program-governance/evidence-gaps', data);
  }
  async getEvidenceGapStats(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/evidence-gaps/stats');
  }
  async getEvidenceGap(gapId: string): Promise<EvidenceGap> {
    return this.get(`/program-governance/evidence-gaps/${gapId}`);
  }
  async updateEvidenceGap(gapId: string, data: Record<string, any>): Promise<EvidenceGap> {
    return this.put(`/program-governance/evidence-gaps/${gapId}`, data);
  }
  async assignEvidenceGap(gapId: string, data: Record<string, any>): Promise<EvidenceGap> {
    return this.post(`/program-governance/evidence-gaps/${gapId}/assign`, data);
  }
  async updateEvidenceGapStatus(gapId: string, data: Record<string, any>): Promise<EvidenceGap> {
    return this.patch(`/program-governance/evidence-gaps/${gapId}/status`, data);
  }
  // Program Governance Audit
  async listProgramGovernanceAudit(
    params?: Record<string, any>,
  ): Promise<{ data: ProgramGovernanceAuditEvent[] }> {
    return this.get(`/program-governance/audit${this.buildQuery(params)}`);
  }
  async getProgramGovernanceEntityAudit(
    entityType: string,
    entityId: string,
  ): Promise<{ data: ProgramGovernanceAuditEvent[] }> {
    return this.get(`/program-governance/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.7.0: Program Governance -- Obligation Mappings & RACI ──
  async listObligationMappings(
    params?: Record<string, any>,
  ): Promise<{ data: ObligationMapping[] }> {
    return this.get(`/program-governance/obligation-mappings${this.buildQuery(params)}`);
  }
  async createObligationMapping(data: Record<string, any>): Promise<ObligationMapping> {
    return this.post('/program-governance/obligation-mappings', data);
  }
  async updateObligationMapping(
    mappingId: string,
    data: Record<string, any>,
  ): Promise<ObligationMapping> {
    return this.patch(`/program-governance/obligation-mappings/${mappingId}`, data);
  }
  async deleteObligationMapping(mappingId: string): Promise<void> {
    return this.del(`/program-governance/obligation-mappings/${mappingId}`);
  }
  async listRaciAssignments(params?: Record<string, any>): Promise<{ data: RaciAssignment[] }> {
    return this.get(`/program-governance/raci-assignments${this.buildQuery(params)}`);
  }
  async createRaciAssignment(data: Record<string, any>): Promise<RaciAssignment> {
    return this.post('/program-governance/raci-assignments', data);
  }
  async updateRaciAssignment(
    assignmentId: string,
    data: Record<string, any>,
  ): Promise<RaciAssignment> {
    return this.patch(`/program-governance/raci-assignments/${assignmentId}`, data);
  }
  async deleteRaciAssignment(assignmentId: string): Promise<void> {
    return this.del(`/program-governance/raci-assignments/${assignmentId}`);
  }
  async getProgramStaleness(programId: string): Promise<any> {
    return this.get(`/monitoring/programs/${programId}/staleness`);
  }
  // ── Sprint 21.7.0: Program Governance -- Governance Documents ──
  async listGovernanceDocuments(
    params?: Record<string, any>,
  ): Promise<{ data: GovernanceDocument[] }> {
    return this.get(`/program-governance/governance-documents${this.buildQuery(params)}`);
  }
  async createGovernanceDocument(data: Record<string, any>): Promise<GovernanceDocument> {
    return this.post('/program-governance/governance-documents', data);
  }
  async updateGovernanceDocumentStatus(docId: string, status: string): Promise<GovernanceDocument> {
    return this.patch(`/program-governance/governance-documents/${docId}/status`, { status });
  }
  async acknowledgeGovernanceDocument(
    docId: string,
    department?: string,
  ): Promise<DocumentAcknowledgment> {
    return this.post(`/program-governance/governance-documents/${docId}/acknowledge`, {
      department: department || '',
    });
  }
  // ── Sprint 27.5.1: Governance Model & Document Issuance (UC-VN-17) ──
  async listGovernanceModels(
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<GovernanceModel>> {
    return this.get(`/program-governance/governance-models${this.buildQuery(params)}`);
  }
  async createGovernanceModel(data: Record<string, any>): Promise<GovernanceModel> {
    return this.post('/program-governance/governance-models', data);
  }
  async updateGovernanceModel(
    modelId: string,
    data: Record<string, any>,
  ): Promise<GovernanceModel> {
    return this.patch(`/program-governance/governance-models/${modelId}`, data);
  }
  async deleteGovernanceModel(modelId: string): Promise<void> {
    return this.del(`/program-governance/governance-models/${modelId}`);
  }
  /**
   * Sprint 43.7.0 Wave 5 — per-model export. When `format` is a binary
   * format (csv / xlsx / pdf / docx) returns an AttachmentRef persisted
   * through UnifiedExportService (call `downloadPlatformFile()` to pull
   * the bytes). When `format === 'json'` the backend returns the JSON
   * payload inline (also as an AttachmentRef when UnifiedExport is
   * wired). Consumers should pass the ref through the File Component
   * download helper.
   */
  async exportGovernanceModel(
    modelId: string,
    format: 'csv' | 'xlsx' | 'json' | 'pdf' | 'docx',
  ): Promise<any> {
    return this.get(
      `/program-governance/governance-models/${modelId}/export${this.buildQuery({ format })}`,
    );
  }
  /**
   * Sprint 43.7.2 Wave 3 — R-33 list-export migration.
   *
   * Each method routes the page's "export list" action through the
   * backend UnifiedExportService (csv / xlsx / pdf / docx) and returns
   * an AttachmentRef. The caller is expected to invoke
   * `downloadPlatformFile(ref.file_id, ref.filename)` to trigger the
   * browser download. These replace in-browser `downloadClientExport()`
   * usages on the 7 Program Governance list/dashboard pages.
   */
  async exportGovernanceDocumentsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/governance-documents/export${this.buildQuery(params)}`);
  }
  async exportDocumentIssuanceList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/document-issuance/export${this.buildQuery(params)}`);
  }
  async exportDocumentChangeLog(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/document-change-log/export${this.buildQuery(params)}`);
  }
  async exportMaturityModelsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/models/export${this.buildQuery(params)}`);
  }
  async exportRemediationPlansList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/remediation-plans/export${this.buildQuery(params)}`);
  }
  async exportReportingPeriodsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/reporting-periods/export${this.buildQuery(params)}`);
  }
  async exportTrendMetricsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/trends/export${this.buildQuery(params)}`);
  }
  async exportAuditReadiness(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/readiness/export${this.buildQuery(params)}`);
  }
  /**
   * Sprint 43.7.2 Wave 3 residual — R-33 closeout exports for the 4
   * program-governance pages missed by the initial audit. Each returns
   * an AttachmentRef; the caller invokes
   * `downloadPlatformFile(ref.file_id, ref.filename)` to download.
   */
  async exportAcknowledgmentTracking(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/acknowledgments/export${this.buildQuery(params)}`);
  }
  async exportDocumentDraftingList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/program-governance/governance-documents/drafting/export${this.buildQuery(params)}`,
    );
  }
  async exportProgramHealth(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/program-health/export${this.buildQuery(params)}`);
  }
  async exportRaciMatrix(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/program-governance/raci/export${this.buildQuery(params)}`);
  }
  async assignDocumentDrafter(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/assign-drafter`, data);
  }
  async submitDocumentReview(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/review`, data);
  }
  async approveGovernanceDocument(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/approve`, data);
  }
  async issueGovernanceDocument(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/issue`, data);
  }
  async supersedeGovernanceDocument(docId: string, data: Record<string, any>): Promise<any> {
    return this.post(`/program-governance/governance-documents/${docId}/supersede`, data);
  }
  async listDocumentAcknowledgments(params?: Record<string, any>): Promise<{ data: any[] }> {
    return this.get(`/program-governance/acknowledgments${this.buildQuery(params)}`);
  }
  async sendAcknowledgmentReminder(docId: string): Promise<void> {
    return this.post(`/program-governance/governance-documents/${docId}/send-reminder`, {});
  }
  async listDocumentChangeLog(params?: Record<string, any>): Promise<{ data: any[] }> {
    return this.get(`/program-governance/document-change-log${this.buildQuery(params)}`);
  }
  async listReviewCadences(params?: Record<string, any>): Promise<{ data: any[] }> {
    return this.get(`/program-governance/review-cadences${this.buildQuery(params)}`);
  }
  async createReviewCadence(data: Record<string, any>): Promise<any> {
    return this.post('/program-governance/review-cadences', data);
  }
  async updateReviewCadence(cadenceId: string, data: Record<string, any>): Promise<any> {
    return this.patch(`/program-governance/review-cadences/${cadenceId}`, data);
  }
  // ── Sprint 21.7.0: Program Governance -- Remediation Plans ──
  async listRemediationPlans(params?: Record<string, any>): Promise<{ data: RemediationPlan[] }> {
    return this.get(`/program-governance/remediation-plans${this.buildQuery(params)}`);
  }
  async createRemediationPlan(data: Record<string, any>): Promise<RemediationPlan> {
    return this.post('/program-governance/remediation-plans', data);
  }
  async updateRemediationPlanStatus(planId: string, status: string): Promise<RemediationPlan> {
    return this.patch(`/program-governance/remediation-plans/${planId}/status`, { status });
  }
  // ── Sprint 21.7.0: Program Governance -- Cross-Framework Assessment ──
  async getCrossFrameworkAssessment(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/cross-framework-assessment');
  }
  async getConsolidatedGapRegister(): Promise<Record<string, unknown>> {
    return this.get('/program-governance/consolidated-gap-register');
  }
  async createAssessmentCriteria(data: Record<string, any>): Promise<AssessmentCriteria> {
    return this.post('/program-governance/assessment-criteria', data);
  }
  // ── Sprint 42.2.3 E5b: Generic client-side export pass-through ──
  // Used by `utils/csv-export.ts → downloadClientExport()`. Accepts
  // either pre-built CSV string OR row data + format choice (CSV/XLSX).
  // Persists in File Component → FilesBrowserPage.
  async exportClientCSV(payload: {
    csv?: string;
    data?: Record<string, unknown>[];
    format?: 'csv' | 'xlsx';
    filename_base: string;
    source_module: string;
    source_page?: string;
  }): Promise<AttachmentRef> {
    return this.post('/attachments/client-csv-export', payload);
  }

  // ── Sprint 42.2.3 E3: Access Accountability -- generic CSV pass-through ──
  // Pages that previously built CSV in-browser now POST it here for
  // unified File Component persistence.
  async exportAccessAccountabilityCSV(payload: {
    export_type: string;
    csv: string;
    filename_base: string;
  }): Promise<AttachmentRef> {
    return this.post('/access-accountability/exports/csv', payload);
  }

  // ── Sprint 44.0.6 Wave 3: Access Accountability — multi-format list exports ──
  //
  // R-33 migration replaces the 1-format CSV pass-through above with
  // 4-format (csv/xlsx/pdf/docx) backend routes that call
  // UnifiedExportService.generateFromDefinition. Each method returns an
  // `AttachmentRef` that can be handed to `downloadPlatformFile`.
  async exportAccessSessionEventsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/sessions/export${this.buildQuery(params)}`);
  }
  async exportAccessAnchoringProofsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/proofs/export${this.buildQuery(params)}`);
  }
  async exportAccessJustificationsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/justifications/export${this.buildQuery(params)}`);
  }
  async exportAccessInvestigationsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/investigations/export-list${this.buildQuery(params)}`);
  }
  async exportAccessInvestigationCase(
    investigationId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/access-accountability/investigations/${encodeURIComponent(investigationId)}/export-file${this.buildQuery(params)}`,
    );
  }
  async exportAccessLogList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/access-log/export-list${this.buildQuery(params)}`);
  }
  async exportAccessPoliciesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/policies/export${this.buildQuery(params)}`);
  }
  async exportAccessReviewCampaignsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/access-accountability/review-campaigns/export${this.buildQuery(params)}`);
  }
  async exportAccessEvidencePackagesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/access-accountability/evidence-packages/export-list${this.buildQuery(params)}`,
    );
  }
  async exportAccessEvidencePackageDetail(
    packageId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/access-accountability/evidence-packages/${encodeURIComponent(packageId)}/export-file${this.buildQuery(params)}`,
    );
  }

  // ── Sprint 2.6.0: Access Accountability -- Session Events ──
  async listSessionEvents(params?: Record<string, any>) {
    return this.get(`/access-accountability/sessions${this.buildQuery(params)}`);
  }
  async getSessionEvent(id: string) {
    return this.get(`/access-accountability/sessions/${id}`);
  }
  async getActiveSessions(params?: Record<string, any>) {
    return this.get(`/access-accountability/sessions/active${this.buildQuery(params)}`);
  }
  async getSessionAnalytics(params?: Record<string, any>) {
    return this.get(`/access-accountability/sessions/analytics${this.buildQuery(params)}`);
  }
  async createSessionEvent(payload: Record<string, any>) {
    return this.post('/access-accountability/sessions', payload);
  }
  async revokeSession(sessionId: string, reason: string) {
    return this.post(`/access-accountability/sessions/${sessionId}/revoke`, { reason });
  }
  // ── Sprint 2.6.0: Access Accountability -- Access Log ─────
  async listAccessLog(params?: Record<string, any>) {
    return this.get(`/access-accountability/access-log${this.buildQuery(params)}`);
  }
  async getAccessLogEntry(id: string) {
    return this.get(`/access-accountability/access-log/${id}`);
  }
  async getAccessLogByResource(
    resourceType: string,
    resourceId: string,
    params?: Record<string, any>,
  ) {
    return this.get(
      `/access-accountability/access-log/resource/${resourceType}/${resourceId}${this.buildQuery(params)}`,
    );
  }
  async getUserAccessSummary(userId: string, params?: Record<string, any>) {
    return this.get(
      `/access-accountability/access-log/user/${userId}/summary${this.buildQuery(params)}`,
    );
  }
  async exportAccessLog(params?: Record<string, any>) {
    return this.get(`/access-accountability/access-log/export${this.buildQuery(params)}`);
  }
  async createAccessLogEntry(payload: Record<string, any>) {
    return this.post('/access-accountability/access-log', payload);
  }
  // ── Sprint 2.6.0: Access Accountability -- Justifications ──
  async listJustifications(params?: Record<string, any>) {
    return this.get(`/access-accountability/justifications${this.buildQuery(params)}`);
  }
  async getJustification(id: string) {
    return this.get(`/access-accountability/justifications/${id}`);
  }
  async getJustificationByAccessId(accessId: string) {
    return this.get(`/access-accountability/justifications/access/${accessId}`);
  }
  async getJustificationReviewQueue(params?: Record<string, any>) {
    return this.get(`/access-accountability/justifications/review-queue${this.buildQuery(params)}`);
  }
  async createJustification(payload: Record<string, any>) {
    return this.post('/access-accountability/justifications', payload);
  }
  async reviewJustification(id: string, payload: Record<string, any>) {
    return this.post(`/access-accountability/justifications/${id}/review`, payload);
  }
  async declineAccess(id: string, reason: string) {
    return this.post(`/access-accountability/justifications/${id}/decline`, { reason });
  }
  // ── Sprint 2.6.0: Access Accountability -- Investigations ──
  async listInvestigations(params?: Record<string, any>) {
    return this.get(`/access-accountability/investigations${this.buildQuery(params)}`);
  }
  async getInvestigation(investigationId: string) {
    return this.get(`/access-accountability/investigations/${investigationId}`);
  }
  async createInvestigation(payload: Record<string, any>) {
    return this.post('/access-accountability/investigations', payload);
  }
  async updateInvestigation(investigationId: string, payload: Record<string, any>) {
    return this.patch(`/access-accountability/investigations/${investigationId}`, payload);
  }
  async addInvestigationTimeline(investigationId: string, entry: Record<string, any>) {
    return this.post(`/access-accountability/investigations/${investigationId}/timeline`, entry);
  }
  async linkInvestigationAccessIds(investigationId: string, accessIds: string[]) {
    return this.post(`/access-accountability/investigations/${investigationId}/link-access`, {
      access_ids: accessIds,
    });
  }
  async linkInvestigationJustificationIds(investigationId: string, justificationIds: string[]) {
    return this.post(
      `/access-accountability/investigations/${investigationId}/link-justifications`,
      { justification_ids: justificationIds },
    );
  }
  async closeInvestigation(investigationId: string, payload: Record<string, any>) {
    return this.post(`/access-accountability/investigations/${investigationId}/close`, payload);
  }
  async exportInvestigation(investigationId: string) {
    return this.get(`/access-accountability/investigations/${investigationId}/export`);
  }
  async getPatternAnalysis(userId: string, params?: Record<string, any>) {
    return this.get(
      `/access-accountability/investigations/pattern/${userId}${this.buildQuery(params)}`,
    );
  }
  // ── Sprint 2.6.0: Access Accountability -- Proofs ──────────
  async listAccessProofs(params?: Record<string, any>) {
    return this.get(`/access-accountability/proofs${this.buildQuery(params)}`);
  }
  async getAccessProof(id: string) {
    return this.get(`/access-accountability/proofs/${id}`);
  }
  async getAccessProofByAccessId(accessId: string) {
    return this.get(`/access-accountability/proofs/access/${accessId}`);
  }
  async getAnchoringStatus() {
    return this.get('/access-accountability/proofs/status');
  }
  async anchorAccessProof(accessId: string) {
    return this.post('/access-accountability/proofs/anchor', { access_id: accessId });
  }
  async batchAnchorAccessProofs(accessIds: string[]) {
    return this.post('/access-accountability/proofs/batch-anchor', { access_ids: accessIds });
  }
  async verifyAccessProofChain(params?: Record<string, any>) {
    return this.post('/access-accountability/proofs/verify', params || {});
  }
  // ── Sprint 2.6.0: Access Accountability -- Config ──────────
  async getAccessAccountabilityConfig() {
    return this.get('/access-accountability/config');
  }
  async upsertAccessAccountabilityConfig(payload: Record<string, any>) {
    return this.put('/access-accountability/config', payload);
  }
  // ── Sprint 2.6.0: Access Accountability -- Audit ───────────
  async listAccessAccountabilityAudit(params?: Record<string, any>) {
    return this.get(`/access-accountability/audit${this.buildQuery(params)}`);
  }
  async getAccessAccountabilityEntityAudit(entityType: string, entityId: string) {
    return this.get(`/access-accountability/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 21.8.0: Access Accountability -- Access Policies ──
  async listAccessPolicies(params?: Record<string, any>) {
    return this.get(`/access-accountability/policies${this.buildQuery(params)}`);
  }
  async createAccessPolicy(data: Record<string, any>) {
    return this.post('/access-accountability/policies', data);
  }
  // ── Sprint 21.8.0: Access Accountability -- Review Campaigns ──
  async listAcctbltyReviewCampaigns(params?: Record<string, any>) {
    return this.get(`/access-accountability/review-campaigns${this.buildQuery(params)}`);
  }
  async createAcctbltyReviewCampaign(data: Record<string, any>) {
    return this.post('/access-accountability/review-campaigns', data);
  }
  async certifyAccessReview(campaignId: string, data: Record<string, any>) {
    return this.post(`/access-accountability/review-campaigns/${campaignId}/certify`, data);
  }
  // ── Sprint 21.8.0: Access Accountability -- Investigation Workflow ──
  async createInvestigationFromAnomaly(data: Record<string, any>) {
    return this.post('/access-accountability/investigations/from-anomaly', data);
  }
  async updateInvestigationWorkflow(investigationId: string, status: string) {
    return this.patch(`/access-accountability/investigations/${investigationId}/workflow`, {
      status,
    });
  }
  async linkInvestigationToRemediation(investigationId: string, remediationId: string) {
    return this.post(`/access-accountability/investigations/${investigationId}/remediation`, {
      remediation_id: remediationId,
    });
  }
  async generateAuthorityEvidencePackage(investigationId: string) {
    return this.post(
      `/access-accountability/investigations/${investigationId}/authority-evidence`,
      {},
    );
  }
  // ── Sprint 21.8.0: Access Accountability -- Evidence Packages ──
  async generateAccessEvidencePackage(data: Record<string, any>) {
    return this.post('/access-accountability/evidence-packages', data);
  }
  async listAccessEvidencePackages(params?: Record<string, any>) {
    return this.get(`/access-accountability/evidence-packages${this.buildQuery(params)}`);
  }
  async getEvidencePackageCompleteness(packageId: string) {
    return this.get(`/access-accountability/evidence-packages/${packageId}/completeness`);
  }
  async getEvidencePackageArtifacts(packageId: string) {
    return this.get(`/access-accountability/evidence-packages/${packageId}/artifacts`);
  }
  async signOffEvidencePackage(packageId: string, data: Record<string, any>) {
    return this.post(`/access-accountability/evidence-packages/${packageId}/sign-off`, data);
  }
  async closeEvidencePackage(packageId: string, data: Record<string, any>) {
    return this.post(`/access-accountability/evidence-packages/${packageId}/close`, data);
  }
  async exportEvidencePackage(packageId: string) {
    return this.post(`/access-accountability/evidence-packages/${packageId}/export`, {});
  }
  async getAccessCertificationHistory(params?: Record<string, any>) {
    return this.get(`/access-accountability/certification-history${this.buildQuery(params)}`);
  }
  // ── Sprint 2.7.0: Consent Properties -- Multi-Property Governance ──
  // Properties
  async listConsentProperties(params?: Record<string, any>) {
    return this.get(`/consent-properties/properties${this.buildQuery(params)}`);
  }
  async getConsentProperty(propertyId: string) {
    return this.get(`/consent-properties/properties/${propertyId}`);
  }
  async createConsentProperty(payload: Record<string, any>) {
    return this.post('/consent-properties/properties', payload);
  }
  async updateConsentProperty(propertyId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/properties/${propertyId}`, payload);
  }
  async decommissionConsentProperty(propertyId: string) {
    return this.post(`/consent-properties/properties/${propertyId}/decommission`, {});
  }
  // Sprint 28.2.1: Sensitive Data Review
  async submitSensitiveDataReview(
    propertyId: string,
    payload: { action: 'approved' | 'rejected'; notes: string },
  ) {
    return this.post(`/consent-properties/properties/${propertyId}/sensitive-review`, payload);
  }
  // Sprint 28.2.1: Withdrawal Propagation
  async propagatePropertyWithdrawal(propertyId: string, payload: Record<string, any>) {
    return this.post(`/consent-properties/properties/${propertyId}/propagate-withdrawal`, payload);
  }
  async listPropertyWithdrawals(propertyId: string, params?: Record<string, any>) {
    return this.get(
      `/consent-properties/properties/${propertyId}/withdrawals${this.buildQuery(params)}`,
    );
  }
  // Groups
  async listConsentPropertyGroups(params?: Record<string, any>) {
    return this.get(`/consent-properties/groups${this.buildQuery(params)}`);
  }
  async getConsentPropertyGroup(groupId: string) {
    return this.get(`/consent-properties/groups/${groupId}`);
  }
  async createConsentPropertyGroup(payload: Record<string, any>) {
    return this.post('/consent-properties/groups', payload);
  }
  async updateConsentPropertyGroup(groupId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/groups/${groupId}`, payload);
  }
  async deleteConsentPropertyGroup(groupId: string) {
    return this.del(`/consent-properties/groups/${groupId}`);
  }
  async getConsentPropertyGroupDashboard(groupId: string) {
    return this.get(`/consent-properties/groups/${groupId}/dashboard`);
  }
  // Bindings
  async listConsentPropertyBindings(params?: Record<string, any>) {
    return this.get(`/consent-properties/bindings${this.buildQuery(params)}`);
  }
  async getConsentPropertyBindingsByProperty(propertyId: string) {
    return this.get(`/consent-properties/bindings/property/${propertyId}`);
  }
  async getConsentPropertyBindingMatrix() {
    return this.get('/consent-properties/bindings/matrix');
  }
  async createConsentPropertyBinding(payload: Record<string, any>) {
    return this.post('/consent-properties/bindings', payload);
  }
  async bulkCreateConsentPropertyBindings(payload: Record<string, any>) {
    return this.post('/consent-properties/bindings/bulk', payload);
  }
  async deleteConsentPropertyBinding(bindingId: string) {
    return this.del(`/consent-properties/bindings/${bindingId}`);
  }
  async updateConsentPropertyBindingStatus(bindingId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/bindings/${bindingId}/status`, payload);
  }
  // Change Requests
  async listConsentChangeRequests(params?: Record<string, any>) {
    return this.get(`/consent-properties/change-requests${this.buildQuery(params)}`);
  }
  async getConsentChangeRequestPipeline() {
    return this.get('/consent-properties/change-requests/pipeline');
  }
  async getConsentChangeRequest(crId: string) {
    return this.get(`/consent-properties/change-requests/${crId}`);
  }
  async getConsentChangeRequestImpact(crId: string) {
    return this.get(`/consent-properties/change-requests/${crId}/impact`);
  }
  async createConsentChangeRequest(payload: Record<string, any>) {
    return this.post('/consent-properties/change-requests', payload);
  }
  async updateConsentChangeRequest(crId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/change-requests/${crId}`, payload);
  }
  async submitConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/submit`, {});
  }
  async approveConsentChangeRequest(crId: string, payload: Record<string, any>) {
    return this.post(`/consent-properties/change-requests/${crId}/approve`, payload);
  }
  async scheduleConsentChangeRequest(crId: string, payload: Record<string, any>) {
    return this.post(`/consent-properties/change-requests/${crId}/schedule`, payload);
  }
  async deployConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/deploy`, {});
  }
  async advanceConsentChangeRequestStage(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/advance-stage`, {});
  }
  async completeConsentChangeRequestDeployment(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/complete-deployment`, {});
  }
  async pauseConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/pause`, {});
  }
  async rollbackConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/rollback`, {});
  }
  async cancelConsentChangeRequest(crId: string) {
    return this.post(`/consent-properties/change-requests/${crId}/cancel`, {});
  }
  // Comparison
  async runConsentComparison(payload: Record<string, any>) {
    return this.post('/consent-properties/comparison/compare', payload);
  }
  async listConsentComparisonReports(params?: Record<string, any>) {
    return this.get(`/consent-properties/comparison/reports${this.buildQuery(params)}`);
  }
  async getConsentComparisonReport(reportId: string) {
    return this.get(`/consent-properties/comparison/reports/${reportId}`);
  }
  async saveConsentComparisonNotes(reportId: string, notes: string) {
    return this.patch(`/consent-properties/comparison/reports/${reportId}/notes`, {
      analyst_notes: notes,
    });
  }
  async runConsentInconsistencyScan() {
    return this.post('/consent-properties/comparison/scan', {});
  }
  // Inconsistencies
  async listConsentInconsistencies(params?: Record<string, any>) {
    return this.get(`/consent-properties/inconsistencies${this.buildQuery(params)}`);
  }
  async getConsentInconsistency(id: string) {
    return this.get(`/consent-properties/inconsistencies/${id}`);
  }
  async getConsentInconsistencyTrend() {
    return this.get('/consent-properties/inconsistencies/trend');
  }
  async acknowledgeConsentInconsistency(id: string) {
    return this.post(`/consent-properties/inconsistencies/${id}/acknowledge`, {});
  }
  async resolveConsentInconsistency(id: string, changeRequestId: string) {
    return this.post(`/consent-properties/inconsistencies/${id}/resolve`, {
      change_request_id: changeRequestId,
    });
  }
  async suppressConsentInconsistency(id: string) {
    return this.post(`/consent-properties/inconsistencies/${id}/suppress`, {});
  }
  // Sprint 28.2.1: Owner/DPO-Scoped Review
  async listMyInconsistencyReview(params?: Record<string, any>) {
    return this.get(`/consent-properties/inconsistencies/my-review${this.buildQuery(params)}`);
  }
  async listDpoInconsistencyReview(params?: Record<string, any>) {
    return this.get(`/consent-properties/inconsistencies/dpo-review${this.buildQuery(params)}`);
  }
  async listMyChangeRequestReview(params?: Record<string, any>) {
    return this.get(`/consent-properties/change-requests/my-review${this.buildQuery(params)}`);
  }
  // Snapshot Schedules
  async listConsentSnapshotSchedules(params?: Record<string, any>) {
    return this.get(`/consent-properties/snapshot-schedules${this.buildQuery(params)}`);
  }
  async getConsentSnapshotSchedule(scheduleId: string) {
    return this.get(`/consent-properties/snapshot-schedules/${scheduleId}`);
  }
  async createConsentSnapshotSchedule(payload: Record<string, any>) {
    return this.post('/consent-properties/snapshot-schedules', payload);
  }
  async updateConsentSnapshotSchedule(scheduleId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/snapshot-schedules/${scheduleId}`, payload);
  }
  async pauseConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/pause`, {});
  }
  async resumeConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/resume`, {});
  }
  async disableConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/disable`, {});
  }
  async executeConsentSnapshotSchedule(scheduleId: string) {
    return this.post(`/consent-properties/snapshot-schedules/${scheduleId}/execute`, {});
  }
  // Golden Templates
  async listConsentGoldenTemplates(params?: Record<string, any>) {
    return this.get(`/consent-properties/golden-templates${this.buildQuery(params)}`);
  }
  async getConsentGoldenTemplate(templateId: string) {
    return this.get(`/consent-properties/golden-templates/${templateId}`);
  }
  async createConsentGoldenTemplate(payload: Record<string, any>) {
    return this.post('/consent-properties/golden-templates', payload);
  }
  async updateConsentGoldenTemplate(templateId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/golden-templates/${templateId}`, payload);
  }
  async updateConsentGoldenTemplateStatus(templateId: string, payload: Record<string, any>) {
    return this.patch(`/consent-properties/golden-templates/${templateId}/status`, payload);
  }
  // Sprint 20.7.0 — Golden Template Application & Rollout
  async applyTemplateToProperty(templateId: string, propertyId: string) {
    return this.post(`/consent-properties/golden-templates/${templateId}/apply-property`, {
      property_id: propertyId,
    });
  }
  async applyTemplateToGroup(templateId: string, groupId: string) {
    return this.post(`/consent-properties/golden-templates/${templateId}/apply-group`, {
      group_id: groupId,
    });
  }
  async getTemplateApplicationHistory(templateId: string) {
    return this.get(`/consent-properties/golden-templates/${templateId}/applications`);
  }
  async listTemplateRollouts(params?: Record<string, any>) {
    return this.get(`/consent-properties/rollouts${this.buildQuery(params)}`);
  }
  async confirmRolloutProperty(rolloutId: string, propertyId: string) {
    return this.post(`/consent-properties/rollouts/${rolloutId}/confirm/${propertyId}`, {});
  }
  async rejectRolloutProperty(rolloutId: string, propertyId: string, reason?: string) {
    return this.post(`/consent-properties/rollouts/${rolloutId}/reject/${propertyId}`, { reason });
  }
  async getRolloutSummary(rolloutId: string) {
    return this.get(`/consent-properties/rollouts/${rolloutId}/summary`);
  }
  // Sprint 20.7.0 — Property Consent Analytics
  async getPropertyAnalytics(propertyId: string) {
    return this.get(`/consent-properties/analytics/property/${propertyId}`);
  }
  async getPropertyComplianceScore(propertyId: string) {
    return this.get(`/consent-properties/analytics/property/${propertyId}/compliance`);
  }
  async getNonCompliantProperties() {
    return this.get('/consent-properties/analytics/non-compliant');
  }
  // Evidence Packs
  async generateConsentEvidencePack(payload: Record<string, any>) {
    return this.post('/consent-properties/evidence-packs/generate', payload);
  }
  async listConsentEvidencePacks(params?: Record<string, any>) {
    return this.get(`/consent-properties/evidence-packs${this.buildQuery(params)}`);
  }
  async exportConsentEvidencePack(packId: string): Promise<AttachmentRef> {
    return this.post(`/consent-properties/evidence-packs/${encodeURIComponent(packId)}/export`, {});
  }
  /**
   * Sprint 44.0.1 Wave 3 — R-33 list-export migration for Consent
   * Properties. Both methods route the page's "export list" action
   * through the backend UnifiedExportService (csv / xlsx / pdf / docx)
   * and return an AttachmentRef. Caller invokes
   * `downloadPlatformFile(ref.file_id, ref.filename)` to trigger the
   * browser download. These replace in-browser `downloadClientExport()`
   * usages on BindingMatrixPage and PropertyRegistryPage.
   */
  async exportConsentPropertyBindings(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/consent-properties/bindings/export${this.buildQuery(params)}`);
  }
  async exportConsentPropertyRegistry(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/consent-properties/properties/export${this.buildQuery(params)}`);
  }
  async exportConsentPropertyChangeRequests(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/consent-properties/change-requests/export${this.buildQuery(params)}`);
  }
  // Dashboard
  async getConsentPropertyDashboard() {
    return this.get('/consent-properties/dashboard');
  }
  // Audit
  async listConsentPropertyAudit(params?: Record<string, any>) {
    return this.get(`/consent-properties/audit${this.buildQuery(params)}`);
  }
  async getConsentPropertyEntityAudit(entityType: string, entityId: string) {
    return this.get(`/consent-properties/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 2.8.0: International Packaging and Localization ──
  // Market Packs
  async listMarketPacks(params?: Record<string, any>) {
    return this.get(`/localization/market-packs${this.buildQuery(params)}`);
  }
  async getMarketPack(packId: string) {
    return this.get(`/localization/market-packs/${packId}`);
  }
  async createMarketPack(data: any) {
    return this.post('/localization/market-packs', data);
  }
  async updateMarketPack(packId: string, data: any) {
    return this.patch(`/localization/market-packs/${packId}`, data);
  }
  async updateMarketPackStatus(packId: string, status: string) {
    return this.post(`/localization/market-packs/${packId}/status`, { status });
  }
  async installMarketPack(packId: string) {
    return this.post(`/localization/market-packs/${packId}/install`, {});
  }
  async uninstallMarketPack(packId: string) {
    return this.post(`/localization/market-packs/${packId}/uninstall`, {});
  }
  // Sprint 45.8.0 — Help-Content Pack admin
  async getHelpContentPacks() {
    return this.get('/localization/help-content-packs');
  }
  async getHelpContentPackCatalog() {
    return this.get('/localization/help-content-packs/catalog');
  }
  async reinstallHelpContentPack() {
    return this.post('/localization/help-content-packs/reinstall', {});
  }
  async uninstallHelpContentPack(packCode: string) {
    return this.post(`/localization/help-content-packs/${packCode}/uninstall`, {});
  }
  // Pack Artifacts
  async listPackArtifacts(params?: Record<string, any>) {
    return this.get(`/localization/artifacts${this.buildQuery(params)}`);
  }
  async getPackArtifact(artifactId: string) {
    return this.get(`/localization/artifacts/${artifactId}`);
  }
  async listPackArtifactsByPack(packId: string, params?: Record<string, any>) {
    return this.get(`/localization/artifacts/pack/${packId}${this.buildQuery(params)}`);
  }
  async createPackArtifact(data: any) {
    return this.post('/localization/artifacts', data);
  }
  async updatePackArtifact(artifactId: string, data: any) {
    return this.patch(`/localization/artifacts/${artifactId}`, data);
  }
  async deletePackArtifact(artifactId: string) {
    return this.del(`/localization/artifacts/${artifactId}`);
  }
  // Language Packs
  async listLanguagePacks(params?: Record<string, any>) {
    return this.get(`/localization/language-packs${this.buildQuery(params)}`);
  }
  async getLanguagePack(packId: string) {
    return this.get(`/localization/language-packs/${packId}`);
  }
  async getLanguagePackCompleteness(packId: string) {
    return this.get(`/localization/language-packs/${packId}/completeness`);
  }
  async createLanguagePack(data: any) {
    return this.post('/localization/language-packs', data);
  }
  async updateLanguagePack(packId: string, data: any) {
    return this.patch(`/localization/language-packs/${packId}`, data);
  }
  async activateLanguagePack(packId: string) {
    return this.post(`/localization/language-packs/${packId}/activate`, {});
  }
  async deactivateLanguagePack(packId: string) {
    return this.post(`/localization/language-packs/${packId}/deactivate`, {});
  }
  async uninstallLanguagePack(packId: string) {
    return this.del(`/localization/language-packs/${packId}`);
  }
  async listAvailableLanguagePacks() {
    return this.get('/localization/language-packs/available');
  }
  /** Sprint 26.4.2: Public endpoint — installed languages for UI selectors (no auth required). */
  async listInstalledLanguages(): Promise<{
    data: Array<{ locale: string; display_name: string }>;
    total: number;
  }> {
    return this.get('/localization/public/installed-languages') as any;
  }
  async installLanguagePackFromRegistry(locale: string) {
    return this.post('/localization/language-packs/install', { locale });
  }
  // Regulatory Frameworks
  async listRegulatoryFrameworks(params?: Record<string, any>) {
    return this.get(`/localization/frameworks${this.buildQuery(params)}`);
  }
  async getRegulatoryFramework(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}`);
  }
  async getFrameworkRequirements(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/requirements`);
  }
  async createRegulatoryFramework(data: any) {
    return this.post('/localization/frameworks', data);
  }
  async updateRegulatoryFramework(frameworkId: string, data: any) {
    return this.patch(`/localization/frameworks/${frameworkId}`, data);
  }
  async updateRegulatoryFrameworkStatus(frameworkId: string, status: string) {
    return this.post(`/localization/frameworks/${frameworkId}/status`, { status });
  }
  async addFrameworkRequirement(frameworkId: string, data: any) {
    return this.post(`/localization/frameworks/${frameworkId}/requirements`, data);
  }
  async removeFrameworkRequirement(frameworkId: string, requirementId: string) {
    return this.del(`/localization/frameworks/${frameworkId}/requirements/${requirementId}`);
  }
  // Sprint 25.5.0: Framework detail endpoints
  async getFrameworkProcedures(
    frameworkId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(
      `/localization/frameworks/${frameworkId}/procedures?page=${page}&page_size=${pageSize}`,
    );
  }
  async getFrameworkFinesRisks(
    frameworkId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(
      `/localization/frameworks/${frameworkId}/fines-risks?page=${page}&page_size=${pageSize}`,
    );
  }
  // Sprint 46.9.0 — list regulatory guidances for a framework. The route
  // takes the framework_code (not the framework_id) for symmetry with how
  // pack-installer + the guidance service key records.
  async getFrameworkGuidances(
    frameworkCode: string,
    params: {
      type?: string;
      category?: string;
      module?: string;
      status?: string;
      search?: string;
      page?: number;
      page_size?: number;
      sort?: string;
    } = {},
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return this.get(
      `/localization/frameworks/${encodeURIComponent(frameworkCode)}/guidances${qs ? `?${qs}` : ''}`,
    );
  }
  async recordGuidanceViewed(frameworkCode: string, guidanceId: string): Promise<void> {
    return this.post(
      `/localization/frameworks/${encodeURIComponent(frameworkCode)}/guidances/${encodeURIComponent(guidanceId)}/viewed`,
    );
  }
  // Sprint 26.4.0.3: Get framework legislative stack
  async getFrameworkStack(frameworkId: string): Promise<Record<string, unknown>> {
    return this.get(`/localization/frameworks/${frameworkId}/stack`);
  }
  // Sprint 25.5.0.2: Update stack layer status
  async updateStackLayerStatus(
    frameworkId: string,
    layerCode: string,
    status: string,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/localization/frameworks/${frameworkId}/stack/${layerCode}/status`, {
      status,
    });
  }
  // Regulatory Mappings
  async listRegulatoryMappings(params?: Record<string, any>) {
    return this.get(`/localization/mappings${this.buildQuery(params)}`);
  }
  async getRegulatoryMapping(mappingId: string) {
    return this.get(`/localization/mappings/${mappingId}`);
  }
  async listMappingsByFramework(frameworkId: string) {
    return this.get(`/localization/mappings/framework/${frameworkId}`);
  }
  async listMappingsByModule(module: string) {
    return this.get(`/localization/mappings/module/${module}`);
  }
  async seedRegulatoryMappings() {
    return this.post('/localization/mappings/seed', {});
  }
  async createRegulatoryMapping(data: any) {
    return this.post('/localization/mappings', data);
  }
  async updateRegulatoryMapping(mappingId: string, data: any) {
    return this.patch(`/localization/mappings/${mappingId}`, data);
  }
  async deleteRegulatoryMapping(mappingId: string) {
    return this.del(`/localization/mappings/${mappingId}`);
  }
  // Pack Submissions
  async listPackSubmissions(params?: Record<string, any>) {
    return this.get(`/localization/submissions${this.buildQuery(params)}`);
  }
  async getPackSubmission(submissionId: string) {
    return this.get(`/localization/submissions/${submissionId}`);
  }
  async createPackSubmission(data: any) {
    return this.post('/localization/submissions', data);
  }
  async startPackSubmissionReview(submissionId: string, reviewerIds: string[]) {
    return this.post(`/localization/submissions/${submissionId}/review`, {
      reviewer_ids: reviewerIds,
    });
  }
  async addPackSubmissionComment(submissionId: string, data: any) {
    return this.post(`/localization/submissions/${submissionId}/comment`, data);
  }
  async approvePackSubmission(submissionId: string) {
    return this.post(`/localization/submissions/${submissionId}/approve`, {});
  }
  async installPackSubmission(submissionId: string) {
    return this.post(`/localization/submissions/${submissionId}/install`, {});
  }
  async rejectPackSubmission(submissionId: string, reason: string) {
    return this.post(`/localization/submissions/${submissionId}/reject`, { reason });
  }
  // Pack install from zip (Sprint 26.7.0 Round 4)
  async installPackFromZip(zipBase64: string, filename: string) {
    return this.post('/localization/submissions/install-zip', { zip_base64: zipBase64, filename });
  }
  async listAllInstalledPacks() {
    return this.get('/localization/submissions/installed-all');
  }
  // Localization Audit
  async listLocalizationAudit(params?: Record<string, any>) {
    return this.get(`/localization/audit${this.buildQuery(params)}`);
  }
  async getLocalizationEntityAudit(entityType: string, entityId: string) {
    return this.get(`/localization/audit/${entityType}/${entityId}`);
  }
  // ── Sprint 7.5.0: Pack Catalog & Install Lifecycle ───────────
  // Pack Catalog
  async listPackCatalog(params?: Record<string, any>) {
    return this.get(`/localization/catalog${this.buildQuery(params)}`);
  }
  async getPackCatalogEntry(marketCode: string) {
    return this.get(`/localization/catalog/${marketCode}`);
  }
  async listPackCatalogClusters() {
    return this.get('/localization/catalog/clusters');
  }
  async getPackInstallStatus() {
    return this.get('/localization/install-status');
  }
  async installPackFromCatalog(marketCode: string, autoInstallDependencies: boolean = false) {
    return this.post(`/localization/catalog/${marketCode}/install`, {
      auto_install_dependencies: autoInstallDependencies,
    });
  }
  async uninstallPackFromCatalog(marketCode: string, force: boolean = false) {
    return this.post(`/localization/catalog/${marketCode}/uninstall`, { force });
  }
  async installCluster(cluster: string) {
    return this.post(`/localization/catalog/clusters/${cluster}/install`, {});
  }
  // ── Sprint 2.9.0: Partner Marketplace ────────────────────────
  // Partner Organizations
  async listPartnerOrgs(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/partner-orgs${this.buildQuery(params)}`);
  }
  async getPartnerOrg(partnerId: string) {
    return this.get(`/partner-marketplace/partner-orgs/${partnerId}`);
  }
  async createPartnerOrg(data: any) {
    return this.post('/partner-marketplace/partner-orgs', data);
  }
  async updatePartnerOrg(partnerId: string, data: any) {
    return this.patch(`/partner-marketplace/partner-orgs/${partnerId}`, data);
  }
  async updatePartnerOrgStatus(partnerId: string, status: string) {
    return this.patch(`/partner-marketplace/partner-orgs/${partnerId}/status`, { status });
  }
  async addManagedOrg(partnerId: string, orgId: string) {
    return this.post(`/partner-marketplace/partner-orgs/${partnerId}/managed-orgs`, {
      org_id: orgId,
    });
  }
  async removeManagedOrg(partnerId: string, orgId: string) {
    return this.del(`/partner-marketplace/partner-orgs/${partnerId}/managed-orgs/${orgId}`);
  }
  // Service Tiers
  async listServiceTiers(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/service-tiers${this.buildQuery(params)}`);
  }
  async getServiceTier(tierId: string) {
    return this.get(`/partner-marketplace/service-tiers/${tierId}`);
  }
  async compareServiceTiers(tierIds: string[]) {
    return this.get(`/partner-marketplace/service-tiers/compare?tier_ids=${tierIds.join(',')}`);
  }
  async createServiceTier(data: any) {
    return this.post('/partner-marketplace/service-tiers', data);
  }
  async updateServiceTier(tierId: string, data: any) {
    return this.patch(`/partner-marketplace/service-tiers/${tierId}`, data);
  }
  async updateServiceTierStatus(tierId: string, status: string) {
    return this.patch(`/partner-marketplace/service-tiers/${tierId}/status`, { status });
  }
  // Deliverable Templates
  async listDeliverableTemplates(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/deliverable-templates${this.buildQuery(params)}`);
  }
  async getDeliverableTemplate(templateId: string) {
    return this.get(`/partner-marketplace/deliverable-templates/${templateId}`);
  }
  async createDeliverableTemplate(data: any) {
    return this.post('/partner-marketplace/deliverable-templates', data);
  }
  async updateDeliverableTemplate(templateId: string, data: any) {
    return this.patch(`/partner-marketplace/deliverable-templates/${templateId}`, data);
  }
  // Deliverable Instances
  async listDeliverables(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/deliverables${this.buildQuery(params)}`);
  }
  async getDeliverable(instanceId: string) {
    return this.get(`/partner-marketplace/deliverables/${instanceId}`);
  }
  async createDeliverable(data: any) {
    return this.post('/partner-marketplace/deliverables', data);
  }
  async updateDeliverable(instanceId: string, data: any) {
    return this.patch(`/partner-marketplace/deliverables/${instanceId}`, data);
  }
  async updateDeliverableStatus(instanceId: string, status: string) {
    return this.patch(`/partner-marketplace/deliverables/${instanceId}/status`, { status });
  }
  async bulkUpdateDeliverableStatus(instanceIds: string[], status: string) {
    return this.post('/partner-marketplace/deliverables/bulk-status', {
      instance_ids: instanceIds,
      status,
    });
  }
  async generateDeliverableSchedule(orgId: string, tierId: string) {
    return this.post('/partner-marketplace/deliverables/generate-schedule', {
      org_id: orgId,
      tier_id: tierId,
    });
  }
  // Brand Profiles
  async listBrandProfiles(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/brand-profiles${this.buildQuery(params)}`);
  }
  async getBrandProfile(brandId: string) {
    return this.get(`/partner-marketplace/brand-profiles/${brandId}`);
  }
  async createBrandProfile(data: any) {
    return this.post('/partner-marketplace/brand-profiles', data);
  }
  async updateBrandProfile(brandId: string, data: any) {
    return this.patch(`/partner-marketplace/brand-profiles/${brandId}`, data);
  }
  async setBrandProfileDefault(brandId: string) {
    return this.post(`/partner-marketplace/brand-profiles/${brandId}/set-default`, {});
  }
  // Support Tickets
  async listSupportTickets(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/support-tickets${this.buildQuery(params)}`);
  }
  async getSupportTicket(ticketId: string) {
    return this.get(`/partner-marketplace/support-tickets/${ticketId}`);
  }
  async createSupportTicket(data: any) {
    return this.post('/partner-marketplace/support-tickets', data);
  }
  async updateSupportTicket(ticketId: string, data: any) {
    return this.patch(`/partner-marketplace/support-tickets/${ticketId}`, data);
  }
  async updateSupportTicketStatus(ticketId: string, status: string) {
    return this.patch(`/partner-marketplace/support-tickets/${ticketId}/status`, { status });
  }
  async addTicketNote(ticketId: string, note: { type: string; content: string }) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/notes`, note);
  }
  async addTicketAttachment(ticketId: string, attachment: any) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/attachments`, attachment);
  }
  async escalateTicket(ticketId: string, toTier: string, reason: string) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/escalate`, {
      to_tier: toTier,
      reason,
    });
  }
  async deescalateTicket(ticketId: string, toTier: string, reason: string) {
    return this.post(`/partner-marketplace/support-tickets/${ticketId}/deescalate`, {
      to_tier: toTier,
      reason,
    });
  }
  // Escalation
  async getEscalationDashboard() {
    return this.get('/partner-marketplace/escalation/dashboard');
  }
  async getEscalatedTickets(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/escalation/tickets${this.buildQuery(params)}`);
  }
  async evaluateAutoEscalation() {
    return this.post('/partner-marketplace/escalation/auto-evaluate', {});
  }
  // Workflow Templates
  async listWorkflowTemplates(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/workflow-templates${this.buildQuery(params)}`);
  }
  async getWorkflowTemplate(templateId: string) {
    return this.get(`/partner-marketplace/workflow-templates/${templateId}`);
  }
  async createWorkflowTemplate(data: any) {
    return this.post('/partner-marketplace/workflow-templates', data);
  }
  async updateWorkflowTemplate(templateId: string, data: any) {
    return this.patch(`/partner-marketplace/workflow-templates/${templateId}`, data);
  }
  // Workflow Instances
  async listWorkflowInstances(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/workflows${this.buildQuery(params)}`);
  }
  async getWorkflowInstance(instanceId: string) {
    return this.get(`/partner-marketplace/workflows/${instanceId}`);
  }
  async createWorkflowInstance(data: any) {
    return this.post('/partner-marketplace/workflows', data);
  }
  async updateWorkflowInstanceStatus(instanceId: string, status: string) {
    return this.patch(`/partner-marketplace/workflows/${instanceId}/status`, { status });
  }
  async advanceWorkflowStep(instanceId: string, stepNumber: number, data?: any) {
    return this.post(`/partner-marketplace/workflows/${instanceId}/advance`, {
      step_number: stepNumber,
      ...data,
    });
  }
  async cancelWorkflowInstance(instanceId: string) {
    return this.post(`/partner-marketplace/workflows/${instanceId}/cancel`, {});
  }
  // Reports
  async listPartnerReports(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/reports${this.buildQuery(params)}`);
  }
  async getPartnerReport(reportId: string) {
    return this.get(`/partner-marketplace/reports/${reportId}`);
  }
  async generatePartnerReport(data: any) {
    return this.post('/partner-marketplace/reports/generate', data);
  }
  // Partner Audit
  async listPartnerAudit(params?: Record<string, any>) {
    return this.get(`/partner-marketplace/audit${this.buildQuery(params)}`);
  }
  async getPartnerAuditEvent(eventId: string) {
    return this.get(`/partner-marketplace/audit/${eventId}`);
  }
  async exportPartnerAudit(params?: Record<string, any>) {
    return this.post(`/partner-marketplace/audit/export${this.buildQuery(params)}`, {});
  }
  // ── Sprint 44.0.8 Wave 3 — 14 Partner Workspace list/detail exports ─
  //
  // 4-format (csv/xlsx/pdf/docx) backend routes via UnifiedExportService.
  // Each returns an AttachmentRef ({ file_id, filename, ... }) consumed
  // by `downloadPlatformFile(file_id, filename)`. The pre-existing
  // `exportPartnerAudit` (POST /audit/export, 1-format JSON metadata
  // response, Sprint 42.0.0 F1) is kept for programmatic consumers.
  async exportPartnerOrgsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/partner-orgs/export${this.buildQuery(params)}`);
  }
  async exportPartnerOrgDetail(
    partnerId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/partner-marketplace/partner-orgs/${partnerId}/export${this.buildQuery(params)}`,
    );
  }
  async exportPartnerDashboard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/dashboard/export${this.buildQuery(params)}`);
  }
  async exportPartnerReportsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/reports/export${this.buildQuery(params)}`);
  }
  async exportPartnerAuditList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/audit/export-list${this.buildQuery(params)}`);
  }
  async exportPartnerWorkflowTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/workflow-templates/export${this.buildQuery(params)}`);
  }
  async exportPartnerWorkflowInstancesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/workflows/export${this.buildQuery(params)}`);
  }
  async exportPartnerDeliverablesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/deliverables/export${this.buildQuery(params)}`);
  }
  async exportPartnerDeliverableTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/deliverable-templates/export${this.buildQuery(params)}`);
  }
  async exportPartnerSupportTicketsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/support-tickets/export${this.buildQuery(params)}`);
  }
  async exportPartnerTicketDetail(
    ticketId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(
      `/partner-marketplace/support-tickets/${ticketId}/export${this.buildQuery(params)}`,
    );
  }
  async exportPartnerEscalationDashboard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/escalation/export${this.buildQuery(params)}`);
  }
  async exportPartnerServiceTiersList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/service-tiers/export${this.buildQuery(params)}`);
  }
  async exportPartnerBrandProfilesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/partner-marketplace/brand-profiles/export${this.buildQuery(params)}`);
  }
  // ── Dashboard ───────────────────────────────────────────────
  async getDashboardSummary(params?: {
    framework_code?: string;
  }): Promise<Record<string, unknown>> {
    return this.get(`/dashboard/summary${this.buildQuery(params)}`);
  }
  // ── Sprint 3.0.0: Auth ──────────────────────────────────────
  async login(payload: {
    email: string;
    password: string;
  }): Promise<{ token: string; refresh_token: string; user: Record<string, unknown> }> {
    return this.post('/auth/login', payload, false);
  }
  async logoutApi(): Promise<void> {
    return this.post('/auth/logout', {});
  }
  async getMe(): Promise<UserResponse> {
    return this.get('/auth/me');
  }
  async changePassword(payload: { current_password: string; new_password: string }): Promise<void> {
    return this.post('/auth/change-password', payload);
  }
  // ── Sprint 26.0.0: Module Tips ─────────────────────────────────
  async getModuleTip(moduleId: string): Promise<Record<string, unknown>> {
    return this.get(`/onboarding/module-tips/${encodeURIComponent(moduleId)}`);
  }
  async getModuleTips(params?: Record<string, string>): Promise<Record<string, unknown>> {
    return this.get(`/onboarding/module-tips${this.buildQuery(params)}`);
  }
  // ── Sprint 26.0.0: User Preferences ────────────────────────────
  async getPreferences(): Promise<Record<string, unknown>> {
    return this.get('/auth/preferences');
  }
  async updatePreferences(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.patch('/auth/preferences', payload);
  }
  // ── Sprint 26.0.0: Self-service Profile ────────────────────────
  async updateMyProfile(payload: {
    display_name?: string;
    phone?: string | null;
    department?: string | null;
    title?: string | null;
  }): Promise<UserResponse> {
    return this.patch('/auth/me', payload);
  }
  // ── Sprint 3.1.0: Token Refresh ────────────────────────────────────
  async refreshToken(payload: {
    refresh_token: string;
  }): Promise<{ token: string; refresh_token: string }> {
    return this.post('/auth/refresh', payload, false);
  }
  // ── Sprint 3.1.0: Password Reset ──────────────────────────────
  async forgotPassword(payload: { email: string }): Promise<void> {
    return this.post('/auth/forgot-password', payload, false);
  }
  async resetPassword(payload: { token: string; new_password: string }): Promise<void> {
    return this.post('/auth/reset-password', payload, false);
  }
  // ── Sprint 3.1.0: Email Verification ──────────────────────────
  async verifyEmail(payload: { token: string }): Promise<void> {
    return this.post('/auth/verify-email', payload, false);
  }
  async requestEmailVerification(payload: { email: string }): Promise<void> {
    return this.post('/auth/request-email-verification', payload);
  }
  // ── Sprint 3.1.0: MFA ─────────────────────────────────────────
  async enrollMfa(): Promise<MfaEnrollmentResponse> {
    return this.post('/mfa/enroll', {});
  }
  async confirmMfa(payload: { code: string }): Promise<MfaConfirmationResponse> {
    return this.post('/mfa/confirm', payload);
  }
  async verifyMfa(payload: { code: string }): Promise<{ success: boolean }> {
    return this.post('/mfa/verify', payload);
  }
  async getMfaStatus(): Promise<MfaStatus> {
    return this.get('/mfa/status');
  }
  async disableMfa(): Promise<void> {
    return this.del('/mfa');
  }
  async regenerateBackupCodes(): Promise<{ backup_codes: string[] }> {
    return this.post('/mfa/backup-codes/regenerate', {});
  }
  // ── Sprint 3.1.0: SSO Config ──────────────────────────────────
  async getSsoPublicInfo(orgId: string): Promise<SsoConfigResponse> {
    return this.get(`/sso/public-info?org_id=${encodeURIComponent(orgId)}`);
  }
  async listSsoConfigs(): Promise<SsoConfigResponse[]> {
    return this.get('/sso/configs');
  }
  async getSsoConfig(configId: string): Promise<SsoConfigResponse> {
    return this.get(`/sso/configs/${configId}`);
  }
  async createSsoConfig(payload: Record<string, unknown>): Promise<SsoConfigResponse> {
    return this.post('/sso/configs', payload);
  }
  async updateSsoConfig(
    configId: string,
    payload: Record<string, unknown>,
  ): Promise<SsoConfigResponse> {
    return this.patch(`/sso/configs/${configId}`, payload);
  }
  async deleteSsoConfig(configId: string): Promise<void> {
    return this.del(`/sso/configs/${configId}`);
  }
  // ── Sprint 3.0.0: Users ─────────────────────────────────────
  async listUsers(params?: Record<string, string>): Promise<PaginatedResponse<UserResponse>> {
    return this.get(`/users${this.buildQuery(params)}`);
  }
  async getUser(userId: string): Promise<UserResponse> {
    return this.get(`/users/${userId}`);
  }
  async createUser(payload: Record<string, unknown>): Promise<UserResponse> {
    return this.post('/users', payload);
  }
  async updateUser(userId: string, payload: Record<string, unknown>): Promise<UserResponse> {
    return this.patch(`/users/${userId}`, payload);
  }
  async deactivateUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/deactivate`, {});
  }
  async reactivateUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/reactivate`, {});
  }
  async removeUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/remove`, {});
  }
  async unlockUser(userId: string): Promise<UserResponse> {
    return this.patch(`/users/${userId}/unlock`, {});
  }
  async getUserRoles(userId: string): Promise<RoleAssignment[]> {
    return this.get(`/users/${userId}/roles`);
  }
  async assignUserRole(userId: string, payload: Record<string, unknown>): Promise<RoleAssignment> {
    return this.post(`/users/${userId}/roles`, payload);
  }
  async revokeUserRole(userId: string, assignmentId: string): Promise<void> {
    return this.del(`/users/${userId}/roles/${assignmentId}`);
  }
  // ── Sprint 3.0.0: Roles ─────────────────────────────────────
  async listRoles(params?: Record<string, string>): Promise<PaginatedResponse<Role>> {
    return this.get(`/roles${this.buildQuery(params)}`);
  }
  async getRole(roleId: string): Promise<Role> {
    return this.get(`/roles/${roleId}`);
  }
  async createRole(payload: Record<string, unknown>): Promise<Role> {
    return this.post('/roles', payload);
  }
  async updateRole(roleId: string, payload: Record<string, unknown>): Promise<Role> {
    return this.patch(`/roles/${roleId}`, payload);
  }
  async deleteRole(roleId: string): Promise<void> {
    return this.del(`/roles/${roleId}`);
  }
  // ── Sprint 3.2.0: API Keys ─────────────────────────────────────
  async listApiKeys(params?: Record<string, string>): Promise<ApiKeyResponse[]> {
    return this.get(`/api-keys${this.buildQuery(params)}`);
  }
  async createApiKey(payload: Record<string, unknown>): Promise<ApiKeyCreateResponse> {
    return this.post('/api-keys', payload);
  }
  async getApiKey(keyId: string): Promise<ApiKeyResponse> {
    return this.get(`/api-keys/${keyId}`);
  }
  async updateApiKey(keyId: string, payload: Record<string, unknown>): Promise<ApiKeyResponse> {
    return this.patch(`/api-keys/${keyId}`, payload);
  }
  async revokeApiKey(keyId: string): Promise<ApiKeyResponse> {
    return this.del(`/api-keys/${keyId}`);
  }
  async rotateApiKey(keyId: string): Promise<ApiKeyCreateResponse> {
    return this.post(`/api-keys/${keyId}/rotate`, {});
  }
  // ── Sprint 3.2.0: Service Accounts ─────────────────────────────
  async listServiceAccounts(params?: Record<string, string>): Promise<ServiceAccountResponse[]> {
    return this.get(`/service-accounts${this.buildQuery(params)}`);
  }
  async createServiceAccount(
    payload: Record<string, unknown>,
  ): Promise<ServiceAccountCreateResponse> {
    return this.post('/service-accounts', payload);
  }
  async getServiceAccount(id: string): Promise<ServiceAccountResponse> {
    return this.get(`/service-accounts/${id}`);
  }
  async deactivateServiceAccount(id: string): Promise<ServiceAccountResponse> {
    return this.patch(`/service-accounts/${id}/deactivate`, {});
  }
  // ── Sprint 3.2.0: Delegations ──────────────────────────────────
  async listDelegations(params?: Record<string, string>): Promise<DelegationResponse[]> {
    return this.get(`/delegations${this.buildQuery(params)}`);
  }
  async createDelegation(payload: Record<string, unknown>): Promise<DelegationResponse> {
    return this.post('/delegations', payload);
  }
  async getDelegation(delegationId: string): Promise<DelegationResponse> {
    return this.get(`/delegations/${delegationId}`);
  }
  async revokeDelegation(delegationId: string): Promise<DelegationResponse> {
    return this.del(`/delegations/${delegationId}`);
  }
  // ── Sprint 3.2.0: Break-Glass ──────────────────────────────────
  async requestBreakGlass(payload: Record<string, unknown>): Promise<BreakGlassResponse> {
    return this.post('/breakglass/request', payload);
  }
  async listBreakGlassRequests(params?: Record<string, string>): Promise<BreakGlassResponse[]> {
    return this.get(`/breakglass${this.buildQuery(params)}`);
  }
  async getBreakGlassRequest(requestId: string): Promise<BreakGlassResponse> {
    return this.get(`/breakglass/${requestId}`);
  }
  async approveBreakGlass(requestId: string): Promise<BreakGlassResponse> {
    return this.post(`/breakglass/${requestId}/approve`, {});
  }
  async denyBreakGlass(requestId: string, reason: string): Promise<BreakGlassResponse> {
    return this.post(`/breakglass/${requestId}/deny`, { reason });
  }
  // ── Sprint 3.2.0: Org Settings (IP Allowlist + Session) ────────
  async getOrgIpAllowlist(): Promise<IpAllowlistSettings> {
    return this.get('/org-settings/ip-allowlist');
  }
  async updateOrgIpAllowlist(payload: Record<string, unknown>): Promise<IpAllowlistSettings> {
    return this.put('/org-settings/ip-allowlist', payload);
  }
  async getOrgSessionSettings(): Promise<SessionSettings> {
    return this.get('/org-settings/sessions');
  }
  async updateOrgSessionSettings(payload: Record<string, unknown>): Promise<SessionSettings> {
    return this.put('/org-settings/sessions', payload);
  }
  // ── Sprint 46.2.0: Org sector self-identification ──────────────
  async getOrgSectors(): Promise<{ sectors: string[] }> {
    return this.get('/org-settings/sectors');
  }
  async updateOrgSectors(sectors: string[]): Promise<{ sectors: string[]; updated?: string }> {
    return this.patch('/org-settings/sectors', { sectors });
  }
  // ── Sprint 46.2.0: Sector overlay catalog + install + mode ─────
  async listSectorOverlays(): Promise<{ data: SectorOverlayCatalogEntry[] }> {
    return this.get('/localization/regulatory/sector-overlays');
  }
  async listInstalledSectorOverlays(): Promise<{ data: InstalledSectorOverlay[] }> {
    return this.get('/localization/regulatory/sector-overlays/installed');
  }
  async installSectorOverlay(
    packCode: string,
    installedAs?: 'direct' | 'reference',
  ): Promise<InstalledSectorOverlay> {
    return this.post(
      `/localization/regulatory/sector-overlays/${encodeURIComponent(packCode)}/install`,
      {
        ...(installedAs ? { installed_as: installedAs } : {}),
      },
    );
  }
  async uninstallSectorOverlay(packCode: string): Promise<{
    pack_code: string;
    framework_code: string | null;
    frameworks_deleted: number;
    deadlines_deleted: number;
    uninstalled_at: string;
  }> {
    return this.del(
      `/localization/regulatory/sector-overlays/${encodeURIComponent(packCode)}/uninstall`,
    );
  }
  async updateInstalledFrameworkMode(
    frameworkId: string,
    installedAs: 'direct' | 'reference',
    reasonNote?: string,
  ): Promise<InstalledSectorOverlay> {
    return this.patch(
      `/localization/regulatory/installed-frameworks/${encodeURIComponent(frameworkId)}`,
      {
        installed_as: installedAs,
        ...(reasonNote ? { reason_note: reasonNote } : {}),
      },
    );
  }
  // ── Sprint 46.3.0: Phased deadline engine ─────────────────────
  async listPhasedObligationEarlyImplementations(): Promise<{
    data: PhasedObligationEarlyImplementationRecord[];
  }> {
    return this.get('/localization/regulatory/phased-obligations/early-implementations');
  }
  async markPhasedObligationEarlyImplemented(
    deadlineCode: string,
    payload: {
      framework_code: string;
      effective_at: string;
      evidence_attachment_id?: string;
      reason_note?: string;
    },
  ): Promise<PhasedObligationEarlyImplementationRecord> {
    return this.post(
      `/localization/regulatory/phased-obligations/${encodeURIComponent(deadlineCode)}/early-implement`,
      payload,
    );
  }
  // ── Sprint 3.2.0: Session Management ───────────────────────────
  async listMySessions(): Promise<Session[]> {
    // The page fetches its own user_id from AuthContext and passes it
    // This is a convenience wrapper; the page calls listUserSessions directly
    return [];
  }
  async listUserSessions(userId: string): Promise<Session[]> {
    return this.get(`/users/${userId}/sessions`);
  }
  async terminateSession(userId: string, sessionId: string): Promise<void> {
    return this.del(`/users/${userId}/sessions/${sessionId}`);
  }
  async terminateAllSessions(userId: string): Promise<void> {
    return this.del(`/users/${userId}/sessions`);
  }
  async extendSession(): Promise<{ token: string }> {
    return this.post('/auth/extend-session', {});
  }
  // ── Sprint 3.3.0: Access Review Campaigns ─────────────────────
  async listAccessReviewCampaigns(
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<AccessReviewCampaign>> {
    return this.get(`/access-reviews${this.buildQuery(params)}`);
  }
  async createAccessReviewCampaign(data: Record<string, any>): Promise<AccessReviewCampaign> {
    return this.post('/access-reviews', data);
  }
  async getAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.get(`/access-reviews/${id}`);
  }
  async launchAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.post(`/access-reviews/${id}/launch`);
  }
  async completeAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.post(`/access-reviews/${id}/complete`);
  }
  async cancelAccessReviewCampaign(id: string): Promise<AccessReviewCampaign> {
    return this.post(`/access-reviews/${id}/cancel`);
  }
  async listAccessReviewItems(
    campaignId: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<AccessReviewItemResponse>> {
    return this.get(`/access-reviews/${campaignId}/items${this.buildQuery(params)}`);
  }
  async submitAccessReviewDecision(
    campaignId: string,
    itemId: string,
    data: Record<string, any>,
  ): Promise<AccessReviewItemResponse> {
    return this.patch(`/access-reviews/${campaignId}/items/${itemId}`, data);
  }
  async bulkAccessReviewDecision(
    campaignId: string,
    data: Record<string, any>,
  ): Promise<AccessReviewItemResponse[]> {
    return this.post(`/access-reviews/${campaignId}/items/bulk`, data);
  }
  async getMyReviews(params?: Record<string, any>): Promise<AccessReviewItemResponse[]> {
    return this.get(`/access-reviews/my-reviews${this.buildQuery(params)}`);
  }
  // ── Sprint 3.3.0: Access Review Analytics ─────────────────────
  async getRoleUsageStats(): Promise<RoleUsageStat[]> {
    return this.get('/access-reviews/analytics/role-usage');
  }
  async getOverPrivilegedUsers(params?: Record<string, any>): Promise<OverPrivilegedUser[]> {
    return this.get(`/access-reviews/analytics/over-privileged${this.buildQuery(params)}`);
  }
  async getTrimRecommendations(): Promise<TrimRecommendation[]> {
    return this.get('/access-reviews/analytics/recommendations');
  }
  // ── Sprint 3.3.0: Attestation Schedules ───────────────────────
  async listAttestationSchedules(
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<AttestationScheduleResponse>> {
    return this.get(`/attestation-schedules${this.buildQuery(params)}`);
  }
  async createAttestationSchedule(data: Record<string, any>): Promise<AttestationScheduleResponse> {
    return this.post('/attestation-schedules', data);
  }
  async getAttestationSchedule(id: string): Promise<AttestationScheduleResponse> {
    return this.get(`/attestation-schedules/${id}`);
  }
  async updateAttestationSchedule(
    id: string,
    data: Record<string, any>,
  ): Promise<AttestationScheduleResponse> {
    return this.patch(`/attestation-schedules/${id}`, data);
  }
  async deactivateAttestationSchedule(id: string): Promise<AttestationScheduleResponse> {
    return this.del(`/attestation-schedules/${id}`);
  }
  async triggerAttestationCycle(id: string): Promise<AttestationScheduleResponse> {
    return this.post(`/attestation-schedules/${id}/trigger`);
  }
  async getAttestationScheduleHistory(
    id: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/attestation-schedules/${id}/history${this.buildQuery(params)}`);
  }
  // ── Sprint 3.3.0: SoD Rules ───────────────────────────────────
  async listSodRules(params?: Record<string, any>): Promise<PaginatedResponse<SodRuleResponse>> {
    return this.get(`/sod-rules${this.buildQuery(params)}`);
  }
  async createSodRule(data: Record<string, any>): Promise<SodRuleResponse> {
    return this.post('/sod-rules', data);
  }
  async getSodRule(id: string): Promise<SodRuleResponse> {
    return this.get(`/sod-rules/${id}`);
  }
  async updateSodRule(id: string, data: Record<string, any>): Promise<SodRuleResponse> {
    return this.patch(`/sod-rules/${id}`, data);
  }
  async deactivateSodRule(id: string): Promise<SodRuleResponse> {
    return this.del(`/sod-rules/${id}`);
  }
  async getSodViolations(): Promise<SodViolation[]> {
    return this.get('/sod-rules/violations');
  }
  async checkSodAssignment(userId: string, roleId: string): Promise<SodCheckResult> {
    return this.get(`/sod-rules/check?user_id=${userId}&role_id=${roleId}`);
  }
  async grantSodException(
    ruleId: string,
    data: Record<string, any>,
  ): Promise<SodExceptionResponse> {
    return this.post(`/sod-rules/${ruleId}/exceptions`, data);
  }
  async listSodExceptions(ruleId: string): Promise<SodExceptionResponse[]> {
    return this.get(`/sod-rules/${ruleId}/exceptions`);
  }
  // ── Sprint 3.6.0: Webhooks ──────────────────────────────────────
  async listWebhooks(params?: Record<string, string>): Promise<PaginatedResponse<WebhookConfig>> {
    return this.get(`/webhooks${this.buildQuery(params)}`);
  }
  async createWebhook(data: Record<string, unknown>): Promise<WebhookConfig> {
    return this.post('/webhooks', data);
  }
  async getWebhook(id: string): Promise<WebhookConfig> {
    return this.get(`/webhooks/${id}`);
  }
  async updateWebhook(id: string, data: Record<string, unknown>): Promise<WebhookConfig> {
    return this.patch(`/webhooks/${id}`, data);
  }
  async deactivateWebhook(id: string): Promise<WebhookConfig> {
    return this.del(`/webhooks/${id}`);
  }
  async testWebhook(id: string): Promise<Record<string, unknown>> {
    return this.post(`/webhooks/${id}/test`, {});
  }
  async listWebhookDeliveries(
    id: string,
    params?: Record<string, string>,
  ): Promise<WebhookDelivery[]> {
    return this.get(`/webhooks/${id}/deliveries${this.buildQuery(params)}`);
  }
  async retryWebhookDelivery(webhookId: string, deliveryId: string): Promise<WebhookDelivery> {
    return this.post(`/webhooks/${webhookId}/deliveries/${deliveryId}/retry`, {});
  }
  // ── Sprint 3.6.0: Domains ──────────────────────────────────────
  async listDomains(params?: Record<string, string>): Promise<PaginatedResponse<DomainMapping>> {
    return this.get(`/domains${this.buildQuery(params)}`);
  }
  async addDomain(data: { domain: string }): Promise<DomainMapping> {
    return this.post('/domains', data);
  }
  async verifyDomain(id: string): Promise<Record<string, unknown>> {
    return this.post(`/domains/${id}/verify`, {});
  }
  async deleteDomain(id: string): Promise<void> {
    return this.del(`/domains/${id}`);
  }
  // ── Sprint 3.6.0: Export Schedules ──────────────────────────────
  async listExportSchedules(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ExportSchedule>> {
    return this.get(`/export-schedules${this.buildQuery(params)}`);
  }
  async createExportSchedule(data: Record<string, unknown>): Promise<ExportSchedule> {
    return this.post('/export-schedules', data);
  }
  async updateExportSchedule(id: string, data: Record<string, unknown>): Promise<ExportSchedule> {
    return this.patch(`/export-schedules/${id}`, data);
  }
  async deactivateExportSchedule(id: string): Promise<ExportSchedule> {
    return this.del(`/export-schedules/${id}`);
  }
  async triggerExportSchedule(id: string): Promise<ExportRun> {
    return this.post(`/export-schedules/${id}/trigger`, {});
  }
  async listExportRuns(id: string, params?: Record<string, string>): Promise<ExportRun[]> {
    return this.get(`/export-schedules/${id}/runs${this.buildQuery(params)}`);
  }
  // ── Sprint 3.6.0: Email Log ──────────────────────────────────────
  async listEmailLog(params?: Record<string, string>): Promise<PaginatedResponse<EmailLogEntry>> {
    return this.get(`/email-log${this.buildQuery(params)}`);
  }
  async getEmailLogStats(): Promise<Record<string, unknown>> {
    return this.get('/email-log/stats');
  }
  async getEmailLogEntry(id: string): Promise<EmailLogEntry> {
    return this.get(`/email-log/${id}`);
  }
  // ── Sprint 4.0.0 -- Board Report ───────────────────────────
  async generateBoardReport(params: {
    date_from: string;
    date_to: string;
    language: string;
    sections?: string[];
  }): Promise<Record<string, unknown>> {
    return this.post('/governance/board-report', params);
  }
  // ── Sprint 4.0.0 -- Quality Gates ─────────────────────────
  async listQualityGates(
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/rights-requests/quality-gates${this.buildQuery(params)}`);
  }
  async createQualityGate(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/quality-gates', data);
  }
  async updateQualityGate(
    ruleId: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/rights-requests/quality-gates/${ruleId}`, data);
  }
  async deactivateQualityGate(ruleId: string): Promise<{ message: string }> {
    return this.del(`/rights-requests/quality-gates/${ruleId}`);
  }
  async evaluateQualityGates(
    formType: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/quality-gates/evaluate', { form_type: formType, payload });
  }
  // ── Sprint 4.0.0 -- Escalation Ladder ─────────────────────
  async getEscalationLadder(): Promise<Record<string, unknown>> {
    return this.get('/rights-requests/escalation-ladder');
  }
  async replaceEscalationLadder(tiers: unknown[]): Promise<Record<string, unknown>> {
    return this.put('/rights-requests/escalation-ladder', { tiers });
  }
  async getCaseEscalations(
    casePublicId: string,
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/rights-requests/cases/${casePublicId}/escalations${this.buildQuery(params)}`);
  }
  // ── Sprint 4.0.0 -- Communications ────────────────────────
  async listCaseCommunications(
    casePublicId: string,
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(
      `/rights-requests/cases/${casePublicId}/communications${this.buildQuery(params)}`,
    );
  }
  async createCaseCommunication(
    casePublicId: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post(`/rights-requests/cases/${casePublicId}/communications`, data);
  }
  async getCaseCommunication(
    casePublicId: string,
    commId: string,
  ): Promise<Record<string, unknown>> {
    return this.get(`/rights-requests/cases/${casePublicId}/communications/${commId}`);
  }
  // ── Sprint 4.1.0 -- Privacy Scanner ────────────────────────
  async getMonitoringConfig(): Promise<PrivacyScannerConfig> {
    return this.get('/privacy-scanner/monitoring/config');
  }
  async updateMonitoringConfig(config: Record<string, unknown>): Promise<PrivacyScannerConfig> {
    return this.put('/privacy-scanner/monitoring/config', config);
  }
  async testMonitoringConnection(): Promise<Record<string, unknown>> {
    return this.post('/privacy-scanner/monitoring/config/test', {});
  }
  async getMonitoringDashboard(): Promise<Record<string, unknown>> {
    return this.get('/privacy-scanner/monitoring/dashboard');
  }
  async getScannerConfig(): Promise<Record<string, unknown>> {
    return this.get('/privacy-scanner/real-time/config');
  }
  async updateScannerConfig(config: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.put('/privacy-scanner/real-time/config', config);
  }
  async testScannerConnection(): Promise<Record<string, unknown>> {
    return this.post('/privacy-scanner/real-time/config/test', {});
  }
  async launchPrivacyScan(request: Record<string, unknown>): Promise<PrivacyScanResult> {
    return this.post('/privacy-scanner/real-time/scans', request);
  }
  async getPrivacyScanStatus(scanId: string): Promise<PrivacyScanProgress> {
    return this.get(`/privacy-scanner/real-time/scans/${scanId}/status`);
  }
  async getPrivacyScanResult(scanId: string): Promise<PrivacyScanResult> {
    return this.get(`/privacy-scanner/real-time/scans/${scanId}`);
  }
  async getPrivacyScanHistory(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<PrivacyScanResult>> {
    return this.get(`/privacy-scanner/real-time/scans${this.buildQuery(params)}`);
  }
  async comparePrivacyScans(scanId1: string, scanId2: string): Promise<ScanComparisonResult> {
    return this.post('/privacy-scanner/real-time/scans/compare', {
      scan_id_1: scanId1,
      scan_id_2: scanId2,
    });
  }
  // ── Sprint 4.2.0: AesirX CMP Consent Integration ──────────────
  // Webhook Config
  async getCmpWebhookConfig(): Promise<CmpWebhookConfig> {
    return this.get('/consent/cmp-webhook/config');
  }
  async createCmpWebhookConfig(payload: Record<string, unknown>): Promise<CmpWebhookConfig> {
    return this.post('/consent/cmp-webhook/config', payload);
  }
  async updateCmpWebhookConfig(payload: Record<string, unknown>): Promise<CmpWebhookConfig> {
    return this.patch('/consent/cmp-webhook/config', payload);
  }
  async deleteCmpWebhookConfig(): Promise<{ message: string }> {
    return this.del('/consent/cmp-webhook/config');
  }
  // Bulk Sync
  async startCmpBulkSync(events: any[]): Promise<BulkSyncJob> {
    return this.post('/consent/cmp-webhook/bulk-sync', { events });
  }
  async getCmpBulkSyncStatus(jobId: string): Promise<BulkSyncJob> {
    return this.get(`/consent/cmp-webhook/bulk-sync/${jobId}`);
  }
  async remapCmpRecords(): Promise<Record<string, unknown>> {
    return this.post('/consent/cmp-webhook/remap', {});
  }
  // Purpose Mappings
  async listCmpPurposeMappings(): Promise<CmpPurposeMapping[]> {
    return this.get('/consent/cmp-purpose-mappings');
  }
  async upsertCmpPurposeMapping(payload: Record<string, unknown>): Promise<CmpPurposeMapping> {
    return this.post('/consent/cmp-purpose-mappings', payload);
  }
  async deleteCmpPurposeMapping(mappingId: string): Promise<{ message: string }> {
    return this.del(`/consent/cmp-purpose-mappings/${mappingId}`);
  }
  async autoMapCmpPurposes(): Promise<{ mappings_created: number; mappings_failed: number }> {
    return this.post('/consent/cmp-purpose-mappings/auto-map', {});
  }
  // Web Consent Records
  async listWebConsentRecords(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<WebConsentRecord>> {
    return this.get(`/consent/web-records${this.buildQuery(params)}`);
  }
  async getWebConsentAnalytics(params?: Record<string, string>): Promise<WebConsentAnalytics> {
    return this.get(`/consent/web-records/analytics${this.buildQuery(params)}`);
  }
  async overrideWebConsentRecordStatus(
    recordId: string,
    body: { action: 'revoke' | 'reject'; reason?: string },
  ): Promise<{ record_id: string; admin_status: string }> {
    return this.patch(`/consent/web-records/${recordId}/status`, body);
  }
  // ── Sprint 20.6.0: Consent Governance Maturity ──────
  // Epic 1: Withdrawal Propagation
  async propagateWithdrawal(
    workflowId: string,
    linkedSystems: Array<{ system_id: string; system_name: string; owner_id: string }>,
  ): Promise<Record<string, unknown>> {
    return this.post(`/consent/propagation/${workflowId}`, {
      linked_systems: linkedSystems,
    });
  }
  async listPropagationTasks(workflowId: string): Promise<WithdrawalPropagationTask[]> {
    return this.get(`/consent/propagation/${workflowId}`);
  }
  async confirmPropagation(taskId: string): Promise<WithdrawalPropagationTask> {
    return this.post(`/consent/propagation/${taskId}/confirm`, {});
  }
  async failPropagation(taskId: string, reason: string): Promise<WithdrawalPropagationTask> {
    return this.post(`/consent/propagation/${taskId}/fail`, { reason });
  }
  async getPropagationSummary(workflowId: string): Promise<Record<string, unknown>> {
    return this.get(`/consent/propagation/${workflowId}/summary`);
  }
  // Epic 2: Sensitive Data
  async flagSensitivePolicy(policyId: string, categories: string[]): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/sensitive`, { categories });
  }
  async listSensitivePolicies(): Promise<ConsentPolicy[]> {
    return this.get('/consent/policies/sensitive');
  }
  async requireEnhancedReview(policyId: string): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/enhanced-review`, {});
  }
  // Sprint 28.1.3: Submit enhanced review decision
  async submitEnhancedReview(
    policyId: string,
    data: { action: 'approved' | 'rejected'; notes: string },
  ): Promise<ConsentPolicy> {
    return this.post(`/consent/policies/${policyId}/enhanced-review/submit`, data);
  }
  // Epic 3: Mismatch Reconciliation
  async createReconciliationTask(
    data: Record<string, unknown>,
  ): Promise<MismatchReconciliationTask> {
    return this.post('/consent/reconciliation', data);
  }
  async listReconciliationTasks(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<MismatchReconciliationTask>> {
    return this.get(`/consent/reconciliation${this.buildQuery(params)}`);
  }
  async resolveReconciliationTask(
    taskId: string,
    data: { resolution: string; notes: string },
  ): Promise<MismatchReconciliationTask> {
    return this.post(`/consent/reconciliation/${taskId}/resolve`, data);
  }
  async getReconciliationDashboard(): Promise<Record<string, unknown>> {
    return this.get('/consent/reconciliation/dashboard');
  }
  // Epic 4: Bidirectional CMP Sync
  async createSyncEvent(data: Record<string, unknown>): Promise<CmpSyncEvent> {
    return this.post('/consent/sync', data);
  }
  async listSyncEvents(params?: Record<string, string>): Promise<PaginatedResponse<CmpSyncEvent>> {
    return this.get(`/consent/sync${this.buildQuery(params)}`);
  }
  async resolveSyncConflict(
    syncId: string,
    resolutionAction: 'accept_cmp' | 'keep_grc',
  ): Promise<CmpSyncEvent> {
    return this.post(`/consent/sync/${syncId}/resolve`, { resolution_action: resolutionAction });
  }
  async detectSyncDrift(): Promise<{ detected: number; skipped: number; total_compared: number }> {
    return this.post('/consent/sync/detect', {});
  }
  // ── Sprint 4.3.0 -- CMP DSAR/DSR Sync ────────────────────
  // Outbound sync config
  async getCmpDsarSyncConfig(): Promise<Record<string, unknown>> {
    return this.get('/rights-requests/cmp-sync/config');
  }
  async createCmpDsarSyncConfig(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/cmp-sync/config', payload);
  }
  async updateCmpDsarSyncConfig(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch('/rights-requests/cmp-sync/config', payload);
  }
  async testCmpDsarSyncConnection(): Promise<Record<string, unknown>> {
    return this.post('/rights-requests/cmp-sync/config/test', {});
  }
  // Sync history & resync
  async getCaseSyncHistory(casePublicId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/rights-requests/cases/${casePublicId}/sync-history`);
  }
  async triggerCaseResync(casePublicId: string): Promise<Record<string, unknown>> {
    return this.post(`/rights-requests/cases/${casePublicId}/resync`, {});
  }
  // ── Sprint 20.4.0: DSR Fulfillment, Response & Extension ──────
  async assignDsrFulfillment(
    caseId: string,
    assignments: any[],
  ): Promise<DepartmentFulfillmentTask[]> {
    return this.post(`/rights-requests/cases/${caseId}/fulfillment/assign`, { assignments });
  }
  async listDsrFulfillmentTasks(caseId: string): Promise<DepartmentFulfillmentTask[]> {
    return this.get(`/rights-requests/cases/${caseId}/fulfillment`);
  }
  async updateDsrFulfillmentTask(
    taskId: string,
    updates: Record<string, unknown>,
  ): Promise<DepartmentFulfillmentTask> {
    return this.patch(`/rights-requests/fulfillment/${taskId}`, updates);
  }
  async deleteDsrFulfillmentTask(taskId: string): Promise<void> {
    return this.del(`/rights-requests/fulfillment/${taskId}`);
  }
  async dsrDepartmentSignoff(taskId: string): Promise<DepartmentFulfillmentTask> {
    return this.post(`/rights-requests/fulfillment/${taskId}/signoff`, {});
  }
  async getDsrFulfillmentSummary(caseId: string): Promise<Record<string, unknown>> {
    return this.get(`/rights-requests/cases/${caseId}/fulfillment/summary`);
  }
  async suggestDsrDataStores(caseId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/rights-requests/cases/${caseId}/data-stores`);
  }
  async generateDsrResponse(caseId: string, templateId: string): Promise<DsrResponseRecord> {
    return this.post(`/rights-requests/cases/${caseId}/response/generate`, {
      template_id: templateId,
    });
  }
  async sendDsrResponse(
    caseId: string,
    responseId: string,
    deliveryMethod: string,
  ): Promise<DsrResponseRecord> {
    return this.post(`/rights-requests/cases/${caseId}/response/${responseId}/send`, {
      delivery_method: deliveryMethod,
    });
  }
  async listDsrResponseTemplates(rightType?: string): Promise<DsrResponseTemplate[]> {
    const params = rightType ? { right_type: rightType } : undefined;
    // Backend wraps in { data: [...] }; callers (TicketDetailPage, ResponsePanel,
    // CommunicationsPanel) expect a plain array. Unwrap here so all consumers work.
    const raw = await this.get<{ data: DsrResponseTemplate[] } | DsrResponseTemplate[]>(
      `/rights-requests/response-templates${this.buildQuery(params)}`,
    );
    if (Array.isArray(raw)) return raw;
    return Array.isArray((raw as { data?: DsrResponseTemplate[] })?.data)
      ? (raw as { data: DsrResponseTemplate[] }).data
      : [];
  }
  async createDsrResponseTemplate(payload: Record<string, unknown>): Promise<DsrResponseTemplate> {
    return this.post('/rights-requests/response-templates', payload);
  }
  // ── Sprint 43.2.0 batch 3 (E1) — DSR response template CRUD ────
  async getDsrResponseTemplate(templateId: string): Promise<DsrResponseTemplate> {
    return this.get(`/rights-requests/response-templates/${templateId}`);
  }
  async updateDsrResponseTemplate(
    templateId: string,
    updates: Partial<{
      right_type: string;
      outcome: string;
      locale: string;
      subject: string;
      body_template: string;
      is_active: boolean;
      official_form_id: string | null;
    }>,
  ): Promise<DsrResponseTemplate> {
    return this.patch(`/rights-requests/response-templates/${templateId}`, updates);
  }
  async deactivateDsrResponseTemplate(templateId: string): Promise<DsrResponseTemplate> {
    return this.del(`/rights-requests/response-templates/${templateId}`);
  }
  async cloneDsrResponseTemplate(
    templateId: string,
    newLocale: string,
  ): Promise<DsrResponseTemplate> {
    return this.post(`/rights-requests/response-templates/${templateId}/clone`, {
      locale: newLocale,
    });
  }
  async listDsrExtensions(caseId: string): Promise<ExtensionRequest[]> {
    return this.get(`/rights-requests/cases/${caseId}/extension`);
  }
  async requestDsrExtension(
    caseId: string,
    reason: string,
    days: number,
  ): Promise<ExtensionRequest> {
    return this.post(`/rights-requests/cases/${caseId}/extension`, {
      reason,
      additional_days: days,
    });
  }
  async approveDsrExtension(
    caseId: string,
    extensionId: string,
    decision: string,
  ): Promise<ExtensionRequest> {
    return this.post(`/rights-requests/cases/${caseId}/extension/${extensionId}/approve`, {
      decision,
    });
  }
  // ── Sprint 4.4.0: Module Configuration ─────────────────────────
  async getModuleConfig(): Promise<ModuleConfig> {
    return this.get('/module-config');
  }
  async updateModuleConfig(payload: {
    modules: Record<string, { enabled: boolean }>;
  }): Promise<ModuleConfig> {
    return this.put('/module-config', payload);
  }
  // ── Sprint 7.6.0: Form Engine ─────────────────────────────────
  async listFormTemplates(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormTemplate>> {
    return this.get(`/forms/templates${this.buildQuery(params)}`);
  }
  async getFormTemplate(id: string): Promise<FormTemplate> {
    return this.get(`/forms/templates/${id}`);
  }
  async createFormTemplate(data: any): Promise<FormTemplate> {
    return this.post('/forms/templates', data);
  }
  async updateFormTemplate(id: string, data: any): Promise<FormTemplate> {
    return this.put(`/forms/templates/${id}`, data);
  }
  async createFormInstance(data: any): Promise<FormInstance> {
    return this.post('/forms/instances', data);
  }
  async listFormInstances(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormInstance>> {
    return this.get(`/forms/instances${this.buildQuery(params)}`);
  }
  async getFormInstance(id: string): Promise<FormInstance> {
    return this.get(`/forms/instances/${id}`);
  }
  async updateFormInstance(id: string, data: any): Promise<FormInstance> {
    return this.put(`/forms/instances/${id}`, data);
  }
  async submitFormInstance(id: string): Promise<FormInstance> {
    return this.post(`/forms/instances/${id}/submit`, {});
  }
  async approveFormInstance(id: string): Promise<FormInstance> {
    return this.post(`/forms/instances/${id}/approve`, {});
  }
  async updateFilingStatus(id: string, filingStatus: string): Promise<FormInstance> {
    return this.patch(`/forms/instances/${id}/filing-status`, { filing_status: filingStatus });
  }
  async generateFormOutput(id: string, data: any): Promise<FormOutput> {
    return this.post(`/forms/instances/${id}/generate`, data);
  }
  async listFormOutputs(instanceId: string): Promise<FormOutput[]> {
    return this.get(`/forms/instances/${instanceId}/outputs`);
  }
  /**
   * Fix Sprint 20260418-0500 Finding 14 — R-33 migration. Replaces
   * `window.print()` on FormOutputsPage with a backend-rendered PDF
   * persisted via UnifiedExportService. Returns AttachmentRef so the
   * frontend can call `downloadPlatformFile`.
   */
  async exportFormOutputPrint(
    instanceId: string,
    params: { format: 'pdf' },
  ): Promise<AttachmentRef> {
    return this.get(`/forms/instances/${instanceId}/print-export${this.buildQuery(params)}`);
  }
  async downloadFormOutput(outputId: string): Promise<Blob> {
    return this.get(`/forms/outputs/${outputId}/download`);
  }
  async listFormAuditEvents(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormAuditEvent>> {
    return this.get(`/forms/audit${this.buildQuery(params)}`);
  }
  // ── FU2b (2026-04-21): Cover-letter preview + snapshot ─────────
  async previewCoverLetter(payload: {
    dossier_id: string;
    locale: string;
    stage?: 'initial' | 'supplement' | 'resubmission';
    body_paragraph?: string;
    additional_notes?: string | null;
    subject?: string;
  }): Promise<{ data_url: string; context: Record<string, unknown> }> {
    return this.post('/forms/cover-letter/preview', payload);
  }
  async snapshotCoverLetter(payload: {
    dossier_id: string;
    locale: string;
    stage?: 'initial' | 'supplement' | 'resubmission';
    body_paragraph?: string;
    additional_notes?: string | null;
    subject?: string;
    regenerate?: boolean;
  }): Promise<{ snapshot: Record<string, unknown> }> {
    return this.post('/forms/cover-letter/snapshot', payload);
  }
  // ── Sprint 37.1.0: Form Orchestration ──────────────────────────
  // fix-sprint-20260411-B F13 — payload/response types imported from
  // the shared barrel so the frontend, backend, and type system agree
  // on a single contract. Previously these signatures were inlined
  // both here and in the route handler.
  async orchestrationCreate(
    data: CreateWithFormPayload,
  ): Promise<{ entityId: string; formInstanceId: string }> {
    return this.post('/forms/orchestration/create', data);
  }
  async orchestrationUpdateValues(
    data: UpdateFormValuesPayload,
  ): Promise<UpdateFormValuesResponse> {
    return this.put('/forms/orchestration/update-values', data);
  }
  async orchestrationSubmit(formInstanceId: string): Promise<SubmitFormResponse> {
    return this.post('/forms/orchestration/submit', { formInstanceId });
  }
  async orchestrationFinalize(formInstanceId: string): Promise<FinalizeFormResponse> {
    return this.post('/forms/orchestration/finalize', { formInstanceId });
  }
  // ── Sprint 10.6.0: Form Catalog & Template Management ──────────
  async listFormCatalog(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<FormCatalogEntryWithStatus>> {
    return this.get(`/forms/catalog${this.buildQuery(params)}`);
  }
  async listFormCatalogCategories(
    params?: Record<string, string>,
  ): Promise<Record<string, unknown>[]> {
    return this.get(`/forms/catalog/categories${this.buildQuery(params)}`);
  }
  async getFormInstallStatus(): Promise<FormInstallStatusResponse> {
    return this.get('/forms/install-status');
  }
  async getFormCatalogEntry(schemaId: string): Promise<FormCatalogEntryWithStatus> {
    return this.get(`/forms/catalog/${schemaId}`);
  }
  async installFormFromCatalog(schemaId: string): Promise<Record<string, unknown>> {
    return this.post(`/forms/catalog/${schemaId}/install`, {});
  }
  async uninstallFormFromCatalog(schemaId: string): Promise<Record<string, unknown>> {
    return this.post(`/forms/catalog/${schemaId}/uninstall`, {});
  }
  async installFormCategory(
    category: string,
    marketCode?: string,
  ): Promise<Record<string, unknown>> {
    const qs = marketCode ? `?market_code=${encodeURIComponent(marketCode)}` : '';
    return this.post(`/forms/catalog/category/${category}/install${qs}`, {});
  }
  // ── Sprint 15.7.0: Dossier Management ─────────────────────────
  async listDossiers(params?: Record<string, string>): Promise<PaginatedResponse<Dossier>> {
    return this.get(`/forms/dossiers${this.buildQuery(params)}`);
  }
  async getDossier(id: string): Promise<Dossier> {
    return this.get(`/forms/dossiers/${id}`);
  }
  async createDossier(payload: any): Promise<Dossier> {
    return this.post('/forms/dossiers', payload);
  }
  async updateDossier(id: string, payload: any): Promise<Dossier> {
    return this.put(`/forms/dossiers/${id}`, payload);
  }
  async transitionDossier(id: string, payload: any): Promise<Dossier> {
    return this.post(`/forms/dossiers/${id}/transition`, payload);
  }
  async attachDossierForm(id: string, payload: any): Promise<Dossier> {
    return this.post(`/forms/dossiers/${id}/attachments`, payload);
  }
  async detachDossierForm(id: string, formId: string): Promise<{ message: string }> {
    return this.del(`/forms/dossiers/${id}/attachments/${formId}`);
  }
  // ── Sprint 15.7.0: Compliance Rules ───────────────────────────
  async listComplianceRules(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ComplianceRuleDocument>> {
    return this.get(`/compliance-rules${this.buildQuery(params)}`);
  }
  async getComplianceRule(id: string): Promise<ComplianceRuleDocument> {
    return this.get(`/compliance-rules/${id}`);
  }
  async createComplianceRule(payload: any): Promise<ComplianceRuleDocument> {
    return this.post('/compliance-rules', payload);
  }
  async updateComplianceRule(id: string, payload: any): Promise<ComplianceRuleDocument> {
    return this.put(`/compliance-rules/${id}`, payload);
  }
  async listComplianceRulesByFramework(code: string): Promise<ComplianceRuleDocument[]> {
    return this.get(`/compliance-rules/framework/${code}`);
  }
  // ── Sprint 48.3.0: MCP Phase 2 pending-write queue ──────────
  async listMcpPendingWrites(params?: {
    status?: 'pending' | 'approved' | 'declined' | 'expired' | 'executed';
  }): Promise<{
    data: Array<{
      _id: string;
      api_key_id: string;
      api_key_prefix: string;
      tool_slug: string;
      preview: string;
      status: 'pending' | 'approved' | 'declined' | 'expired' | 'executed';
      created_at: string;
      expires_at: string;
      executed_at: string | null;
    }>;
    total: number;
  }> {
    return this.get(
      `/mcp-connector/pending-writes${this.buildQuery(params as Record<string, string> | undefined)}`,
    );
  }
  async approveMcpPendingWrite(
    id: string,
  ): Promise<{ status: string; execution_result_excerpt: string | null }> {
    return this.post(`/mcp-connector/pending-writes/${id}/approve`, {});
  }
  async declineMcpPendingWrite(id: string): Promise<{ status: string }> {
    return this.post(`/mcp-connector/pending-writes/${id}/decline`, {});
  }
  // ── Sprint 48.2.9: Forseti audit walkthrough generator ──────
  /** GET /api/v1/audits — list audits available to the current org. */
  async listAudits(params?: { limit?: number }): Promise<{
    data: Array<{ _id: string; name?: string; status?: string; framework_codes?: string[] }>;
  }> {
    const q = params?.limit ? `?limit=${params.limit}` : '';
    return this.get(`/audits${q}`);
  }
  async generateAuditWalkthrough(auditId: string): Promise<{
    draft_id: string;
    audit_id: string;
    audit_name?: string;
    controls: Array<{
      control_id: string;
      control_label: string;
      framework_code?: string;
      requirement_id?: string;
      opening_question: string;
      expected_evidence: string[];
      followup_probes: string[];
      potential_gaps: string[];
      evidence_count: number;
    }>;
    citations: Array<{ framework_code: string; requirement_id: string; article_ref?: string }>;
    billable_units: number;
  }> {
    return this.post(`/audits/${auditId}/generate-walkthrough`, {});
  }
  async resolveAuditWalkthrough(
    draftId: string,
    decision: 'accepted' | 'rejected',
  ): Promise<{ status: string }> {
    return this.post(`/audits/walkthrough/${draftId}/resolve`, { decision });
  }
  async exportAuditWalkthroughDocx(draftId: string): Promise<unknown> {
    return this.post(`/audits/walkthrough/${draftId}/export-docx`, {});
  }
  // ── Sprint 48.2.9: Forseti privilege creep detection ────────
  async scanPrivilegeCreep(): Promise<{
    scan_id: string;
    scanned_user_count: number;
    findings: Array<{
      id: string;
      user_id: string;
      user_email?: string;
      job_function_title?: string;
      role_ids: string[];
      unexpected_permissions: string[];
      expected_permissions_summary: string[];
      severity: 'critical' | 'high' | 'medium' | 'low';
      rationale: string;
      suggested_remediation: string;
    }>;
    billable_units: number;
  }> {
    return this.post('/access-accountability/scan-privilege-creep', {});
  }
  async listPrivilegeCreepFindings(params?: {
    status?: 'open' | 'dismissed' | 'remediated';
  }): Promise<{
    data: Array<{
      _id: string;
      user_id: string;
      user_email?: string;
      job_function_title?: string;
      severity: 'critical' | 'high' | 'medium' | 'low';
      unexpected_permissions: string[];
      suggested_remediation: string;
      status: 'open' | 'dismissed' | 'remediated';
      produced_at: string;
    }>;
  }> {
    return this.get(
      `/access-accountability/privilege-creep/findings${this.buildQuery(
        params as Record<string, string> | undefined,
      )}`,
    );
  }
  async resolvePrivilegeCreepFinding(
    findingId: string,
    decision: 'dismissed' | 'remediated',
    note?: string,
  ): Promise<{ status: string }> {
    return this.post(`/access-accountability/privilege-creep/findings/${findingId}/resolve`, {
      decision,
      note,
    });
  }
  // ── Sprint 48.2.8: Compliance rule auto-mapping ─────────────
  async runComplianceRuleMapping(packCode: string): Promise<{
    draft_id: string;
    pack_code: string;
    suggestions: Array<{
      id: string;
      kind: 'new_rule' | 'refresh_existing' | 'no_change';
      framework_code: string;
      requirement_id: string;
      article_ref?: string;
      existing_rule_id?: string;
      existing_rule_name?: string;
      proposed_title: string;
      proposed_body: string;
      source_quote: string;
    }>;
    billable_units: number;
  }> {
    return this.post(`/compliance-rules/map-from-pack/${encodeURIComponent(packCode)}`, {});
  }
  async resolveComplianceRuleMapping(
    draftId: string,
    acceptedSuggestionIds: string[],
  ): Promise<{ status: string; created_rule_ids: string[] }> {
    return this.post(`/compliance-rules/map-from-pack/${draftId}/resolve`, {
      accepted_suggestion_ids: acceptedSuggestionIds,
    });
  }
  // ── Sprint 11.5.0: Org Groups (Phase 9) ─────────────────────
  async createOrgGroup(data: {
    name: string;
    description: string;
    settings?: any;
  }): Promise<OrgGroup> {
    return this.post('/org-groups', data);
  }
  async listOrgGroups(params?: Record<string, string>): Promise<PaginatedResponse<OrgGroup>> {
    return this.get(`/org-groups${this.buildQuery(params)}`);
  }
  async getOrgGroup(groupId: string): Promise<OrgGroup> {
    return this.get(`/org-groups/${groupId}`);
  }
  async updateOrgGroup(groupId: string, data: any): Promise<OrgGroup> {
    return this.patch(`/org-groups/${groupId}`, data);
  }
  async dissolveOrgGroup(groupId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}`);
  }
  async addGroupMember(groupId: string, data: { org_id: string }): Promise<OrgGroupMembership> {
    return this.post(`/org-groups/${groupId}/members`, data);
  }
  async removeGroupMember(groupId: string, orgId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/members/${orgId}`);
  }
  async listGroupMembers(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: OrgGroupMembership[] }> {
    return this.get(`/org-groups/${groupId}/members${this.buildQuery(params)}`);
  }
  async getGroupHierarchy(groupId: string): Promise<Record<string, unknown>> {
    return this.get(`/org-groups/${groupId}/hierarchy`);
  }
  async getGroupAudit(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<GroupAuditEntry>> {
    return this.get(`/org-groups/${groupId}/audit${this.buildQuery(params)}`);
  }
  // ── Sprint 11.6.0: Group Shared Data & Settings Inheritance ──
  async publishSharedVendor(groupId: string, data: any): Promise<GroupSharedVendor> {
    return this.post(`/org-groups/${groupId}/shared/vendors`, data);
  }
  async listSharedVendors(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedVendor[] }> {
    return this.get(`/org-groups/${groupId}/shared/vendors${this.buildQuery(params)}`);
  }
  async updateSharedVendor(
    groupId: string,
    vendorId: string,
    data: any,
  ): Promise<GroupSharedVendor> {
    return this.patch(`/org-groups/${groupId}/shared/vendors/${vendorId}`, data);
  }
  async retireSharedVendor(groupId: string, vendorId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/shared/vendors/${vendorId}`);
  }
  async publishSharedFramework(groupId: string, data: any): Promise<GroupSharedFramework> {
    return this.post(`/org-groups/${groupId}/shared/frameworks`, data);
  }
  async listSharedFrameworks(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedFramework[] }> {
    return this.get(`/org-groups/${groupId}/shared/frameworks${this.buildQuery(params)}`);
  }
  async updateSharedFramework(
    groupId: string,
    frameworkId: string,
    data: any,
  ): Promise<GroupSharedFramework> {
    return this.patch(`/org-groups/${groupId}/shared/frameworks/${frameworkId}`, data);
  }
  async retireSharedFramework(groupId: string, frameworkId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/shared/frameworks/${frameworkId}`);
  }
  async publishSharedFormTemplate(groupId: string, data: any): Promise<Record<string, unknown>> {
    return this.post(`/org-groups/${groupId}/shared/form-templates`, data);
  }
  async retireSharedFormTemplate(
    groupId: string,
    templateId: string,
  ): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/shared/form-templates/${templateId}`);
  }
  async listSharedFormTemplates(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/org-groups/${groupId}/shared/form-templates${this.buildQuery(params)}`);
  }
  async getMergedVendors(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedVendor[] }> {
    return this.get(`/org-groups/${groupId}/merged/vendors${this.buildQuery(params)}`);
  }
  async getMergedFrameworks(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupSharedFramework[] }> {
    return this.get(`/org-groups/${groupId}/merged/frameworks${this.buildQuery(params)}`);
  }
  async getInheritanceStatus(groupId: string): Promise<OrgSettingsInheritance> {
    return this.get(`/org-groups/${groupId}/settings/inheritance`);
  }
  async setInheritanceMode(
    groupId: string,
    category: string,
    data: { mode: string; local_override?: any },
  ): Promise<OrgSettingsInheritance> {
    return this.put(`/org-groups/${groupId}/settings/inheritance/${category}`, data);
  }
  async clearInheritanceOverride(groupId: string, category: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/settings/inheritance/${category}/override`);
  }
  async getGroupDefaults(groupId: string, category?: string): Promise<Record<string, unknown>> {
    const query = category ? `?category=${category}` : '';
    return this.get(`/org-groups/${groupId}/settings/defaults${query}`);
  }
  // ── Sprint 11.7.0: Cross-Org User Roles & Group Audit ──────
  async assignGroupRole(
    groupId: string,
    data: { user_id: string; group_role: string },
  ): Promise<GroupUserAssignment> {
    return this.post(`/org-groups/${groupId}/users`, data);
  }
  async revokeGroupRole(groupId: string, userId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/users/${userId}`);
  }
  async listGroupUsers(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupUserAssignment[] }> {
    return this.get(`/org-groups/${groupId}/users${this.buildQuery(params)}`);
  }
  async getMyMemberships(): Promise<{ data: UserOrgMembership[] }> {
    return this.get('/org-groups/my-memberships');
  }
  async switchOrg(data: { target_org_id: string }): Promise<OrgSwitchResponse> {
    return this.post('/org-groups/switch-org', data);
  }
  async getAccessibleOrgs(groupId: string): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/org-groups/${groupId}/accessible-orgs`);
  }
  async getGroupAuditConsolidated(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<GroupAuditEntry>> {
    return this.get(`/org-groups/${groupId}/audit/consolidated${this.buildQuery(params)}`);
  }
  async getGroupAuditSummary(groupId: string): Promise<GroupAuditSummary> {
    return this.get(`/org-groups/${groupId}/audit/summary`);
  }
  // Sprint 42.2.3 E2 — returns AttachmentRef when format='csv'; legacy
  // format='json' still returns the raw {data} envelope.
  async exportGroupAudit(
    groupId: string,
    data: any,
  ): Promise<AttachmentRef | Record<string, unknown>> {
    return this.post(`/org-groups/${groupId}/audit/export`, data);
  }
  async searchGroupAudit(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<GroupAuditEntry>> {
    return this.get(`/org-groups/${groupId}/audit/search${this.buildQuery(params)}`);
  }
  // ── Sprint 11.8.0: Group Dashboard & Reporting ─────────────
  async getGroupDashboard(groupId: string): Promise<GroupDashboardData> {
    return this.get(`/org-groups/${groupId}/dashboard`);
  }
  async getGroupDashboardWidget(
    groupId: string,
    widgetType: string,
  ): Promise<GroupDashboardWidget> {
    return this.get(`/org-groups/${groupId}/dashboard/${widgetType}`);
  }
  async refreshGroupDashboard(groupId: string): Promise<Record<string, unknown>> {
    return this.post(`/org-groups/${groupId}/dashboard/refresh`, {});
  }
  async requestGroupReport(groupId: string, data: any): Promise<GroupReport> {
    return this.post(`/org-groups/${groupId}/reports`, data);
  }
  async listGroupReports(
    groupId: string,
    params?: Record<string, string>,
  ): Promise<{ data: GroupReport[] }> {
    return this.get(`/org-groups/${groupId}/reports${this.buildQuery(params)}`);
  }
  async getGroupReport(groupId: string, reportId: string): Promise<GroupReport> {
    return this.get(`/org-groups/${groupId}/reports/${reportId}`);
  }
  async deleteGroupReport(groupId: string, reportId: string): Promise<{ message: string }> {
    return this.del(`/org-groups/${groupId}/reports/${reportId}`);
  }
  async downloadGroupReport(groupId: string, reportId: string): Promise<Record<string, unknown>> {
    return this.get(`/org-groups/${groupId}/reports/${reportId}/download`);
  }
  // ── Phase 9 Extension: Organization Management ──────────────────
  async listOrganizations(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/organizations${this.buildQuery(params)}`);
  }
  async getOrganization(orgId: string): Promise<Record<string, unknown>> {
    return this.get(`/organizations/${orgId}`);
  }
  async createOrganization(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations', payload);
  }
  async updateOrganization(
    orgId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/organizations/${orgId}`, payload);
  }
  async deactivateOrganization(orgId: string): Promise<{ message: string }> {
    return this.del(`/organizations/${orgId}`);
  }
  // ── Phase 9 Extension: Intake Config Management ────────────────
  async listIntakeConfigs(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<Record<string, unknown>>> {
    return this.get(`/organizations/intake-configs/list${this.buildQuery(params)}`);
  }
  async getIntakeConfig(configId: string): Promise<Record<string, unknown>> {
    return this.get(`/organizations/intake-configs/${configId}`);
  }
  async createIntakeConfig(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations/intake-configs', payload);
  }
  async updateIntakeConfig(
    configId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/organizations/intake-configs/${configId}`, payload);
  }
  async deleteIntakeConfig(configId: string): Promise<{ message: string }> {
    return this.del(`/organizations/intake-configs/${configId}`);
  }

  // ── Sprint 22.3.0: Organizations Module Maturity ────────────
  async createDepartment(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations/departments', payload);
  }
  async listDepartments(): Promise<Record<string, unknown>[]> {
    return this.get('/organizations/departments');
  }
  async getDepartmentHierarchy(): Promise<Record<string, unknown>> {
    return this.get('/organizations/departments/hierarchy');
  }
  async mapUserToDepartment(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.post('/organizations/departments/user-mappings', payload);
  }
  async listDepartmentUsers(departmentId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/organizations/departments/${departmentId}/users`);
  }
  async removeDepartmentUser(mappingId: string): Promise<void> {
    return this.del(`/organizations/departments/user-mappings/${mappingId}`);
  }
  async assignFrameworkToEntity(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post('/organizations/entity-framework-assignments', payload);
  }
  async listEntityFrameworkAssignments(entityId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/organizations/entities/${entityId}/framework-assignments`);
  }
  async removeEntityFrameworkAssignment(assignmentId: string): Promise<void> {
    return this.del(`/organizations/entity-framework-assignments/${assignmentId}`);
  }
  async getComplianceRollup(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs =
      params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/organizations/compliance-rollup${qs}`);
  }
  async getEntityComplianceSummary(entityId: string): Promise<Record<string, unknown>> {
    return this.get(`/organizations/entities/${entityId}/compliance-summary`);
  }
  async listDepartmentsByUser(userId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/organizations/departments/by-user/${userId}`);
  }

  // ── Sprint 44.0.7 Wave 3: Organizations + Org Groups — multi-format list exports ──
  //
  // R-33 migration replaces 1-format `downloadClientCSV` / `downloadClientExport`
  // (and the new-Blob JSON fallback on GroupAuditPage) with 4-format
  // (csv/xlsx/pdf/docx) backend routes that call
  // UnifiedExportService.generateFromDefinition. Each method returns an
  // `AttachmentRef` that can be handed to `downloadPlatformFile`.
  async exportOrganizationsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/export${this.buildQuery(params)}`);
  }
  async exportOrganizationIntakeConfigsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/intake-configs/export${this.buildQuery(params)}`);
  }
  async exportOrganizationDepartmentsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/departments/export${this.buildQuery(params)}`);
  }
  async exportOrganizationFrameworkAssignmentsList(
    entityId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/organizations/entities/${encodeURIComponent(entityId)}/framework-assignments/export${this.buildQuery(params)}`,
    );
  }
  async exportOrganizationComplianceRollupList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/organizations/compliance-rollup/export${this.buildQuery(params)}`);
  }
  async exportOrgGroupsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(`/org-groups/export${this.buildQuery(params)}`);
  }
  async exportOrgGroupDashboard(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/dashboard/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupReportsList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/reports/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupConsolidatedAudit(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/audit/export-list${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupPerOrgAudit(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/audit/export-per-org${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupUsersList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/users/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupSharedVendorsList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/shared/vendors/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupSharedFrameworksList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/shared/frameworks/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupSharedFormTemplatesList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/shared/form-templates/export${this.buildQuery(params)}`,
    );
  }
  async exportOrgGroupMergedVendorsList(
    groupId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<AttachmentRef> {
    return this.get(
      `/org-groups/${encodeURIComponent(groupId)}/merged/vendors/export${this.buildQuery(params)}`,
    );
  }

  // ── Sprint 43.2.0 batch 3 (E2) — Department CRUD extensions ────
  async updateDepartment(
    departmentId: string,
    updates: { name?: string; description?: string },
  ): Promise<Record<string, unknown>> {
    return this.patch(`/organizations/departments/${departmentId}`, updates);
  }
  async deactivateDepartment(departmentId: string): Promise<Record<string, unknown>> {
    return this.post(`/organizations/departments/${departmentId}/deactivate`, {});
  }
  async deleteDepartment(
    departmentId: string,
    reassignTo: string | null,
  ): Promise<{ cases_reassigned: number; department_deleted: boolean }> {
    return this.del(`/organizations/departments/${departmentId}`, { reassignTo });
  }
  async getDepartmentActiveCaseCount(departmentId: string): Promise<{ count: number }> {
    return this.get(`/organizations/departments/${departmentId}/active-case-count`);
  }

  // ── Sprint 43.2.0 batch 3 (E4) — Retention policies ────────────
  async listOrgRetentionPolicies(): Promise<OrgRetentionPolicy[]> {
    return this.get('/organizations/retention-policies');
  }
  async createOrgRetentionPolicy(payload: {
    target_type: string;
    retention_days: number;
    policy_owner: string;
    effective_from?: string;
  }): Promise<OrgRetentionPolicy> {
    return this.post('/organizations/retention-policies', payload);
  }
  async updateOrgRetentionPolicy(
    policyId: string,
    updates: Partial<{
      target_type: string;
      retention_days: number;
      policy_owner: string;
      effective_from: string;
    }>,
  ): Promise<OrgRetentionPolicy> {
    return this.patch(`/organizations/retention-policies/${policyId}`, updates);
  }
  async deactivateOrgRetentionPolicy(policyId: string): Promise<void> {
    return this.post(`/organizations/retention-policies/${policyId}/deactivate`, {});
  }

  // ── Sprint 17.3.0: Notification System ──────────────────────

  async listNotifications(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/notifications${qs}`);
  }

  async getNotificationStats(): Promise<Record<string, unknown>> {
    return this.get('/notifications/stats');
  }

  async markNotificationRead(notificationId: string): Promise<Record<string, unknown>> {
    return this.patch(`/notifications/${notificationId}/read`);
  }

  async markAllNotificationsRead(category?: string): Promise<Record<string, unknown>> {
    return this.post('/notifications/read-all', category ? { category } : {});
  }

  async archiveNotification(notificationId: string): Promise<Record<string, unknown>> {
    return this.patch(`/notifications/${notificationId}/archive`);
  }

  async bulkArchiveNotifications(olderThan: string): Promise<Record<string, unknown>> {
    return this.post('/notifications/archive-bulk', { older_than: olderThan });
  }

  async getNotificationPreferences(): Promise<Record<string, unknown>> {
    return this.get('/notifications/preferences');
  }

  async updateNotificationPreferences(
    updates: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.patch('/notifications/preferences', updates);
  }

  async watchEntity(
    module: string,
    entityId: string,
    entityType: string,
  ): Promise<Record<string, unknown>> {
    return this.post('/notifications/watch', {
      module,
      entity_id: entityId,
      entity_type: entityType,
    });
  }

  async unwatchEntity(module: string, entityId: string): Promise<void> {
    return this.del(`/notifications/watch/${module}/${entityId}`);
  }

  async getEmailHistory(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/notifications/email-history${qs}`);
  }

  async getAdminEmailQueue(): Promise<Record<string, unknown>> {
    return this.get('/notifications/admin/queue');
  }

  async getAdminEmailQueueList(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.get(`/notifications/admin/queue/list${qs}`);
  }

  async retryFailedEmail(emailId: string): Promise<Record<string, unknown>> {
    return this.post(`/notifications/admin/retry/${emailId}`);
  }

  async processEmailQueue(): Promise<{ processed: number }> {
    return this.post('/notifications/admin/queue/process') as Promise<{ processed: number }>;
  }

  // ── Sprint 17.4.0: Task Management ─────────────────────────
  async getTasks(params?: Record<string, any>): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks${this.buildQuery(params)}`);
  }
  async getTask(id: string): Promise<TaskRecord> {
    return this.get(`/tasks/${id}`);
  }
  async createTask(payload: Record<string, unknown>): Promise<TaskRecord> {
    return this.post('/tasks', payload);
  }
  async updateTask(id: string, payload: Record<string, unknown>): Promise<TaskRecord> {
    return this.patch(`/tasks/${id}`, payload);
  }
  async updateTmTaskStatus(id: string, status: string): Promise<TaskRecord> {
    return this.patch(`/tasks/${id}/status`, { status });
  }
  async deleteTask(id: string): Promise<void> {
    return this.del(`/tasks/${id}`);
  }
  async bulkUpdateTaskStatus(taskIds: string[], status: string): Promise<Record<string, unknown>> {
    return this.patch('/tasks/bulk/status', { task_ids: taskIds, status });
  }
  // Sprint 47.5.8.5 — convert a task into a Forseti AI Workflow Run.
  // Reuses the existing /api/v1/ai-workflows run pipeline; the route on
  // /tasks/:id stamps source.linked_workflow_run_ids and emits the
  // ai_advisor_task_converted_to_workflow_run audit event.
  async convertTaskToWorkflowRun(
    taskId: string,
    body: {
      workflow_id: string;
      attachment_ids?: string[];
      persona_slug_override?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<{ run_id: string }> {
    return this.post(`/tasks/${taskId}/convert-to-workflow-run`, body);
  }
  async assignTmTask(id: string, userIds: string[]): Promise<TaskRecord> {
    return this.post(`/tasks/${id}/assign`, { user_ids: userIds });
  }
  async unassignTask(id: string, userId: string): Promise<void> {
    return this.del(`/tasks/${id}/assign/${userId}`);
  }
  async getMyTasks(params?: Record<string, any>): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/my${this.buildQuery(params)}`);
  }
  async getOverdueTasks(params?: Record<string, any>): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/overdue${this.buildQuery(params)}`);
  }
  async getTaskStats(): Promise<Record<string, unknown>> {
    return this.get('/tasks/stats');
  }
  async getCalendarTasks(params: Record<string, any>): Promise<{ data: TaskListItem[] }> {
    return this.get(`/tasks/calendar${this.buildQuery(params)}`);
  }
  async getTaskComments(
    id: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<TaskComment>> {
    return this.get(`/tasks/${id}/comments${this.buildQuery(params)}`);
  }
  async addTaskComment(id: string, content: string): Promise<TaskComment> {
    return this.post(`/tasks/${id}/comments`, { content });
  }
  async updateTaskComment(id: string, commentId: string, content: string): Promise<TaskComment> {
    return this.patch(`/tasks/${id}/comments/${commentId}`, { content });
  }
  async deleteTaskComment(id: string, commentId: string): Promise<void> {
    return this.del(`/tasks/${id}/comments/${commentId}`);
  }
  async updateTmTaskChecklist(
    id: string,
    itemId: string,
    completed: boolean,
  ): Promise<Record<string, unknown>> {
    return this.patch(`/tasks/${id}/checklist/${itemId}`, { completed });
  }
  async addTaskChecklistItem(id: string, label: string): Promise<Record<string, unknown>> {
    return this.post(`/tasks/${id}/checklist`, { label });
  }
  async getTasksByEntity(module: string, entityId: string): Promise<TaskListItem[]> {
    return this.get(`/tasks/by-entity/${module}/${entityId}`);
  }
  async addTaskDependency(
    id: string,
    taskId: string,
    type: string,
    description?: string,
  ): Promise<Record<string, unknown>> {
    return this.post(`/tasks/${id}/dependencies`, {
      task_id: taskId,
      dependency_type: type,
      description,
    });
  }
  async removeTaskDependency(id: string, depId: string): Promise<void> {
    return this.del(`/tasks/${id}/dependencies/${depId}`);
  }
  async getTaskAudit(
    id: string,
    params?: Record<string, any>,
  ): Promise<{ data: Record<string, unknown>[] }> {
    return this.get(`/tasks/${id}/audit${this.buildQuery(params)}`);
  }
  // Workflow instance methods (task-scoped)
  async getTaskWorkflow(taskId: string): Promise<WorkflowInstance> {
    return this.get(`/tasks/${taskId}/workflow`);
  }
  async getTaskWorkflowTransitions(taskId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/tasks/${taskId}/workflow/transitions`);
  }
  async transitionTaskWorkflow(
    taskId: string,
    toStepId: string,
    notes?: string,
  ): Promise<WorkflowInstance> {
    return this.post(`/tasks/${taskId}/workflow/transition`, { to_step_id: toStepId, notes });
  }
  async getTaskWorkflowHistory(taskId: string): Promise<Record<string, unknown>[]> {
    return this.get(`/tasks/${taskId}/workflow/history`);
  }
  async getTaskWorkflowSla(taskId: string): Promise<Record<string, unknown>> {
    return this.get(`/tasks/${taskId}/workflow/sla`);
  }
  // --- Workflow definition CRUD (Sprint 17.5.0) ---
  async getWorkflows(params?: Record<string, any>): Promise<PaginatedResponse<WorkflowDefinition>> {
    return this.get(`/workflows${this.buildQuery(params)}`);
  }
  async getWorkflow(id: string): Promise<WorkflowDefinition> {
    return this.get(`/workflows/${id}`);
  }
  async createWorkflow(payload: Record<string, any>): Promise<WorkflowDefinition> {
    return this.post('/workflows', payload);
  }
  async updateWorkflow(id: string, payload: Record<string, any>): Promise<WorkflowDefinition> {
    return this.patch(`/workflows/${id}`, payload);
  }
  async activateWorkflow(id: string): Promise<WorkflowDefinition> {
    return this.post(`/workflows/${id}/activate`, {});
  }
  async archiveWorkflow(id: string): Promise<WorkflowDefinition> {
    return this.post(`/workflows/${id}/archive`, {});
  }
  async deleteWorkflow(id: string): Promise<void> {
    return this.del(`/workflows/${id}`);
  }
  async validateWorkflow(id: string): Promise<Record<string, unknown>> {
    return this.get(`/workflows/${id}/validate`);
  }
  async getWorkflowTemplates(): Promise<WorkflowTemplate[]> {
    return this.get('/workflows/templates');
  }
  async createWorkflowFromTemplate(templateKey: string): Promise<WorkflowDefinition> {
    return this.post('/workflows/from-template', { template_key: templateKey });
  }
  // ── Sprint 44.0.9 Wave 3 — 12 Task Management list/detail exports ─
  //
  // 4-format (csv/xlsx/pdf/docx) backend routes via UnifiedExportService.
  // Each returns an AttachmentRef ({ file_id, filename, ... }) consumed
  // by `downloadPlatformFile(file_id, filename)`. The pre-existing
  // `GET /tasks/export` (Sprint 17.0.0, TM_EXPORT-gated, returns a flat
  // JSON array) is kept for programmatic consumers; the new
  // `GET /tasks/export-list` drives the UI. Sprint 44.0.9 CLOSES the
  // 44.0.x Standard/Enterprise QA finalization series.
  async exportTaskList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/export-list${this.buildQuery(params)}`);
  }
  async exportTaskBoard(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/board/export${this.buildQuery(params)}`);
  }
  async exportTaskDetail(
    taskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${taskId}/export${this.buildQuery(params)}`);
  }
  async exportMyTasksList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/my/export${this.buildQuery(params)}`);
  }
  async exportOverdueTasksList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/overdue/export${this.buildQuery(params)}`);
  }
  async exportCalendarTasks(
    params: Record<string, unknown> & {
      format: 'csv' | 'xlsx' | 'pdf' | 'docx';
      start_date: string;
      end_date: string;
    },
  ): Promise<any> {
    return this.get(`/tasks/calendar/export${this.buildQuery(params)}`);
  }
  async exportWorkflowList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/workflows/export${this.buildQuery(params)}`);
  }
  async exportWorkflowDetail(
    workflowId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/workflows/${workflowId}/export${this.buildQuery(params)}`);
  }
  async exportWorkflowTemplateCatalog(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/workflows/templates/export${this.buildQuery(params)}`);
  }
  async exportTaskConflicts(
    masterTaskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${masterTaskId}/conflicts/export${this.buildQuery(params)}`);
  }
  async exportTaskConsolidation(
    masterTaskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${masterTaskId}/consolidation/export${this.buildQuery(params)}`);
  }
  async exportTaskSectionAssignments(
    taskId: string,
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/tasks/${taskId}/sections/export${this.buildQuery(params)}`);
  }
  // --- Team workload (Sprint 17.5.0) ---
  async getTeamWorkload(params?: Record<string, any>): Promise<Record<string, unknown>> {
    return this.get(`/tasks/team-workload${this.buildQuery(params)}`);
  }
  // --- Task search for dependency modal (Sprint 17.5.0) ---
  async searchTasks(
    query: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/search${this.buildQuery({ q: query, ...params })}`);
  }
  // --- Task dependency graph (Sprint 17.5.0) ---
  async getTaskDependencyGraph(id: string): Promise<Record<string, unknown>> {
    return this.get(`/tasks/${id}/dependency-graph`);
  }

  // ── Sprint 17.6.0: Dashboard task widgets + Cross-org ──────────
  async getDashboardTaskSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/tasks/summary');
  }
  async getDashboardUpcomingTasks(days = 7): Promise<Record<string, unknown>> {
    return this.get(`/dashboard/tasks/upcoming?days=${days}`);
  }
  async getDashboardOverdueTasks(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/tasks/overdue');
  }
  // ── Sprint 20.2.0: Persona-aware dashboard widgets ──────────
  async getDashboardPersonaSummary(frameworkCode?: string): Promise<Record<string, unknown>> {
    const q = frameworkCode ? `?framework_code=${encodeURIComponent(frameworkCode)}` : '';
    return this.get(`/dashboard/persona-summary${q}`);
  }
  async getDashboardFilingLifecycle(frameworkCode?: string): Promise<Record<string, unknown>> {
    const q = frameworkCode ? `?framework_code=${encodeURIComponent(frameworkCode)}` : '';
    return this.get(`/dashboard/filing-lifecycle${q}`);
  }
  async getDashboardDepartmentConsolidation(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/department-consolidation');
  }
  async getDashboardVendorRiskSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/vendor-risk-summary');
  }
  async getDashboardDiscoverySummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/discovery-summary');
  }
  async getDashboardAiGovernanceSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/ai-governance-summary');
  }
  async getDashboardBreachSummary(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/breach-summary');
  }
  async getDashboardAuditReadiness(frameworkCode?: string): Promise<Record<string, unknown>> {
    const q = frameworkCode ? `?framework_code=${encodeURIComponent(frameworkCode)}` : '';
    return this.get(`/dashboard/audit-readiness${q}`);
  }
  async getDashboardMyDepartment(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/my-department');
  }
  // Sprint 22.7.3: Department-scoped widget endpoints
  async getDashboardMyTasks(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/my-tasks');
  }
  async getDashboardMyDepartmentStatus(): Promise<Record<string, unknown>> {
    return this.get('/dashboard/my-department-status');
  }
  // Sprint 22.7.1: Dashboard user preferences
  async getDashboardPreferences(): Promise<UserDashboardPreferences> {
    return this.get('/dashboard/preferences');
  }
  async saveDashboardPreferences(payload: {
    widgets: Record<string, { visible: boolean; collapsed: boolean }>;
    sections: { core_metrics: boolean; task_management: boolean; compliance_insights: boolean };
  }): Promise<UserDashboardPreferences> {
    return this.put('/dashboard/preferences', payload);
  }
  async getDashboardPreferenceDefaults(): Promise<UserDashboardPreferences> {
    return this.get('/dashboard/preferences/defaults');
  }
  async getCrossOrgTasks(
    orgGroupId: string,
    params?: Record<string, any>,
  ): Promise<PaginatedResponse<TaskListItem>> {
    return this.get(`/tasks/cross-org/${orgGroupId}${this.buildQuery(params)}`);
  }
  async getCrossOrgCalendarTasks(
    orgGroupId: string,
    params: Record<string, any>,
  ): Promise<{ data: TaskListItem[] }> {
    return this.get(`/tasks/cross-org/${orgGroupId}/calendar${this.buildQuery(params)}`);
  }
  async getCrossOrgTaskStats(orgGroupId: string): Promise<Record<string, unknown>> {
    return this.get(`/tasks/cross-org/${orgGroupId}/stats`);
  }
  async getOrgGroups(params?: Record<string, any>): Promise<PaginatedResponse<OrgGroup>> {
    return this.get(`/org-groups${this.buildQuery(params)}`);
  }

  // ── Sprint 18.0.0: License & Package Management ──────────────

  async getLicense(): Promise<License> {
    return this.get('/license');
  }

  async checkLicense(payload: { license_key: string }): Promise<Record<string, unknown>> {
    return this.post('/license/check', payload);
  }

  async activateLicense(payload: { license_key: string }): Promise<License> {
    return this.post('/license/activate', payload);
  }

  async deactivateLicense(): Promise<{ message: string }> {
    return this.post('/license/deactivate', {});
  }

  async validateLicense(): Promise<Record<string, unknown>> {
    return this.post('/license/validate', {});
  }

  async getPackageInfo(): Promise<PackageInfo> {
    return this.get('/package');
  }

  async changePackage(request: {
    target_tier: string;
    license_key: string;
  }): Promise<PackageChangeResult> {
    return this.post('/package/change', request);
  }

  // ── Sprint 18.3.0: Settings Defaults (public, no auth) ──────

  async getDefaults(): Promise<Record<string, unknown>> {
    return this.get('/settings/defaults');
  }

  // ── Sprint 20.3.0: Assessments -- Dossier Filings ──────────
  async listDossierFilings(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DossierFiling>> {
    return this.get(`/assessments/dossier-filings${this.buildQuery(params)}`);
  }
  async createDossierFiling(payload: Record<string, unknown>): Promise<DossierFiling> {
    return this.post('/assessments/dossier-filings', payload);
  }
  async getDossierFiling(filingId: string): Promise<DossierFiling> {
    return this.get(`/assessments/dossier-filings/${filingId}`);
  }
  async updateDossierFiling(
    filingId: string,
    payload: Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.patch(`/assessments/dossier-filings/${filingId}`, payload);
  }
  async assignDossierSections(filingId: string, assignments: any[]): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/assign-sections`, { assignments });
  }
  async submitDossierForInternalReview(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/submit-internal-review`);
  }
  async dossierLegalSignoff(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/legal-signoff`);
  }
  async approveDossierFiling(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/approve`);
  }
  // Sprint 44.4.0 — submission artifact hardening
  async retryDossierMaterialisation(filingId: string): Promise<{
    filing_id: string;
    submission_dossier_id: string;
    materialisation_status: 'pending' | 'success' | 'failed';
    created: boolean;
  }> {
    return this.post(`/assessments/dossier-filings/${filingId}/retry-materialisation`);
  }
  async getDossierMaterialisationStatus(filingId: string): Promise<{
    filing_id: string;
    status: 'pending' | 'success' | 'failed' | null;
    dossier_id: string | null;
    failure_reason: string | null;
    last_attempt_at: string | null;
    failed_at: string | null;
    retry_count: number;
    eligible_to_retry: boolean;
  }> {
    return this.get(`/assessments/dossier-filings/${filingId}/materialisation-status`);
  }
  async getDossierMaterialisationHealth(): Promise<{
    approved_not_materialised: Array<{
      filing_id: string;
      title: string;
      form_type: string;
      approved_at: string | null;
    }>;
    failed_materialisations: Array<{
      filing_id: string;
      title: string;
      form_type: string;
      failure_reason: string | null;
      failed_at: string | null;
      retry_count: number;
    }>;
    orphan_dossiers: Array<{
      dossier_id: string;
      source_filing_id: string;
      dossier_type: string;
      created_at: string;
    }>;
  }> {
    return this.get(`/assessments/admin/dossier-materialisation-health`);
  }
  async submitDossierToMps(
    filingId: string,
    submission: Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/submit-mps`, submission);
  }
  async recordDossierSupplementRequest(
    filingId: string,
    request: Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/supplement-request`, request);
  }
  async submitDossierSupplement(filingId: string, responseNotes: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/submit-supplement`, {
      response_notes: responseNotes,
    });
  }
  async resubmitDossier(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/resubmit`);
  }
  async recordDossierOutcome(
    filingId: string,
    outcome: string,
    notes: string,
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/outcome`, { outcome, notes });
  }
  async closeDossierFiling(filingId: string): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/close`);
  }
  async createChangeFiling(
    filingId: string,
    payload: Record<string, unknown>,
  ): Promise<DossierFiling> {
    return this.post(`/assessments/dossier-filings/${filingId}/change-filing`, payload);
  }
  async getDossierLineage(filingId: string): Promise<Record<string, unknown>> {
    return this.get(`/assessments/dossier-filings/${filingId}/lineage`);
  }
  async getDossierTimers(filingId: string): Promise<Record<string, unknown>> {
    return this.get(`/assessments/dossier-filings/${filingId}/timers`);
  }
  // ── Sprint 21.9.0: Compliance Forms -- Filing Case Links ──
  async linkFormToFilingCase(data: Record<string, any>) {
    return this.post('/forms/filing-case-links', data);
  }
  async listFilingCaseLinks(params?: Record<string, any>) {
    return this.get(`/forms/filing-case-links${this.buildQuery(params)}`);
  }
  async deleteFilingCaseLink(linkId: string) {
    return this.del(`/forms/filing-case-links/${linkId}`);
  }
  // Sprint 42.2.3 E2 — returns AttachmentRef.
  async exportFilingCaseLinksCSV(): Promise<AttachmentRef> {
    return this.get('/forms/filing-case-links/export/csv');
  }
  // ── Sprint 21.9.0: Compliance Forms -- Official Labels ──
  async setOfficialLabel(data: Record<string, any>) {
    return this.put('/forms/official-labels', data);
  }
  async listOfficialLabels(params?: Record<string, any>) {
    return this.get(`/forms/official-labels${this.buildQuery(params)}`);
  }
  // ── Sprint 21.9.0: Compliance Forms -- Auto-Population ──
  async autoPopulateFormData(instanceId: string, sourceCaseId: string) {
    return this.post(`/forms/${instanceId}/auto-populate`, { source_case_id: sourceCaseId });
  }
  // ── Sprint 25.4.5.1: Template Sections for Section Routing ──
  async listTemplateSections(templateId: string) {
    return this.get<{ sections: Array<{ section_id: string; label: string }> }>(
      `/forms/templates/${templateId}/sections`,
    );
  }
  // ── Sprint 21.9.0: Compliance Forms -- Section Routing ──
  async assignFormSection(data: Record<string, any>) {
    return this.post('/forms/section-assignments', data);
  }
  async listFormSectionAssignments(instanceId: string, params?: Record<string, any>) {
    return this.get(`/forms/${instanceId}/section-assignments${this.buildQuery(params)}`);
  }
  async updateFormSectionStatus(assignmentId: string, status: string) {
    return this.patch(`/forms/section-assignments/${assignmentId}/status`, { status });
  }
  async deleteSectionAssignment(assignmentId: string) {
    return this.del(`/forms/section-assignments/${assignmentId}`);
  }
  // ── Sprint 21.9.0: Compliance Forms -- Completeness Validation ──
  async validateFormCompleteness(instanceId: string) {
    return this.get(`/forms/${instanceId}/completeness`);
  }
  // ── Sprint 21.9.0: Compliance Forms -- Submission Packages ──
  async assembleSubmissionPackage(data: Record<string, any>) {
    return this.post('/forms/submission-packages', data);
  }
  async listSubmissionPackages(params?: Record<string, any>) {
    return this.get(`/forms/submission-packages${this.buildQuery(params)}`);
  }
  async getSubmissionPackage(packageId: string) {
    return this.get(`/forms/submission-packages/${packageId}`);
  }
  async updateSubmissionPackage(packageId: string, data: Record<string, any>) {
    return this.patch(`/forms/submission-packages/${packageId}`, data);
  }
  async deleteSubmissionPackage(packageId: string) {
    return this.del(`/forms/submission-packages/${packageId}`);
  }
  // Sprint 27.0.1 — Form template changelog
  async getFormTemplateChangelog(templateId: string) {
    return this.get(`/forms/templates/${templateId}/changelog`);
  }
  // Sprint 27.1.0 — Assessment routing
  async routeFormToAssessment(instanceId: string) {
    return this.post(`/forms/instances/${instanceId}/route`, {});
  }
  // Sprint 27.0.2 — Document generation
  async generatePackageDocument(packageId: string, component: string) {
    return this.post(`/forms/submission-packages/${packageId}/generate/${component}`, {});
  }
  async listPackageDocuments(packageId: string) {
    return this.get(`/forms/submission-packages/${packageId}/documents`);
  }
  // Sprint 27.0.2 — Organization branding
  async updateOrgBranding(orgId: string, data: Record<string, any>) {
    return this.patch(`/organizations/${orgId}/branding`, data);
  }
  // ── Sprint 22.0.0: Audit Trail -- Unified Audit ──
  async queryUnifiedAudit(params?: Record<string, any>) {
    return this.get(`/audit/unified${this.buildQuery(params)}`);
  }
  async getContributorLineage(entityType: string, entityId: string) {
    return this.get(`/audit/lineage/${entityType}/${entityId}`);
  }
  async getAuditSummaryByModule(params?: Record<string, any>) {
    return this.get(`/audit/summary-by-module${this.buildQuery(params)}`);
  }
  // ── Sprint 22.0.0: Audit Trail -- Evidence Packs ──
  async createAuditEvidencePack(data: Record<string, any>) {
    return this.post('/audit/evidence-packs', data);
  }
  async listAuditEvidencePacks(params?: Record<string, any>) {
    return this.get(`/audit/evidence-packs${this.buildQuery(params)}`);
  }
  async getAuditEvidencePack(packId: string) {
    return this.get(`/audit/evidence-packs/${packId}`);
  }
  async updateAuditEvidencePackStatus(packId: string, status: string) {
    return this.patch(`/audit/evidence-packs/${packId}/status`, { status });
  }
  async checkAuditPackCompleteness(packId: string) {
    return this.get(`/audit/evidence-packs/${packId}/completeness`);
  }
  async exportAuditPack(packId: string) {
    return this.get(`/audit/evidence-packs/${packId}/export`);
  }
  // ── Sprint 22.0.0: Audit Trail -- Periods & Integrity ──
  async createAuditPeriod(data: Record<string, any>) {
    return this.post('/audit/audit-periods', data);
  }
  async listAuditPeriods(params?: Record<string, any>) {
    return this.get(`/audit/audit-periods${this.buildQuery(params)}`);
  }
  async deleteAuditPeriod(periodId: string) {
    return this.del(`/audit/audit-periods/${periodId}`);
  }
  async verifyAuditIntegrity(entityId: string) {
    return this.get(`/audit/integrity/${entityId}`);
  }

  // ── Sprint 22.1.0: Task Management Module Maturity ──────────────

  // Section-Level Assignment
  async assignTaskSection(taskId: string, data: Record<string, any>) {
    return this.post(`/tasks/${taskId}/sections`, data);
  }
  async listTaskSectionAssignments(taskId: string) {
    return this.get(`/tasks/${taskId}/sections`);
  }
  async updateTaskSectionStatus(assignmentId: string, status: string, completionPct: number) {
    return this.patch(`/tasks/sections/${assignmentId}/status`, {
      status,
      completion_pct: completionPct,
    });
  }

  // Parent-Child Task Model
  async createChildTask(masterTaskId: string, childTaskId: string, departmentId: string) {
    return this.post(`/tasks/${masterTaskId}/children`, {
      child_task_id: childTaskId,
      department_id: departmentId,
    });
  }
  async listChildTasks(masterTaskId: string) {
    return this.get(`/tasks/${masterTaskId}/children`);
  }
  async deleteChildTask(masterTaskId: string, childId: string) {
    return this.del(`/tasks/${masterTaskId}/children/${childId}`);
  }
  async getConsolidationProgress(masterTaskId: string) {
    return this.get(`/tasks/${masterTaskId}/consolidation`);
  }

  // Department-Scoped Views
  async listDepartmentTasks(departmentId: string, params?: Record<string, any>) {
    return this.get(`/tasks/department/${departmentId}${this.buildQuery(params)}`);
  }
  async getDepartmentTaskSummary(departmentId: string) {
    return this.get(`/tasks/department/${departmentId}/summary`);
  }

  // Conflict Detection & Resolution
  async detectTaskConflicts(masterTaskId: string, sectionRef: string, submissions: any[]) {
    return this.post(`/tasks/${masterTaskId}/conflicts/detect`, {
      section_ref: sectionRef,
      submissions,
    });
  }
  async listTaskConflicts(masterTaskId: string) {
    return this.get(`/tasks/${masterTaskId}/conflicts`);
  }
  async resolveTaskConflict(conflictId: string) {
    return this.patch(`/tasks/conflicts/${conflictId}/resolve`, {});
  }

  // Section-Level Revision
  async requestSectionRevision(taskId: string, sectionRef: string, data: Record<string, any>) {
    return this.post(`/tasks/${taskId}/sections/${sectionRef}/revision`, data);
  }

  // ── Sprint 22.2.0: Localization Module Maturity ──────────────

  // Framework-Module & Persona Linkage
  async linkModuleToFramework(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/modules`, data);
  }
  async listFrameworkModuleLinks(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/modules`);
  }
  async linkPersonaToFramework(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/personas`, data);
  }
  async listFrameworkPersonaLinks(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/personas`);
  }

  // Submission Channels & Deadlines
  async addSubmissionChannel(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/channels`, data);
  }
  async listSubmissionChannels(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/channels`);
  }
  async addRegulatoryDeadline(frameworkId: string, data: Record<string, any>) {
    return this.post(`/localization/frameworks/${frameworkId}/deadlines`, data);
  }
  async listRegulatoryDeadlines(frameworkId: string) {
    return this.get(`/localization/frameworks/${frameworkId}/deadlines`);
  }

  // Framework Lifecycle Dashboard & Updates
  async getFrameworkCoverageDashboard() {
    return this.get('/localization/framework-dashboard');
  }
  async createRegulatoryUpdate(data: Record<string, any>) {
    return this.post('/localization/regulatory-updates', data);
  }
  async listRegulatoryUpdates(params?: Record<string, any>) {
    return this.get(`/localization/regulatory-updates${this.buildQuery(params)}`);
  }
  async assessRegulatoryUpdate(updateId: string, impactAssessment: string) {
    return this.patch(`/localization/regulatory-updates/${updateId}/assess`, {
      impact_assessment: impactAssessment,
    });
  }

  // ── Sprint 25.3.5.1: Dossier section status ──────────────
  async updateDossierSectionStatus(filingId: string, sectionId: string, status: string) {
    return this.patch(`/assessments/dossier-filings/${filingId}/sections/${sectionId}/status`, {
      status,
    });
  }

  // ── Sprint 25.3.7.1: Bulk label review ────────────────────
  async bulkReviewLabels(labelIds: string[], action: 'approve' | 'reject') {
    return this.post('/classification/labels/bulk-review', { labelIds, action });
  }

  async bulkApproveLabels(labelIds: string[]) {
    return this.post('/classification/labels/bulk-approve', { label_ids: labelIds });
  }

  async bulkRejectLabels(labelIds: string[]) {
    return this.post('/classification/labels/bulk-reject', { label_ids: labelIds });
  }

  // ── Sprint 25.4.1.1: Bulk discovery operations ───────────
  async bulkCreateCycles(templateId: string, scopeSystemIds: string[], count: number) {
    return this.post('/discovery/cycles/bulk-create', {
      template_id: templateId,
      scope_system_ids: scopeSystemIds,
      count,
    });
  }
  async bulkAssignCycleScope(cycleIds: string[], scopeSystemIds: string[]) {
    return this.post('/discovery/cycles/bulk-scope', {
      cycle_ids: cycleIds,
      scope_system_ids: scopeSystemIds,
    });
  }
  async bulkAdvanceCycles(cycleIds: string[], targetStatus: string) {
    return this.post('/discovery/cycles/bulk-advance', {
      cycle_ids: cycleIds,
      target_status: targetStatus,
    });
  }

  // ── Sprint 27.9.1: Discovery Export + Downstream Flagging ─
  // Sprint 42.2.3 E2 — returns AttachmentRef (was Blob).
  async discoveryExport(
    exportType: string,
    params?: Record<string, string>,
  ): Promise<AttachmentRef> {
    return this.get(`/discovery/export/${exportType}${this.buildQuery(params)}`);
  }
  /**
   * Sprint 44.0.2 Wave 3 — R-33 list-export migration for 8 Data Discovery
   * list/dashboard pages that previously used `downloadClientExport()`.
   *
   * Each method routes the page's "export list" action through the backend
   * UnifiedExportService (csv / xlsx / pdf / docx) and returns an
   * AttachmentRef. The caller invokes `downloadPlatformFile(ref.file_id,
   * ref.filename)` to trigger the browser download. The 4 other Discovery
   * pages (Dashboard, Cycles, OwnerReview, Lineage) already route through
   * the legacy `discoveryExport()` above (Sprint 42.2.3 E2).
   */
  async exportDiscoveryClassificationRulesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/classification-rules/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryDedupRulesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/dedup-rules/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryImportTemplatesList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/import-templates/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryConnectorsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/connectors/export${this.buildQuery(params)}`);
  }
  async exportDiscoveryScansList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/scans/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryStagingList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/staging/export${this.buildQuery(params)}`);
  }
  async exportDiscoverySuggestionsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/suggestions/list-export${this.buildQuery(params)}`);
  }
  async exportDiscoveryAnalyticsList(
    params: Record<string, unknown> & { format: 'csv' | 'xlsx' | 'pdf' | 'docx' },
  ): Promise<any> {
    return this.get(`/discovery/analytics/export${this.buildQuery(params)}`);
  }
  async listDiscoveryFlags(params?: Record<string, any>) {
    return this.get(`/discovery/flags${this.buildQuery(params)}`);
  }
  async acknowledgeDiscoveryFlag(flagId: string) {
    return this.post(`/discovery/flags/${flagId}/acknowledge`, {});
  }
  async resolveDiscoveryFlag(flagId: string) {
    return this.post(`/discovery/flags/${flagId}/resolve`, {});
  }
  async getDiscoveryDeltaSummary(cycleId: string) {
    return this.get(`/discovery/cycles/${cycleId}/delta-summary`);
  }
  async getRecentDeltaAlerts(params?: Record<string, any>) {
    return this.get(`/discovery/delta-alerts${this.buildQuery(params)}`);
  }

  // ── Sprint 25.4.2.1: Security scan import ─────────────────
  async importSecurityScan(
    connectorId: string,
    format: 'sarif' | 'json',
    data: Record<string, unknown>,
  ) {
    return this.post('/connectors/security-reviews/import-scan', {
      connector_id: connectorId,
      format,
      data,
    });
  }

  // ── Sprint 25.4.6.1: Evidence pack export ─────────────────
  async exportEvidencePack(packId: string, format: 'csv' | 'pdf' = 'csv') {
    return this.get(`/audit/evidence-packs/${packId}/export?format=${format}`);
  }

  // ── Sprint 28.3.1: Vendor Onboarding Lifecycle ──────────────
  async listOnboardingCases(params?: Record<string, any>) {
    return this.get(`/vendor-risk/onboarding-cases${this.buildQuery(params)}`);
  }
  async createOnboardingCase(payload: Record<string, unknown>) {
    return this.post('/vendor-risk/onboarding-cases', payload);
  }
  async getOnboardingCase(caseId: string) {
    return this.get(`/vendor-risk/onboarding-cases/${caseId}`);
  }
  async updateOnboardingCase(caseId: string, payload: Record<string, unknown>) {
    return this.patch(`/vendor-risk/onboarding-cases/${caseId}`, payload);
  }
  async transitionOnboardingCaseStatus(caseId: string, status: string) {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/status`, { status });
  }
  async listOnboardingApprovals(caseId: string) {
    return this.get(`/vendor-risk/onboarding-cases/${caseId}/approvals`);
  }
  async decideOnboardingApproval(approvalId: string, payload: Record<string, unknown>) {
    return this.post(`/vendor-risk/onboarding-approvals/${approvalId}/decide`, payload);
  }
  async exportOnboardingCase(caseId: string) {
    return this.get(`/vendor-risk/onboarding-cases/${caseId}/export`);
  }
  /**
   * Sprint 41.2.0 E8 — UC-VN-14 Step 10 MPS workflow direct integration.
   *
   * Creates a new MPS dossier filing from the vendor onboarding case
   * context and links it back onto the case. Server-side requires
   * dpia_id + title + form_type ('phu_luc_i' | 'phu_luc_ii').
   */
  async createDossierFilingFromCase(
    caseId: string,
    payload: {
      dpia_id: string;
      title: string;
      form_type: 'phu_luc_i' | 'phu_luc_ii';
    },
  ): Promise<{ filing_id: string; title: string; form_type: string; status: string }> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/dossier-filing`, payload);
  }
  // Sprint 41.2.0 E7 — Sub-Processor Register canonical store.
  //
  // Backed by the real SubProcessorRegisterService (was a phantom
  // endpoint in Sprint 41.1.0 V7; retrofitted in 41.2.0).
  async listSubProcessorRegister(params?: Record<string, string>): Promise<{
    data: SubProcessorRegisterRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/vendor-risk/sub-processor-register${this.buildQuery(params)}`);
  }
  async createSubProcessorRegister(
    payload: Record<string, unknown>,
  ): Promise<SubProcessorRegisterRecord> {
    return this.post('/vendor-risk/sub-processor-register', payload);
  }
  async updateSubProcessorRegister(
    subProcessorId: string,
    payload: Record<string, unknown>,
  ): Promise<SubProcessorRegisterRecord> {
    return this.patch(`/vendor-risk/sub-processor-register/${subProcessorId}`, payload);
  }
  // fix-sprint-20260411-A F9 — backend now returns a paginated
  // envelope; caller can override page/page_size when needed.
  async getSubProcessorHistory(
    subProcessorId: string,
    params: { page?: number; page_size?: number } = {},
  ): Promise<{
    data: SubProcessorRegisterChange[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.page_size) search.set('page_size', String(params.page_size));
    const q = search.toString();
    return this.get(
      `/vendor-risk/sub-processor-register/${subProcessorId}/history${q ? `?${q}` : ''}`,
    );
  }
  /**
   * Sprint 41.2.0 E7 — CSV export performed client-side from the list
   * response. No backend export endpoint needed; keeps scope tight and
   * the caller controls the exact column layout.
   */
  exportSubProcessorsCsv(rows: SubProcessorRegisterRecord[]): string {
    if (rows.length === 0) return '';
    const headers = [
      'sub_processor_id',
      'name',
      'services',
      'data_categories',
      'processing_countries',
      'dpa_chain_status',
      'certifications',
      'last_assessment_date',
      'next_assessment_date',
      'onboarding_case_id',
      'created_at',
      'updated_at',
    ];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const cells = headers.map((h) => {
        const v = (r as unknown as Record<string, unknown>)[h];
        if (Array.isArray(v)) return JSON.stringify(v.join('; '));
        return JSON.stringify(v ?? '');
      });
      lines.push(cells.join(','));
    }
    return lines.join('\n');
  }
  // ── Sprint 28.7.1: Multi-Department Report Consolidation ────

  async createConsolidationCase(payload: Record<string, unknown>): Promise<any> {
    return this.post('/consolidation', payload);
  }
  async listConsolidationCases(params?: Record<string, string>): Promise<any> {
    return this.get(`/consolidation${this.buildQuery(params)}`);
  }
  async getConsolidationCase(caseId: string): Promise<any> {
    return this.get(`/consolidation/${caseId}`);
  }
  async updateConsolidationCase(caseId: string, payload: Record<string, unknown>): Promise<any> {
    return this.patch(`/consolidation/${caseId}`, payload);
  }
  async deleteConsolidationCase(caseId: string): Promise<void> {
    return this.del(`/consolidation/${caseId}`);
  }
  async transitionConsolidationCase(caseId: string, status: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/transition`, { status });
  }
  async closeConsolidationCase(caseId: string, dispositionNote: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/close`, { disposition_note: dispositionNote });
  }
  async assignConsolidationSection(caseId: string, payload: Record<string, unknown>): Promise<any> {
    return this.post(`/consolidation/${caseId}/sections`, payload);
  }
  async autoAssignConsolidationSections(caseId: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/sections/auto-assign`, {});
  }
  async listConsolidationSections(caseId: string, mySections?: boolean): Promise<any> {
    const params: Record<string, string> = {};
    if (mySections) params.my_sections = 'true';
    return this.get(`/consolidation/${caseId}/sections${this.buildQuery(params)}`);
  }
  async getConsolidationSection(sectionId: string): Promise<any> {
    return this.get(`/consolidation/sections/${sectionId}`);
  }
  async submitConsolidationSection(
    sectionId: string,
    payload: Record<string, unknown>,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/submit`, payload);
  }
  async resubmitConsolidationSection(
    sectionId: string,
    payload: Record<string, unknown>,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/resubmit`, payload);
  }
  async saveSectionProgress(sectionId: string, data: Record<string, unknown>): Promise<any> {
    return this.patch(`/consolidation/sections/${sectionId}/progress`, { data });
  }
  async acceptConsolidationSection(sectionId: string): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/accept`, {});
  }
  async returnConsolidationSection(sectionId: string, comment: string): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/return`, { comment });
  }
  async addConsolidationComment(
    sectionId: string,
    text: string,
    parentCommentId?: string,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/comments`, {
      text,
      parent_comment_id: parentCommentId,
    });
  }
  async addConsolidationEvidence(
    sectionId: string,
    evidence: Record<string, unknown>,
  ): Promise<any> {
    return this.post(`/consolidation/sections/${sectionId}/evidence`, evidence);
  }
  async consolidateCase(caseId: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/consolidate`, {});
  }
  async listConsolidationFlags(caseId: string, status?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    return this.get(`/consolidation/${caseId}/flags${this.buildQuery(params)}`);
  }
  async resolveConsolidationFlag(
    flagId: string,
    action: 'resolve' | 'dismiss',
    notes: string,
  ): Promise<any> {
    return this.post(`/consolidation/flags/${flagId}/resolve`, { action, notes });
  }
  async decideConsolidationApproval(
    caseId: string,
    stepId: string,
    decision: string,
    notes?: string,
  ): Promise<any> {
    return this.post(`/consolidation/${caseId}/approvals/${stepId}/decide`, { decision, notes });
  }
  async generateConsolidationExport(caseId: string): Promise<any> {
    return this.post(`/consolidation/${caseId}/export`, {});
  }
  async getConsolidationExportArtifact(caseId: string, artifactType: string): Promise<any> {
    return this.get(`/consolidation/${caseId}/export/${artifactType}`);
  }
  async getConsolidationFormWizard(caseId: string): Promise<any> {
    return this.get(`/consolidation/${caseId}/form-wizard`);
  }
  async getConsolidationAudit(caseId: string): Promise<any> {
    return this.get(`/consolidation/${caseId}/audit`);
  }

  // ── Sprint 28.6.1: Connector artifact export ────────────────
  // Sprint 44.0.3 Waves 2+3 — R-33 migration. The route now renders
  // through UnifiedExportService and returns an AttachmentRef. Callers
  // must follow with `downloadPlatformFile(ref.file_id, ref.filename)`
  // to trigger the download. Previously the route returned raw JSON
  // and callers discarded the response (no download ever happened).
  async exportConnectorArtifact(
    type: string,
    format: 'csv' | 'xlsx' | 'pdf' | 'docx' = 'csv',
  ): Promise<any> {
    return this.get(
      `/connectors/export/${encodeURIComponent(type)}?format=${encodeURIComponent(format)}`,
    );
  }

  // ── Sprint 28.6.1: Automation Planning CRUD ──────────────────
  // Backend route + DB collection still use the "planning-programs" name;
  // the user-facing "automation" rename only landed on the page, the
  // method names, and the export-artifact alias.
  async listAutomationPrograms(params?: Record<string, any>) {
    return this.get(`/connectors/planning-programs${this.buildQuery(params)}`);
  }
  async getAutomationProgram(id: string) {
    return this.get(`/connectors/planning-programs/${id}`);
  }
  async createAutomationProgram(payload: Record<string, any>) {
    return this.post('/connectors/planning-programs', payload);
  }
  async updateAutomationProgram(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/planning-programs/${id}`, payload);
  }
  async updateAutomationProgramStatus(id: string, status: string) {
    return this.patch(`/connectors/planning-programs/${id}/status`, { status });
  }

  // ── Sprint 28.6.1: System Inventory CRUD ─────────────────────
  async listSystemInventory(params?: Record<string, any>) {
    return this.get(`/connectors/system-inventory${this.buildQuery(params)}`);
  }
  async getSystemInventoryItem(id: string) {
    return this.get(`/connectors/system-inventory/${id}`);
  }
  async createSystemInventoryItem(payload: Record<string, any>) {
    return this.post('/connectors/system-inventory', payload);
  }
  async updateSystemInventoryItem(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/system-inventory/${id}`, payload);
  }

  // ── Sprint 28.6.1: Traceability Matrix CRUD ──────────────────
  async listTraceabilityEntries(params?: Record<string, any>) {
    return this.get(`/connectors/traceability-matrix${this.buildQuery(params)}`);
  }
  async getTraceabilityEntry(id: string) {
    return this.get(`/connectors/traceability-matrix/${id}`);
  }
  async createTraceabilityEntry(payload: Record<string, any>) {
    return this.post('/connectors/traceability-matrix', payload);
  }
  async updateTraceabilityEntry(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/traceability-matrix/${id}`, payload);
  }

  // ── Sprint 40.1.0: Assessment Trigger Config ─────────────────
  async getAssessmentTriggerConfig(): Promise<AssessmentTriggerConfig> {
    return this.get('/assessments/trigger-config');
  }
  async updateAssessmentTriggerConfig(
    config: Partial<AssessmentTriggerConfig>,
  ): Promise<AssessmentTriggerConfig> {
    return this.put('/assessments/trigger-config', config);
  }

  // ── Sprint 28.6.1: Gap Register CRUD ─────────────────────────
  async listGapRegisterEntries(params?: Record<string, any>) {
    return this.get(`/connectors/gap-register${this.buildQuery(params)}`);
  }
  async getGapRegisterEntry(id: string) {
    return this.get(`/connectors/gap-register/${id}`);
  }
  async createGapRegisterEntry(payload: Record<string, any>) {
    return this.post('/connectors/gap-register', payload);
  }
  async updateGapRegisterEntry(id: string, payload: Record<string, any>) {
    return this.patch(`/connectors/gap-register/${id}`, payload);
  }

  // ── Sprint 40.2.0: Data Subject Profiles ────────────────────
  async listDataSubjects(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DataSubjectProfile>> {
    return this.get(`/data-subjects${this.buildQuery(params)}`);
  }
  async createDataSubject(payload: Record<string, unknown>): Promise<DataSubjectProfile> {
    return this.post('/data-subjects', payload);
  }
  async getDataSubject(profileId: string): Promise<any> {
    return this.get(`/data-subjects/${profileId}`);
  }
  async updateDataSubject(profileId: string, payload: Record<string, unknown>): Promise<any> {
    return this.patch(`/data-subjects/${profileId}`, payload);
  }
  async deleteDataSubject(profileId: string): Promise<void> {
    return this.del(`/data-subjects/${profileId}`);
  }
  async linkCaseToProfile(profileId: string, caseId: string): Promise<any> {
    return this.post(`/data-subjects/${profileId}/link-case`, { case_id: caseId });
  }
  async linkWorkflowToProfile(profileId: string, workflowId: string): Promise<any> {
    return this.post(`/data-subjects/${profileId}/link-workflow`, { workflow_id: workflowId });
  }
  async getDataSubjectTimeline(profileId: string): Promise<DataSubjectTimelineEntry[]> {
    const res: any = await this.get(`/data-subjects/${profileId}/timeline`);
    return res.data ?? res;
  }
  // Sprint 43.6.0 — Web consent records for a data subject profile
  async listDataSubjectWebConsentRecords(
    profileId: string,
    params?: Record<string, string>,
  ): Promise<{ data: any[]; total: number; page: number; page_size: number }> {
    return this.get(`/data-subjects/${profileId}/web-consent-records${this.buildQuery(params)}`);
  }

  // ── Sprint 41.6.0 V4 — create onboarding approval ──────────────
  async createOnboardingApproval(
    caseId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.post(`/vendor-risk/onboarding-cases/${caseId}/approvals`, payload);
  }

  // ── Sprint 41.6.0 V6.7 — evidence bundle export ────────────────
  async exportVendorEvidenceBundle(caseId: string): Promise<AttachmentRef> {
    return this.post(`/vendor-risk/cases/${caseId}/export/evidence-bundle`);
  }

  // ── Sprint 43.1.0 batch 3 E6 — Rights Request export + auto-response ───
  // The legacy `exportCaseEvidencePack` (Sprint 41.6.0 V7.3) pointed at
  // `/cases/:id/export/evidence-pack` which produced a 7-field stub. The
  // assembly batch (43.1.0) splits this into two artefacts:
  //   - Case File PDF       (POST /export/pdf)
  //   - Phu luc VI ZIP pack (POST /export/zip)
  // and adds the auto-generate + cross-border-check endpoints below.
  async exportCasePdf(casePublicId: string): Promise<AttachmentRef> {
    const r = await this.post<{ data: AttachmentRef }>(
      `/rights-requests/cases/${casePublicId}/export/pdf`,
    );
    return r.data;
  }

  async exportCaseZip(casePublicId: string): Promise<AttachmentRef> {
    const r = await this.post<{ data: AttachmentRef }>(
      `/rights-requests/cases/${casePublicId}/export/zip`,
    );
    return r.data;
  }

  async autoGenerateResponse(
    casePublicId: string,
    opts: {
      template_type: string;
      locale: 'vi' | 'en';
      method: 'email' | 'portal' | 'postal' | 'in_person';
      recipient: { email?: string; portal_user_id?: string; postal_address?: string };
      additional_attachments?: Array<{ file_id: string; filename: string }>;
    },
  ): Promise<{
    subject: string;
    body: string;
    delivered_at: string;
    response_ref: AttachmentRef;
    delivery_evidence_ref?: AttachmentRef;
    placeholders_missing: string[];
    effective_method: 'email' | 'portal' | 'postal' | 'in_person';
  }> {
    const r = await this.post<{ data: any }>(
      `/rights-requests/cases/${casePublicId}/responses/auto-generate`,
      opts,
    );
    return r.data;
  }

  async checkCrossBorder(casePublicId: string): Promise<{
    triggered: boolean;
    reason: string;
    dossier_id?: string;
    subject_country: string | null;
    org_country: string | null;
  }> {
    const r = await this.get<{ data: any }>(
      `/rights-requests/cases/${casePublicId}/cross-border-check`,
    );
    return r.data;
  }

  /**
   * @deprecated Use `exportCasePdf` (richer 13-section PDF) and
   * `exportCaseZip` (Phu luc VI evidence pack) instead. Retained as a
   * thin alias so legacy call sites keep working until batch 4 rewires
   * `TicketDetailPage.handleExportPDF` / `handleExportZIP`.
   */
  async exportCaseEvidencePack(casePublicId: string): Promise<AttachmentRef> {
    return this.exportCasePdf(casePublicId);
  }

  // ── Sprint 41.6.0 V8.4 — incident report PDF export ────────────
  async exportIncidentReport(incidentId: string): Promise<AttachmentRef> {
    return this.post(`/incidents/${incidentId}/export/pdf`);
  }

  // ── Sprint 43.2.0 batch 4 (E5) — Breach-trigger actions ─────────
  //
  // `flagCaseAsBreach` flags a DSR case as breach-causing and starts
  // the 72h MPS filing clock (Mau so 08 / vn-mops-filing-rules.md
  // Rule 6). Permission-gated at BREACH_FLAG (DPO tier).
  //
  // `listCaseBreaches` drives the red banner at the top of
  // TicketDetailPage — non-empty list means the case has been flagged.
  async flagCaseAsBreach(
    casePublicId: string,
    breachType: 'misdirected_response' | 'unauthorised_disclosure' | 'identity_mismatch' | 'other',
    description?: string,
  ): Promise<{ breach_id: string; filing_deadline: string; filing_id?: string }> {
    const r = await this.post<{
      data: { breach_id: string; filing_deadline: string; filing_id?: string };
    }>(`/rights-requests/cases/${casePublicId}/flag-breach`, {
      breach_type: breachType,
      ...(description ? { description } : {}),
    });
    return r.data;
  }

  async listCaseBreaches(casePublicId: string): Promise<DataBreach[]> {
    const r = await this.get<{ data: DataBreach[] }>(
      `/rights-requests/cases/${casePublicId}/breaches`,
    );
    return r.data ?? [];
  }

  // ── Sprint 47.5.1 — Forseti Provider + Token Billing ─────────────
  // Family plan §6.2; engineering: docs/engineering/ai-legal-advisor.md §3+§4
  async forsetiProviderHealth(): Promise<{
    ok: boolean;
    provider?: string;
    last_checked?: string;
    reason?: string;
    help_url?: string;
  }> {
    return this.post('/llm-relay/health', {});
  }

  async forsetiSetApiKey(
    action: 'set' | 'clear',
    key?: string,
  ): Promise<{ ok: boolean; key_set: boolean }> {
    return this.post('/llm-relay/api-key', { action, ...(key ? { key } : {}) });
  }

  async forsetiListUsage(params?: {
    feature?: string;
    user_id?: string;
    from_date?: string;
    to_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: unknown[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.feature) qs.set('feature', params.feature);
    if (params?.user_id) qs.set('user_id', params.user_id);
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/llm-relay/usage${suffix}`);
  }

  async forsetiGetQuota(): Promise<{
    organization_id: string;
    period: string;
    bucket_total: number;
    bucket_consumed: number;
    bucket_remaining: number;
    last_reconciled_at: string;
    bundle_history: unknown[];
  }> {
    return this.get('/llm-relay/quota');
  }

  async forsetiQuotaHistory(): Promise<{ data: unknown[] }> {
    return this.get('/llm-relay/quota/history');
  }

  // Sprint 47.5.8 — Advisor audit lineage (cross-module Forseti graph)
  async getAdvisorLineage(params: LineageQuery): Promise<{
    data: import('@vn-grc/shared').LineageGraph;
    total_nodes: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    qs.set('record_type', params.record_type);
    qs.set('record_id', params.record_id);
    if (params.date_start) qs.set('date_start', params.date_start);
    if (params.date_end) qs.set('date_end', params.date_end);
    if (params.actor_user_id) qs.set('actor_user_id', params.actor_user_id);
    if (params.page) qs.set('page', String(params.page));
    if (params.page_size) qs.set('page_size', String(params.page_size));
    return this.get(`/audit/advisor-lineage?${qs}`);
  }

  async exportAdvisorLineage(
    /** Sprint 47.9.1 — XLSX added alongside PDF/CSV. */
    params: LineageQuery & { format: 'pdf' | 'csv' | 'xlsx' },
  ): Promise<{ data: import('@vn-grc/shared').AttachmentRef }> {
    const qs = new URLSearchParams();
    qs.set('record_type', params.record_type);
    qs.set('record_id', params.record_id);
    qs.set('format', params.format);
    return this.get(`/audit/advisor-lineage/export?${qs}`);
  }

  /**
   * Sprint 47.5.8.6 — list AI-generated evidence in actionable states.
   * Defaults to draft_ai_generated + pending_review.
   */
  async listEvidenceDrafts(params?: {
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ data: unknown[]; total: number; page: number; page_size: number }> {
    return this.get(`/evidence/drafts${this.buildQuery(params ?? {})}`);
  }

  async evidenceMoveToReview(evidenceId: string): Promise<{ data: unknown }> {
    return this.post(`/evidence/${evidenceId}/move-to-review`);
  }

  async evidenceApprove(evidenceId: string): Promise<{ data: unknown }> {
    return this.post(`/evidence/${evidenceId}/approve`);
  }

  async evidenceReject(evidenceId: string, reason: string): Promise<{ data: unknown }> {
    return this.post(`/evidence/${evidenceId}/reject`, { reason });
  }

  async forsetiQuotaOverride(targetId: string, reason: string): Promise<{ ok: boolean }> {
    return this.post('/llm-relay/quota/override', { target_id: targetId, reason });
  }

  async forsetiQuotaTopup(units: number, reason: string): Promise<unknown> {
    return this.post('/llm-relay/quota/topup', { units, reason });
  }

  // Sprint 48.5.x — self-service "Buy More" billing.
  // Flow A: open a Stripe checkout session via aesirx.io.
  async forsetiCreatePurchaseIntent(
    packageCode: import('@vn-grc/shared').ForsetiBundleCode,
    returnUrl?: string,
  ): Promise<{ checkout_url: string }> {
    return this.post('/llm-relay/quota/purchase-intent', {
      package_code: packageCode,
      ...(returnUrl ? { return_url: returnUrl } : {}),
    });
  }

  // Flow B: redeem a paid invoice into billing units. Returns the updated
  // quota counter (plus `already_redeemed` when the invoice was seen before).
  async forsetiRedeemInvoice(
    packageCode: import('@vn-grc/shared').ForsetiBundleCode,
    invoiceId: string,
  ): Promise<{
    bucket_total: number;
    bucket_remaining: number;
    already_redeemed?: boolean;
  }> {
    return this.post('/llm-relay/quota/redeem', {
      package_code: packageCode,
      invoice_id: invoiceId,
    });
  }

  async forsetiRunCostEstimate(
    feature: 'chat' | 'workflow' | 'tabular' | 'redline',
    targetId: string,
    inputCount: number,
  ): Promise<{
    target_id: string;
    estimated_billable_units: number;
    confidence: 'low' | 'medium' | 'high';
    input_count: number;
  }> {
    const qs = new URLSearchParams({
      feature,
      target_id: targetId,
      input_count: String(inputCount),
    });
    return this.get(`/llm-relay/run-cost-estimate?${qs}`);
  }

  // ── Sprint 47.9.7 — Forseti MCP connector (Phase 1, read-only) ───
  async mcpKeysList(): Promise<{ data: McpApiKeyListItem[]; total: number }> {
    return this.get('/mcp-connector/api-keys');
  }
  async mcpKeysIssue(payload: McpApiKeyIssueRequest): Promise<McpApiKeyIssueResponse> {
    return this.post('/mcp-connector/api-keys', payload);
  }
  async mcpKeysRevoke(keyId: string): Promise<void> {
    return this.del(`/mcp-connector/api-keys/${keyId}`);
  }
  async mcpKeysRotate(keyId: string): Promise<McpApiKeyRotateResponse> {
    return this.post(`/mcp-connector/api-keys/${keyId}/rotate`, {});
  }

  // ── Sprint 47.5.2 — Forseti Projects ─────────────────────────────
  // Family plan §3.1 + §6.3; engineering: docs/engineering/ai-legal-advisor.md §8
  async forsetiListProjects(params?: {
    shared_with_me?: boolean;
    owner_user_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: Array<import('@vn-grc/shared').Project>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.shared_with_me) qs.set('shared_with_me', '1');
    if (params?.owner_user_id) qs.set('owner_user_id', params.owner_user_id);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/projects${suffix}`);
  }

  async forsetiCreateProject(body: {
    name: string;
    description?: string;
    visibility?: import('@vn-grc/shared').ProjectVisibility;
    cm_number?: string;
  }): Promise<import('@vn-grc/shared').Project> {
    return this.post('/projects', body);
  }

  async forsetiGetProject(id: string): Promise<{
    project: import('@vn-grc/shared').Project;
    folders: Array<import('@vn-grc/shared').ProjectFolder>;
  }> {
    return this.get(`/projects/${id}`);
  }

  async forsetiUpdateProject(
    id: string,
    body: {
      name?: string;
      description?: string;
      visibility?: import('@vn-grc/shared').ProjectVisibility;
      cm_number?: string;
    },
  ): Promise<import('@vn-grc/shared').Project> {
    return this.patch(`/projects/${id}`, body);
  }

  async forsetiDeleteProject(id: string): Promise<void> {
    await this.del(`/projects/${id}`);
  }

  async forsetiCreateFolder(
    projectId: string,
    body: { name: string; parent_folder_id?: string },
  ): Promise<import('@vn-grc/shared').ProjectFolder> {
    return this.post(`/projects/${projectId}/folders`, body);
  }

  async forsetiUpdateFolder(
    projectId: string,
    folderId: string,
    body: { name?: string; parent_folder_id?: string | null },
  ): Promise<import('@vn-grc/shared').ProjectFolder> {
    return this.patch(`/projects/${projectId}/folders/${folderId}`, body);
  }

  async forsetiDeleteFolder(projectId: string, folderId: string): Promise<void> {
    await this.del(`/projects/${projectId}/folders/${folderId}`);
  }

  async forsetiListProjectDocuments(
    projectId: string,
    folderId?: string,
  ): Promise<{
    data: Array<import('@vn-grc/shared').ProjectDocumentMembership>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const suffix = folderId !== undefined ? `?folder_id=${encodeURIComponent(folderId)}` : '';
    return this.get(`/projects/${projectId}/documents${suffix}`);
  }

  async forsetiAttachDocument(
    projectId: string,
    body: { attachment_id: string; folder_id?: string; filename_cached: string },
  ): Promise<import('@vn-grc/shared').ProjectDocumentMembership> {
    return this.post(`/projects/${projectId}/documents`, body);
  }

  async forsetiUnattachDocument(projectId: string, attachmentId: string): Promise<void> {
    await this.del(`/projects/${projectId}/documents/${attachmentId}`);
  }

  async forsetiListProjectMembers(projectId: string): Promise<{
    data: Array<import('@vn-grc/shared').ProjectMember>;
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/projects/${projectId}/members`);
  }

  async forsetiAddProjectMember(
    projectId: string,
    body: {
      user_id?: string;
      org_group_id?: string;
      role: 'admin' | 'editor' | 'viewer';
    },
  ): Promise<import('@vn-grc/shared').ProjectMember> {
    return this.post(`/projects/${projectId}/members`, body);
  }

  async forsetiRemoveProjectMember(projectId: string, userId: string): Promise<void> {
    await this.del(`/projects/${projectId}/members/${userId}`);
  }

  // ── Sprint 47.5.3a — Forseti Document Review ─────────────────────
  // Family plan §6.4; engineering: docs/engineering/ai-legal-advisor.md §5
  // Sprint 47.8.0a — index of documents under Forseti review.
  async forsetiListDocumentsUnderReview(params?: { page?: number; page_size?: number }): Promise<{
    data: Array<{
      attachment_id: string;
      display_name: string;
      filename?: string;
      linked_record_type: string | null;
      linked_record_id: string | null;
      /**
       * Sprint 47.5.9.3.3 (T-3) — backend-resolved display label
       * (DPIA name, etc.). Optional — falls back to type · id.
       */
      linked_record_label?: string;
      pending_edits_count: number;
      last_activity_at: string;
    }>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/document-review/documents${suffix}`);
  }

  async forsetiListDocumentVersions(
    attachmentId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: Array<import('@vn-grc/shared').DocumentVersion>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/document-review/${attachmentId}/versions${suffix}`);
  }

  async forsetiCreateDocumentVersion(
    attachmentId: string,
    body: {
      // Sprint 47.9.3 — text_body is optional for the PDF / DOCX upload
      // path. When omitted, the backend extracts text from the
      // attachment bytes via PdfTextExtractor / extractDocxText. For
      // plaintext substrates (.txt / .md), the frontend continues to
      // pre-compute and submit text_body.
      text_body?: string;
      source?: import('@vn-grc/shared').DocumentVersionSource;
      display_name?: string;
      chat_message_id?: string;
    },
  ): Promise<import('@vn-grc/shared').DocumentVersion> {
    return this.post(`/document-review/${attachmentId}/versions`, body);
  }

  async forsetiPromoteDocumentVersion(
    attachmentId: string,
    versionId: string,
  ): Promise<{ ok: true }> {
    return this.post(`/document-review/${attachmentId}/versions/${versionId}/promote`, {});
  }

  async forsetiListDocumentEdits(
    attachmentId: string,
    params?: {
      status?: 'pending' | 'accepted' | 'rejected';
      page?: number;
      page_size?: number;
    },
  ): Promise<{
    data: Array<import('@vn-grc/shared').DocumentEdit>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/document-review/${attachmentId}/edits${suffix}`);
  }

  async forsetiProposeEdit(body: {
    attachment_id: string;
    version_id: string;
    change_id: string;
    deleted_text: string;
    inserted_text: string;
    context_before?: string;
    context_after?: string;
    chat_message_id?: string;
  }): Promise<{ edit_id: string }> {
    return this.post('/document-review/edits', body);
  }

  async forsetiAcceptEdit(editId: string): Promise<import('@vn-grc/shared').DocumentVersion> {
    return this.post(`/document-review/edits/${editId}/accept`, {});
  }

  async forsetiRejectEdit(editId: string): Promise<{ ok: true }> {
    return this.post(`/document-review/edits/${editId}/reject`, {});
  }

  // ── Sprint 47.4.5: Immutable Audit Trails (frontend bindings) ──────
  async getImmutableAuditSettings(): Promise<{ settings: ImmutableAuditTrailsOrgSettings }> {
    return this.get('/platform/immutable-audit-trails/settings');
  }
  async updateImmutableAuditSettings(
    patch: Partial<ImmutableAuditTrailsOrgSettings> & { tier_a_off_acknowledged?: boolean },
  ): Promise<{ settings: ImmutableAuditTrailsOrgSettings }> {
    return this.patch('/platform/immutable-audit-trails/settings', patch);
  }
  /**
   * Sprint 47.4.7 — rotate the AesirX credential. Server generates the
   * new credential_id + signing_secret; the signing_secret never reaches
   * the frontend.
   */
  async rotateImmutableAuditCredential(): Promise<{
    data: {
      credential_id: string;
      credential_id_display: string;
      rotated_at: string;
      grace_window_minutes: number;
    };
  }> {
    return this.post('/platform/immutable-audit-trails/credentials/rotate', {});
  }
  async listProofLinks(params?: Record<string, string>): Promise<{
    data: ProofLink[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/platform/immutable-audit-trails/proof-links${this.buildQuery(params)}`);
  }
  async getProofLink(
    proofLinkId: string,
  ): Promise<{ data: ProofLink; receipt: ProofReceipt | null }> {
    return this.get(`/platform/immutable-audit-trails/proof-links/${proofLinkId}`);
  }
  /**
   * Sprint 47.4.5 — batch lookup used by the Proof column on every audit
   * page (the global /audit + /audit/unified endpoints also support
   * `?with_proof=true` server-side; this batch is for per-module audit
   * pages and timelines that never had server-side joins).
   */
  async lookupProofLinksBySource(
    events: Array<{ source_collection?: string; source_event_id: string }>,
  ): Promise<{ data: Record<string, ProofLink> }> {
    return this.post('/platform/immutable-audit-trails/proof-links/lookup', { events });
  }
  async getIatUsageSummary(): Promise<{
    balance: number;
    current_month: {
      consumed: number;
      refunded: number;
      granted: number;
      adjustment: number;
      net: number;
    };
    period_start: string;
    period_end: string;
  }> {
    return this.get('/platform/immutable-audit-trails/usage/summary');
  }
  async getIatUsageHistory(params?: Record<string, string>): Promise<{
    data: ProofUsageRecord[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/platform/immutable-audit-trails/usage/history${this.buildQuery(params)}`);
  }
  async redeemIatCredits(body: {
    purchase_id: string;
    package: IatCreditPackageCode;
    units?: number;
    price_paid_usd?: number;
  }): Promise<{
    credit_package: ProofCreditPackage;
    balance: number;
    pricing_card: { units: number; price_per_unit_usd: number; total_usd: number };
  }> {
    return this.post('/platform/immutable-audit-trails/credits/redeem', body);
  }

  // Sprint 48.5.x — IAT self-service "Buy More" billing (mirrors Forseti).
  // Flow A: open a Stripe checkout session via aesirx.io.
  async iatCreatePurchaseIntent(
    packageCode: IatCreditPackageCode,
    returnUrl?: string,
  ): Promise<{ checkout_url: string }> {
    return this.post('/platform/immutable-audit-trails/credits/purchase-intent', {
      package: packageCode,
      ...(returnUrl ? { return_url: returnUrl } : {}),
    });
  }

  // Flow B: verify a customer-supplied invoice against aesirx.io and, if
  // paid + org + package match, credit the selected package into the ledger.
  async iatRedeemInvoice(body: { package: IatCreditPackageCode; invoice_id: string }): Promise<{
    credit_package: ProofCreditPackage;
    balance: number;
    pricing_card: { units: number; price_per_unit_usd: number; total_usd: number };
  }> {
    return this.post('/platform/immutable-audit-trails/credits/redeem-invoice', body);
  }
  async listIatAlerts(status?: 'active' | 'dismissed'): Promise<{ data: ProofAlert[] }> {
    const qs = status ? `?status=${status}` : '';
    return this.get(`/platform/immutable-audit-trails/alerts${qs}`);
  }
  async dismissIatAlert(alertId: string): Promise<{ data: ProofAlert }> {
    return this.post(`/platform/immutable-audit-trails/alerts/${alertId}/dismiss`, {});
  }

  // ── Sprint 47.4.6: IAT Integrity Center + per-link verification ────
  async verifyProofLink(proofLinkId: string): Promise<{
    data: {
      proof_link_id: string;
      proof_reference_id: string | null;
      module: string;
      event_type: string;
      proof_mode: 'direct' | 'batch';
      verdict: string;
      local: { verdict: string; expected_hash: string; recomputed_hash: string };
      service: { verdict: string; verification_ref: unknown; checked_at: string | null };
    };
  }> {
    return this.post(`/platform/immutable-audit-trails/proof-links/${proofLinkId}/verify`, {});
  }
  async startIntegrityRun(body: {
    period_start: string;
    period_end: string;
    modules?: string[];
  }): Promise<{ data: IntegrityRunRow }> {
    return this.post('/platform/immutable-audit-trails/integrity-center/runs', body);
  }
  async getIntegrityRun(runId: string): Promise<{ data: IntegrityRunRow }> {
    return this.get(`/platform/immutable-audit-trails/integrity-center/runs/${runId}`);
  }
  async listIntegrityIssues(
    runId: string,
    params?: { page?: string; page_size?: string; verdict?: string },
  ): Promise<{ data: IntegrityIssueRow[]; total: number; page: number; page_size: number }> {
    return this.get(
      `/platform/immutable-audit-trails/integrity-center/runs/${runId}/issues${this.buildQuery(params)}`,
    );
  }
  async exportIntegrityPack(body: {
    period_start: string;
    period_end: string;
    modules?: string[];
    framework_code?: string;
  }): Promise<{
    data: {
      pack_id: string;
      run: IntegrityRunRow;
      self_reference_audit_id: string | null;
      artifacts: {
        manifest: Record<string, unknown>;
        summary_html: string;
        summary_filename: string;
        manifest_filename: string;
      };
    };
  }> {
    return this.post(
      '/platform/immutable-audit-trails/integrity-center/integrity-pack/export',
      body,
    );
  }

  // ── Sprint 47.5.4a — Forseti chat shell ──────────────────────────
  // Family plan §6.5; engineering: docs/engineering/ai-legal-advisor.md §5–§9
  async forsetiListChats(params?: {
    all_users?: boolean;
    project_id?: string;
    parent_record_type?: string;
    parent_record_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: Array<import('@vn-grc/shared').ChatSession>;
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.all_users) qs.set('all_users', '1');
    if (params?.project_id) qs.set('project_id', params.project_id);
    if (params?.parent_record_type) qs.set('parent_record_type', params.parent_record_type);
    if (params?.parent_record_id) qs.set('parent_record_id', params.parent_record_id);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.get(`/ai-advisor/chats${suffix}`);
  }

  async forsetiCreateChat(body: {
    persona_slug?: import('@vn-grc/shared').ForsetiPersonaSlug;
    project_id?: string;
    parent_record?: import('@vn-grc/shared').ParentRecordRef;
    advisor_context?: import('@vn-grc/shared').AdvisorChatContext;
    title?: string;
  }): Promise<import('@vn-grc/shared').ChatSession> {
    return this.post('/ai-advisor/chats', body);
  }

  async forsetiGetChat(id: string): Promise<{
    chat: import('@vn-grc/shared').ChatSession;
    messages: Array<import('@vn-grc/shared').ChatMessage>;
  }> {
    return this.get(`/ai-advisor/chats/${id}`);
  }

  async forsetiUpdateChat(
    id: string,
    body: {
      title?: string;
      persona_slug?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<import('@vn-grc/shared').ChatSession> {
    return this.patch(`/ai-advisor/chats/${id}`, body);
  }

  async forsetiDeleteChat(id: string): Promise<void> {
    await this.del(`/ai-advisor/chats/${id}`);
  }

  async forsetiListPersonas(): Promise<{
    data: Array<{
      slug: import('@vn-grc/shared').ForsetiPersonaSlug;
      display_name: string;
      scope: string;
      equipped_skills: string[];
      preloaded_regulatory_packs?: string[];
      avatar_svg: string;
      greetings: Record<string, string>;
    }>;
  }> {
    return this.get('/ai-advisor/personas');
  }

  async forsetiReloadRegistry(): Promise<{
    personas_loaded: number;
    skills_loaded: number;
  }> {
    return this.post('/ai-advisor/registry/reload', {});
  }

  // ── Sprint 47.5.4c — transcript export + non-LLM create-task ────
  async forsetiExportChat(chatId: string): Promise<import('@vn-grc/shared').AttachmentRef> {
    return this.post(`/ai-advisor/chats/${chatId}/export`, {});
  }

  async forsetiCreateTaskFromChat(
    chatId: string,
    body: {
      title: string;
      description?: string;
      due_date?: string;
      assignee_user_id?: string;
      assignee_org_group_id?: string;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
      linked_workflow_id?: string;
    },
  ): Promise<{ task_id: string }> {
    return this.post(`/ai-advisor/chats/${chatId}/tasks`, body);
  }

  // ── Sprint 48.0.0b — Persona handoff (sub-agent orchestration) ──
  async forsetiSuggestHandoff(
    chatId: string,
    body: { question: string },
  ): Promise<{
    suggested_persona_slug: import('@vn-grc/shared').ForsetiPersonaSlug | null;
    reason: string | null;
    matched_rule_index: number;
  }> {
    return this.post(`/ai-advisor/chats/${chatId}/handoff/suggest`, body);
  }

  async forsetiHandoffChat(
    chatId: string,
    body: import('@vn-grc/shared').HandoffPayload,
  ): Promise<import('@vn-grc/shared').HandoffResponse> {
    return this.post(`/ai-advisor/chats/${chatId}/handoff`, body);
  }

  // ── Sprint 48.1.1 (finish) — DSR batch autocomplete ──────────────
  async dsrBatchAutocomplete(
    rows: Array<{
      request_id: string;
      requester_email: string;
      request_type:
        | 'access'
        | 'deletion'
        | 'correction'
        | 'objection'
        | 'portability'
        | 'restriction'
        | 'consent_withdrawal';
      deadline?: string;
      notes?: string;
    }>,
  ): Promise<{
    drafted_count: number;
    failed_count: number;
    drafts: Array<{
      request_id: string;
      draft_id: string;
      status: 'drafted' | 'failed';
      response_template?: string;
      frameworks_cited?: string[];
      verification_task_id?: string;
      billable_units?: number;
      error?: { code: string; message: string };
    }>;
  }> {
    return this.post('/cases/batch-autocomplete', { rows });
  }

  async dsrBatchAutocompleteDecision(
    draftId: string,
    accept: boolean,
  ): Promise<{ status: 'accepted' | 'rejected' }> {
    return this.post(`/cases/batch-autocomplete/${draftId}/decision`, { accept });
  }

  // ── Sprint 48.1.2 (finish) — RoPA hygiene autofix ──────────────
  async ropaAutofixDraft(
    activityId: string,
    fieldKey:
      | 'description'
      | 'purpose'
      | 'lawful_basis'
      | 'data_subjects'
      | 'data_categories'
      | 'recipients'
      | 'cross_border_transfers'
      | 'retention_period'
      | 'security_measures',
    frameworkCode: 'pdpl' | 'gdpr' | 'decree-165' | 'iso-27701',
  ): Promise<{
    draft_id: string;
    activity_id: string;
    field_key: string;
    framework_code: string;
    suggested_value: string;
    framework_citation: string;
    needs_human_input: boolean;
    human_input_message?: string;
  }> {
    return this.post(`/data-mapping/ropa-completeness/${activityId}/autofix/${fieldKey}`, {
      framework_code: frameworkCode,
    });
  }

  async ropaAutofixDecision(
    draftId: string,
    accept: boolean,
  ): Promise<{ status: 'accepted' | 'rejected' }> {
    return this.post(`/data-mapping/ropa-completeness/autofix/${draftId}/decision`, { accept });
  }

  // ── Sprint 48.0.0c — Forseti memory browse + curate ─────────────
  async forsetiMemoryList(params?: {
    category?: import('@vn-grc/shared').MemoryCategory;
    scope?: import('@vn-grc/shared').MemoryScope;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').OrgMemory[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.scope) q.set('scope', params.scope);
    if (params?.page) q.set('page', String(params.page));
    if (params?.page_size) q.set('page_size', String(params.page_size));
    const query = q.toString();
    return this.get(`/ai-advisor/memory${query ? `?${query}` : ''}`);
  }

  async forsetiMemoryUpdate(
    memoryId: string,
    body: {
      content?: string;
      category?: import('@vn-grc/shared').MemoryCategory;
      anchor?: string;
    },
  ): Promise<import('@vn-grc/shared').OrgMemory> {
    return this.patch(`/ai-advisor/memory/${memoryId}`, body);
  }

  async forsetiMemoryDelete(memoryId: string): Promise<void> {
    await this.del(`/ai-advisor/memory/${memoryId}`);
  }

  async forsetiMemoryPromote(memoryId: string): Promise<import('@vn-grc/shared').OrgMemory> {
    return this.post(`/ai-advisor/memory/${memoryId}/promote`, {});
  }

  // ── Sprint 47.5.5 — AI Workflows ────────────────────────────────
  async forsetiWorkflowsList(params?: {
    type?: 'assistant' | 'tabular';
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').Workflow[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.type) qs.set('type', params.type);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    return this.get(`/ai-workflows?${qs.toString()}`);
  }

  // Sprint 47.5.9.3.4 — closes finding 3.5; was previously achieved by
  // calling forsetiWorkflowsList() and finding by id, which scaled
  // poorly with workflow count.
  async forsetiWorkflowsGet(id: string): Promise<import('@vn-grc/shared').Workflow> {
    return this.get(`/ai-workflows/${encodeURIComponent(id)}`);
  }

  async forsetiWorkflowsCreate(body: {
    title: string;
    description?: string;
    type: import('@vn-grc/shared').WorkflowType;
    prompt_md: string;
    columns_config?: Record<string, unknown>[];
    persona_affinity?: import('@vn-grc/shared').ForsetiPersonaSlug;
    practice?: string;
    produces_tasks?: boolean;
    produces_evidence?: boolean;
  }): Promise<import('@vn-grc/shared').Workflow> {
    return this.post('/ai-workflows', body);
  }

  async forsetiWorkflowsUpdate(
    id: string,
    body: {
      title?: string;
      description?: string;
      prompt_md?: string;
      persona_affinity?: import('@vn-grc/shared').ForsetiPersonaSlug;
      practice?: string;
      produces_tasks?: boolean;
      produces_evidence?: boolean;
    },
  ): Promise<import('@vn-grc/shared').Workflow> {
    return this.patch(`/ai-workflows/${id}`, body);
  }

  async forsetiWorkflowsDelete(id: string): Promise<void> {
    await this.del(`/ai-workflows/${id}`);
  }

  async forsetiWorkflowsRun(
    id: string,
    body: {
      project_id?: string;
      attachment_ids?: string[];
      parent_record?: { type: import('@vn-grc/shared').ParentRecordRef['type']; id: string };
      persona_slug_override?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<{ run_id: string }> {
    return this.post(`/ai-workflows/${id}/run`, body);
  }

  async forsetiWorkflowsGetRun(runId: string): Promise<import('@vn-grc/shared').WorkflowRun> {
    return this.get(`/ai-workflows/runs/${runId}`);
  }

  async forsetiWorkflowsShare(
    id: string,
    body: {
      shared_with_user_ids?: string[];
      shared_with_org_group_ids?: string[];
      allow_edit: boolean;
    },
  ): Promise<void> {
    await this.post(`/ai-workflows/${id}/share`, body);
  }

  async forsetiWorkflowsListHidden(): Promise<{
    data: import('@vn-grc/shared').WorkflowHiddenEntry[];
  }> {
    return this.get('/ai-workflows/hidden');
  }

  async forsetiWorkflowsHide(workflowId: string): Promise<void> {
    await this.post('/ai-workflows/hidden', { workflow_id: workflowId });
  }

  async forsetiWorkflowsUnhide(workflowId: string): Promise<void> {
    await this.del(`/ai-workflows/hidden/${workflowId}`);
  }

  async forsetiWorkflowsReloadBuiltin(): Promise<{ loaded: number }> {
    return this.post('/ai-workflows/reload-builtin', {});
  }

  // ── Sprint 47.5.6 — Tabular Reviews ─────────────────────────────
  async forsetiTabularReviewsList(params?: {
    project_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').TabularReview[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.project_id) qs.set('project_id', params.project_id);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    return this.get(`/tabular-reviews?${qs.toString()}`);
  }

  async forsetiTabularReviewsCreate(body: {
    title: string;
    project_id?: string;
    attachment_ids?: string[];
  }): Promise<import('@vn-grc/shared').TabularReview> {
    return this.post('/tabular-reviews', body);
  }

  async forsetiTabularReviewsGet(id: string): Promise<{
    review: import('@vn-grc/shared').TabularReview;
    cells: import('@vn-grc/shared').TabularCell[];
    /**
     * Sprint 47.5.9.3.3 (T-3) — partial map of attachment_id → filename
     * projected by the backend so the TR table row header can render
     * filenames instead of raw ObjectIds. Optional + partial.
     */
    attachment_filenames?: Record<string, string>;
  }> {
    return this.get(`/tabular-reviews/${id}`);
  }

  async forsetiTabularReviewsUpdate(
    id: string,
    body: { title?: string; columns_config?: import('@vn-grc/shared').TabularColumn[] },
  ): Promise<import('@vn-grc/shared').TabularReview> {
    return this.patch(`/tabular-reviews/${id}`, body);
  }

  async forsetiTabularReviewsDelete(id: string): Promise<void> {
    await this.del(`/tabular-reviews/${id}`);
  }

  async forsetiTabularReviewsAddColumn(
    id: string,
    body: {
      name: string;
      prompt: string;
      return_format: import('@vn-grc/shared').TabularColumnReturnFormat;
      presets?: string[];
      citations_required?: boolean;
    },
  ): Promise<import('@vn-grc/shared').TabularColumn> {
    return this.post(`/tabular-reviews/${id}/columns`, body);
  }

  async forsetiTabularReviewsRemoveColumn(id: string, index: number): Promise<void> {
    await this.del(`/tabular-reviews/${id}/columns/${index}`);
  }

  async forsetiTabularReviewsAttachDocs(id: string, attachmentIds: string[]): Promise<void> {
    await this.post(`/tabular-reviews/${id}/documents`, { attachment_ids: attachmentIds });
  }

  async forsetiTabularReviewsDetachDoc(id: string, attachmentId: string): Promise<void> {
    await this.del(`/tabular-reviews/${id}/documents/${attachmentId}`);
  }

  async forsetiTabularReviewsRun(id: string): Promise<{ review_id: string; cell_count: number }> {
    return this.post(`/tabular-reviews/${id}/run`, {});
  }

  async forsetiTabularReviewsRunCell(
    id: string,
    documentId: string,
    columnIndex: number,
  ): Promise<{ review_id: string }> {
    return this.post(`/tabular-reviews/${id}/cells/${documentId}/${columnIndex}/run`, {});
  }

  async forsetiTabularReviewsCancel(id: string): Promise<{ ok: boolean }> {
    return this.post(`/tabular-reviews/${id}/cancel`, {});
  }

  async forsetiTabularReviewsGetCostEstimate(id: string): Promise<{
    estimated_billable_units: number;
    confidence: 'low' | 'medium' | 'high';
    target_id: string;
    organization_id: string;
    feature: string;
    input_count: number;
    created_at: string;
  }> {
    return this.get(`/tabular-reviews/${id}/export-cost-estimate`);
  }

  async forsetiTabularReviewsExport(
    id: string,
    format: 'xlsx' | 'csv' = 'xlsx',
  ): Promise<import('@vn-grc/shared').AttachmentRef> {
    return this.get(`/tabular-reviews/${id}/export?format=${format}`);
  }

  // ── Sprint 47.7.2 — Tabular cell discussion threads ─────────────
  async forsetiTabularReviewsThreadsSummary(reviewId: string): Promise<{
    data: import('@vn-grc/shared').TabularCellThreadSummary[];
    total: number;
    page: number;
    page_size: number;
  }> {
    return this.get(`/tabular-reviews/${reviewId}/threads-summary`);
  }

  async forsetiTabularReviewsCellThreadGet(
    reviewId: string,
    documentId: string,
    columnIndex: number,
  ): Promise<{ thread: import('@vn-grc/shared').TabularCellThread | null }> {
    return this.get(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread`,
    );
  }

  async forsetiTabularReviewsCellThreadPost(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    body_md: string,
  ): Promise<{ thread: import('@vn-grc/shared').TabularCellThread }> {
    return this.post(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread/posts`,
      { body_md },
    );
  }

  async forsetiTabularReviewsCellThreadEdit(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    postId: string,
    body_md: string,
  ): Promise<{ thread: import('@vn-grc/shared').TabularCellThread }> {
    return this.patch(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread/posts/${postId}`,
      { body_md },
    );
  }

  async forsetiTabularReviewsCellThreadDelete(
    reviewId: string,
    documentId: string,
    columnIndex: number,
    postId: string,
  ): Promise<void> {
    await this.del(
      `/tabular-reviews/${reviewId}/cells/${encodeURIComponent(documentId)}/${columnIndex}/thread/posts/${postId}`,
    );
  }

  // ── Sprint 47.7.2 — Workflow schedules ──────────────────────────
  async forsetiWorkflowSchedulesList(
    workflowId: string,
    params?: { page?: number; page_size?: number },
  ): Promise<{
    data: import('@vn-grc/shared').WorkflowSchedule[];
    total: number;
    page: number;
    page_size: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/ai-workflows/${workflowId}/schedules${tail}`);
  }

  async forsetiWorkflowSchedulesCreate(
    workflowId: string,
    body: {
      frequency: import('@vn-grc/shared').WorkflowScheduleFrequency;
      time_utc: string;
      timezone: string;
      day_of_week?: number;
      day_of_month?: number;
      project_id?: string;
      default_attachment_ids?: string[];
      default_parent_record?: import('@vn-grc/shared').ParentRecordRef;
      persona_slug_override?: import('@vn-grc/shared').ForsetiPersonaSlug;
    },
  ): Promise<import('@vn-grc/shared').WorkflowSchedule> {
    return this.post(`/ai-workflows/${workflowId}/schedules`, body);
  }

  async forsetiWorkflowSchedulesUpdate(
    scheduleId: string,
    body: Partial<{
      frequency: import('@vn-grc/shared').WorkflowScheduleFrequency;
      time_utc: string;
      timezone: string;
      day_of_week: number;
      day_of_month: number;
      status: import('@vn-grc/shared').WorkflowScheduleStatus;
      project_id: string;
      default_attachment_ids: string[];
    }>,
  ): Promise<import('@vn-grc/shared').WorkflowSchedule> {
    return this.patch(`/ai-workflows/schedules/${scheduleId}`, body);
  }

  async forsetiWorkflowSchedulesDelete(scheduleId: string): Promise<void> {
    await this.del(`/ai-workflows/schedules/${scheduleId}`);
  }

  async forsetiWorkflowSchedulesTrigger(scheduleId: string): Promise<{ run_id: string }> {
    return this.post(`/ai-workflows/schedules/${scheduleId}/trigger`, {});
  }

  // ── Sprint 47.7.3 — Cross-framework conflicts ──────────────────
  async forsetiCrossFrameworkConflictsList(params?: {
    framework_code?: string;
    topic?: string;
    severity?: import('@vn-grc/shared').FrameworkConflictSeverity;
    status?:
      | 'acknowledged'
      | 'unacknowledged'
      | import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    data: import('@vn-grc/shared').FrameworkConflictWithAcknowledgement[];
    total: number;
    page: number;
    page_size: number;
    /** Sprint 47.9.1 — installed framework codes for the org; frontend
     *  uses this to fall back when the conflict list is empty so the
     *  filter selectors still show useful options. */
    installed_frameworks: string[];
  }> {
    const qs = new URLSearchParams();
    if (params?.framework_code) qs.set('framework_code', params.framework_code);
    if (params?.topic) qs.set('topic', params.topic);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.page_size) qs.set('page_size', String(params.page_size));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return this.get(`/ai-advisor/cross-framework-conflicts${tail}`);
  }

  async forsetiCrossFrameworkConflictsGet(
    conflictId: string,
  ): Promise<import('@vn-grc/shared').FrameworkConflictWithAcknowledgement> {
    return this.get(`/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}`);
  }

  async forsetiCrossFrameworkConflictsAcknowledge(
    conflictId: string,
    body: {
      status: import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
      assigned_to_user_id?: string;
      due_date?: string;
      notes?: string;
      resolution_md?: string;
    },
  ): Promise<import('@vn-grc/shared').FrameworkConflictAcknowledgement> {
    return this.post(
      `/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}/acknowledge`,
      body,
    );
  }

  async forsetiCrossFrameworkConflictsUpdateAcknowledgement(
    conflictId: string,
    body: {
      status: import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
      assigned_to_user_id?: string;
      due_date?: string;
      notes?: string;
      resolution_md?: string;
    },
  ): Promise<import('@vn-grc/shared').FrameworkConflictAcknowledgement> {
    return this.patch(
      `/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}/acknowledge`,
      body,
    );
  }

  async forsetiCrossFrameworkConflictsExport(params?: {
    framework_code?: string;
    topic?: string;
    severity?: import('@vn-grc/shared').FrameworkConflictSeverity;
    status?:
      | 'acknowledged'
      | 'unacknowledged'
      | import('@vn-grc/shared').FrameworkConflictAcknowledgementStatus;
    q?: string;
    /** Sprint 47.9.1 — XLSX added alongside CSV. */
    format?: 'csv' | 'xlsx';
  }): Promise<import('@vn-grc/shared').AttachmentRef> {
    const qs = new URLSearchParams();
    qs.set('format', params?.format ?? 'csv');
    if (params?.framework_code) qs.set('framework_code', params.framework_code);
    if (params?.topic) qs.set('topic', params.topic);
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    return this.get(`/ai-advisor/cross-framework-conflicts/export?${qs.toString()}`);
  }

  async forsetiCrossFrameworkConflictsUnacknowledge(conflictId: string): Promise<void> {
    await this.del(
      `/ai-advisor/cross-framework-conflicts/${encodeURIComponent(conflictId)}/acknowledge`,
    );
  }

  // ── Sprint 58.x.x (Phase 25) — PDPL Evidence Pack frontend (Sprint E1) ──
  // Routes shipped in Sprints A–C: pack assembly + email draft + manual
  // sent-proof + customer-controlled provider config. Send/timeline land
  // in Sprint E2 (separate PR).

  async assemblePdplEvidencePack(
    input: PdplEvidencePackAssembleInput,
  ): Promise<PdplEvidencePackAssembleResult> {
    return this.post('/pdpl-evidence-packs/assemble', input);
  }
  async getPdplEvidencePackEligibility(
    filingId: string,
  ): Promise<PdplEvidencePackEligibilityContext> {
    return this.get(
      `/pdpl-evidence-packs/eligibility/dossier-filings/${encodeURIComponent(filingId)}`,
    );
  }
  async getPdplEvidencePack(packId: string): Promise<PdplEvidencePackAssemblyDoc> {
    return this.get(`/pdpl-evidence-packs/${encodeURIComponent(packId)}`);
  }

  async composePdplEmailDraft(
    packId: string,
    input: PdplEmailDraftComposeInputPayload,
  ): Promise<PdplEmailDraftComposeResult> {
    return this.post(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/email-drafts`, input);
  }
  async editPdplEmailDraft(
    packId: string,
    draftId: string,
    changes: PdplEmailDraftEditChanges,
  ): Promise<PdplEmailDraftComposeResult> {
    return this.patch(
      `/pdpl-evidence-packs/${encodeURIComponent(packId)}/email-drafts/${encodeURIComponent(draftId)}`,
      { changes },
    );
  }
  async listPdplEmailDrafts(packId: string): Promise<{ items: PdplEmailDraftDoc[] }> {
    return this.get(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/email-drafts`);
  }
  async uploadPdplSentProof(
    packId: string,
    input: PdplSentProofUploadPayload,
  ): Promise<PdplEmailSentProofUploadResult> {
    return this.post(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/sent-proof`, input);
  }
  async listPdplSentProofs(packId: string): Promise<{ items: PdplEmailSentProofDoc[] }> {
    return this.get(`/pdpl-evidence-packs/${encodeURIComponent(packId)}/sent-proofs`);
  }

  // ── Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + send-record timeline ──

  async sendPdplEvidencePackEmail(
    input: PdplEmailSendRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.post('/pdpl-evidence-packs/send', input);
  }
  async listPdplSendRecords(packId: string): Promise<{ items: PdplEmailSendRecord[] }> {
    return this.get(`/pdpl-evidence-packs/send-records?pack_id=${encodeURIComponent(packId)}`);
  }
  async getPdplSendRecord(sendId: string): Promise<PdplEmailSendRecord> {
    return this.get(`/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}`);
  }
  async recordPdplAuthorityResponse(
    sendId: string,
    input: PdplAuthorityResponseRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.post(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/authority-response`,
      input,
    );
  }
  async reclassifyPdplAuthorityResponse(
    sendId: string,
    input: PdplAuthorityResponseRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.patch(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/authority-response`,
      input,
    );
  }
  async recordPdplHardCopyFollowup(
    sendId: string,
    input: PdplHardCopyFollowupRequestPayload,
  ): Promise<PdplEmailSendRecord> {
    return this.post(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/hard-copy-followup`,
      input,
    );
  }
  async updatePdplHardCopyFollowup(
    sendId: string,
    input: PdplHardCopyFollowupUpdatePayload,
  ): Promise<PdplEmailSendRecord> {
    return this.patch(
      `/pdpl-evidence-packs/send-records/${encodeURIComponent(sendId)}/hard-copy-followup`,
      input,
    );
  }

  async listPdplEmailProviders(
    status?: PdplEmailProviderStatus,
  ): Promise<{ items: PdplEmailProviderConfigRedacted[] }> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.get(`/pdpl-evidence-packs/email-providers${query}`);
  }
  async getPdplEmailProvider(configId: string): Promise<PdplEmailProviderConfigRedacted> {
    return this.get(`/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}`);
  }
  async createPdplEmailProvider(
    input: PdplEmailProviderCreateInput,
  ): Promise<PdplEmailProviderConfigRedacted> {
    return this.post('/pdpl-evidence-packs/email-providers', input);
  }
  async acceptPdplEmailProviderConsent(
    configId: string,
    payload: { verbatim_text: string; locale: 'en' | 'vi' },
  ): Promise<PdplEmailProviderConfigRedacted> {
    return this.post(
      `/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}/consent`,
      payload,
    );
  }
  async testPdplEmailProviderConnection(
    configId: string,
  ): Promise<PdplEmailProviderConfigRedacted> {
    return this.post(
      `/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}/test-connection`,
      {},
    );
  }
  async disablePdplEmailProvider(configId: string): Promise<PdplEmailProviderConfigRedacted> {
    return this.post(
      `/pdpl-evidence-packs/email-providers/${encodeURIComponent(configId)}/disable`,
      {},
    );
  }
}

// ── Sprint 58.4.0 — PDPL frontend payload shapes ────────────────────
// Wire-format types the frontend uses for the PDPL endpoints. They mirror
// the backend shared types but adapt binary fields to base64 strings for
// JSON transmission.

export interface PdplEvidencePackAssembleInput {
  dossier_id: string;
  org_slug?: string;
  pack_type: 'dpia' | 'otia';
  parts: {
    cover_letter_pdf_base64: string;
    authority_form_pdf_base64: string;
    supporting_evidence: PdplPartFilePayload[];
    safeguards: PdplPartFilePayload[];
    prior_correspondence?: PdplPartFilePayload[];
    signature_evidence: PdplPartFilePayload[];
  };
  ai_assistance?: {
    ai_run_id: string;
    ai_workflow_template: string;
    ai_evidence_status: 'draft_ai_generated';
  };
}

export interface PdplPartFilePayload {
  filename: string;
  mime_type: string;
  base64: string;
}

export interface PdplEmailDraftComposeInputPayload {
  org_slug?: string;
  dossier_id: string;
  pack_sha256: string;
  pack_type: 'dpia' | 'otia';
  locale: 'en' | 'vi';
  overrides?: {
    subject?: string;
    body?: string;
    cover_letter_body_paragraph?: string;
    cover_letter_additional_notes?: string;
  };
}

export interface PdplEmailDraftEditChanges {
  subject?: string;
  body?: string;
  cover_letter_body_paragraph?: string | null;
  cover_letter_additional_notes?: string | null;
}

export interface PdplSentProofUploadPayload {
  proof_file: { filename: string; mime_type: string; base64: string };
  draft_id?: string | null;
  draft_version_at_send?: number | null;
  envelope: {
    from: string;
    to: string;
    subject_at_send: string;
    sent_at: string;
    attachment_sha256_at_send: string;
  };
}

// ── Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send wire payloads ──

export interface PdplEmailSendRequestPayload {
  pack_id: string;
  draft_id: string;
  provider_config_id: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  /** send_id of an earlier failed send this transmission is a retry of.
   *  When supplied, the backend verifies the referenced record belongs to
   *  the same org + pack + draft and had `outcome.status === 'failed'`
   *  before chaining the retry via `PdplEmailSendRecord.retry_of`. */
  retry_of_send_id?: string;
}

export interface PdplAuthorityResponseRequestPayload {
  classification:
    | 'acknowledgement'
    | 'acceptance'
    | 'rejection'
    | 'supplement_request'
    | 'no_response';
  received_at?: string | null;
  free_text?: string | null;
  evidence_file?: { filename: string; mime_type: string; base64: string };
  /** Only used on PATCH (reclassification). */
  reason?: string | null;
}

export interface PdplHardCopyFollowupRequestPayload {
  posted_at: string;
  courier: string;
  tracking_number: string;
  recipient: string;
  receipt_file?: { filename: string; mime_type: string; base64: string };
}

export interface PdplHardCopyFollowupUpdatePayload {
  delivered_at?: string | null;
  delivery_notes?: string | null;
  receipt_file?: { filename: string; mime_type: string; base64: string };
}

// ── Sprint 47.4.6: response shapes for the new IAT integrity routes ──
export interface IntegrityRunRow {
  run_id: string;
  org_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  period_start: string;
  period_end: string;
  modules: string[] | null;
  total_links: number;
  processed_links: number;
  summary: {
    total: number;
    verified: number;
    drifted: number;
    hash_mismatch: number;
    source_missing: number;
    merkle_mismatch: number;
    not_found: number;
    chain_unreachable: number;
    service_unreachable: number;
    pending: number;
  };
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
  triggered_by: string;
}

export interface IntegrityIssueRow {
  run_id: string;
  org_id: string;
  proof_link_id: string;
  proof_reference_id: string | null;
  module: string;
  event_type: string;
  proof_mode: 'direct' | 'batch';
  verdict: string;
  expected_hash: string;
  recomputed_hash: string;
  raised_at: string;
}
/** Singleton API client instance. */
export const apiClient = new ApiClient();
